import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import "./App.css";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { supabase } from "./lib/supabase";

const DIFFICULTY_COLORS = { easy: "#4ade80", moderate: "#e8c84a", hard: "#fb923c", expert: "#f87171" };

// ─── NEARBY / RECENTLY VISITED SUGGESTIONS ───────────────────────────────────



const ACTIVITY_SUGGESTIONS = {
  kids: ["Rocky Mountain National Park", "Denver Zoo", "Elitch Gardens", "Children's Museum Denver", "Butterfly Pavilion"],
  noKids: ["14er Hiking", "Craft Brewery Tour", "Red Rocks Concert", "Whitewater Rafting", "Escape Room Denver"],
  both: ["Garden of the Gods", "Mesa Verde", "Colorado River Rafting", "Maroon Bells Hike", "Black Canyon NP"]
};


// ─── PACK LIST TEMPLATES ─────────────────────────────────────────────────────
const PACK_LIST_TEMPLATES = {
  "🏖 Beach": ["Sunscreen SPF 50+", "Swimsuit", "Beach towel", "Flip flops", "Sunglasses", "Hat / sun hat", "After-sun lotion", "Waterproof phone case", "Snorkel gear", "Reusable water bottle"],
  "⛷ Ski / Winter": ["Ski jacket", "Thermal base layers", "Ski gloves", "Goggles", "Beanie", "Neck gaiter", "Hand warmers", "Lip balm (SPF)", "Boot dryer", "Après-ski shoes"],
  "🥾 Hiking": ["Trail map + compass", "Water filter / purification tablets", "First aid kit", "Trekking poles", "Headlamp + batteries", "High-energy snacks", "Blister kit", "Emergency whistle", "Rain layer", "Sunscreen"],
  "🌆 City Break": ["Comfortable walking shoes", "Day pack / crossbody bag", "Travel adapter", "Portable charger", "Reusable tote bag", "Umbrella", "Guidebook or offline maps", "Earplugs (for busy hotels)", "Laundry bag", "Snacks for transit"],
  "🌍 International": ["Passport + copies", "Travel insurance docs", "Visa / entry docs", "Local currency", "Travel adapter (multi-region)", "Prescription meds + extra supply", "International SIM / eSIM", "Photocopy of ID", "Vaccination records", "Emergency contact card"],
  "🏕 Camping": ["Tent + stakes + poles", "Sleeping bag (rated for temp)", "Sleeping pad", "Camping stove + fuel", "Cookware set", "Headlamp + extra batteries", "Fire starter / matches", "Bear canister or hang bag", "Biodegradable soap", "Tarp / rain fly"],
  "👶 Family / Kids": ["Snacks + juice boxes", "Baby wipes (always)", "Kids sunscreen", "Portable white noise machine", "Change of clothes × 2", "First aid kit", "Insect repellent", "Entertainment (tablets, books)", "Stroller / carrier", "Kids' melatonin"],
};

// ─── SHARED HELPERS ──────────────────────────────────────────────────────────


// Parse a comma-separated tag string into a trimmed, non-empty array.
const parseTags = str => str.split(",").map(t => t.trim()).filter(Boolean);

const STATE_NAMES = {
  "alabama":"AL","alaska":"AK","arizona":"AZ","arkansas":"AR","california":"CA",
  "colorado":"CO","connecticut":"CT","delaware":"DE","florida":"FL","georgia":"GA",
  "hawaii":"HI","idaho":"ID","illinois":"IL","indiana":"IN","iowa":"IA","kansas":"KS",
  "kentucky":"KY","louisiana":"LA","maine":"ME","maryland":"MD","massachusetts":"MA",
  "michigan":"MI","minnesota":"MN","mississippi":"MS","missouri":"MO","montana":"MT",
  "nebraska":"NE","nevada":"NV","new hampshire":"NH","new jersey":"NJ","new mexico":"NM",
  "new york":"NY","north carolina":"NC","north dakota":"ND","ohio":"OH","oklahoma":"OK",
  "oregon":"OR","pennsylvania":"PA","rhode island":"RI","south carolina":"SC",
  "south dakota":"SD","tennessee":"TN","texas":"TX","utah":"UT","vermont":"VT",
  "virginia":"VA","washington":"WA","west virginia":"WV","wisconsin":"WI","wyoming":"WY"
};

// Returns true if entry matches a lowercase search query against name/city/state/cuisine.
// Returns true if the destination conventionally uses miles (US, UK, Myanmar, Liberia).
function usesImperial(destination) {
  const d = (destination || "").toLowerCase();
  const usStates = [", al",", ak",", az",", ar",", ca",", co",", ct",", de",", fl",", ga",", hi",", id",", il",", in",", ia",", ks",", ky",", la",", me",", md",", ma",", mi",", mn",", ms",", mo",", mt",", ne",", nv",", nh",", nj",", nm",", ny",", nc",", nd",", oh",", ok",", or",", pa",", ri",", sc",", sd",", tn",", tx",", ut",", vt",", va",", wa",", wv",", wi",", wy",", dc"];
  if (usStates.some(s => d.includes(s))) return true;
  return ["usa","united states","united kingdom","uk","england","scotland","wales","northern ireland","myanmar","burma","liberia"].some(k => d.includes(k));
}

// State matching supports both abbreviation ("CO") and full name ("colorado").
function entryMatchesSearch(e, q) {
  const stateAbbr = (e.state || "").toLowerCase();
  const fullStateName = Object.keys(STATE_NAMES).find(k => STATE_NAMES[k] === e.state) || "";
  const stateMatches = stateAbbr.includes(q) || fullStateName.includes(q);
  return e.name.toLowerCase().includes(q) ||
    e.city?.toLowerCase().includes(q) ||
    stateMatches ||
    (e.cuisine || "").toLowerCase().includes(q);
}

// Normalise and bulk-import all places from a trip plan into the user's log.
async function importTripToLogs(trip, setEntries, setImportToLogsMsg, userId) {
  const days = trip.plan?.days || [];
  const tripEntries = days.flatMap(d => {
    const items = d.entries || d.items || [];
    return items.map((e, j) => ({
      ...e,
      id: "imp_" + (e.id || j) + "_" + Date.now(),
      userId: userId,
      verdict: null,
      rating: 0,
      notes: "",
      tags: e.tags || [],
      photos: [],
      type: e.type || "activity",
      city: e.city || trip.destination || "",
      country: e.country || "USA"
    }));
  }).filter(e => e.name && e.name.trim());
  if (tripEntries.length === 0) return;
  const { data, error } = await supabase.from("entries")
    .insert(tripEntries.map(e => localEntryToDb(e, userId)))
    .select();
  if (!error && data) {
    setEntries(prev => [...data.map(dbEntryToLocal), ...prev]);
  } else {
    setEntries(prev => [...tripEntries, ...prev]);
  }
  setImportToLogsMsg("✓ " + tripEntries.length + " place" + (tripEntries.length !== 1 ? "s" : "") + " imported — head to My Logs to add your ratings and notes!");
  setTimeout(() => setImportToLogsMsg(""), 5000);
}


const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5 MB

// ─── DAY MAP + COPY BUTTONS ───────────────────────────────────────────────────
const MAP_CAP = 10;
function DayMapButton({ day, onOpenMap, onDelete }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const places = (day.entries || day.items || []);
  const capped = places.slice(0, MAP_CAP);

  // Use stored coords when available so Google Maps gets exact pins, not name-guesses
  const mapUrl = "https://www.google.com/maps/dir/" +
    capped.map(e => e.lat != null && e.lon != null
      ? `${e.lat},${e.lon}`
      : encodeURIComponent([e.name, e.city].filter(Boolean).join(" "))
    ).join("/");

  const copyText = (day.name || ("Day " + day.day)) + "\n" +
    capped.map((e, i) => `${i + 1}. ${e.name}${e.city ? " — " + e.city : ""}${e.note || e.notes ? " (" + (e.note || e.notes) + ")" : ""}`).join("\n") +
    (places.length > MAP_CAP ? `\n(+ ${places.length - MAP_CAP} more)` : "");

  const handleCopy = () => {
    navigator.clipboard.writeText(copyText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      const estimatedHeight = onDelete ? 130 : 95;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < estimatedHeight
        ? Math.max(8, rect.top - estimatedHeight - 4)
        : rect.bottom + 4;
      setPos({ top, right: window.innerWidth - rect.right });
    }
    setOpen(v => !v);
  };

  if (places.length === 0 && !onDelete) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
      {places.length > 0 && (
        <button
          onClick={e => { e.stopPropagation(); if (onOpenMap) onOpenMap(); else window.open(mapUrl, "_blank"); }}
          style={{ display: "inline-flex", alignItems: "center", background: "#f5f5f5",
            border: "none", borderRadius: 8, padding: "5px 7px", fontSize: 15, color: "#555",
            textDecoration: "none", cursor: "pointer" }}
          title={places.length > MAP_CAP ? `Map shows first ${MAP_CAP} places` : "Open map"}>
          🗺
        </button>
      )}
      {(places.length > 0 || onDelete) && (
        <button ref={btnRef} onClick={toggleMenu}
          style={{ background: "#f5f5f5", border: "none", borderRadius: 8, padding: "5px 7px",
            fontSize: 16, color: "#555", cursor: "pointer", letterSpacing: 1, lineHeight: 1 }}>⋯</button>
      )}
      {open && createPortal(
        <div onClick={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}
          style={{ position: "fixed", top: pos.top, right: pos.right,
            background: "#fff", border: "1px solid #efefef", borderRadius: 12, overscrollBehavior: "contain",
            boxShadow: "0 4px 20px rgba(0,0,0,0.13)", zIndex: 99999, minWidth: 180,
            overflow: "hidden" }}>
          {places.length > 0 && (
            <a href={mapUrl} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
                fontSize: 13, color: "#222", cursor: "pointer", textDecoration: "none",
                borderBottom: "1px solid #f5f5f5" }}
              onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
              onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
              <span style={{ fontSize: 15 }}>🌐</span>
              Open in Google Maps
            </a>
          )}
          {places.length > 0 && (
            <div onClick={handleCopy}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
                fontSize: 13, color: copied ? "#22c55e" : "#222", cursor: "pointer",
                borderBottom: onDelete ? "1px solid #f5f5f5" : "none" }}
              onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
              onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
              <span style={{ fontSize: 15 }}>{copied ? "✓" : "📋"}</span>
              {copied ? "Copied!" : "Copy day"}
            </div>
          )}
          {onDelete && (
            <div onClick={() => { onDelete(); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
                fontSize: 13, color: "#dc2626", cursor: "pointer" }}
              onMouseEnter={ev => ev.currentTarget.style.background = "#fff5f5"}
              onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
              <span style={{ fontSize: 15 }}>🗑</span>
              Delete day
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── PHOTO GALLERY BUTTON + FULLSCREEN ───────────────────────────────────────
function PhotoStrip({ photos = [], inline = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const count = photos.length;
  const hasPhotos = count > 0;

  return (
    <>
      <button
        onClick={e => { e.stopPropagation(); if (hasPhotos) { setCurrent(0); setOpen(true); } }}
        disabled={!hasPhotos}
        style={{
          background: hasPhotos ? "#f8f8f8" : "#fafafa",
          border: `1px solid ${hasPhotos ? "#efefef" : "#f0f0f0"}`,
          borderRadius: 8,
          padding: inline ? "6px 14px" : "7px 14px",
          fontSize: 13,
          color: hasPhotos ? "#555" : "#bbb",
          cursor: hasPhotos ? "pointer" : "default",
          display: "inline-flex", alignItems: "center", gap: 6
        }}>
        🖼 {count > 0 ? `${count} Photo${count !== 1 ? "s" : ""}` : "Photos"}
      </button>

      {/* Full screen gallery */}
      {open && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          width: "100vw", height: "100vh",
          background: "#000", zIndex: 99999,
          display: "flex", flexDirection: "column",
          margin: 0, padding: 0
        }}>

          {/* Top bar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "20px 24px", flexShrink: 0 }}>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>
              {photos.length > 1 ? `${current + 1} / ${photos.length}` : ""}
            </div>
            <button onClick={() => setOpen(false)}
              style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%",
                width: 38, height: 38, color: "#fff", fontSize: 20, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          </div>

          {/* Image area */}
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", overflow: "hidden" }}
            onClick={() => setOpen(false)}>
            <img src={photos[current]} alt=""
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />

            {/* Left arrow — always show if not first */}
            {photos.length > 1 && current > 0 && (
              <button onClick={e => { e.stopPropagation(); setCurrent(current - 1); }}
                style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%",
                  width: 48, height: 48, color: "#fff", fontSize: 26, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
            )}

            {/* Right arrow */}
            {photos.length > 1 && current < photos.length - 1 && (
              <button onClick={e => { e.stopPropagation(); setCurrent(current + 1); }}
                style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%",
                  width: 48, height: 48, color: "#fff", fontSize: 26, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
            )}
          </div>

          {/* Thumbnail strip */}
          {photos.length > 1 && (
            <div style={{ display: "flex", gap: 8, padding: "16px 24px 28px",
              overflowX: "auto", justifyContent: "center", flexShrink: 0 }}>
              {photos.map((p, i) => (
                <img key={i} src={p} alt=""
                  onClick={() => setCurrent(i)}
                  style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, cursor: "pointer",
                    border: `2px solid ${i === current ? "#fff" : "transparent"}`,
                    opacity: i === current ? 1 : 0.45, flexShrink: 0, transition: "all 0.15s" }} />
              ))}
            </div>
          )}

          {/* Tap outside hint */}
          <div style={{ textAlign: "center", paddingBottom: 16, color: "rgba(255,255,255,0.3)", fontSize: 12 }}>
            Tap photo to close
          </div>
        </div>
      )}
    </>
  );
}

// ─── TAG BADGE COMPONENT ─────────────────────────────────────────────────────
const TAG_COLORS = {
  "Globe Trotter": "#e8c84a",
  "Foodie Oracle": "#f4845f",
  "Brew Connoisseur": "#7ab4a6",
  "Local Expert": "#a78bfa",
  "Ramen Pilgrim": "#fb923c",
  "Adventure Seeker": "#34d399",
  "Trail Blazer": "#60a5fa",
  "Coffee Pilgrim": "#d4a574",
  "Brunch Queen": "#f9a8d4",
  "Spice Hunter": "#ef4444",
  "Taco Tracker": "#fbbf24",
  "Street Food Sage": "#fb923c",
};

function TravelerTag({ tag }) {
  const color = TAG_COLORS[tag] || "#94a3b8";
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}55`,
      borderRadius: "20px", padding: "2px 10px", fontSize: "11px",
      fontFamily: "'DM Sans', sans-serif", fontWeight: 600, letterSpacing: "0.03em",
      whiteSpace: "nowrap"
    }}>{tag}</span>
  );
}

// ─── STAR RATING ─────────────────────────────────────────────────────────────
function StarRating({ value, onChange, size = 18 }) {
  return (
    <div style={{ display: "flex", gap: 3 }}>
      {[1,2,3,4,5].map(s => (
        <span key={s} onClick={() => onChange && onChange(s)}
          style={{ fontSize: size, cursor: onChange ? "pointer" : "default",
            color: s <= value ? "#e8c84a" : "#ddd", transition: "color 0.15s" }}>★</span>
      ))}
    </div>
  );
}

// ─── VERDICT BADGE ───────────────────────────────────────────────────────────
function VerdictBadge({ verdict }) {
  if (!verdict) return null;
  const config = verdict === "must_go"
    ? { label: "MUST GO", bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" }
    : { label: "SKIP IT", bg: "#fee2e2", color: "#dc2626", border: "#fecaca" };
  return (
    <span style={{
      background: config.bg, color: config.color, border: `1px solid ${config.border}`,
      borderRadius: "20px", padding: "2px 9px", fontSize: "10px",
      fontFamily: "'DM Sans', sans-serif", fontWeight: 700, letterSpacing: "0.06em"
    }}>{config.label}</span>
  );
}

// ─── COVER PHOTO WITH DRAG REPOSITION ────────────────────────────────────────
const _ENTRY_PHOTOS = {
  restaurant: [
    "photo-1569050467447-ce54b3bbc37d", // sushi close-up
    "photo-1555126634-323283e090fa", // pasta dish
    "photo-1565299624946-b28f40a0ae38", // pizza
    "photo-1567306226416-28f0efdc88ce", // tacos
    "photo-1540189549336-e6e99c3679fe", // salad bowl
    "photo-1504674900247-0877df9cc836", // steak
    "photo-1512621776951-a57141f2eefd", // veggie bowl
    "photo-1476224203421-9ac39bcb3327", // burger
  ],
  cafe: [
    "photo-1495474472287-4d71bcdd2085", // latte art
    "photo-1501339847302-ac426a4a7cbb", // coffee shop interior
    "photo-1509042239860-f550ce710b93", // espresso
    "photo-1461023058943-07fcbe16d735", // pastry & coffee
  ],
  brewery: [
    "photo-1535958636474-b021ee887b13", // craft beer pints
    "photo-1558642452-9d2a7deb7f62", // brewery interior
    "photo-1436076863939-06870fe779c2", // beer glasses
    "photo-1532634896-26909d0d4b89", // tap beer
  ],
  hotel: [
    "photo-1566073771259-6a8506099945", // hotel room
    "photo-1582719478250-c89cae4dc85b", // luxury hotel
    "photo-1445019980597-93fa8acb246c", // hotel lobby
  ],
  hiking: [
    "photo-1551632811-561732d1e306", // mountain trail
    "photo-1508193638397-1c4234db14d8", // forest path
    "photo-1464822759023-fed622ff2c3b", // mountain vista
    "photo-1486870591958-9b9d0d1dda99", // alpine hiking
  ],
  activity: [
    "photo-1476611338391-6f395a0dd82e", // national park
    "photo-1500534314209-a25ddb2bd429", // outdoor scenery
    "photo-1441974231531-c6227db76b6e", // forest walk
    "photo-1518495973542-4542c06a5843", // nature sunlight
  ],
};

function _entryFallbackPhoto(type, name) {
  const t = TYPE_NORMALIZE[type] || type || "restaurant";
  const pool = _ENTRY_PHOTOS[t] || _ENTRY_PHOTOS.restaurant;
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return `https://images.unsplash.com/${pool[hash % pool.length]}?w=600&q=80`;
}

function CoverPhoto({ entry, height = 120, onEdit, onPositionChange, repositioning = false, onDoneReposition, onCancelReposition }) {
  const hasUserPhoto = entry.photos && entry.photos.length > 0;
  const normPos = (p) => {
    if (p === "top") return 0;
    if (p === "bottom") return 100;
    if (typeof p === "number") return p;
    if (typeof p === "string" && p.includes("%")) return parseFloat(p) || 50;
    return 50;
  };
  const [livePos, setLivePos] = useState(null);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const dragRef = useRef({ startY: 0, startPos: 50 });
  const posY = livePos !== null ? livePos : normPos(entry.coverPosition);
  const [imgError, setImgError] = useState(false);

  // Drag is only active when the user has explicitly entered reposition mode
  const draggable = repositioning && hasUserPhoto && !!onPositionChange;

  const onPointerDown = (ev) => {
    if (!draggable) return;
    ev.currentTarget.setPointerCapture(ev.pointerId);
    const start = normPos(entry.coverPosition);
    dragRef.current = { startY: ev.clientY, startPos: start };
    setLivePos(start);
    setDragging(true);
  };
  const onPointerMove = (ev) => {
    if (!dragging) return;
    const delta = dragRef.current.startY - ev.clientY;
    const newPos = Math.max(0, Math.min(100, dragRef.current.startPos + delta * 0.6));
    setLivePos(newPos);
  };
  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    // Position is saved only when the user taps Done, not on release
  };
  const onLostPointerCapture = () => {
    setDragging(false);
  };

  const photoSrc = hasUserPhoto ? entry.photos[0] : _entryFallbackPhoto(entry.type, entry.name);

  return (
    <div ref={containerRef} style={{ position: "relative", height, overflow: "hidden", flexShrink: 0,
      cursor: draggable ? (dragging ? "grabbing" : "grab") : "default",
      touchAction: draggable ? "none" : "auto" }}
      onPointerDown={draggable ? onPointerDown : undefined}
      onPointerMove={draggable ? onPointerMove : undefined}
      onPointerUp={draggable ? onPointerUp : undefined}
      onPointerCancel={draggable ? onPointerUp : undefined}
      onLostPointerCapture={draggable ? onLostPointerCapture : undefined}>
      {!imgError ? (
        <img src={photoSrc} alt={entry.name}
          style={{ width: "100%", height: "100%", objectFit: "cover",
            objectPosition: `center ${posY}%`, userSelect: "none", pointerEvents: "none" }}
          onError={() => setImgError(true)} />
      ) : (() => {
        const t = TYPE_NORMALIZE[entry.type] || entry.type || "restaurant";
        const cfg = {
          restaurant: { bg: "linear-gradient(135deg, #fef3c7 0%, #f59e0b 100%)", icon: "🍽" },
          cafe:       { bg: "linear-gradient(135deg, #fde8d0 0%, #92400e 100%)", icon: "☕" },
          brewery:    { bg: "linear-gradient(135deg, #ede9fe 0%, #6d28d9 100%)", icon: "🍺" },
          hotel:      { bg: "linear-gradient(135deg, #dbeafe 0%, #1d4ed8 100%)", icon: "🏨" },
          hiking:     { bg: "linear-gradient(135deg, #d1fae5 0%, #065f46 100%)", icon: "🥾" },
          activity:   { bg: "linear-gradient(135deg, #e0f2fe 0%, #0369a1 100%)", icon: "🏔" },
        }[t] || { bg: "linear-gradient(135deg, #f3f4f6 0%, #6b7280 100%)", icon: "📍" };
        return (
          <div style={{ width: "100%", height: "100%", background: cfg.bg,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 36, pointerEvents: "none", userSelect: "none" }}>
            {cfg.icon}
          </div>
        );
      })()}
      {entry.isPrivate && (
        <span style={{ position: "absolute", top: 6, left: 6, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 9, borderRadius: 4, padding: "2px 6px", fontWeight: 600 }}>🔒 Private</span>
      )}
      {onEdit && (
        <button onClick={ev => { ev.stopPropagation(); onEdit(); }}
          style={{ position: "absolute", top: 6, right: 6, background: "rgba(255,255,255,0.85)", border: "1px solid #e8e8e8", borderRadius: 6, padding: "3px 7px", fontSize: 12, cursor: "pointer", color: "#555", lineHeight: 1 }}>
          ✏️
        </button>
      )}
      {repositioning && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.32)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", padding: "12px 12px 14px", pointerEvents: "none" }}>
          <div style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", color: "#fff", fontSize: 11, borderRadius: 6, padding: "4px 10px", fontFamily: "'DM Sans', sans-serif" }}>
            {dragging ? "↕ repositioning…" : "↕ Drag to reposition"}
          </div>
          <div style={{ display: "flex", gap: 8, pointerEvents: "auto" }}>
            <button onClick={ev => { ev.stopPropagation(); setLivePos(null); setDragging(false); onCancelReposition?.(); }}
              style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.45)", color: "#fff", borderRadius: 8, padding: "6px 18px", fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={ev => { ev.stopPropagation(); if (livePos !== null) onPositionChange?.(Math.round(livePos)); setLivePos(null); setDragging(false); onDoneReposition?.(); }}
              style={{ background: "#fff", border: "none", color: "#1a1a1a", borderRadius: 8, padding: "6px 18px", fontSize: 12, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", cursor: "pointer" }}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── EXPANDABLE NOTE ─────────────────────────────────────────────────────────
function ExpandableNote({ text, limit = 120 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > limit;
  return (
    <div
      onClick={isLong ? e => { e.stopPropagation(); setExpanded(x => !x); } : undefined}
      style={{ fontSize: 12, color: "#555", fontStyle: "italic", lineHeight: 1.5, marginTop: 4, cursor: isLong ? "pointer" : "default" }}>
      "{expanded || !isLong ? text : text.slice(0, limit) + "…"}"
      {isLong && <span style={{ color: "#bbb", fontSize: 10, marginLeft: 5, fontStyle: "normal" }}>{expanded ? "less ▲" : "more ▼"}</span>}
    </div>
  );
}

// ─── ACTIVITY CARD ──────────────────────────────────────────────────────────
const CATEGORY_ICON = { hiking: "🥾", kids: "👶", scenic: "🏞", outdoor: "⛺", national_park: "🏕", mountain: "⛰", nature_reserve: "🌿" };
const CATEGORY_LABEL = { hiking: "HIKING", kids: "KIDS", scenic: "SCENIC DRIVE", outdoor: "ADVENTURE", national_park: "NATIONAL PARK", mountain: "MOUNTAIN", nature_reserve: "NATURE RESERVE" };

function DifficultyBadge({ difficulty }) {
  const color = DIFFICULTY_COLORS[difficulty] || "#94a3b8";
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}55`,
      borderRadius: "4px", padding: "2px 8px", fontSize: "10px",
      fontFamily: "'DM Sans', sans-serif", fontWeight: 700, letterSpacing: "0.08em",
      textTransform: "uppercase"
    }}>{difficulty}</span>
  );
}

// eslint-disable-next-line no-unused-vars
function ActivityCard({ activity, user, compact = false, onEdit, onTogglePrivate, isPrivate, onAddPhoto }) {
  const icon = CATEGORY_ICON[activity.category] || "📍";
  const catLabel = CATEGORY_LABEL[activity.category] || "ACTIVITY";
  return (
    <div style={{
      background: "#fff", border: "1px solid #efefef", borderRadius: "12px",
      padding: compact ? "14px 16px" : "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      transition: "border-color 0.2s, transform 0.2s",
      cursor: "default"
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#34d39944"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#222"; e.currentTarget.style.transform = "none"; }}>

      {/* Category label */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ color: "#34d399", fontSize: 10, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.1em" }}>
          {icon} {catLabel}
        </span>
        {activity.kidFriendly && (
          <span style={{ background: "#60a5fa22", color: "#60a5fa", border: "1px solid #60a5fa55",
            borderRadius: "4px", padding: "2px 8px", fontSize: "10px",
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>
            👶 KID FRIENDLY
          </span>
        )}
      </div>

      {/* Title + difficulty */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <a href={`https://www.google.com/maps/search/${encodeURIComponent(activity.name + ' ' + (activity.city || ''))}`}
          target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          style={{ color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: compact ? 15 : 17, fontWeight: 600, textDecoration: "none" }}
          onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; e.currentTarget.querySelector('span').style.color = "#1a73e8"; }}
          onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; e.currentTarget.querySelector('span').style.color = "#4285f4"; }}>
          {activity.name} <span style={{ color: "#4285f4", fontSize: compact ? 12 : 13, fontWeight: 400 }}>↗</span>
        </a>
        <DifficultyBadge difficulty={activity.difficulty} />
      </div>

      <div style={{ color: "#666", fontSize: 12, marginTop: 3, fontFamily: "'DM Sans', sans-serif" }}>
        {activity.city}, {activity.state}
      </div>

      {/* Stats row */}
      {(activity.distance || activity.elevation || activity.elevGain) && (
        <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
          {activity.distance && (
            <div>
              <div style={{ color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600 }}>{activity.distance}</div>
              <div style={{ color: "#555", fontSize: 10, fontFamily: "'DM Sans', sans-serif" }}>DISTANCE</div>
            </div>
          )}
          {activity.elevation && (
            <div>
              <div style={{ color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600 }}>{activity.elevation}</div>
              <div style={{ color: "#555", fontSize: 10, fontFamily: "'DM Sans', sans-serif" }}>ELEVATION</div>
            </div>
          )}
          {activity.elevGain && (
            <div>
              <div style={{ color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600 }}>{activity.elevGain}</div>
              <div style={{ color: "#555", fontSize: 10, fontFamily: "'DM Sans', sans-serif" }}>ELEV GAIN</div>
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {activity.notes && !compact && (
        <div style={{ marginTop: 10, color: "#999", fontSize: 13, lineHeight: 1.5, fontStyle: "italic" }}>
          "{activity.notes}"
        </div>
      )}


      {/* User attribution */}
      {user && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6, borderTop: "1px solid #efefef", paddingTop: 10 }}>
          <span style={{ fontSize: 14 }}>{user.avatar}</span>
          <span
            onClick={e => { e.stopPropagation(); user.onViewProfile && user.onViewProfile(user.id); }}
            style={{ color: "#555", fontSize: 11, cursor: user.onViewProfile ? "pointer" : "default",
              textDecoration: user.onViewProfile ? "underline" : "none" }}>
            @{user.username}
          </span>
          <div style={{ marginLeft: 4, display: "flex", gap: 4 }}>
            {user.tags.slice(0,1).map(t => <TravelerTag key={t} tag={t} />)}
          </div>
        </div>
      )}

      {/* Edit / Private / Photo / Gallery */}
      {!compact && (onEdit || onAddPhoto) && (
        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {onEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit(); }}
              style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8, padding: "6px 14px",
                color: "#555", fontSize: 12, cursor: "pointer" }}>
              ✏️ Edit
            </button>
          )}
          {onTogglePrivate && (
            <button onClick={e => { e.stopPropagation(); onTogglePrivate(); }}
              style={{ background: isPrivate ? "#eff6ff" : "#f8f8f8",
                border: `1px solid ${isPrivate ? "#bfdbfe" : "#efefef"}`,
                borderRadius: 8, padding: "6px 14px",
                color: isPrivate ? "#3b82f6" : "#555", fontSize: 12, cursor: "pointer" }}>
              {isPrivate ? "🔒 Private" : "🌐 Public"}
            </button>
          )}
          {onAddPhoto && (
            <label style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8, padding: "6px 14px",
              color: "#555", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
              📷 Photo
              <input type="file" accept="image/*" style={{ display: "none" }} onClick={e => e.stopPropagation()}
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = ev => onAddPhoto(ev.target.result);
                  reader.readAsDataURL(file);
                  e.target.value = "";
                }} />
            </label>
          )}
          <PhotoStrip photos={activity.photos || []} inline />
        </div>
      )}
    </div>
  );
}

async function uploadPhoto(file, userId) {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${userId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from('photos').upload(path, file);
  if (error) return null;
  const { data } = supabase.storage.from('photos').getPublicUrl(path);
  return data?.publicUrl || null;
}

// ─── ADD ENTRY MODAL ─────────────────────────────────────────────────────────
function AddEntryModal({ onClose, onSave, entries, prefill, userId }) {
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const [form, setForm] = useState({
    name: prefill?.name || "",
    city: prefill?.city || "",
    state: prefill?.state || "",
    country: prefill?.country || "USA",
    type: prefill?.type || "restaurant",
    cuisine: prefill?.cuisine || "",
    dish: prefill?.dish || "",
    rating: prefill?.rating || 0,
    verdict: prefill?.verdict || "must_go",
    notes: prefill?.notes || prefill?.note || "",
    privateNote: "",
    tags: (prefill?.tags || []).join(", "),
    isPrivate: false,
    kidFriendly: prefill?.kidFriendly || false,
    visitDate: ""
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const locationBiasRef = useRef(null); // { lat, lon } from CitySearch

  const KNOWN_PLACES = {
    "pearl of siam": { name: "Pearl of Siam", city: "Aurora", state: "CO", country: "USA", cuisine: "Thai", type: "restaurant" },
    "el metate": { name: "El Metate Taco", city: "Aurora", state: "CO", country: "USA", cuisine: "Mexican", type: "restaurant" },
    "el metate taco": { name: "El Metate Taco", city: "Aurora", state: "CO", country: "USA", cuisine: "Mexican", type: "restaurant" },
    "sherpa house": { name: "Sherpa House", city: "Golden", state: "CO", country: "USA", cuisine: "Nepali / Tibetan", type: "restaurant" },
    "endless grind": { name: "Endless Grind Coffee", city: "Aurora", state: "CO", country: "USA", cuisine: "Coffee", type: "cafe" },
    "endless grind coffee": { name: "Endless Grind Coffee", city: "Aurora", state: "CO", country: "USA", cuisine: "Coffee", type: "cafe" },
    "rioja": { name: "Rioja", city: "Denver", state: "CO", country: "USA", cuisine: "Spanish Tapas", type: "restaurant" },
    "hop alley": { name: "Hop Alley", city: "Denver", state: "CO", country: "USA", cuisine: "Chinese", type: "restaurant" },
    "avery brewing": { name: "Avery Brewing", city: "Boulder", state: "CO", country: "USA", cuisine: "Craft Beer", type: "brewery" },
    "snooze": { name: "Snooze AM Eatery", city: "Denver", state: "CO", country: "USA", cuisine: "Breakfast", type: "restaurant" },
    "sushi den": { name: "Sushi Den", city: "Denver", state: "CO", country: "USA", cuisine: "Japanese", type: "restaurant" },
    "uncle ramen": { name: "Uncle Ramen", city: "Denver", state: "CO", country: "USA", cuisine: "Japanese Ramen", type: "restaurant" },
    "himchuli": { name: "Himchuli", city: "Aurora", state: "CO", country: "USA", cuisine: "Nepali / Indian", type: "restaurant" },
    "yak & yeti": { name: "Yak & Yeti", city: "Arvada", state: "CO", country: "USA", cuisine: "Nepali / Indian", type: "restaurant" },
    "kathmandu kitchen": { name: "Kathmandu Kitchen", city: "Aurora", state: "CO", country: "USA", cuisine: "Nepali", type: "restaurant" },
    "sweet bloom": { name: "Sweet Bloom Coffee", city: "Lakewood", state: "CO", country: "USA", cuisine: "Coffee", type: "cafe" },
    "huckleberry roasters": { name: "Huckleberry Roasters", city: "Denver", state: "CO", country: "USA", cuisine: "Coffee", type: "cafe" },
    "rosenberg's": { name: "Rosenberg's Bagels", city: "Denver", state: "CO", country: "USA", cuisine: "Deli / Bagels", type: "restaurant" },
    "star kitchen": { name: "Star Kitchen", city: "Denver", state: "CO", country: "USA", cuisine: "Chinese Dim Sum", type: "restaurant" },
    "clint's bakery": { name: "Clint's Bakery", city: "Breckenridge", state: "CO", country: "USA", cuisine: "Bakery / Coffee", type: "cafe" },
    "outer range brewing": { name: "Outer Range Brewing", city: "Frisco", state: "CO", country: "USA", cuisine: "Craft Beer", type: "brewery" },
    "domo": { name: "Domo", city: "Denver", state: "CO", country: "USA", cuisine: "Japanese Country Food", type: "restaurant" },
  };

  const nameDebounceRef = useRef(null);
  const nameInputRef = useRef(null);
  const [nameDropPos, setNameDropPos] = useState({ top: 0, left: 0, width: 0 });

  const OSM_TYPE_MAP = {
    // food & drink
    restaurant: "restaurant", fast_food: "restaurant", food_court: "restaurant", diner: "restaurant",
    bar: "brewery", pub: "brewery", brewery: "brewery", biergarten: "brewery",
    cafe: "cafe", coffee_shop: "cafe", bakery: "cafe",
    // hotel / lodging
    hotel: "hotel", hostel: "hotel", motel: "hotel", guest_house: "hotel", apartment: "hotel", chalet: "hotel", camp_site: "hotel",
    // activities / attractions
    park: "activity", nature_reserve: "activity", national_park: "activity", forest: "activity",
    hiking: "activity", trail: "activity", path: "activity", peak: "activity",
    sports_centre: "activity", fitness_centre: "activity", swimming_pool: "activity",
    attraction: "activity", viewpoint: "activity", museum: "activity", artwork: "activity",
    zoo: "activity", theme_park: "activity", water_park: "activity",
    // shopping / other → activity bucket
    mall: "activity", supermarket: "activity", shop: "activity", clothes: "activity",
  };
  const OSM_KEY_FALLBACK = { tourism: "hotel", leisure: "activity", natural: "activity", highway: "activity", shop: "activity" };

  const handleNameChange = (val) => {
    setForm(f => ({ ...f, name: val }));
    clearTimeout(nameDebounceRef.current);
    if (val.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    // Local matches first
    const q = val.toLowerCase();
    const localMatches = Object.entries(KNOWN_PLACES)
      .filter(([key]) => key.includes(q))
      .map(([, place]) => place)
      .filter((v, i, arr) => arr.findIndex(x => x.name === v.name) === i)
      .slice(0, 3);
    if (localMatches.length > 0) { setSuggestions(localMatches); setShowSuggestions(true); }
    // Then fetch from photon
    nameDebounceRef.current = setTimeout(() => {
      const bias = locationBiasRef.current;
      const biasParam = bias ? `&lat=${bias.lat}&lon=${bias.lon}` : "";
      fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(val)}&limit=8${biasParam}`)
        .then(r => r.json())
        .then(data => {
          const seen = new Set(localMatches.map(m => m.name.toLowerCase()));
          const apiMatches = (data.features || [])
            .filter(f => f.properties.name)
            .map(f => {
              const p = f.properties;
              const type = OSM_TYPE_MAP[p.osm_value] || OSM_KEY_FALLBACK[p.osm_key] || "activity";
              const city = p.city || p.town || p.village || p.municipality || p.county || p.locality || p.district || "";
              const state = p.state || "";
              const country = p.country || "";
              return { name: p.name, city, state, country, type, cuisine: p.osm_value || "" };
            })
            .filter(r => { const k = r.name.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
            .slice(0, 6);
          const all = [...localMatches, ...apiMatches];
          setSuggestions(all);
          setShowSuggestions(all.length > 0);
          if (nameInputRef.current) {
            const rect = nameInputRef.current.getBoundingClientRect();
            setNameDropPos({ top: rect.bottom, left: rect.left, width: rect.width });
          }
        })
        .catch(() => {});
    }, 350);
  };

  const selectSuggestion = (place) => {
    const KNOWN_TYPES = ["restaurant","hotel","brewery","cafe","activity","hiking"];
    const rawType = place.type || "";
    const normalizedType = KNOWN_TYPES.includes(rawType) ? rawType : (TYPE_NORMALIZE[rawType] || "activity");
    setForm(f => ({
      ...f,
      ...place,
      type: normalizedType,
      city: place.city || f.city,
      state: place.state || f.state,
      country: place.country || f.country,
    }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const inp = (field, label, placeholder, type = "text") => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</label>
      <input type={type} value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        placeholder={placeholder}
        style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
          padding: "11px 12px", color: "#000", fontSize: 14,
          marginTop: 4, boxSizing: "border-box", outline: "none" }} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 20, padding: "28px 28px 24px", width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", animation: "slideUp 0.22s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ color: "#000", fontFamily: "'Cormorant Garamond', 'Lora', serif", margin: 0, fontSize: 22, fontWeight: 600 }}>{prefill ? "Review & Log" : "Log a Place"}</h2>
          <button onClick={onClose} style={{ background: "#f5f5f5", border: "none", borderRadius: "50%", width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#666", fontSize: 20, cursor: "pointer", flexShrink: 0 }}>×</button>
        </div>



        <div style={{ height: 1, background: "#f0f0f0", marginBottom: 20 }} />

        <CitySearch onSelect={({ city, state, country, lat, lon }) => {
          setForm(f => ({ ...f, city, state, country }));
          locationBiasRef.current = { lat, lon };
        }} />

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: fieldErrors.name ? "#dc2626" : "#666", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>Place Name</label>
          <input ref={nameInputRef} value={form.name} onChange={e => { handleNameChange(e.target.value); setFieldErrors(fe => ({...fe, name: false})); }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            onFocus={() => form.name.length >= 2 && setShowSuggestions(true)}
            onKeyDown={e => e.key === "Escape" && setShowSuggestions(false)}
            placeholder="Search restaurant, café, park, trail…"
            autoComplete="off"
            style={{ width: "100%", background: "#f8f8f8", border: `1px solid ${fieldErrors.name ? "#dc2626" : "#efefef"}`, borderRadius: 10,
              padding: "11px 12px", color: "#000", fontSize: 14, marginTop: 4,
              boxSizing: "border-box", outline: "none" }} />
          {fieldErrors.name && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>Required</div>}
          {form.name.length >= 2 && showSuggestions && createPortal(
            <div onWheel={e => e.stopPropagation()} style={{ position: "fixed", top: nameDropPos.top + 4, left: nameDropPos.left, width: nameDropPos.width,
              background: "#fff", border: "1px solid #efefef", borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 99999, overflowY: "auto", maxHeight: 280, overscrollBehavior: "contain" }}>
              {suggestions.map((s, i) => (
                <div key={i} onMouseDown={() => selectSuggestion(s)}
                  style={{ padding: "12px 14px", cursor: "pointer", borderBottom: "1px solid #f5f5f5",
                    display: "flex", alignItems: "center", gap: 10 }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8f8f8"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                  <span style={{ fontSize: 18 }}>{{ restaurant:"🍽", brewery:"🍺", cafe:"☕", activity:"🏔" }[s.type] || "📍"}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "#000" }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{[s.city, s.state].filter(Boolean).join(", ")}{s.cuisine ? ` · ${s.cuisine}` : ""}</div>
                  </div>
                </div>
              ))}
              <a href={`https://www.google.com/maps/search/${encodeURIComponent(form.name)}`}
                target="_blank" rel="noopener noreferrer"
                onClick={() => setShowSuggestions(false)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
                  borderTop: suggestions.length > 0 ? "1px solid #f5f5f5" : "none",
                  textDecoration: "none", background: "#fafafa" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
                onMouseLeave={e => e.currentTarget.style.background = "#fafafa"}>
                <span style={{ fontSize: 18 }}>🔍</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#000" }}>Search "{form.name}" on Google Maps</div>
                  <div style={{ fontSize: 12, color: "#888" }}>Opens Google Maps to find the place</div>
                </div>
                <span style={{ marginLeft: "auto", fontSize: 12, color: "#aaa" }}>↗</span>
              </a>
            </div>,
            document.body
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <label style={{ color: fieldErrors.city ? "#dc2626" : "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>City</label>
            <input value={form.city} onChange={e => { setForm(f => ({...f, city: e.target.value})); setFieldErrors(fe => ({...fe, city: false})); }}
              style={{ width: "100%", background: "#f8f8f8", border: `1px solid ${fieldErrors.city ? "#dc2626" : "#efefef"}`, borderRadius: 8,
                padding: "10px 12px", color: "#000", fontSize: 14, marginTop: 4, boxSizing: "border-box", outline: "none", fontFamily: "'DM Sans', sans-serif" }} />
            {fieldErrors.city && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>Required</div>}
          </div>
          <div>
            <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>State</label>
            <input value={form.state} onChange={e => setForm(f => ({...f, state: e.target.value}))}
              style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                padding: "10px 12px", color: "#000", fontSize: 14, marginTop: 4, boxSizing: "border-box", outline: "none", fontFamily: "'DM Sans', sans-serif" }} />
          </div>
          <div>
            <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Country</label>
            <input value={form.country} onChange={e => setForm(f => ({...f, country: e.target.value}))}
              style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                padding: "10px 12px", color: "#000", fontSize: 14, marginTop: 4, boxSizing: "border-box", outline: "none", fontFamily: "'DM Sans', sans-serif" }} />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Visit Date <span style={{ color: "#bbb", fontWeight: 400 }}>— optional</span></label>
          <input type="date" value={form.visitDate}
            onChange={e => setForm(f => ({ ...f, visitDate: e.target.value }))}
            style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
              padding: "10px 12px", color: form.visitDate ? "#000" : "#aaa", fontSize: 14, marginTop: 4,
              boxSizing: "border-box", outline: "none", fontFamily: "'DM Sans', sans-serif" }} />
        </div>

        <div style={{ marginTop: 14, marginBottom: 14 }}>
          <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Type</label>
          <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            {["restaurant","hotel","brewery","cafe","activity","hiking"].map(t => (
              <button key={t} onClick={() => setForm(f => ({...f, type: t}))}
                style={{ background: form.type === t ? "#000" : "#f8f8f8",
                  border: `1px solid ${form.type === t ? "#000" : "#efefef"}`,
                  color: form.type === t ? "#fff" : "#555",
                  borderRadius: 6, padding: "6px 12px", cursor: "pointer",
                  fontSize: 12, fontFamily: "'DM Sans', sans-serif", textTransform: "capitalize" }}>
                {{ restaurant: "🍽", hotel: "🏨", brewery: "🍺", cafe: "☕", activity: "🏔", hiking: "🥾" }[t]} {t}
              </button>
            ))}
            <button onClick={() => setForm(f => ({...f, kidFriendly: !f.kidFriendly}))}
              style={{ background: form.kidFriendly ? "#fef9c3" : "#f8f8f8",
                border: `1px solid ${form.kidFriendly ? "#fde047" : "#efefef"}`,
                color: form.kidFriendly ? "#854d0e" : "#555",
                borderRadius: 6, padding: "6px 12px", cursor: "pointer",
                fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
              👶 Kid Friendly
            </button>
          </div>
        </div>

        {["restaurant","cafe","brewery"].includes(form.type) && inp("cuisine", "Cuisine / Category", "e.g. Spanish Tapas, IPA Brewery")}
        {["restaurant","cafe","brewery"].includes(form.type) && inp("dish", "Must-Try Dish or Drink", "What should everyone order?")}

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: fieldErrors.rating ? "#dc2626" : "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Rating</label>
          <StarRating value={form.rating} onChange={r => { setForm(f => ({...f, rating: r})); setFieldErrors(fe => ({...fe, rating: false})); }} size={24} />
          {fieldErrors.rating && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>Required</div>}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: fieldErrors.verdict ? "#dc2626" : "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Verdict</label>
          <div style={{ display: "flex", gap: 10, marginTop: 6, border: fieldErrors.verdict ? "1px solid #dc2626" : "none", borderRadius: fieldErrors.verdict ? 8 : 0, padding: fieldErrors.verdict ? 6 : 0 }}>
            {[["must_go","MUST GO","#4ade80"],["skip","SKIP IT","#f87171"]].map(([v,l,c]) => (
              <button key={v} onClick={() => { setForm(f => ({...f, verdict: v})); setFieldErrors(fe => ({...fe, verdict: false})); }}
                style={{ flex: 1, background: form.verdict === v ? c + "22" : "#f8f8f8",
                  border: `1px solid ${form.verdict === v ? c : "#efefef"}`,
                  color: form.verdict === v ? c : "#555",
                  borderRadius: 6, padding: "8px", cursor: "pointer",
                  fontSize: 12, fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}>
                {l}
              </button>
            ))}
          </div>
          {fieldErrors.verdict && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>Required</div>}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Notes</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
            placeholder="What made it special? What to avoid? Tips for others…"
            rows={3}
            style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
              padding: "10px 12px", color: "#000", fontSize: 13, marginTop: 4,
              boxSizing: "border-box", outline: "none", resize: "vertical",
              fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }} />
        </div>

        {inp("tags", "Tags", "date night, spicy, hidden gem (comma separated)")}

        {/* Photo upload */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ color: "#666", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 8 }}>Photos (optional)</label>
          {photos.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {photos.map((photo, i) => (
                <div key={photo.slice(-20)} style={{ position: "relative" }}>
                  <img src={photo} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid #efefef" }} />
                  <button onClick={() => setPhotos(p => p.filter((_, idx) => idx !== i))}
                    style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%",
                      background: "#000", border: "none", color: "#fff", fontSize: 11, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: uploadingPhotos ? "default" : "pointer",
            background: "#f8f8f8", border: "1px dashed #ddd", borderRadius: 8, padding: "10px 16px",
            fontSize: 13, color: "#555", width: "100%", justifyContent: "center" }}>
            {uploadingPhotos ? "Uploading…" : "📷 Add Photos"}
            <input type="file" accept="image/*" multiple style={{ display: "none" }} disabled={uploadingPhotos}
              onChange={async e => {
                const all = Array.from(e.target.files);
                const valid = all.filter(f => ALLOWED_IMAGE_TYPES.includes(f.type) && f.size <= MAX_PHOTO_SIZE);
                e.target.value = "";
                const rejected = all.length - valid.length;
                if (rejected > 0) {
                  const tooLarge = all.some(f => f.size > MAX_PHOTO_SIZE);
                  const wrongType = all.some(f => !ALLOWED_IMAGE_TYPES.includes(f.type));
                  setPhotoError(tooLarge && wrongType
                    ? "Some photos skipped — must be JPEG, PNG, WebP or GIF under 5 MB"
                    : tooLarge ? `${rejected === 1 ? "Photo" : "Some photos"} skipped — max 5 MB each`
                    : "Unsupported format — use JPEG, PNG, WebP, or GIF");
                } else {
                  setPhotoError(null);
                }
                if (!valid.length) return;
                setUploadingPhotos(true);
                const urls = await Promise.all(valid.map(f => uploadPhoto(f, userId)));
                const succeeded = urls.filter(Boolean);
                if (succeeded.length === 0) {
                  setPhotoError("Photos couldn't be uploaded. Check your connection and try again.");
                } else {
                  setPhotos(p => [...p, ...succeeded]);
                }
                setUploadingPhotos(false);
              }} />
          </label>
          {photoError && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>
              {photoError}
            </div>
          )}
        </div>

        {saveError && (
          <div style={{ marginBottom: 10, padding: "10px 12px", background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 8, color: "#dc2626", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
            {saveError}
          </div>
        )}
        <button onClick={async () => {
          const errs = { name: !form.name.trim(), city: !form.city.trim(), rating: !form.rating, verdict: !form.verdict };
          if (errs.name || errs.city || errs.rating || errs.verdict) { setFieldErrors(errs); return; }
          const newEntry = {
            ...form,
            id: "e" + Date.now(), userId: userId,
            photos,
            tags: parseTags(form.tags)
          };
          setSaveError(null);
          setSaving(true);
          const ok = await onSave(newEntry);
          setSaving(false);
          if (ok) onClose();
          else setSaveError("Couldn't save entry. Check your connection and try again.");
        }}
          disabled={saving}
          style={{ width: "100%", background: saving ? "#555" : "#000",
            border: "none", borderRadius: 8, padding: "13px", color: "#fff",
            fontSize: 15, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
            letterSpacing: "0.02em" }}>
          {saving ? "Saving…" : "Save to My Travels"}
        </button>
      </div>
    </div>
  );
}

// ─── LOCATION SEARCH (Log a Place) ──────────────────────────────────────────
function CitySearch({ onSelect }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = (q) => {
    if (!q || q.length < 2) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&layer=city&layer=county&layer=state&layer=country`)
      .then(r => r.json())
      .then(data => {
        const seen = new Set();
        const results = (data.features || []).map(f => {
          const p = f.properties;
          const [lon, lat] = f.geometry.coordinates;
          const city = p.name;
          const state = p.state || "";
          const country = p.country || "";
          const label = [city, state, country].filter(Boolean).join(", ");
          return { label, city, state, country, lat, lon };
        }).filter(r => {
          if (!r.label || seen.has(r.label)) return false;
          seen.add(r.label); return true;
        });
        setSuggestions(results);
        if (results.length > 0 && inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setDropPos({ top: rect.bottom, left: rect.left, width: rect.width });
        }
        setOpen(results.length > 0);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    setSelected(false);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 350);
  };

  const handleSelect = (item) => {
    setQuery(item.label);
    setSelected(true);
    onSelect(item);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>City / Area <span style={{ color: "#bbb", fontWeight: 400 }}>— pick first to narrow place search</span></label>
      <div style={{ position: "relative" }}>
        <input ref={inputRef} value={query} onChange={handleChange}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onFocus={() => suggestions.length > 0 && !selected && setOpen(true)}
          placeholder="e.g. Denver, Tokyo, Paris…"
          autoComplete="off"
          style={{ width: "100%", background: selected ? "#f0fdf4" : "#f8f8f8",
            border: `1px solid ${selected ? "#86efac" : "#efefef"}`, borderRadius: 8,
            padding: "10px 12px", color: "#000", fontSize: 14, marginTop: 4,
            boxSizing: "border-box", outline: "none" }}
        />
        {loading && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#aaa" }}>...</span>}
        {selected && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#22c55e" }}>✓</span>}
      </div>
      {open && suggestions.length > 0 && createPortal(
        <div onWheel={e => e.stopPropagation()} style={{ position: "fixed", top: dropPos.top + 4, left: dropPos.left, width: dropPos.width,
          background: "#fff", border: "1px solid #efefef", borderRadius: 8, zIndex: 99999,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)", overflow: "hidden" }}>
          {suggestions.map((s, i) => (
            <div key={i} onMouseDown={() => handleSelect(s)}
              style={{ padding: "10px 14px", fontSize: 13, color: "#222", cursor: "pointer",
                borderBottom: i < suggestions.length - 1 ? "1px solid #f5f5f5" : "none" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8f8f8"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              📍 {s.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── DESTINATION AUTOCOMPLETE ────────────────────────────────────────────────
function DestinationAutocomplete({ value, onChange, onSelect, placeholder, style, onKeyDown }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = (q) => {
    if (!q || q.length < 2) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=8&layer=city&layer=county&layer=district&layer=state&layer=country`)
      .then(r => r.json())
      .then(data => {
        const seen = new Set();
        const results = (data.features || [])
          .map(f => {
            const p = f.properties;
            const parts = [p.city || p.name];
            if (p.state && p.state !== parts[0]) parts.push(p.state);
            if (p.country && p.country !== parts[0]) parts.push(p.country);
            return parts.filter(Boolean).join(", ");
          })
          .filter(label => { if (seen.has(label)) return false; seen.add(label); return true; });
        setSuggestions(results);
        if (results.length > 0 && inputRef.current) {
          const r = inputRef.current.getBoundingClientRect();
          setDropPos({ top: r.bottom, left: r.left, width: r.width });
        }
        setOpen(results.length > 0);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 350);
  };

  const handleSelect = (label) => {
    onChange(label);
    onSelect && onSelect(label);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative" }}>
      <input ref={inputRef} value={value} onChange={handleChange}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || "City, country..."}
        style={style}
        autoComplete="off"
      />
      {loading && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#aaa" }}>...</span>}
      {open && suggestions.length > 0 && createPortal(
        <div onWheel={e => e.stopPropagation()} style={{ position: "fixed", top: dropPos.top + 4, left: dropPos.left, width: dropPos.width,
          background: "#fff", border: "1px solid #efefef", borderRadius: 8, zIndex: 99999,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)", overflow: "hidden" }}>
          {suggestions.map((s, i) => (
            <div key={i} onMouseDown={() => handleSelect(s)}
              style={{ padding: "10px 14px", fontSize: 13, color: "#222", cursor: "pointer", borderBottom: i < suggestions.length - 1 ? "1px solid #f5f5f5" : "none" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8f8f8"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              {s}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── PLACE NAME AUTOCOMPLETE (Nominatim — finds national parks, landmarks) ────
function PlaceNameAutocomplete({ value, onChange, onSelect, placeholder, style }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = (q) => {
    if (!q || q.length < 2) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=8&addressdetails=1`, {
      headers: { "Accept-Language": "en" }
    })
      .then(r => r.json())
      .then(data => {
        const seen = new Set();
        const results = (data || []).map(f => {
          const name = f.name || f.display_name.split(",")[0];
          const state = f.address?.state || "";
          const country = f.address?.country || "";
          const cls = f.class || "";
          const type = f.type || "";
          let typeLabel = null;
          if (cls === "boundary" || type === "national_park" || f.display_name?.toLowerCase().includes("national park")) typeLabel = "🏕 National Park";
          else if (cls === "natural" && ["peak","ridge","mountain"].includes(type)) typeLabel = "⛰ Mountain";
          else if (cls === "leisure" && type === "nature_reserve") typeLabel = "🌿 Nature Reserve";
          else if (cls === "tourism" && type === "attraction") typeLabel = "📍 Attraction";
          else if (cls === "place" && ["city","town","village"].includes(type)) typeLabel = "🏙 City";
          const label = [name, state, country].filter(Boolean).join(", ");
          return { label, typeLabel };
        }).filter(f => {
          if (seen.has(f.label)) return false;
          seen.add(f.label);
          return true;
        });
        setSuggestions(results);
        if (results.length > 0 && inputRef.current) {
          const r = inputRef.current.getBoundingClientRect();
          setDropPos({ top: r.bottom, left: r.left, width: r.width });
        }
        setOpen(results.length > 0);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 400);
  };

  const handleSelect = (label) => {
    onChange(label);
    onSelect && onSelect(label);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative", flex: 1 }}>
      <input ref={inputRef} value={value} onChange={handleChange}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder || "Search places, parks, restaurants…"}
        style={style}
        autoComplete="off"
      />
      {loading && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#aaa" }}>…</span>}
      {open && suggestions.length > 0 && createPortal(
        <div onWheel={e => e.stopPropagation()} style={{ position: "fixed", top: dropPos.top + 4, left: dropPos.left, width: dropPos.width,
          background: "#fff", border: "1px solid #efefef", borderRadius: 10, zIndex: 99999,
          boxShadow: "0 4px 20px rgba(0,0,0,0.13)", overflow: "hidden" }}>
          {suggestions.map((s, i) => (
            <div key={i} onMouseDown={() => handleSelect(s.label)}
              style={{ padding: "9px 14px", fontSize: 13, color: "#222", cursor: "pointer",
                borderBottom: i < suggestions.length - 1 ? "1px solid #f5f5f5" : "none",
                display: "flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8f8f8"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              {s.typeLabel && <span style={{ fontSize: 11, color: "#888", flexShrink: 0 }}>{s.typeLabel}</span>}
              <span>{s.label}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── ACCOMMODATION AUTOCOMPLETE (Nominatim — address-focused) ────────────────
function AccommodationAutocomplete({ value, onChange, onSelect, onBlur, onKeyDown, placeholder, inputStyle }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = (q) => {
    if (!q || q.length < 3) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=6&addressdetails=1`, {
      headers: { "Accept-Language": "en" }
    })
      .then(r => r.json())
      .then(data => {
        const seen = new Set();
        const results = (data || []).filter(f => {
          if (seen.has(f.display_name)) return false;
          seen.add(f.display_name);
          return true;
        }).map(f => {
          const name = f.name || f.display_name.split(",")[0];
          const addr = f.address;
          const parts = [addr?.road, addr?.city || addr?.town || addr?.village, addr?.country].filter(Boolean);
          return { name, address: parts.join(", "), full: f.display_name };
        });
        setSuggestions(results);
        if (results.length > 0 && inputRef.current) {
          const r = inputRef.current.getBoundingClientRect();
          setDropPos({ top: r.bottom, left: r.left, width: r.width });
        }
        setOpen(results.length > 0);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  };

  return (
    <div style={{ flex: 1, position: "relative" }}>
      <input
        ref={inputRef}
        autoFocus
        value={value}
        onChange={e => {
          onChange(e.target.value);
          clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 400);
        }}
        onBlur={() => { setTimeout(() => setOpen(false), 150); onBlur && onBlur(); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder || "Hotel, Airbnb, campsite…"}
        style={inputStyle}
        autoComplete="off"
      />
      {loading && <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#bbb" }}>…</span>}
      {open && suggestions.length > 0 && createPortal(
        <div onWheel={e => e.stopPropagation()} style={{ position: "fixed", top: dropPos.top + 4, left: dropPos.left, width: Math.max(dropPos.width, 260),
          background: "#fff", border: "1px solid #efefef", borderRadius: 10, zIndex: 99999,
          boxShadow: "0 4px 20px rgba(0,0,0,0.13)", overflow: "hidden" }}>
          {suggestions.map((s, i) => (
            <div key={i} onMouseDown={() => { onChange(s.address ? `${s.name}, ${s.address}` : s.name); onSelect && onSelect(s.address ? `${s.name}, ${s.address}` : s.name); setOpen(false); }}
              style={{ padding: "8px 14px", cursor: "pointer", borderBottom: i < suggestions.length - 1 ? "1px solid #f5f5f5" : "none" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8f8f8"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <div style={{ fontSize: 13, color: "#222", fontWeight: 500 }}>{s.name}</div>
              {s.address && <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>{s.address}</div>}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── TRIP PLANNER MODAL ──────────────────────────────────────────────────────

function TripPlannerModal({ entries, onClose, onSaveTrip, savedTrips, onUpdateTrip, editTrip }) {
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const isEditing = !!editTrip;
  const [prefs, setPrefs] = useState({ destination: "Colorado", days: 3, kids: false, types: [], invitedFriends: [], startDate: "", endDate: "" });
  // Manual plan state — pre-fill from editTrip if editing, or restore draft for new trips
  const [manualDestination, setManualDestination] = useState(() => {
    if (isEditing) return editTrip.destination || "";
    try { return JSON.parse(localStorage.getItem("ts_trip_draft") || "{}").destination || ""; } catch { return ""; }
  });
  const [manualDays, setManualDays] = useState(() => {
    if (isEditing) return String(editTrip.days || 3);
    try { return JSON.parse(localStorage.getItem("ts_trip_draft") || "{}").days || "3"; } catch { return "3"; }
  });
  const [manualStartDate, setManualStartDate] = useState(isEditing ? (editTrip.startDate || "") : "");
  const [manualEndDate, setManualEndDate] = useState(isEditing ? (editTrip.endDate || "") : "");
  const [manualSections, setManualSections] = useState(() => {
    if (isEditing && editTrip.plan?.days?.length) {
      return editTrip.plan.days.map((d, i) => ({
        id: "s" + (i + 1),
        name: d.name || d.activity || `Day ${i + 1}`,
        notes: d.sectionNotes || d.notes || "",
        items: (d.entries || d.items || []).map(e => ({ ...e }))
      }));
    }
    return Array.from({ length: 3 }, (_, i) => ({ id: "s" + (i + 1), name: `Day ${i + 1}`, items: [] }));
  });
  const [manualSearch, setManualSearch] = useState("");
  const [manualNotes, setManualNotes] = useState(() => {
    if (!isEditing) {
      try { return JSON.parse(localStorage.getItem("ts_trip_draft") || "{}").notes || ""; } catch { return ""; }
    }
    if (editTrip.draft && editTrip.ideas?.length)
      return editTrip.ideas.map(i => `- ${i.name}${i.notes ? ": " + i.notes : ""}`).join("\n");
    return editTrip.notes || "";
  });
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualGenerated, setManualGenerated] = useState(false);

  // Auto-save draft to localStorage for new trips (not edits)
  useEffect(() => {
    if (isEditing) return;
    try { localStorage.setItem("ts_trip_draft", JSON.stringify({ destination: manualDestination, days: manualDays, notes: manualNotes })); }
    catch { /* storage full — silently skip */ }
  }, [isEditing, manualDestination, manualDays, manualNotes]);

  const [googlePlacesResults, setGooglePlacesResults] = useState([]);
  const [googlePlacesLoading, setGooglePlacesLoading] = useState(false);
  const [searchDropPos, setSearchDropPos] = useState({ top: 0, left: 0, width: 0 });
  const searchDebounceRef = useRef(null);
  const searchInputRef = useRef(null);
  const dragItemRef = useRef(null);
  const generateAbortRef = useRef(null);
  useEffect(() => () => generateAbortRef.current?.abort(), []);



  const generateFromManualNotes = async () => {
    if (!manualNotes.trim()) return;
    setManualLoading(true);
    setManualError("");
    try {
      const n = parseInt(manualDays) || 3;
      const dest = manualDestination || "the destination";
      const distUnit = usesImperial(dest) ? "miles" : "km";
      const distLabel = distUnit === "miles" ? "mi" : "km";
      // Include any places already manually added to sections
      const existingPlaces = manualSections
        .flatMap(s => (s.items || []).map(item => `${item.name}${item.city ? " (" + item.city + ")" : ""} — already assigned to ${s.name}`))
        .join("\n");

      const genController = new AbortController();
      generateAbortRef.current = genController;
      const response = await fetch("/api/generate-itinerary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: genController.signal,
        body: JSON.stringify({
          model: "llama-3.3-70b-versatile",
          max_tokens: 3500,
          messages: [{
            role: "user",
            // User content is bracketed in XML tags so injected text cannot
            // break out of the <user_notes> context and override instructions.
            content: `You are an expert travel itinerary planner. Build a ${n}-day trip to ${dest} using the user's notes below.

Instructions:
1. Extract every place, restaurant, bar, attraction, or activity mentioned in the notes.
2. Group places by neighborhood or geographic area — places within walking distance or a short drive belong on the same day to minimize driving.
3. Name each day after its area: "Day 1 — Downtown & LoDo", "Day 2 — RiNo & Five Points", etc.
4. Order days logically as a loop: start near the arrival area, explore outward, end near departure.
5. 3–5 places per day, ordered by time of day: breakfast → lunch → afternoon activity → dinner → bar/evening.
6. For each place infer: type (restaurant/cafe/brewery/bar/hotel/activity/hiking/shopping), cuisine if food, city if mentioned.
7. If the user says "must go" or "highly recommend" for a place, set verdict to "must_go". Otherwise use "want_to_try".
8. If the user specifies a day for a place ("on day 2, go to X"), honor it.
9. If a city isn't mentioned for a place, default to ${dest}.
10. For each item in the notes, apply these rules:
    a. If it is a specific named place (e.g. "Napali Coast", "Blue Bottle Coffee") — use it exactly as-is.
    b. If it is a generic activity or category (e.g. "coffee", "beach", "hike", "food", "drive", "lunch") — suggest 1–2 real, well-known places at ${dest} that best match that activity. The place names must actually exist.
    c. If the user says "plan my full itinerary" or "suggest places" — generate a complete, realistic itinerary with real well-known places at ${dest} for each day.
    Never fabricate a place name that does not exist in the real world.${existingPlaces ? `\n11. These places are already in the plan — incorporate them into the appropriate day:\n${existingPlaces}` : ""}
11. For every place EXCEPT the first in each day, estimate the travel distance from the previous stop using ${distUnit} (e.g. "0.8 ${distLabel}") and approximate travel mode + time (e.g. "10 min walk" or "5 min drive"). Set distanceFromPrev and travelTimeFromPrev. Leave both null for the first place of each day.

User notes:
<user_notes>${manualNotes}</user_notes>

Return ONLY valid JSON — no explanation, no markdown fences, no backticks. Schema:
{"days":[{"id":"s1","name":"Day 1 — Neighborhood Name","items":[{"id":"i1_1","name":"Place Name","type":"restaurant","note":"short tip from the notes","city":"City Name","state":"CO","verdict":"must_go","cuisine":"Italian","distanceFromPrev":null,"travelTimeFromPrev":null}]}]}`
          }]
        })
      });
      if (!response.ok && response.status === 429) throw new Error("AI is over capacity right now. Wait a minute and try again.");
      const data = await response.json();
      if (data.error) {
        const t = data.error.type || "";
        if (t === "rate_limit_error" || t === "overloaded_error" || response.status === 429)
          throw new Error("AI is over capacity right now. Wait a minute and try again.");
        throw new Error(data.error.message || "AI service error. Please try again.");
      }
      const text = data.content?.[0]?.text || "";
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("AI response didn't contain valid JSON. Try simplifying your notes.");
      let parsed;
      try { parsed = JSON.parse(jsonMatch[0]); }
      catch { throw new Error("AI returned malformed JSON. Please try again."); }
      if (parsed.days && parsed.days.length > 0) {
        setManualSections(parsed.days.map((d, i) => ({
          id: d.id || ("s" + (i + 1)),
          name: d.name || `Day ${i + 1}`,
          notes: d.notes || d.sectionNotes || "",
          items: (d.items || d.entries || []).filter(e => e && e.name?.trim()).map(e => ({
            ...e,
            id: e.id || ("ai_" + Date.now() + "_" + Math.random().toString(36).slice(2)),
            distanceFromPrev: e.distanceFromPrev || null,
            travelTimeFromPrev: e.travelTimeFromPrev || null
          }))
        })));
        setManualGenerated(true);
      } else {
        throw new Error("AI returned an empty plan. Add more place names to your notes.");
      }
    } catch(err) {
      setManualError((err.message || "Could not generate itinerary. Check your connection and try again."));
    }
    setManualLoading(false);
  };



  // Manual mode helpers
  const addSection = () => setManualSections(s => [...s, { id: "s" + Date.now(), name: `Day ${s.length + 1}`, notes: "", items: [] }]);
  const addToSection = (sectionId, entry) => {
    setManualSections(s => s.map(sec => sec.id === sectionId ? { ...sec, items: [...sec.items, entry] } : sec));
  };
  const removeFromSection = (sectionId, entryId) => {
    setManualSections(s => s.map(sec => sec.id === sectionId ? { ...sec, items: sec.items.filter(e => e.id !== entryId) } : sec));
  };
  const updateSectionName = (sectionId, name) => {
    setManualSections(s => s.map(sec => sec.id === sectionId ? { ...sec, name } : sec));
  };
  const updateSectionNotes = (sectionId, notes) => {
    setManualSections(s => s.map(sec => sec.id === sectionId ? { ...sec, notes } : sec));
  };
  const deleteSection = (sectionId) => setManualSections(s => s.filter(sec => sec.id !== sectionId));

  const friendEntries = entries;
  const manualSearchResults = manualSearch.trim().length >= 2
    ? friendEntries.filter(e =>
        e.name.toLowerCase().includes(manualSearch.toLowerCase()) ||
        e.city?.toLowerCase().includes(manualSearch.toLowerCase()) ||
        e.state?.toLowerCase().includes(manualSearch.toLowerCase())
      ).slice(0, 8)
    : [];

  useEffect(() => {
    if (manualSearch.trim().length < 2) { setGooglePlacesResults([]); return; }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setGooglePlacesLoading(true);
      const results = await searchGooglePlaces(manualSearch, manualDestination);
      setGooglePlacesResults(results);
      setGooglePlacesLoading(false);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [manualSearch, manualDestination]);

  const friendsForDest = manualDestination.trim()
    ? entries.filter(e =>
        e.city?.toLowerCase().includes(manualDestination.toLowerCase()) ||
        e.state?.toLowerCase().includes(manualDestination.toLowerCase())
      )
    : [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 20, padding: "28px 28px 24px", width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto", animation: "slideUp 0.22s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ color: "#000", fontFamily: "'Cormorant Garamond', 'Lora', serif", margin: 0, fontSize: 22, fontWeight: 600 }}>
            Plan a Trip
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#aaa", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ height: 1, background: "#f0f0f0", marginBottom: 20 }} />

        {/* UNIFIED PLAN FORM */}
        <div>
            {/* Destination + Days */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>Destination</label>
                <DestinationAutocomplete value={manualDestination} onChange={setManualDestination}
                  placeholder="e.g. Denver, Paris..."
                  style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                    padding: "11px 12px", color: "#000", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>Days</label>
                <input type="number" min="1" value={manualDays} onChange={e => {
                  const n = parseInt(e.target.value) || 1;
                  setManualDays(e.target.value);
                  // Auto-fill end date when days + start are set
                  if (manualStartDate) {
                    const start = new Date(manualStartDate);
                    start.setDate(start.getDate() + n - 1);
                    setManualEndDate(start.toISOString().split("T")[0]);
                  }
                  if (!manualGenerated) setManualSections(Array.from({length: n}, (_, i) => manualSections[i] || { id: "s" + (i+1), name: `Day ${i+1}`, items: [] }));
                }}
                  placeholder="3"
                  style={{ width: 70, background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                    padding: "11px 12px", color: "#000", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>

            {/* Start + End date */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>Start Date (optional)</label>
                <input type="date" value={manualStartDate} onChange={e => {
                  setManualStartDate(e.target.value);
                  if (manualEndDate && e.target.value > manualEndDate) setManualEndDate("");
                  if (e.target.value && manualDays) {
                    const start = new Date(e.target.value);
                    start.setDate(start.getDate() + (parseInt(manualDays) || 1) - 1);
                    setManualEndDate(start.toISOString().split("T")[0]);
                  }
                }}
                  style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                    padding: "11px 12px", color: "#000", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>End Date (optional)</label>
                <input type="date" value={manualEndDate} min={manualStartDate || undefined} onChange={e => {
                  if (manualStartDate && e.target.value && e.target.value < manualStartDate) return;
                  setManualEndDate(e.target.value);
                  if (manualStartDate && e.target.value) {
                    const diff = Math.round((new Date(e.target.value) - new Date(manualStartDate)) / 86400000) + 1;
                    if (diff > 0) {
                      setManualDays(String(diff));
                      if (!manualGenerated) setManualSections(Array.from({length: diff}, (_, i) => manualSections[i] || { id: "s" + (i+1), name: `Day ${i+1}`, items: [] }));
                    }
                  }
                }}
                  style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                    padding: "11px 12px", color: "#000", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>

            {/* Kids + Interests */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>Traveling with Kids?</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[["yes", true], ["no", false]].map(([l, v]) => (
                    <button key={l} onClick={() => setPrefs(p => ({...p, kids: v}))}
                      style={{ flex: 1, background: prefs.kids === v ? "#000" : "#f8f8f8",
                        border: `1px solid ${prefs.kids === v ? "#000" : "#efefef"}`,
                        color: prefs.kids === v ? "#fff" : "#555",
                        borderRadius: 6, padding: "8px", cursor: "pointer", fontSize: 13 }}>
                      {l === "yes" ? "👨‍👩‍👧 Yes" : "🧳 No"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>Auto-fill from Network</label>
                <button onClick={() => {
                  const filteredEntries = entries.filter(e =>
                    e.verdict === "must_go" &&
                    (prefs.types.length === 0 || prefs.types.includes(e.type)) &&
                    (!manualDestination.trim() || e.city?.toLowerCase().includes(manualDestination.toLowerCase()) || e.state?.toLowerCase().includes(manualDestination.toLowerCase()))
                  );
                  const activities = prefs.kids ? ACTIVITY_SUGGESTIONS.kids : ACTIVITY_SUGGESTIONS.noKids;
                  const days = parseInt(manualDays) || 3;
                  setManualSections(Array.from({ length: days }, (_, i) => ({
                    id: "s" + (i + 1),
                    name: `Day ${i + 1}`,
                    notes: activities[i % activities.length] || "",
                    items: filteredEntries.slice(i * 2, i * 2 + 2)
                  })));
                  setManualGenerated(true);
                }}
                  style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef",
                    borderRadius: 6, padding: "8px", color: "#555", fontSize: 13, cursor: "pointer" }}>
                  ⚡ Auto-fill picks
                </button>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>Interests (optional)</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["restaurant","hotel","brewery","cafe","activity","hiking"].map(t => (
                  <button key={t} onClick={() => setPrefs(p => ({...p, types: p.types.includes(t) ? p.types.filter(x=>x!==t) : [...p.types, t]}))}
                    style={{ background: prefs.types.includes(t) ? "#000" : "#f8f8f8",
                      border: `1px solid ${prefs.types.includes(t) ? "#000" : "#efefef"}`,
                      color: prefs.types.includes(t) ? "#fff" : "#555",
                      borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12 }}>
                    {{ restaurant: "🍽", hotel: "🏨", brewery: "🍺", cafe: "☕", activity: "🏔", hiking: "🥾" }[t]} {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes field + AI generate */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>
                Your Notes {manualGenerated ? "(edit & re-generate anytime)" : "(optional)"}
              </label>
              <textarea
                value={manualNotes}
                onChange={e => setManualNotes(e.target.value)}
                placeholder={"Paste restaurant names, neighborhoods, friends' tips, Google Maps saves, or anything — AI will group them geographically into days.\n\nExample:\n- Milk Bar (RiNo) - must go\n- Day 2: Red Rocks amphitheater\n- great brunch spot: Denver Biscuit Co on Colfax"}
                rows={manualGenerated ? 3 : 5}
                style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                  padding: "12px", color: "#000", fontSize: 13, outline: "none",
                  resize: "vertical", lineHeight: 1.6, boxSizing: "border-box" }} />
              {manualError && (
                <div style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>{manualError}</div>
              )}
              {manualNotes.trim() && (
                <button onClick={generateFromManualNotes} disabled={manualLoading}
                  style={{ marginTop: 8, width: "100%", background: manualLoading ? "#888" : "#000",
                    border: "none", borderRadius: 8, padding: "11px", color: "#fff",
                    fontSize: 14, fontWeight: 600, cursor: manualLoading ? "wait" : "pointer" }}>
                  {manualLoading ? "AI is organizing…" : manualGenerated ? "✨ Re-generate from Notes" : "✨ Generate Days from Notes"}
                </button>
              )}
              {!manualNotes.trim() && !manualGenerated && (
                <button onClick={() => setManualGenerated(true)}
                  style={{ marginTop: 8, width: "100%", background: "#f8f8f8", border: "1px solid #efefef",
                    borderRadius: 8, padding: "11px", color: "#555", fontSize: 14, cursor: "pointer" }}>
                  Skip — Build Manually
                </button>
              )}
            </div>

            {manualGenerated && (
              <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 13, color: "#555" }}>✨ AI organized your notes — edit below or re-generate above</div>
                <button onClick={() => { setManualGenerated(false); setManualNotes(""); setManualSections(Array.from({ length: parseInt(manualDays) || 3 }, (_, i) => ({ id: "s" + (i + 1), name: `Day ${i + 1}`, items: [] }))); }}
                  style={{ background: "none", border: "none", color: "#aaa", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>
                  Clear all
                </button>
              </div>
            )}

            {/* Friends logs for destination */}
            {friendsForDest.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: "#888", fontWeight: 600, marginBottom: 8 }}>
                  👥 FROM YOUR NETWORK · {friendsForDest.length} places
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {friendsForDest.slice(0, 5).map(e => (
                    <div key={e.id} style={{ background: "#f8f8f8", borderRadius: 8, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 16 }}>{{ restaurant:"🍽", brewery:"🍺", cafe:"☕", activity:"🏔" }[e.type] || "📍"}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#000" }}>{e.name}</div>
                        <div style={{ fontSize: 12, color: "#888" }}>{e.city} · {e.cuisine || e.type}</div>
                      </div>
                      <select value="" onChange={ev => { if (ev.target.value) addToSection(ev.target.value, e); }}
                        style={{ background: "#000", color: "#fff", border: "none", borderRadius: 6, padding: "5px 8px", fontSize: 12, cursor: "pointer" }}>
                        <option value="" disabled>+ Add</option>
                        {manualSections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search friends logs + Google */}
            <div style={{ marginBottom: 16 }}>
              <input ref={searchInputRef} value={manualSearch}
                onChange={e => {
                  setManualSearch(e.target.value);
                  if (searchInputRef.current) {
                    const r = searchInputRef.current.getBoundingClientRect();
                    setSearchDropPos({ top: r.bottom, left: r.left, width: r.width });
                  }
                }}
                onFocus={() => {
                  if (searchInputRef.current) {
                    const r = searchInputRef.current.getBoundingClientRect();
                    setSearchDropPos({ top: r.bottom, left: r.left, width: r.width });
                  }
                }}
                placeholder="🔍 Search friends' logs or any place..."
                style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                  padding: "11px 12px", color: "#000", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              {googlePlacesLoading && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#888", padding: "8px 0" }}>Searching places…</div>
              )}
              {(manualSearchResults.length > 0 || googlePlacesResults.length > 0) && createPortal(
                <div style={{ position: "fixed", top: searchDropPos.top + 4, left: searchDropPos.left, width: searchDropPos.width,
                  background: "#fff", border: "1px solid #efefef", borderRadius: 10,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)", zIndex: 99999, maxHeight: 400, overflowY: "auto",
                  overscrollBehavior: "contain" }}
                  onWheel={e => e.stopPropagation()}>
                  {/* Google Places results — shown first */}
                  {googlePlacesResults.map(e => {
                    const typeIcon = { restaurant:"🍽", brewery:"🍺", cafe:"☕", activity:"🏔", hiking:"🥾", museum:"🏛", landmark:"🗺", hotel:"🏨" }[e.type] || "📍";
                    return (
                      <div key={e.id} style={{ padding: "12px 14px", borderBottom: "1px solid #f5f5f5" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{typeIcon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#000", marginBottom: 2 }}>{e.name}</div>
                            <div style={{ fontSize: 12, color: "#888" }}>
                              {e.city}{e.city && e.type ? " · " : ""}{e.type}
                              {e.rating ? <span style={{ marginLeft: 6, color: "#f59e0b", fontWeight: 600 }}>★ {e.rating}</span> : null}
                            </div>
                            {e.notes && <div style={{ fontSize: 11, color: "#bbb", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.notes}</div>}
                          </div>
                          <select value="" onChange={ev => { if (ev.target.value) addToSection(ev.target.value, e); }}
                            style={{ background: "#000", color: "#fff", border: "none", borderRadius: 6, padding: "5px 8px", fontSize: 12, cursor: "pointer", flexShrink: 0 }}>
                            <option value="" disabled>+ Add</option>
                            {manualSections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                  {/* Network results */}
                  {manualSearchResults.length > 0 && (
                    <>
                      <div style={{ padding: "6px 14px 4px", fontSize: 10, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.06em", borderTop: googlePlacesResults.length > 0 ? "1px solid #f0f0f0" : "none" }}>From your network</div>
                      {manualSearchResults.map(e => {
                        const typeIcon = { restaurant:"🍽", brewery:"🍺", cafe:"☕", activity:"🏔", hiking:"🥾", museum:"🏛", landmark:"🗺", hotel:"🏨" }[e.type] || "📍";
                        return (
                          <div key={e.id} style={{ padding: "12px 14px", borderBottom: "1px solid #f5f5f5" }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                              <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{typeIcon}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: "#000", marginBottom: 2 }}>{e.name}</div>
                                <div style={{ fontSize: 12, color: "#888" }}>
                                  {e.city}{e.city && (e.cuisine || e.type) ? " · " : ""}{e.cuisine || e.type}
                                  {e.rating ? <span style={{ marginLeft: 6, color: "#f59e0b", fontWeight: 600 }}>★ {e.rating}</span> : null}
                                </div>
                              </div>
                              <select value="" onChange={ev => { if (ev.target.value) addToSection(ev.target.value, e); }}
                                style={{ background: "#000", color: "#fff", border: "none", borderRadius: 6, padding: "5px 8px", fontSize: 12, cursor: "pointer", flexShrink: 0 }}>
                                <option value="" disabled>+ Add</option>
                                {manualSections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>,
                document.body
              )}
              {manualSearch.trim().length >= 2 && !googlePlacesLoading && manualSearchResults.length === 0 && googlePlacesResults.length === 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                  No matches found.{" "}
                  <a href={`https://www.google.com/maps/search/${encodeURIComponent(manualSearch + (manualDestination ? " " + manualDestination : ""))}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: "#000", fontWeight: 600, textDecoration: "underline" }}>
                    Search on Google Maps ↗
                  </a>
                </div>
              )}
            </div>

            {/* Trip sections */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Your Itinerary</div>
                <button onClick={addSection}
                  style={{ background: "none", border: "1px solid #efefef", borderRadius: 6, padding: "4px 12px",
                    fontSize: 12, color: "#555", cursor: "pointer" }}>
                  + Add Section
                </button>
              </div>
              {manualSections.map(section => (
                <div key={section.id} style={{ background: "#f8f8f8", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <input value={section.name} onChange={e => updateSectionName(section.id, e.target.value)}
                      style={{ flex: 1, background: "transparent", border: "none", fontSize: 14, fontWeight: 600, color: "#000", outline: "none" }} />
                    <button onClick={() => deleteSection(section.id)}
                      style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 16 }}>×</button>
                  </div>
                  <textarea
                    value={section.notes || ""}
                    onChange={e => updateSectionNotes(section.id, e.target.value)}
                    placeholder="Add notes for this day — ideas, timing, reminders..."
                    rows={2}
                    style={{ width: "100%", background: "#fff", border: "1px solid #efefef", borderRadius: 6,
                      padding: "8px 10px", fontSize: 12, color: "#555", outline: "none", resize: "none",
                      lineHeight: 1.5, boxSizing: "border-box", marginBottom: 8 }} />
                  {section.items.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#bbb", textAlign: "center", padding: "8px 0" }}>No places added yet</div>
                  ) : (
                    section.items.map((e, i) => (
                      <div key={i}
                        draggable
                        onDragStart={() => { dragItemRef.current = { sectionId: section.id, index: i }; }}
                        onDragOver={ev => ev.preventDefault()}
                        onDrop={() => {
                          const from = dragItemRef.current;
                          if (!from || from.sectionId !== section.id || from.index === i) return;
                          setManualSections(prev => prev.map(s => {
                            if (s.id !== section.id) return s;
                            const items = [...s.items];
                            const [moved] = items.splice(from.index, 1);
                            items.splice(i, 0, moved);
                            return { ...s, items };
                          }));
                          dragItemRef.current = null;
                        }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: i < section.items.length-1 ? "1px solid #efefef" : "none", cursor: "grab" }}>
                        <span style={{ color: "#ccc", fontSize: 12, cursor: "grab", flexShrink: 0 }}>⠿</span>
                        <select value={e.type || "activity"}
                          onChange={ev => setManualSections(prev => prev.map(s => s.id !== section.id ? s : {
                            ...s, items: s.items.map((it, j) => j === i ? {...it, type: ev.target.value} : it)
                          }))}
                          style={{ background: "none", border: "none", fontSize: 14, cursor: "pointer", padding: 0, color: "#555" }}>
                          <option value="restaurant">🍽</option>
                          <option value="hotel">🏨</option>
                          <option value="brewery">🍺</option>
                          <option value="cafe">☕</option>
                          <option value="activity">🏔</option>
                          <option value="hiking">🥾</option>
                        </select>
                        <input value={e.name}
                          onChange={ev => setManualSections(prev => prev.map(s => s.id !== section.id ? s : {
                            ...s, items: s.items.map((it, j) => j === i ? {...it, name: ev.target.value} : it)
                          }))}
                          style={{ flex: 1, fontSize: 13, color: "#000", background: "transparent", border: "none",
                            borderBottom: "1px dashed #e0e0e0", outline: "none", padding: "2px 0" }} />
                        <button onClick={() => removeFromSection(section.id, e.id)}
                          style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 14 }}>×</button>
                      </div>
                    ))
                  )}
                  {/* Quick add custom place */}
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <input placeholder="+ Add a place..."
                      style={{ flex: 1, background: "#fff", border: "1px solid #e8e8e8", borderRadius: 6,
                        padding: "6px 10px", fontSize: 12, color: "#000", outline: "none" }}
                      onKeyDown={ev => {
                        if (ev.key === "Enter" && ev.target.value.trim()) {
                          const name = ev.target.value.trim();
                          setManualSections(prev => prev.map(s => s.id !== section.id ? s : {
                            ...s, items: [...s.items, { id: "c_" + Date.now(), name, type: "activity", city: manualDestination, state: "", verdict: "must_go", cuisine: "" }]
                          }));
                          ev.target.value = "";
                        }
                      }} />
                  </div>
                </div>
              ))}
            </div>

            {!manualDestination.trim() && (
              <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 8, textAlign: "center" }}>
                Please enter a destination above first
              </div>
            )}
            <button onClick={() => {
              if (!manualDestination.trim()) return;
              const plan = {
                days: manualSections.map((s, i) => ({
                  day: i + 1,
                  city: manualDestination,
                  activity: s.name,
                  name: s.name,
                  sectionNotes: s.notes || "",
                  entries: (s.items || []).map((item, j) => ({
                    id: item.id || ("m_" + i + "_" + j),
                    name: item.name,
                    type: item.type || "activity",
                    city: item.city || manualDestination,
                    state: item.state || "",
                    verdict: item.verdict || "must_go",
                    cuisine: item.cuisine || "",
                    notes: item.note || item.notes || "",
                    lat: item.lat ?? null,
                    lon: item.lon ?? null,
                  }))
                }))
              };
              if (isEditing) {
                onUpdateTrip({ ...editTrip, destination: manualDestination, days: manualSections.length,
                  startDate: manualStartDate || null, endDate: manualEndDate || null,
                  notes: manualNotes || "", plan });
              } else {
                try { localStorage.removeItem("ts_trip_draft"); } catch { /* ignore */ }
                onSaveTrip({ id: "t" + Date.now(), destination: manualDestination, days: manualSections.length,
                  kids: prefs.kids, startDate: manualStartDate || null, endDate: manualEndDate || null,
                  createdAt: new Date().toLocaleDateString(), invitedFriends: [], sharedWith: [],
                  completed: false, notes: manualNotes || "", plan });
              }
              onClose();
            }}
              style={{ width: "100%", background: manualDestination.trim() ? "#000" : "#ccc",
                border: "none", borderRadius: 8, padding: 13,
                color: "#fff", fontSize: 15, fontWeight: 700, cursor: manualDestination.trim() ? "pointer" : "not-allowed" }}>
              {isEditing ? "Save Changes ✓" : "Save Trip ✓"}
            </button>
          </div>

      </div>
    </div>
  );
}

// ─── PROFILE VIEW ────────────────────────────────────────────────────────────
function ProfileView({ user, entries, onBack, savedTrips, onAddToTrip }) {
  const userEntries = entries.filter(e => e.userId === user.id);
  const cities = [...new Set(userEntries.map(e => e.city).filter(Boolean))];
  const isUS = e => !e.country || e.country === "USA" || e.country === "US" || e.country === "United States" || e.country === "United States of America";
  const states = [...new Set(userEntries.filter(isUS).map(e => e.state).filter(Boolean))];
  const normalizeCountry = c => (c === "US" || c === "United States" || c === "United States of America") ? "USA" : c;
  const countries = [...new Set(userEntries.map(e => e.country).filter(Boolean).map(normalizeCountry))];
  const [cityFilter, setCityFilter] = useState(null);
  const [stateFilter, setStateFilter] = useState(null);
  const [countryFilter, setCountryFilter] = useState(null);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [showStatePicker, setShowStatePicker] = useState(false);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [profileSearch, setProfileSearch] = useState("");
  const [verdictFilter, setVerdictFilter] = useState("all");

  const verdictTabs = [
    { id: "all",      label: "All",      color: "#555" },
    { id: "must_go",  label: "Must Go",  color: "#E8873A" },
    { id: "liked",    label: "Liked",    color: "#16a34a" },
    { id: "meh",      label: "Meh",      color: "#d97706" },
    { id: "skip",     label: "Skip",     color: "#ef4444" },
  ];

  const visibleEntries = userEntries.filter(e => {
    if (verdictFilter !== "all" && e.verdict !== verdictFilter) return false;
    if (cityFilter && e.city !== cityFilter) return false;
    if (stateFilter && e.state !== stateFilter) return false;
    if (countryFilter && normalizeCountry(e.country) !== countryFilter) return false;
    if (profileSearch.trim()) {
      const q = profileSearch.toLowerCase();
      return (e.name||"").toLowerCase().includes(q) || (e.city||"").toLowerCase().includes(q)
        || (e.cuisine||"").toLowerCase().includes(q) || (e.notes||"").toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#555", cursor: "pointer",
        fontSize: 19, fontWeight: 600, marginBottom: 20, padding: "4px 0" }}>
        Back
      </button>

      {/* Hero card */}
      <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 16,
        padding: 24, marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{ width: 60, height: 60, background: "#f0f0f0", border: "2px solid #efefef",
            borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28, flexShrink: 0, overflow: "hidden" }}>
            {(user.avatarImg || user.avatar_url)
              ? <img src={user.avatarImg || user.avatar_url} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : user.avatar}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#000", letterSpacing: "-0.02em" }}>{user.name}</div>
            <div style={{ color: "#aaa", fontSize: 12, marginBottom: 10 }}>@{user.username}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(user.tags || []).map(t => <TravelerTag key={t} tag={t} />)}
            </div>
          </div>
        </div>
        {user.bio && (
          <div style={{ color: "#666", fontSize: 13, marginTop: 14, lineHeight: 1.6, borderTop: "1px solid #f5f5f5", paddingTop: 14 }}>
            {user.bio}
          </div>
        )}
        <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
          {[
            { label: "Logs", value: userEntries.length, action: () => { setVerdictFilter("all"); setCityFilter(null); setStateFilter(null); setCountryFilter(null); setShowCityPicker(false); setShowStatePicker(false); setShowCountryPicker(false); }, active: verdictFilter === "all" && !cityFilter && !stateFilter && !countryFilter },
            { label: "Must Go", value: userEntries.filter(e => e.verdict === "must_go").length, action: () => { setVerdictFilter(verdictFilter === "must_go" ? "all" : "must_go"); setShowCityPicker(false); setShowStatePicker(false); setShowCountryPicker(false); }, active: verdictFilter === "must_go" },
            { label: "Cities", value: cities.length, action: () => { setShowCityPicker(s => !s); setShowStatePicker(false); setShowCountryPicker(false); }, active: showCityPicker || !!cityFilter },
            { label: "States", value: states.length, action: () => { setShowStatePicker(s => !s); setShowCityPicker(false); setShowCountryPicker(false); }, active: showStatePicker || !!stateFilter },
            { label: "Countries", value: countries.length, action: () => { setShowCountryPicker(s => !s); setShowCityPicker(false); setShowStatePicker(false); }, active: showCountryPicker || !!countryFilter },
          ].map(({ label, value, action, active }) => (
            <div key={label} onClick={action || undefined} style={{ cursor: action ? "pointer" : "default" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#000" }}>{value}</div>
              <div style={{ fontSize: 11, color: active ? "#000" : "#aaa", fontWeight: active ? 700 : 400, borderBottom: active ? "2px solid #000" : "2px solid transparent", paddingBottom: 1, marginTop: 1, transition: "all 0.15s" }}>{label}</div>
            </div>
          ))}
        </div>

        {showCityPicker && (
          <div style={{ marginTop: 14, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Cities Visited ({cities.length})</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {cityFilter && <button onClick={() => setCityFilter(null)} style={{ background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#555", cursor: "pointer" }}>✕ Clear</button>}
              {cities.map(c => {
                const count = userEntries.filter(e => e.city === c).length;
                return <button key={c} onClick={() => setCityFilter(cityFilter === c ? null : c)}
                  style={{ background: cityFilter === c ? "#000" : "#f8f8f8", border: `1px solid ${cityFilter === c ? "#000" : "#e8e8e8"}`, color: cityFilter === c ? "#fff" : "#444", borderRadius: 20, padding: "4px 12px", fontSize: 11, cursor: "pointer" }}>
                  📍 {c} <span style={{ opacity: 0.6 }}>({count})</span>
                </button>;
              })}
            </div>
          </div>
        )}

        {showStatePicker && (
          <div style={{ marginTop: 14, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>States Visited ({states.length})</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {stateFilter && <button onClick={() => setStateFilter(null)} style={{ background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#555", cursor: "pointer" }}>✕ Clear</button>}
              {states.map(s => {
                const count = userEntries.filter(e => e.state === s).length;
                return <button key={s} onClick={() => setStateFilter(stateFilter === s ? null : s)}
                  style={{ background: stateFilter === s ? "#000" : "#f8f8f8", border: `1px solid ${stateFilter === s ? "#000" : "#e8e8e8"}`, color: stateFilter === s ? "#fff" : "#444", borderRadius: 20, padding: "4px 12px", fontSize: 11, cursor: "pointer" }}>
                  🗺 {s} <span style={{ opacity: 0.6 }}>({count})</span>
                </button>;
              })}
            </div>
          </div>
        )}

        {showCountryPicker && (
          <div style={{ marginTop: 14, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Countries Visited ({countries.length})</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {countryFilter && <button onClick={() => setCountryFilter(null)} style={{ background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#555", cursor: "pointer" }}>✕ Clear</button>}
              {countries.map(c => {
                const count = userEntries.filter(e => e.country === c).length;
                return <button key={c} onClick={() => setCountryFilter(countryFilter === c ? null : c)}
                  style={{ background: countryFilter === c ? "#000" : "#f8f8f8", border: `1px solid ${countryFilter === c ? "#000" : "#e8e8e8"}`, color: countryFilter === c ? "#fff" : "#444", borderRadius: 20, padding: "4px 12px", fontSize: 11, cursor: "pointer" }}>
                  🌍 {c} <span style={{ opacity: 0.6 }}>({count})</span>
                </button>;
              })}
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 12 }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#aaa", pointerEvents: "none" }}>🔍</span>
        <input value={profileSearch} onChange={e => setProfileSearch(e.target.value)}
          placeholder="Search places, city, cuisine..."
          style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
            padding: "11px 12px 11px 34px", color: "#000", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
        {profileSearch && <button onClick={() => setProfileSearch("")}
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 16 }}>×</button>}
      </div>

      {/* Verdict filter tabs */}
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, marginBottom: 14 }}>
        {verdictTabs.map(v => {
          const count = v.id === "all" ? userEntries.length : userEntries.filter(e => e.verdict === v.id).length;
          const active = verdictFilter === v.id;
          return (
            <button key={v.id} onClick={() => setVerdictFilter(v.id)}
              style={{ background: active ? v.color : "#f8f8f8",
                border: `1.5px solid ${active ? v.color : "#e8e8e8"}`,
                color: active ? "#fff" : v.color === "#555" ? "#555" : v.color,
                borderRadius: 20, padding: "6px 14px", fontSize: 12, cursor: "pointer",
                whiteSpace: "nowrap", flexShrink: 0, fontWeight: active ? 700 : 500,
                display: "flex", alignItems: "center", gap: 5 }}>
              {v.label}
              <span style={{ background: active ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.07)",
                borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
        {visibleEntries.length} {verdictFilter === "all" ? "Logs" : verdictFilter.replace("_", " ")} {cityFilter ? `in ${cityFilter}` : stateFilter ? `in ${stateFilter}` : countryFilter ? `in ${countryFilter}` : ""}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {visibleEntries.length === 0 ? (
          <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "52px 20px", color: "#aaa", background: "#fafafa", borderRadius: 16, border: "1px dashed #e8e8e8" }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#555", marginBottom: 4 }}>No logs match your filters</div>
          </div>
        ) : visibleEntries.map(e => {
          const hasUserPhoto = e.photos && e.photos.length > 0;
          const profileTypeKey = TYPE_NORMALIZE[e.type] || e.type || "restaurant";
          const profileCfg = EXPLORE_GRADIENT[profileTypeKey] || { bg: "linear-gradient(135deg, #f3f4f6 0%, #6b7280 100%)", icon: "📍" };
          return (
          <div key={e.id} style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 14, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
            <div style={{ position: "relative" }}>
              {hasUserPhoto ? (
                <CoverPhoto entry={e} height={180} />
              ) : (
                <div style={{ height: 180, background: profileCfg.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56, userSelect: "none" }}>
                  {profileCfg.icon}
                </div>
              )}
              {e.verdict && (
                <span style={{ position: "absolute", bottom: 8, left: 8,
                  background: e.verdict === "must_go" ? "rgba(21,128,61,0.88)" : "rgba(220,38,38,0.82)",
                  color: "#fff", borderRadius: 20, padding: "3px 9px", fontSize: 10,
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 700, letterSpacing: "0.06em",
                  backdropFilter: "blur(4px)" }}>
                  {e.verdict === "must_go" ? "MUST GO" : "SKIP IT"}
                </span>
              )}
            </div>
            <div style={{ padding: "14px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 4 }}>
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(e.name + ' ' + (e.city || ''))}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={ev => ev.stopPropagation()}
                  style={{ color: "#000", fontSize: 14, fontWeight: 700, textDecoration: "none", lineHeight: 1.3 }}
                  onMouseEnter={ev => ev.currentTarget.style.textDecoration = "underline"}
                  onMouseLeave={ev => ev.currentTarget.style.textDecoration = "none"}>
                  {e.name} <span style={{ color: "#4285f4", fontSize: 11, fontWeight: 400 }}>↗</span>
                </a>
                <StarRating value={e.rating} size={11} />
              </div>
              <div style={{ fontSize: 12, color: "#888" }}>
                {e.city}{e.state ? `, ${e.state}` : ""} · {e.cuisine || (TYPE_NORMALIZE[e.type] || e.type)}
              </div>
              <ExpandableNote text={e.notes} />
              {savedTrips && savedTrips.filter(t => !t.completed && !t.draft).length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <select onChange={ev => { if (ev.target.value && onAddToTrip) { onAddToTrip(ev.target.value, e); ev.target.value = ""; }}}
                    style={{ background: "#000", color: "#fff", border: "none", borderRadius: 6, padding: "4px 8px", fontSize: 11, cursor: "pointer" }}
                    defaultValue="">
                    <option value="" disabled>+ Trip</option>
                    {savedTrips.filter(t => !t.completed && !t.draft).map(t => (
                      <option key={t.id} value={t.id}>{t.destination}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        ); })}
      </div>
    </div>
  );
}


// ─── NETWORK TAB ─────────────────────────────────────────────────────────────
function NetworkTab({ entries, onViewProfile, friendState, setFriendState, pendingIncoming, setPendingIncoming, currentUser, friendProfiles, setFriendProfiles }) {
  const [search, setSearch] = useState("");
  const [networkSection, setNetworkSection] = useState("friends");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [outgoingProfiles, setOutgoingProfiles] = useState({});
  const [discoverSuggestions, setDiscoverSuggestions] = useState([]);
  const [loadingDiscover, setLoadingDiscover] = useState(false);
  const searchDebounce = useRef(null);

  const friends = friendProfiles || [];
  const pendingOutgoing = Object.entries(friendState)
    .filter(([, s]) => s === "pending")
    .map(([id]) => ({ id }));

  // Load profiles for any existing pending-outgoing IDs on mount
  useEffect(() => {
    const ids = pendingOutgoing.map(u => u.id);
    if (ids.length === 0) return;
    supabase.from("profiles").select("*").in("id", ids).then(({ data }) => {
      if (!data) return;
      const map = {};
      data.forEach(p => { map[p.id] = p; });
      setOutgoingProfiles(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load discover suggestions when switching to that section
  useEffect(() => {
    if (networkSection !== "discover" || discoverSuggestions.length > 0) return;
    setLoadingDiscover(true);
    const excludeIds = [
      currentUser?.id,
      ...friends.map(f => f.id),
      ...pendingOutgoing.map(u => u.id),
      ...pendingIncoming.map(r => r.from),
    ].filter(Boolean);
    supabase.from("profiles").select("*").not("id", "in", `(${excludeIds.join(",")})`)
      .limit(12).then(({ data }) => {
        setDiscoverSuggestions(data || []);
        setLoadingDiscover(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkSection]);

  useEffect(() => {
    if (search.trim().length < 2) { setSearchResults([]); return; }
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase.from("profiles").select("*")
        .or(`username.ilike.%${search.trim()}%,name.ilike.%${search.trim()}%`)
        .neq("id", currentUser?.id)
        .limit(10);
      setSearchResults(data || []);
      setSearching(false);
    }, 350);
  }, [search, currentUser?.id]);

  const sendRequest = async (profile) => {
    const profileId = profile.id;
    setFriendState(s => ({ ...s, [profileId]: "pending" }));
    setOutgoingProfiles(p => ({ ...p, [profileId]: profile }));
    const { error } = await supabase.from("friendships").insert({ user_id: currentUser.id, friend_id: profileId, status: "pending" });
    if (error) { setFriendState(s => { const n = { ...s }; delete n[profileId]; return n; }); showToast("Couldn't send request. Try again."); }
  };

  const cancelRequest = async (profileId) => {
    setFriendState(s => { const n = { ...s }; delete n[profileId]; return n; });
    const { error } = await supabase.from("friendships").delete().eq("user_id", currentUser.id).eq("friend_id", profileId);
    if (error) { setFriendState(s => ({ ...s, [profileId]: "pending" })); showToast("Couldn't cancel request. Try again."); }
  };

  const acceptRequest = async (req) => {
    const { error } = await supabase.from("friendships").update({ status: "accepted" }).eq("user_id", req.from).eq("friend_id", currentUser.id);
    if (error) { showToast("Couldn't accept request. Try again."); return; }
    setFriendState(s => ({ ...s, [req.from]: "friend" }));
    setPendingIncoming(p => p.filter(r => r.from !== req.from));
    if (req.profile) setFriendProfiles(p => [...p, req.profile]);
  };

  const declineRequest = async (req) => {
    const { error } = await supabase.from("friendships").delete().eq("user_id", req.from).eq("friend_id", currentUser.id);
    if (error) { showToast("Couldn't decline request. Try again."); return; }
    setPendingIncoming(p => p.filter(r => r.from !== req.from));
  };

  const removeFriend = async (profileId) => {
    if (!window.confirm("Remove this friend from your network?")) return;
    setFriendState(s => { const n = { ...s }; delete n[profileId]; return n; });
    setFriendProfiles(p => p.filter(u => u.id !== profileId));
    const { error } = await supabase.from("friendships").delete()
      .or(`and(user_id.eq.${currentUser.id},friend_id.eq.${profileId}),and(user_id.eq.${profileId},friend_id.eq.${currentUser.id})`);
    if (error) {
      setFriendState(s => ({ ...s, [profileId]: "friend" }));
      showToast("Couldn't remove friend. Try again.");
    }
  };

  const getStateBtn = (u) => {
    const state = friendState[u.id];
    if (state === "friend") return (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ color: "#4ade80", fontFamily: "'DM Sans', sans-serif", fontSize: 11, background: "#14532d", border: "1px solid #16a34a", borderRadius: 6, padding: "4px 10px" }}>✓ Friends</span>
        <button onClick={e => { e.stopPropagation(); removeFriend(u.id); }}
          style={{ color: "#ef4444", fontFamily: "'DM Sans', sans-serif", fontSize: 11, background: "#fff0f0", border: "1px solid #fca5a5", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>
          Remove
        </button>
      </div>
    );
    if (state === "pending") return (
      <button onClick={e => { e.stopPropagation(); cancelRequest(u.id); }}
        style={{ color: "#555", fontFamily: "'DM Sans', sans-serif", fontSize: 11, background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
        Pending ✕
      </button>
    );
    return (
      <button onClick={e => { e.stopPropagation(); sendRequest(u); }}
        style={{ color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 11, background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
        + Add
      </button>
    );
  };

  const UserRow = ({ user, onClick }) => {
    const userEntries = entries.filter(e => e.userId === user.id);
    const mustGo = userEntries.filter(e => e.verdict === "must_go").length;
    return (
      <div onClick={onClick}
        style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 12, padding: "16px 18px", cursor: "pointer", transition: "border-color 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "#000"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "#efefef"}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 42, height: 42, background: "#f0f0f0", border: "1px solid #efefef", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
            {user.avatar}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", color: "#000", fontSize: 16 }}>{user.name}</div>
                <div style={{ color: "#555", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>@{user.username}</div>
              </div>
              <div onClick={e => e.stopPropagation()}>
                {getStateBtn(user)}
              </div>
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
              {(user.tags || []).slice(0,2).map(t => <TravelerTag key={t} tag={t} />)}
            </div>
            <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
              <span style={{ color: "#4ade80", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>{mustGo} must-go</span>
              <span style={{ color: "#555", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>{user.citiesCount} cities</span>
              <span style={{ color: "#555", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>{user.countriesCount} countries</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Search Bar */}
      <div style={{ position: "relative", marginBottom: 18 }}>
        <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#555", fontSize: 15, pointerEvents: "none" }}>🔍</span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or @username…"
          style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
            padding: "12px 14px 12px 38px", color: "#000", fontSize: 14,
            fontFamily: "'DM Sans', sans-serif", outline: "none", boxSizing: "border-box",
            transition: "border-color 0.2s" }}
          onFocus={e => e.target.style.borderColor = "#000"}
          onBlur={e => e.target.style.borderColor = "#efefef"}
        />
        {search && (
          <button onClick={() => setSearch("")}
            style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 16 }}>
            ×
          </button>
        )}
      </div>

      {/* Search Results */}
      {search.trim().length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: "#555", fontSize: 11, fontFamily: "'DM Sans', sans-serif", marginBottom: 10 }}>
            {searching ? "SEARCHING…" : `${searchResults.length} RESULT${searchResults.length !== 1 ? "S" : ""} FOR "${search.toUpperCase()}"`}
          </div>
          {!searching && searchResults.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#444", fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
              No travelers found.<br />
              <span style={{ color: "#666", fontSize: 12 }}>Try a different username.</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {searchResults.map(u => <UserRow key={u.id} user={u} onClick={() => onViewProfile(u.id)} />)}
            </div>
          )}
        </div>
      )}

      {/* Section Tabs */}
      {!search && (
        <>
          <div style={{ display: "flex", gap: 0, marginBottom: 18, background: "#111", borderRadius: 8, padding: 3 }}>
            {[["friends", `Friends (${friends.length})`], ["discover", "Discover"]].map(([id, label]) => (
              <button key={id} onClick={() => setNetworkSection(id)}
                style={{ flex: 1, background: networkSection === id ? "#000" : "transparent",
                  border: "none", borderRadius: 6, padding: "8px 4px", cursor: "pointer",
                  color: networkSection === id ? "#fff" : "#555",
                  fontSize: 12, fontFamily: "'DM Sans', sans-serif", fontWeight: networkSection === id ? 700 : 400,
                  transition: "all 0.2s" }}>
                {label}
              </button>
            ))}
          </div>

          {/* FRIENDS SECTION */}
          {networkSection === "friends" && (
            <div>
              {/* Pending incoming requests */}
              {pendingIncoming.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ color: "#000", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", marginBottom: 10 }}>
                    ● {pendingIncoming.length} FRIEND REQUEST{pendingIncoming.length > 1 ? "S" : ""}
                  </div>
                  {pendingIncoming.map(req => (
                    <div key={req.from} style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "14px 16px", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 38, height: 38, background: "#f0f0f0", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                          {req.profile?.avatar || "✈️"}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: "#000", fontFamily: "'DM Sans', sans-serif", fontSize: 15 }}>{req.profile?.name || "Traveler"}</div>
                          <div style={{ color: "#555", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>@{req.profile?.username || req.from}</div>
                        </div>
                        <div style={{ display: "flex", gap: 7 }}>
                          <button onClick={() => acceptRequest(req)}
                            style={{ background: "#4ade8022", border: "1px solid #4ade80", color: "#4ade80", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
                            Accept
                          </button>
                          <button onClick={() => declineRequest(req)}
                            style={{ background: "transparent", border: "1px solid #333", color: "#666", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending outgoing */}
              {pendingOutgoing.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ color: "#555", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", marginBottom: 10 }}>SENT REQUESTS</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {pendingOutgoing.map(u => {
                      const p = outgoingProfiles[u.id] || {};
                      return (
                        <div key={u.id} style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 36, height: 36, background: "#f0f0f0", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                            {p.avatar || "✈️"}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: "#000", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>{p.name || "Traveler"}</div>
                            <div style={{ color: "#555", fontSize: 11, fontFamily: "'DM Sans', sans-serif" }}>@{p.username || u.id}</div>
                          </div>
                          <button onClick={() => cancelRequest(u.id)}
                            style={{ color: "#555", fontFamily: "'DM Sans', sans-serif", fontSize: 11, background: "#fff", border: "1px solid #ddd", borderRadius: 6, padding: "3px 9px", cursor: "pointer", flexShrink: 0 }}>
                            Pending ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ color: "#555", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", marginBottom: 10 }}>
                YOUR NETWORK · {friends.length} TRAVELERS
              </div>
              {friends.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 0", color: "#444", fontFamily: "'DM Sans', sans-serif", fontSize: 13, lineHeight: 1.8 }}>
                  No friends yet.<br />
                  <span style={{ color: "#666", fontSize: 12 }}>Search for travelers or check Discover.</span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {friends.map(u => (
                    <div key={u.id} style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 12, padding: "16px 18px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", cursor: "pointer" }}
                      onClick={() => onViewProfile(u.id)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 42, height: 42, background: "#f0f0f0", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                          {u.avatar || "✈️"}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 16, color: "#000", fontFamily: "'DM Sans', sans-serif" }}>{u.name}</div>
                          <div style={{ fontSize: 11, color: "#555", fontFamily: "'DM Sans', sans-serif" }}>@{u.username}</div>
                        </div>
                        <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                          <span style={{ color: "#4ade80", fontSize: 11, background: "#14532d", border: "1px solid #16a34a", borderRadius: 6, padding: "4px 10px", fontFamily: "'DM Sans', sans-serif" }}>✓ Friends</span>
                          <button onClick={() => removeFriend(u.id)}
                            style={{ color: "#ef4444", fontSize: 11, background: "#fff0f0", border: "1px solid #fca5a5", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* DISCOVER SECTION */}
          {networkSection === "discover" && (
            <div>
              <div style={{ color: "#555", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", marginBottom: 10 }}>
                TRAVELERS YOU MAY KNOW
              </div>
              {loadingDiscover ? (
                <div style={{ textAlign: "center", padding: "32px 0", color: "#666", fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>Loading…</div>
              ) : discoverSuggestions.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 0", color: "#444", fontFamily: "'DM Sans', sans-serif", fontSize: 13, lineHeight: 1.8 }}>
                  No new travelers to suggest right now.<br />
                  <span style={{ color: "#666", fontSize: 12 }}>Try searching by name or @username above.</span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {discoverSuggestions.map(u => <UserRow key={u.id} user={u} onClick={() => onViewProfile(u.id)} />)}
                </div>
              )}
              <div style={{ background: "#0d1a0d", border: "1px solid #1a3a1a", borderRadius: 12, padding: "16px 18px", marginTop: 20 }}>
                <div style={{ color: "#4ade80", fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>✦ ZERO SPONSORS</div>
                <div style={{ color: "#888", fontSize: 13, lineHeight: 1.6 }}>
                  Every recommendation comes from real people. No paid placements. No algorithmic manipulation. Just trusted taste.
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── ADD DIARY MODAL ─────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function AddDiaryModal({ onClose, onSave, entries, userId }) {
  const [form, setForm] = useState({
    title: "", city: "", state: "", country: "USA",
    dateFrom: "", dateTo: "",
    story: "", budget: "", tags: "",
    linkedEntries: []
  });

  const myPlaces = entries;
  const [showLinkPicker, setShowLinkPicker] = useState(false);

  const toggleLink = (eid) => {
    setForm(f => ({
      ...f,
      linkedEntries: f.linkedEntries.includes(eid)
        ? f.linkedEntries.filter(x => x !== eid)
        : [...f.linkedEntries, eid]
    }));
  };

  const inp = (field, label, placeholder, type = "text") => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</label>
      <input type={type} value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        placeholder={placeholder}
        style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
          padding: "11px 12px", color: "#000", fontSize: 14,
          marginTop: 4, boxSizing: "border-box", outline: "none" }} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 20, padding: "28px 28px 24px", width: "100%", maxWidth: 540, maxHeight: "90vh", overflowY: "auto", animation: "slideUp 0.22s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ color: "#000", fontFamily: "'Cormorant Garamond', 'Lora', serif", margin: 0, fontSize: 22, fontWeight: 600 }}>📖 New Diary Entry</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#aaa", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ height: 1, background: "#f0f0f0", marginBottom: 20 }} />

        {inp("title", "Trip Title", "e.g. Summit County Weekend")}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          {inp("city", "City", "Breckenridge")}
          {inp("state", "State", "CO")}
          {inp("country", "Country", "USA")}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {inp("dateFrom", "Date From", "", "date")}
          {inp("dateTo", "Date To", "", "date")}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Your Story</label>
          <textarea value={form.story} onChange={e => setForm(f => ({ ...f, story: e.target.value }))}
            placeholder="Write about your trip — what you did, what surprised you, what you'd tell a friend…"
            rows={5}
            style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
              padding: "10px 12px", color: "#000", fontSize: 13, marginTop: 4,
              boxSizing: "border-box", outline: "none", resize: "vertical",
              fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6 }} />
        </div>

        {inp("budget", "Budget (optional)", "e.g. $650 (lodging, food, gas)")}

        {/* Link places */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Link Places from My Logs</label>
          <button onClick={() => setShowLinkPicker(!showLinkPicker)}
            style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10, padding: "8px 14px",
              color: "#000", fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", width: "100%" }}>
            {showLinkPicker ? "▴ Hide" : `▾ Select places (${form.linkedEntries.length} linked)`}
          </button>
          {showLinkPicker && (
            <div onWheel={e => e.stopPropagation()} style={{ maxHeight: 180, overflowY: "auto", marginTop: 8, background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10, padding: 8, overscrollBehavior: "contain" }}>
              {myPlaces.map(p => {
                const linked = form.linkedEntries.includes(p.id);
                return (
                  <div key={p.id} onClick={() => toggleLink(p.id)}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 6,
                      background: linked ? "#f0f0f0" : "transparent", marginBottom: 2 }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, border: `1px solid ${linked ? "#000" : "#ccc"}`,
                      background: linked ? "#000" : "transparent", display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, color: "#fff", flexShrink: 0 }}>{linked ? "✓" : ""}</span>
                    <span style={{ color: "#000", fontSize: 13 }}>{p.name}</span>
                    <span style={{ color: "#888", fontSize: 11, fontFamily: "'DM Sans', sans-serif" }}>{p.city}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {inp("tags", "Tags", "ski trip, date night, solo (comma separated)")}

        {/* Photos placeholder */}
        <div style={{ marginBottom: 18, background: "#f8f8f8", border: "1px dashed #ddd", borderRadius: 10, padding: "14px", textAlign: "center" }}>
          <div style={{ fontSize: 18, marginBottom: 4 }}>📸</div>
          <div style={{ color: "#aaa", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}>PHOTO UPLOAD COMING SOON</div>
        </div>

        <button onClick={() => {
          if (!form.title || !form.city || !form.story) return;
          const newDiary = {
            ...form,
            id: "d" + Date.now(),
            userId: userId,
            photos: [],
            tags: parseTags(form.tags)
          };
          onSave(newDiary);
          onClose();
        }}
          style={{ width: "100%", background: "#000",
            border: "none", borderRadius: 8, padding: "13px", color: "#fff",
            fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
          Save Diary Entry
        </button>
      </div>
    </div>
  );
}


// ─── EDIT ENTRY MODAL ────────────────────────────────────────────────────────
function EditEntryModal({ entry, onClose, onSave, onDelete, userId }) {
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const [form, setForm] = useState({
    name: entry.name || "",
    city: entry.city || "",
    state: entry.state || "",
    country: entry.country || "USA",
    type: entry.type || "restaurant",
    cuisine: entry.cuisine || "",
    dish: entry.dish || "",
    rating: entry.rating || 0,
    verdict: entry.verdict || "must_go",
    notes: entry.notes || "",
    privateNote: entry.privateNote || "",
    tags: (entry.tags || []).join(", "),
    isPrivate: entry.isPrivate || false,
    kidFriendly: entry.kidFriendly || false
  });
  const [photos, setPhotos] = useState(entry.photos || []);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const inp = (field, label, placeholder, type = "text") => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</label>
      <input type={type} value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        placeholder={placeholder}
        style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
          padding: "11px 12px", color: "#000", fontSize: 14,
          marginTop: 4, boxSizing: "border-box", outline: "none" }} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 20, padding: "28px 28px 24px", width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", animation: "slideUp 0.22s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ color: "#000", fontFamily: "'Cormorant Garamond', 'Lora', serif", margin: 0, fontSize: 22, fontWeight: 600 }}>Edit Log</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#aaa", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ height: 1, background: "#f0f0f0", marginBottom: 20 }} />

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: fieldErrors.name ? "#dc2626" : "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Place Name</label>
          <input value={form.name} onChange={e => { setForm(f => ({...f, name: e.target.value})); setFieldErrors(fe => ({...fe, name: false})); }}
            placeholder="e.g. Rioja"
            style={{ width: "100%", background: "#f8f8f8", border: `1px solid ${fieldErrors.name ? "#dc2626" : "#efefef"}`, borderRadius: 10,
              padding: "11px 12px", color: "#000", fontSize: 14, marginTop: 4, boxSizing: "border-box", outline: "none" }} />
          {fieldErrors.name && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>Required</div>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <label style={{ color: fieldErrors.city ? "#dc2626" : "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>City</label>
            <input value={form.city} onChange={e => { setForm(f => ({...f, city: e.target.value})); setFieldErrors(fe => ({...fe, city: false})); }}
              style={{ width: "100%", background: "#f8f8f8", border: `1px solid ${fieldErrors.city ? "#dc2626" : "#efefef"}`, borderRadius: 10, padding: "11px 12px", color: "#000", fontSize: 14, marginTop: 4, boxSizing: "border-box", outline: "none", fontFamily: "'DM Sans', sans-serif" }} />
            {fieldErrors.city && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>Required</div>}
          </div>
          <div>
            <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>State</label>
            <input value={form.state} onChange={e => setForm(f => ({...f, state: e.target.value}))}
              style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10, padding: "11px 12px", color: "#000", fontSize: 14, marginTop: 4, boxSizing: "border-box", outline: "none", fontFamily: "'DM Sans', sans-serif" }} />
          </div>
          <div>
            <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Country</label>
            <input value={form.country} onChange={e => setForm(f => ({...f, country: e.target.value}))}
              style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10, padding: "11px 12px", color: "#000", fontSize: 14, marginTop: 4, boxSizing: "border-box", outline: "none", fontFamily: "'DM Sans', sans-serif" }} />
          </div>
        </div>

        <div style={{ marginTop: 14, marginBottom: 14 }}>
          <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Type</label>
          <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            {["restaurant","hotel","brewery","cafe","activity","hiking"].map(t => (
              <button key={t} onClick={() => setForm(f => ({...f, type: t}))}
                style={{ background: form.type === t ? "#000" : "#f8f8f8",
                  border: `1px solid ${form.type === t ? "#000" : "#efefef"}`,
                  color: form.type === t ? "#fff" : "#555",
                  borderRadius: 6, padding: "6px 12px", cursor: "pointer",
                  fontSize: 12, fontFamily: "'DM Sans', sans-serif", textTransform: "capitalize" }}>
                {{"restaurant":"🍽","hotel":"🏨","brewery":"🍺","cafe":"☕","activity":"🏔","hiking":"🥾"}[t]} {t}
              </button>
            ))}
            <button onClick={() => setForm(f => ({...f, kidFriendly: !f.kidFriendly}))}
              style={{ background: form.kidFriendly ? "#fef9c3" : "#f8f8f8",
                border: `1px solid ${form.kidFriendly ? "#fde047" : "#efefef"}`,
                color: form.kidFriendly ? "#854d0e" : "#555",
                borderRadius: 6, padding: "6px 12px", cursor: "pointer",
                fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
              👶 Kid Friendly
            </button>
          </div>
        </div>

        {["restaurant","cafe","brewery"].includes(form.type) && inp("cuisine", "Cuisine / Category", "e.g. Spanish Tapas")}
        {["restaurant","cafe","brewery"].includes(form.type) && inp("dish", "Must-Try Dish", "What should everyone order?")}

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: fieldErrors.rating ? "#dc2626" : "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Rating</label>
          <StarRating value={form.rating} onChange={r => { setForm(f => ({...f, rating: r})); setFieldErrors(fe => ({...fe, rating: false})); }} size={24} />
          {fieldErrors.rating && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>Required</div>}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: fieldErrors.verdict ? "#dc2626" : "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Verdict</label>
          <div style={{ display: "flex", gap: 10, marginTop: 6, border: fieldErrors.verdict ? "1px solid #dc2626" : "none", borderRadius: fieldErrors.verdict ? 8 : 0, padding: fieldErrors.verdict ? 6 : 0 }}>
            {[["must_go","MUST GO","#4ade80"],["skip","SKIP IT","#f87171"]].map(([v,l,c]) => (
              <button key={v} onClick={() => { setForm(f => ({...f, verdict: v})); setFieldErrors(fe => ({...fe, verdict: false})); }}
                style={{ flex: 1, background: form.verdict === v ? c + "22" : "#f8f8f8",
                  border: `1px solid ${form.verdict === v ? c : "#efefef"}`,
                  color: form.verdict === v ? c : "#555",
                  borderRadius: 6, padding: "8px", cursor: "pointer",
                  fontSize: 12, fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}>
                {l}
              </button>
            ))}
          </div>
          {fieldErrors.verdict && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>Required</div>}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>Notes</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
            placeholder="What made it special?"
            rows={3}
            style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
              padding: "10px 12px", color: "#000", fontSize: 13, marginTop: 4,
              boxSizing: "border-box", outline: "none", resize: "vertical",
              fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }} />
        </div>

        {inp("privateNote", "Private Note (only you see this)", "Parking tips, secret menu items...")}
        {inp("tags", "Tags", "date night, spicy (comma separated)")}

        {/* Photos */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ color: "#666", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 8 }}>Photos</label>
          {photos.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {photos.map((photo, i) => (
                <div key={photo.slice(-20)} style={{ position: "relative" }}>
                  <img src={photo} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid #efefef" }} />
                  <button onClick={() => setPhotos(p => p.filter((_, idx) => idx !== i))}
                    style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%",
                      background: "#000", border: "none", color: "#fff", fontSize: 11, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                </div>
              ))}
            </div>
          )}
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: uploadingPhotos ? "default" : "pointer",
            background: "#f8f8f8", border: "1px dashed #ddd", borderRadius: 8, padding: "10px 16px",
            fontSize: 13, color: "#555", width: "100%", justifyContent: "center" }}>
            {uploadingPhotos ? "Uploading…" : "📷 Add Photos"}
            <input type="file" accept="image/*" multiple style={{ display: "none" }} disabled={uploadingPhotos}
              onChange={async e => {
                const all = Array.from(e.target.files);
                const valid = all.filter(f => ALLOWED_IMAGE_TYPES.includes(f.type) && f.size <= MAX_PHOTO_SIZE);
                e.target.value = "";
                const rejected = all.length - valid.length;
                if (rejected > 0) {
                  const tooLarge = all.some(f => f.size > MAX_PHOTO_SIZE);
                  const wrongType = all.some(f => !ALLOWED_IMAGE_TYPES.includes(f.type));
                  setPhotoError(tooLarge && wrongType
                    ? "Some photos skipped — must be JPEG, PNG, WebP or GIF under 5 MB"
                    : tooLarge ? `${rejected === 1 ? "Photo" : "Some photos"} skipped — max 5 MB each`
                    : "Unsupported format — use JPEG, PNG, WebP, or GIF");
                } else {
                  setPhotoError(null);
                }
                if (!valid.length) return;
                setUploadingPhotos(true);
                const urls = await Promise.all(valid.map(f => uploadPhoto(f, userId)));
                const succeeded = urls.filter(Boolean);
                if (succeeded.length === 0) {
                  setPhotoError("Photos couldn't be uploaded. Check your connection and try again.");
                } else {
                  setPhotos(p => [...p, ...succeeded]);
                }
                setUploadingPhotos(false);
              }} />
          </label>
          {photoError && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>
              {photoError}
            </div>
          )}
        </div>

        {saveError && (
          <div style={{ marginBottom: 10, padding: "10px 12px", background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 8, color: "#dc2626", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
            {saveError}
          </div>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8, padding: "12px",
              color: "#555", fontSize: 14, cursor: "pointer" }}>
            Cancel
          </button>
          {onDelete && (
            <button onClick={() => { if (window.confirm("Delete this log?")) { onDelete(); onClose(); } }}
              style={{ flex: 1, background: "#fff", border: "1px solid #fecaca", borderRadius: 8, padding: "12px",
                color: "#dc2626", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              🗑 Delete
            </button>
          )}
          <button onClick={async () => {
            const errs = { name: !form.name.trim(), city: !form.city.trim(), rating: !form.rating, verdict: !form.verdict };
            if (errs.name || errs.city || errs.rating || errs.verdict) { setFieldErrors(errs); return; }
            setSaveError(null);
            setSaving(true);
            const ok = await onSave({ ...entry, ...form, photos, tags: parseTags(form.tags) });
            setSaving(false);
            if (ok) onClose();
            else setSaveError("Couldn't save changes. Check your connection and try again.");
          }}
            disabled={saving}
            style={{ flex: 2, background: saving ? "#555" : "#000", border: "none", borderRadius: 8, padding: "12px", color: "#fff",
              fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ─── TRIP MAP MODAL ──────────────────────────────────────────────────────────
// Geocoding cache — persists across modal opens within the session
const _geocodeCache = {};
// Global request queue — ensures at most 1 Nominatim request per 1.1s across ALL components
let _geocodeQueue = Promise.resolve();

// Raw Nominatim fetch (cached by query string, globally rate-limited)
async function _fetchNominatim(query) {
  if (_geocodeCache[query] !== undefined) return _geocodeCache[query];
  return new Promise(resolve => {
    _geocodeQueue = _geocodeQueue.then(async () => {
      await new Promise(r => setTimeout(r, 1100));
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        const result = data[0] ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) } : null;
        _geocodeCache[query] = result; // cache null too so repeated opens skip the request
        resolve(result);
      } catch {
        resolve(null);
      }
    });
  });
}

// Small random jitter so stacked city-level pins spread out slightly
function _jitter(coord) {
  const spread = 0.012; // ~1 km
  return {
    lat: coord.lat + (Math.random() - 0.5) * spread,
    lng: coord.lng + (Math.random() - 0.5) * spread,
    approximate: true,
  };
}

// Geocode an entry. Returns stored coords immediately if present; otherwise queries Nominatim.
// Optional tripDestination improves fallback query accuracy.
async function geocodeEntry(entry, tripDestination) {
  // 1. Use stored coordinates — no network call needed
  const storedLat = parseFloat(entry.lat ?? entry._lat);
  const storedLng = parseFloat(entry.lon ?? entry.lng ?? entry._lng);
  if (!isNaN(storedLat) && !isNaN(storedLng)) {
    return { lat: storedLat, lng: storedLng, approximate: false };
  }

  const { name, city, state, country } = entry;

  // 2. Try place name + destination for stronger context
  if (tripDestination) {
    const destQuery = [name, tripDestination].filter(Boolean).join(", ");
    const destCoord = await _fetchNominatim(destQuery);
    if (destCoord) return { ...destCoord, approximate: true };
  }

  // 3. Try full place name + city/state
  const fullQuery = [name, city, state, country].filter(Boolean).join(", ");
  const exact = await _fetchNominatim(fullQuery);
  if (exact) return { ...exact, approximate: true };

  // 4. Fall back to city + state (or city + country) — marked approximate
  const cityQuery = [city, state, country].filter(Boolean).join(", ");
  if (!cityQuery) return null;
  const cityCoord = await _fetchNominatim(cityQuery);
  if (cityCoord) return _jitter(cityCoord);

  return null;
}

// Day palette — 8 distinct colors
const DAY_COLORS = ["#e74c3c","#3498db","#27ae60","#f39c12","#8e44ad","#16a085","#e67e22","#2c3e50"];

// approximate=true → dashed-border pin (city-level fallback)
function makePinIcon(color, size = 28, opacity = 1, approximate = false, num = null) {
  const pathFill = num !== null ? color : (approximate ? "none" : color);
  const pathStroke = num !== null ? "rgba(0,0,0,0.25)" : (approximate ? color : "rgba(0,0,0,0.25)");
  const pathStrokeWidth = "1";
  const pathOpacity = approximate && num === null ? "0.7" : opacity;
  const inner = num !== null
    ? `<text x="14" y="17" font-size="${num > 9 ? 8 : 9}" font-weight="800" fill="#fff" text-anchor="middle" font-family="Arial,sans-serif">${num}</text>`
    : approximate
      ? `<circle cx="14" cy="12" r="5" fill="none" stroke="${color}" stroke-width="1.5" opacity="0.8"/>`
      : `<circle cx="14" cy="12" r="4" fill="#fff" opacity="0.9"/>`;
  const html = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size * 1.35}" viewBox="0 0 28 38">
    <ellipse cx="14" cy="35" rx="5" ry="2" fill="rgba(0,0,0,0.2)"/>
    <path d="M14 2C8.48 2 4 6.48 4 12c0 7.5 10 24 10 24s10-16.5 10-24C24 6.48 19.52 2 14 2z"
      fill="${pathFill}" stroke="${pathStroke}" stroke-width="${pathStrokeWidth}" opacity="${pathOpacity}"/>
    ${inner}
  </svg>`;
  return L.divIcon({
    html,
    className: "",
    iconSize: [size, size * 1.35],
    iconAnchor: [size / 2, size * 1.35],
    popupAnchor: [0, -(size * 1.35)],
  });
}

// Accommodation pin — house-shaped SVG, distinct from numbered entry pins
function makeAccomIcon(color, size = 30) {
  const html = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size * 1.35}" viewBox="0 0 28 38">
    <ellipse cx="14" cy="35" rx="5" ry="2" fill="rgba(0,0,0,0.2)"/>
    <path d="M14 2C8.48 2 4 6.48 4 12c0 7.5 10 24 10 24s10-16.5 10-24C24 6.48 19.52 2 14 2z"
      fill="${color}" stroke="rgba(0,0,0,0.25)" stroke-width="1"/>
    <text x="14" y="16" font-size="10" font-weight="800" fill="#fff" text-anchor="middle" font-family="Arial,sans-serif">H</text>
  </svg>`;
  return L.divIcon({ html, className: "", iconSize: [size, size * 1.35], iconAnchor: [size / 2, size * 1.35], popupAnchor: [0, -(size * 1.35)] });
}

// Sub-component: fits map to bounds on filter change, and also as new pins resolve during geocoding.
// Won't fight the user after loading is complete — stops refitting once positions stop growing.
function MapBoundsUpdater({ positions, fitKey }) {
  const map = useMap();
  const fittedKey = useRef(null);
  const fittedCount = useRef(0);
  useEffect(() => {
    if (!positions.length) return;
    const sameKey = fittedKey.current === fitKey;
    if (sameKey && positions.length <= fittedCount.current) return; // no new pins, don't fight user
    fittedKey.current = fitKey;
    fittedCount.current = positions.length;
    if (positions.length === 1) {
      map.setView(positions[0], 13, { animate: true });
    } else {
      map.fitBounds(positions.map(p => [p.lat, p.lng]), { padding: [40, 40], animate: true });
    }
  }, [positions, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function TripMapModal({ trip, onClose, onUpdate, initialDayFilter }) {
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const [dayFilter, setDayFilter] = useState(initialDayFilter ?? "all");
  // coordsMap: entryId -> { lat, lng } | null
  const [coordsMap, setCoordsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const listRef = useRef(null);
  const mapRef = useRef(null);
  const markerRefs = useRef({});
  const accomMarkerRefs = useRef({});
  const [fixingId, setFixingId] = useState(null);
  const [fixQuery, setFixQuery] = useState("");
  const [fixResults, setFixResults] = useState([]);
  const [fixLoading, setFixLoading] = useState(false);
  const fixDebounceRef = useRef(null);

  // Flatten all entries with day metadata
  const allEntries = useMemo(() => {
    if (!trip?.plan?.days) return [];
    return trip.plan.days.flatMap((day, di) =>
      (day.entries || day.items || []).map(e => ({
        ...e,
        _dayIdx: di,
        _dayLabel: day.name || `Day ${day.day || di + 1}`,
        _dayColor: DAY_COLORS[di % DAY_COLORS.length],
      }))
    );
  }, [trip]);

  // Accommodations per day
  const allAccoms = useMemo(() => {
    if (!trip?.plan?.days) return [];
    return trip.plan.days
      .map((day, di) => day.accommodation ? {
        id: `accom_${di}`,
        text: day.accommodation,
        accomLat: day.accomLat ?? null,
        accomLon: day.accomLon ?? null,
        _dayIdx: di,
        _dayLabel: day.name || `Day ${day.day || di + 1}`,
        _dayColor: DAY_COLORS[di % DAY_COLORS.length],
      } : null)
      .filter(Boolean);
  }, [trip]);

  const [accomCoordsMap, setAccomCoordsMap] = useState({});

  // Geocode all entries + accommodations sequentially (Nominatim rate limit: 1 req/sec)
  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      const resolvedCoords = {};
      for (const e of allEntries) {
        if (cancelled) break;
        const coord = await geocodeEntry(e, trip.destination);
        resolvedCoords[e.id] = coord;
        if (!cancelled) setCoordsMap(prev => ({ ...prev, [e.id]: coord }));
      }
      const resolvedAccomCoords = {};
      for (const a of allAccoms) {
        if (cancelled) break;
        const coord = a.accomLat != null && a.accomLon != null
          ? { lat: a.accomLat, lng: a.accomLon, approximate: false }
          : await _fetchNominatim(`${a.text}${trip.destination ? ", " + trip.destination : ""}`);
        resolvedAccomCoords[a.id] = coord;
        if (!cancelled && coord) setAccomCoordsMap(prev => ({ ...prev, [a.id]: coord }));
      }
      if (!cancelled) {
        setLoading(false);
        // Backfill: write non-approximate newly-resolved coords back into the trip so they persist
        if (onUpdate) {
          let changed = false;
          const updatedDays = trip.plan.days.map(day => ({
            ...day,
            entries: (day.entries || day.items || []).map(e => {
              const coord = resolvedCoords[e.id];
              if ((e.lat == null || e.lon == null) && coord && !coord.approximate) {
                changed = true;
                return { ...e, lat: coord.lat, lon: coord.lng };
              }
              return e;
            }),
          }));
          // Backfill accommodation coords too
          allAccoms.forEach(a => {
            const coord = resolvedAccomCoords[a.id];
            if ((a.accomLat == null || a.accomLon == null) && coord && !coord.approximate) {
              updatedDays[a._dayIdx] = { ...updatedDays[a._dayIdx], accomLat: coord.lat, accomLon: coord.lng };
              changed = true;
            }
          });
          if (changed) onUpdate({ ...trip, plan: { ...trip.plan, days: updatedDays } });
        }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [allEntries, allAccoms, trip.destination]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredEntries = useMemo(() =>
    dayFilter === "all" ? allEntries : allEntries.filter(e => e._dayIdx === dayFilter),
    [allEntries, dayFilter]
  );

  const mappedEntries = useMemo(() =>
    filteredEntries.filter(e => coordsMap[e.id]),
    [filteredEntries, coordsMap]
  );

  const uniqueDays = useMemo(() => {
    const seen = new Set();
    return allEntries.reduce((acc, e) => {
      if (!seen.has(e._dayIdx)) { seen.add(e._dayIdx); acc.push({ idx: e._dayIdx, label: e._dayLabel, color: e._dayColor }); }
      return acc;
    }, []);
  }, [allEntries]);

  // Scroll the list to an entry when its pin is clicked
  const scrollToEntry = useCallback((id) => {
    setSelectedId(id);
    const el = listRef.current?.querySelector(`[data-entry-id="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // Pan to a marker and open its popup when user clicks an entry row
  const flyToEntry = useCallback((entry) => {
    setSelectedId(entry.id);
    const coord = coordsMap[entry.id];
    if (!coord) return;
    mapRef.current?.flyTo([coord.lat, coord.lng], Math.max(mapRef.current.getZoom(), 14), { animate: true, duration: 0.6 });
    const marker = markerRefs.current[entry.id];
    if (marker) setTimeout(() => marker.openPopup(), 650);
  }, [coordsMap]);

  // Fix-pin search: debounced Google Places lookup when user is correcting a pin location
  useEffect(() => {
    if (!fixQuery.trim() || fixQuery.length < 2) { setFixResults([]); return; }
    clearTimeout(fixDebounceRef.current);
    fixDebounceRef.current = setTimeout(async () => {
      setFixLoading(true);
      const results = await searchGooglePlaces(fixQuery, trip.destination || "");
      setFixResults(results.slice(0, 5));
      setFixLoading(false);
    }, 400);
    return () => clearTimeout(fixDebounceRef.current);
  }, [fixQuery, trip.destination]);

  const applyFixedPin = (entry, place) => {
    if (place.lat == null || place.lon == null) return;
    setCoordsMap(prev => ({ ...prev, [entry.id]: { lat: place.lat, lng: place.lon, approximate: false } }));
    if (onUpdate) {
      const updatedDays = trip.plan.days.map(day => ({
        ...day,
        entries: (day.entries || day.items || []).map(e =>
          e.id === entry.id ? { ...e, lat: place.lat, lon: place.lon } : e
        ),
      }));
      onUpdate({ ...trip, plan: { ...trip.plan, days: updatedDays } });
    }
    setFixingId(null);
    setFixQuery("");
    setFixResults([]);
  };

  const centerPos = mappedEntries.length
    ? [mappedEntries.reduce((s, e) => s + coordsMap[e.id].lat, 0) / mappedEntries.length,
       mappedEntries.reduce((s, e) => s + coordsMap[e.id].lng, 0) / mappedEntries.length]
    : [39.5, -98.35]; // continental US fallback

  const verdictOpacity = v => v === "skip" ? 0.45 : 1;

  return createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1200,
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: "min(96vw, 1100px)", height: "min(90vh, 720px)", background: "#fff",
          borderRadius: 18, overflow: "hidden", display: "flex", flexDirection: "column",
          boxShadow: "0 24px 80px rgba(0,0,0,0.28)" }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px",
          borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#000" }}>🗺 {trip.destination} — Map View</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
              {loading
                ? `Locating ${allEntries.length} places…`
                : `${mappedEntries.length} of ${filteredEntries.length} places mapped${dayFilter === "all" && allAccoms.length ? ` · ${Object.keys(accomCoordsMap).length} of ${allAccoms.length} hotels` : ""}`}
            </div>
          </div>
          {/* Day filter pills */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={() => setDayFilter("all")}
              style={{ borderRadius: 20, padding: "4px 12px", fontSize: 12, cursor: "pointer",
                border: "none", fontWeight: 600,
                background: dayFilter === "all" ? "#000" : "#f0f0f0",
                color: dayFilter === "all" ? "#fff" : "#555" }}>
              All Days
            </button>
            {uniqueDays.map(d => (
              <button key={d.idx} onClick={() => setDayFilter(d.idx)}
                style={{ borderRadius: 20, padding: "4px 12px", fontSize: 12, cursor: "pointer",
                  border: "none", fontWeight: 600,
                  background: dayFilter === d.idx ? d.color : "#f0f0f0",
                  color: dayFilter === d.idx ? "#fff" : "#555" }}>
                {d.label}
              </button>
            ))}
          </div>
          <button onClick={e => { e.stopPropagation(); onClose(); }}
            style={{ background: "none", border: "none", fontSize: 20, color: "#aaa", cursor: "pointer",
              lineHeight: 1, padding: "2px 4px", flexShrink: 0 }}>
            ✕
          </button>
        </div>

        {/* ── Body: map + list ── */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

          {/* Map panel */}
          <div style={{ flex: "0 0 62%", position: "relative" }}>
            {loading && mappedEntries.length === 0 && (
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", background: "#f8f8f8", zIndex: 10, gap: 10 }}>
                <div style={{ fontSize: 28 }}>📍</div>
                <div style={{ fontSize: 13, color: "#888" }}>Geocoding locations…</div>
              </div>
            )}
            <MapContainer center={centerPos} zoom={mappedEntries.length ? 10 : 4}
              style={{ width: "100%", height: "100%" }}
              scrollWheelZoom={true}
              attributionControl={false}
              ref={mapRef}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© OpenStreetMap contributors'
              />
              {mappedEntries.length > 0 && (
                <MapBoundsUpdater positions={mappedEntries.map(e => coordsMap[e.id])} fitKey={dayFilter} />
              )}
              {mappedEntries.map((e, idx) => {
                const coord = coordsMap[e.id];
                const isSelected = selectedId === e.id;
                const icon = makePinIcon(e._dayColor, isSelected ? 36 : 28, verdictOpacity(e.verdict), coord.approximate, idx + 1);
                return (
                  <Marker
                    key={e.id}
                    position={[coord.lat, coord.lng]}
                    icon={icon}
                    ref={r => { markerRefs.current[e.id] = r; }}
                    eventHandlers={{ click: () => { scrollToEntry(e.id); markerRefs.current[e.id]?.openPopup(); } }}>
                    <Popup>
                      <div style={{ minWidth: 140 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#000", marginBottom: 3 }}>{e.name}</div>
                        <div style={{ fontSize: 11, color: "#666" }}>{e.city}{e.state ? `, ${e.state}` : ""}</div>
                        {coord.approximate && (
                          <div style={{ fontSize: 10, color: "#f39c12", marginTop: 3 }}>~ Approximate location</div>
                        )}
                        {e.verdict && (
                          <div style={{ marginTop: 5, fontSize: 11, fontWeight: 600,
                            color: e.verdict === "must_go" ? "#27ae60" : e.verdict === "skip" ? "#e74c3c" : "#888" }}>
                            {e.verdict === "must_go" ? "✓ Must Go" : e.verdict === "skip" ? "✗ Skip" : ""}
                          </div>
                        )}
                        <div style={{ marginTop: 4, display: "inline-block", background: e._dayColor,
                          color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>
                          {e._dayLabel}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
              {/* Accommodation markers */}
              {allAccoms
                .filter(a => accomCoordsMap[a.id] && (dayFilter === "all" || dayFilter === a._dayIdx))
                .map(a => {
                  const coord = accomCoordsMap[a.id];
                  return (
                    <Marker key={a.id} position={[coord.lat, coord.lng]}
                      icon={makeAccomIcon(a._dayColor, 30)}
                      ref={r => { accomMarkerRefs.current[a.id] = r; }}
                      eventHandlers={{ click: () => { setSelectedId(a.id); const el = listRef.current?.querySelector(`[data-accom-id="${a.id}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); } }}>
                      <Popup>
                        <div style={{ minWidth: 140 }}>
                          <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>🏨 Accommodation</div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "#000" }}>{a.text}</div>
                          <div style={{ marginTop: 4, display: "inline-block", background: a._dayColor,
                            color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>
                            {a._dayLabel}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              {/* Route polyline for a single-day view */}
              {dayFilter !== "all" && mappedEntries.length > 1 && (
                <Polyline
                  positions={mappedEntries.map(e => [coordsMap[e.id].lat, coordsMap[e.id].lng])}
                  pathOptions={{ color: mappedEntries[0]._dayColor, weight: 2.5, opacity: 0.55, dashArray: "8 5" }}
                />
              )}
            </MapContainer>
          </div>

          {/* List panel */}
          <div ref={listRef} style={{ flex: 1, overflowY: "auto", borderLeft: "1px solid #f0f0f0", padding: "12px 0", overscrollBehavior: "contain" }}>
            {filteredEntries.length === 0 && (
              <div style={{ padding: "40px 20px", textAlign: "center", color: "#aaa", fontSize: 13 }}>
                No places for this day
              </div>
            )}
            {uniqueDays
              .filter(d => dayFilter === "all" || dayFilter === d.idx)
              .map(day => {
                const dayEntries = filteredEntries.filter(e => e._dayIdx === day.idx);
                const dayAccom = allAccoms.find(a => a._dayIdx === day.idx);
                if (!dayEntries.length && !dayAccom) return null;
                return (
                  <div key={day.idx}>
                    {/* Day section header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px 6px",
                      position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: day.color, flexShrink: 0 }} />
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#000" }}>{day.label}</div>
                    </div>
                    {/* Accommodation row */}
                    {dayAccom && (
                      <div data-accom-id={dayAccom.id}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 16px 4px",
                        borderBottom: "1px solid #f5f5f5", cursor: accomCoordsMap[dayAccom.id] ? "pointer" : "default" }}
                        onClick={() => {
                          const coord = accomCoordsMap[dayAccom.id];
                          if (!coord) return;
                          setSelectedId(dayAccom.id);
                          mapRef.current?.flyTo([coord.lat, coord.lng], Math.max(mapRef.current.getZoom(), 14), { animate: true, duration: 0.6 });
                          setTimeout(() => accomMarkerRefs.current[dayAccom.id]?.openPopup(), 650);
                        }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, background: day.color,
                          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 800, flexShrink: 0 }}>H</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>{dayAccom.text}</div>
                          <div style={{ fontSize: 10, color: "#bbb", marginTop: 1 }}>
                            {accomCoordsMap[dayAccom.id] ? "📍 on map" : loading ? "locating…" : "not mapped"}
                          </div>
                        </div>
                      </div>
                    )}
                    {dayEntries.map((e, i) => {
                      const hasCoord = !!coordsMap[e.id];
                      const isSelected = selectedId === e.id;
                      return (
                        <div key={e.id} data-entry-id={e.id}
                          onClick={() => hasCoord && flyToEntry(e)}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: 10,
                            padding: "9px 16px",
                            cursor: hasCoord ? "pointer" : "default",
                            background: isSelected ? "#fffbe6" : "transparent",
                            borderLeft: isSelected ? `3px solid ${day.color}` : "3px solid transparent",
                            transition: "background 0.15s",
                          }}>
                          <div style={{ width: 22, height: 22, borderRadius: "50%", background: day.color,
                            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1,
                            opacity: e.verdict === "skip" ? 0.4 : 1 }}>
                            {i + 1}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#000",
                              opacity: e.verdict === "skip" ? 0.5 : 1 }}>
                              {e.name}
                              {coordsMap[e.id]?.approximate && (
                                <span style={{ fontSize: 10, color: "#f39c12", marginLeft: 6 }}>
                                  ~city area
                                  {onUpdate && (
                                    <span
                                      onMouseDown={ev => { ev.stopPropagation(); setFixingId(fixingId === e.id ? null : e.id); setFixQuery(""); setFixResults([]); }}
                                      style={{ marginLeft: 5, color: "#4285f4", cursor: "pointer", textDecoration: "underline" }}>
                                      Fix
                                    </span>
                                  )}
                                </span>
                              )}
                              {!hasCoord && loading && <span style={{ fontSize: 10, color: "#bbb", marginLeft: 6 }}>locating…</span>}
                              {!hasCoord && !loading && onUpdate && (
                                <span
                                  onMouseDown={ev => { ev.stopPropagation(); setFixingId(fixingId === e.id ? null : e.id); setFixQuery(""); setFixResults([]); }}
                                  style={{ fontSize: 10, color: "#4285f4", marginLeft: 6, cursor: "pointer", textDecoration: "underline" }}>
                                  + Fix location
                                </span>
                              )}
                              {!hasCoord && !loading && !onUpdate && <span style={{ fontSize: 10, color: "#ccc", marginLeft: 6 }}>not mapped</span>}
                            </div>
                            {fixingId === e.id && (
                              <div onMouseDown={ev => ev.stopPropagation()} style={{ marginTop: 6 }}>
                                <input
                                  autoFocus
                                  value={fixQuery}
                                  onChange={ev => setFixQuery(ev.target.value)}
                                  placeholder="Search for correct location…"
                                  style={{ width: "100%", boxSizing: "border-box", fontSize: 11, padding: "5px 8px",
                                    border: "1.5px solid #4285f4", borderRadius: 7, outline: "none" }}
                                />
                                {fixLoading && <div style={{ fontSize: 10, color: "#aaa", marginTop: 4 }}>Searching…</div>}
                                {fixResults.map(p => (
                                  <div key={p.id} onMouseDown={() => applyFixedPin(e, p)}
                                    style={{ padding: "5px 8px", fontSize: 11, cursor: "pointer", borderRadius: 6,
                                      color: p.lat != null && p.lon != null ? "#111" : "#aaa" }}>
                                    {p.name} {p.lat != null && p.lon != null ? <span style={{ color: "#22c55e" }}>📍</span> : <span style={{ color: "#ccc" }}>(no coords)</span>}
                                    <div style={{ fontSize: 10, color: "#bbb" }}>{p.notes}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>
                              {e.city}{e.state ? `, ${e.state}` : ""}{e.cuisine ? ` · ${e.cuisine}` : ""}
                            </div>
                            {e.verdict && (
                              <span style={{ fontSize: 10, fontWeight: 600, marginTop: 3, display: "inline-block",
                                color: e.verdict === "must_go" ? "#27ae60" : e.verdict === "skip" ? "#e74c3c" : "#888" }}>
                                {e.verdict === "must_go" ? "✓ Must Go" : e.verdict === "skip" ? "✗ Skip" : ""}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </div>
        </div>

        {/* ── Legend ── */}
        <div style={{ borderTop: "1px solid #f0f0f0", padding: "8px 18px", display: "flex",
          gap: 16, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Legend</span>
          <span style={{ fontSize: 11, color: "#555", display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 13 }}>●</span> Pin color = Day
          </span>
          <span style={{ fontSize: 11, color: "#f39c12", fontWeight: 600 }}>○ ~city area (outline pin)</span>
          <span style={{ fontSize: 11, color: "#888" }}>Click pin → highlight row · Click row → open pin</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── MINI TRIP MAP (inline non-interactive preview) ──────────────────────────
function MiniTripMap({ trip, onOpenFull, onUpdate }) {
  const [coordsMap, setCoordsMap] = useState({});
  const [done, setDone] = useState(false);

  const allEntries = useMemo(() => {
    if (!trip?.plan?.days) return [];
    return trip.plan.days.flatMap((day, di) =>
      (day.entries || day.items || []).map(e => ({
        ...e, _dayIdx: di, _dayColor: DAY_COLORS[di % DAY_COLORS.length],
      }))
    );
  }, [trip]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const resolvedCoords = {};
      for (const e of allEntries) {
        if (cancelled) break;
        const coord = await geocodeEntry(e, trip.destination);
        resolvedCoords[e.id] = coord;
        if (!cancelled && coord) setCoordsMap(prev => ({ ...prev, [e.id]: coord }));
        if (e.lat == null || e.lon == null) await new Promise(r => setTimeout(r, 600));
      }
      if (!cancelled) {
        setDone(true);
        // Backfill non-approximate coords back into the trip so future loads skip geocoding
        if (onUpdate) {
          let changed = false;
          const updatedDays = trip.plan.days.map(day => ({
            ...day,
            entries: (day.entries || day.items || []).map(e => {
              const coord = resolvedCoords[e.id];
              if ((e.lat == null || e.lon == null) && coord && !coord.approximate) {
                changed = true;
                return { ...e, lat: coord.lat, lon: coord.lng };
              }
              return e;
            }),
          }));
          if (changed) onUpdate({ ...trip, plan: { ...trip.plan, days: updatedDays } });
        }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [allEntries, trip.destination]); // eslint-disable-line react-hooks/exhaustive-deps

  const mapped = allEntries.filter(e => coordsMap[e.id]);

  if (mapped.length === 0) {
    return (
      <div onClick={onOpenFull} style={{ height: 140, background: "#f8f8f8", display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center",
        cursor: "pointer", gap: 6, borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ fontSize: 26 }}>🗺</div>
        <div style={{ fontSize: 12, color: "#bbb" }}>{done ? "No locations found" : "Locating places…"}</div>
        {done && <div style={{ fontSize: 11, color: "#ccc" }}>Tap to open map</div>}
      </div>
    );
  }

  return (
    <div style={{ position: "relative", height: 150, borderBottom: "1px solid #f0f0f0" }}>
      {/* Non-interactive map rendered under pointer-events:none wrapper */}
      <div style={{ pointerEvents: "none", height: 150 }}>
        <MapContainer center={[mapped[0] ? coordsMap[mapped[0].id].lat : 39.5, mapped[0] ? coordsMap[mapped[0].id].lng : -98.35]}
          zoom={10} style={{ width: "100%", height: "100%" }}
          dragging={false} scrollWheelZoom={false} zoomControl={false}
          attributionControl={false} doubleClickZoom={false} touchZoom={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapBoundsUpdater positions={mapped.map(e => coordsMap[e.id])} />
          {mapped.map(e => (
            <Marker key={e.id} position={[coordsMap[e.id].lat, coordsMap[e.id].lng]}
              icon={makePinIcon(e._dayColor, 22, 1, coordsMap[e.id].approximate)} />
          ))}
        </MapContainer>
      </div>
      {/* Transparent click overlay */}
      <div onClick={onOpenFull} style={{ position: "absolute", inset: 0, cursor: "pointer", zIndex: 1000 }}>
        <div style={{ position: "absolute", bottom: 10, right: 10 }}>
          <div style={{ background: "rgba(255,255,255,0.92)", borderRadius: 8, padding: "5px 11px",
            fontSize: 11, fontWeight: 600, color: "#333", boxShadow: "0 2px 8px rgba(0,0,0,0.13)",
            backdropFilter: "blur(4px)" }}>
            View Full Map →
          </div>
        </div>
        <div style={{ position: "absolute", top: 10, left: 10 }}>
          <div style={{ background: "rgba(255,255,255,0.85)", borderRadius: 8, padding: "3px 9px",
            fontSize: 11, color: "#666", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
            {mapped.length} of {allEntries.length} places mapped
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ADD PLACE INPUT WITH SUGGESTIONS ───────────────────────────────────────
// Maps Google Places types to our internal types
function inferTypeFromGoogleTypes(types) {
  if (!types) return "activity";
  if (types.some(t => ["restaurant","food","meal_takeaway","meal_delivery","cafe","bakery","bar","pub","fast_food","food_court"].includes(t))) {
    if (types.some(t => ["bar","night_club","pub","brewery"].includes(t))) return "brewery";
    if (types.some(t => ["cafe","bakery","coffee"].includes(t))) return "cafe";
    return "restaurant";
  }
  if (types.some(t => ["lodging","hotel","hostel"].includes(t))) return "hotel";
  if (types.some(t => t === "national_park" || t === "nature_reserve")) return "national_park";
  if (types.some(t => ["park","natural_feature","campground","hiking","trail"].includes(t))) return "hiking";
  return "activity";
}

async function searchGooglePlaces(query, destination) {
  try {
    const url = `/api/places?q=${encodeURIComponent(query)}&destination=${encodeURIComponent(destination)}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    if (data.error || data.status === "REQUEST_DENIED") return [];
    return (data.results || []).slice(0, 6).map(p => {
      // _city/_state/_country are set by Nominatim fallback; Google uses formatted_address
      const city = p._city || (() => {
        const parts = (p.formatted_address || "").split(",").map(s => s.trim());
        return parts[1] || parts[0] || "";
      })();
      const state = p._state || (() => {
        const parts = (p.formatted_address || "").split(",").map(s => s.trim());
        return parts[2]?.replace(/\d+/g, "").trim() || "";
      })();
      return {
        id: "gp_" + p.place_id,
        name: p.name,
        type: inferTypeFromGoogleTypes(p.types),
        city,
        state,
        country: p._country || "",
        verdict: "want_to_try",
        cuisine: "",
        notes: "",
        rating: p.rating || null,
        lat: p.geometry?.location?.lat ?? p._lat ?? null,
        lon: p.geometry?.location?.lng ?? p._lng ?? null,
        _source: "google",
      };
    });
  } catch {
    return [];
  }
}

function AddPlaceInput({ entries, trip, onAdd }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [googleResults, setGoogleResults] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const destParts = (trip.destination || "").split(",").map(s => s.trim());
  const destState = destParts[1] || "";
  const destCountry = destParts[2] || destParts[1] || "";

  // Local entries filtered to same region
  const localSuggestions = query.trim().length >= 1
    ? (entries || []).filter(e => {
        const q = query.toLowerCase();
        const matchesQuery = e.name.toLowerCase().includes(q) ||
          (e.city || "").toLowerCase().includes(q) ||
          (e.cuisine || "").toLowerCase().includes(q);
        const sameRegion = destState
          ? (e.state || "").toLowerCase() === destState.toLowerCase()
          : destCountry
            ? (e.country || "").toLowerCase().includes(destCountry.toLowerCase())
            : true;
        return matchesQuery && sameRegion;
      }).slice(0, 4)
    : [];

  // Trigger Google Places search (backend handles Nominatim fallback with proper User-Agent)
  useEffect(() => {
    if (query.trim().length < 2) { setGoogleResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setGoogleLoading(true);
      const results = await searchGooglePlaces(query, trip.destination || "");
      setGoogleResults(results);
      setGoogleLoading(false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [query, trip.destination]);

  const commit = (entry) => {
    if (typeof entry === "string") {
      if (!entry.trim()) return;
      onAdd({ name: entry.trim() });
    } else {
      onAdd(entry);
    }
    setQuery("");
    setOpen(false);
    setGoogleResults([]);
  };

  const handleFocus = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    setOpen(true);
  };

  const hasResults = localSuggestions.length > 0 || googleResults.length > 0 || googleLoading || query.trim();

  const PlaceRow = ({ e, badge }) => (
    <div onMouseDown={() => commit(e)}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px",
        cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
      onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
      onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
      <span style={{ fontSize: 16 }}>
        {{ restaurant: "🍽", hotel: "🏨", brewery: "🍺", cafe: "☕", activity: "🏔", hiking: "🥾", national_park: "🏕" }[e.type] || "📍"}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</div>
        <div style={{ fontSize: 11, color: "#aaa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {e._source === "google"
            ? e.notes
            : [e.cuisine || e.type, e.city, e.state].filter(Boolean).join(" · ")}
        </div>
      </div>
      {badge && <span style={{ fontSize: 9, fontWeight: 700, color: badge.color, background: badge.bg, border: `1px solid ${badge.border}`, borderRadius: 6, padding: "2px 6px", flexShrink: 0 }}>{badge.label}</span>}
    </div>
  );

  return (
    <div style={{ position: "relative", marginTop: 2 }} onClick={e => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (inputRef.current) setRect(inputRef.current.getBoundingClientRect()); }}
        onFocus={e => { handleFocus(); e.target.style.borderColor = "#ccc"; }}
        onBlur={e => { setTimeout(() => setOpen(false), 150); e.target.style.borderColor = "#e8e8e8"; }}
        onKeyDown={ev => { if (ev.key === "Enter") { commit(query); ev.target.blur(); } if (ev.key === "Escape") { setOpen(false); setQuery(""); } }}
        placeholder="+ Add a place…"
        style={{ width: "100%", boxSizing: "border-box", background: "#fff",
          border: "1.5px solid #c4956a", borderRadius: 12, padding: "9px 13px",
          fontSize: 12, color: "#444", outline: "none", cursor: "text" }}
      />
      {open && rect && hasResults && createPortal(
        <div style={{
          position: "fixed", top: rect.bottom + 4, left: rect.left, width: rect.width,
          background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 9999, maxHeight: 340, overflowY: "auto",
          overscrollBehavior: "contain"
        }}
        onWheel={e => e.stopPropagation()}>
          {/* Local network results */}
          {localSuggestions.length > 0 && (
            <>
              <div style={{ padding: "6px 14px 4px", fontSize: 10, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.06em" }}>From your network</div>
              {localSuggestions.map(e => (
                <PlaceRow key={e.id} e={e} badge={e.verdict === "must_go" ? { label: "MUST GO", color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" } : null} />
              ))}
            </>
          )}

          {/* Google Places results */}
          {(googleLoading || googleResults.length > 0) && (
            <>
              <div style={{ padding: "6px 14px 4px", fontSize: 10, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.06em", borderTop: localSuggestions.length > 0 ? "1px solid #f0f0f0" : "none" }}>
                {googleLoading ? "Searching Google Places…" : "Google Places"}
              </div>
              {googleResults.map(e => (
                <PlaceRow key={e.id} e={e} badge={{ label: "Google", color: "#4285f4", bg: "#f0f6ff", border: "#c7d9ff" }} />
              ))}
            </>
          )}

          {/* Fallback: add as free text */}
          {query.trim() && (
            <div onMouseDown={() => commit(query)}
              style={{ padding: "9px 14px", cursor: "pointer", fontSize: 12, color: "#888", fontStyle: "italic", borderTop: "1px solid #f0f0f0" }}
              onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
              onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
              Add "{query}" as new place
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}


// ─── RESERVATIONS TAB ────────────────────────────────────────────────────────
const BLANK_RES = { type: "flight", name: "", ref: "", status: "need_to_reserve", notes: "" };
function ReservationsTab({ trip, onUpdate, initialFilter = "all", onFilterChange }) {
  const RES_TYPES = [
    { id: "flight", icon: "✈️", label: "Flight" },
    { id: "hotel", icon: "🏨", label: "Hotel" },
    { id: "food", icon: "🍽", label: "Restaurant" },
    { id: "experience", icon: "🎟", label: "Experience" },
  ];
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_RES);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const reservations = trip.reservations || [];
  const STATUS_COLORS = { need_to_reserve: "#f59e0b", reserved: "#22c55e", cancelled: "#ef4444", confirmed: "#22c55e", pending: "#f59e0b" };
  const STATUS_LABELS = { need_to_reserve: "Need to Reserve", reserved: "Reserved", cancelled: "Cancelled", confirmed: "Reserved", pending: "Need to Reserve" };

  const changeFilter = f => { setFilter(f); onFilterChange?.(f); };

  const todoCount = reservations.filter(r => r.status === "need_to_reserve" || r.status === "pending").length;
  const doneCount = reservations.filter(r => r.status === "reserved" || r.status === "confirmed").length;
  const cancelledCount = reservations.filter(r => r.status === "cancelled").length;

  const visibleReservations = filter === "todo"
    ? reservations.filter(r => r.status === "need_to_reserve" || r.status === "pending")
    : filter === "done"
    ? reservations.filter(r => r.status === "reserved" || r.status === "confirmed")
    : filter === "cancelled"
    ? reservations.filter(r => r.status === "cancelled")
    : reservations;

  const openAdd = () => { setForm(BLANK_RES); setEditingId(null); setShowForm(true); };
  const openEdit = (r) => { setForm({ type: r.type, name: r.name, ref: r.ref || "", status: r.status, notes: r.notes || "" }); setEditingId(r.id); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(BLANK_RES); };

  const saveReservation = () => {
    if (!form.name.trim()) return;
    if (editingId) {
      onUpdate && onUpdate({ ...trip, reservations: reservations.map(r => r.id === editingId ? { ...r, ...form } : r) });
    } else {
      onUpdate && onUpdate({ ...trip, reservations: [...reservations, { id: "res_" + Date.now(), ...form }] });
    }
    closeForm();
  };

  const resFormJsx = showForm && (
    <div style={{ background: "#fafaf8", border: "1px solid #e8e4dc", borderRadius: 14, padding: "14px 16px", marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#7B2D42", marginBottom: 8 }}>
        {editingId ? "Edit Reservation" : "New Reservation"}
      </div>
      <input autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        onKeyDown={e => { if (e.key === "Enter") saveReservation(); }}
        placeholder="e.g. United Airlines UA 123, Marriott Downtown…"
        style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e8e8e8", borderRadius: 8,
          padding: "9px 12px", fontSize: 13, outline: "none", marginBottom: 8, background: "#fff", color: "#222" }} />
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
          style={{ flex: 1, border: "1px solid #e8e8e8", borderRadius: 8, padding: "8px 10px", fontSize: 12,
            outline: "none", background: "#fff", color: "#222" }}>
          <option value="need_to_reserve">Need to Reserve</option>
          <option value="reserved">Reserved</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
          style={{ flex: 1, border: "1px solid #e8e8e8", borderRadius: 8, padding: "8px 10px", fontSize: 12,
            outline: "none", background: "#fff", color: "#222" }}>
          {RES_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={saveReservation}
          style={{ flex: 1, background: "#7B2D42", border: "none", borderRadius: 8, padding: "9px",
            color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {editingId ? "Update" : "Save"}
        </button>
        <button onClick={closeForm}
          style={{ flex: 1, background: "#f0f0f0", border: "none", borderRadius: 8, padding: "9px",
            color: "#555", fontSize: 12, cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div onClick={e => e.stopPropagation()} style={{ padding: "16px 20px" }}>
      {/* Filter pills */}
      {reservations.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
          {[
            { id: "all", label: `All ${reservations.length}` },
            { id: "todo", label: `To book ${todoCount}`, color: "#f59e0b" },
            { id: "done", label: `Booked ${doneCount}`, color: "#22c55e" },
            ...(cancelledCount ? [{ id: "cancelled", label: `Cancelled ${cancelledCount}`, color: "#ef4444" }] : []),
          ].map(f => (
            <button key={f.id} onClick={() => changeFilter(f.id)}
              style={{ borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", border: "none",
                background: filter === f.id ? (f.color || "#222") : "#f0f0f0",
                color: filter === f.id ? "#fff" : "#666" }}>
              {f.label}
            </button>
          ))}
        </div>
      )}
      {visibleReservations.length === 0 && !showForm && (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#bbb" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 13 }}>{reservations.length === 0 ? "No reservations yet" : "None in this category"}</div>
          {reservations.length === 0 && <div style={{ fontSize: 11, marginTop: 4, marginBottom: 14 }}>Track flights, hotels, restaurants, and experiences</div>}
          {reservations.length === 0 && (
            <button onClick={openAdd}
              style={{ background: "#111", border: "none", borderRadius: 10, padding: "9px 20px",
                color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              + Add Reservation
            </button>
          )}
        </div>
      )}
      {showForm && !editingId && resFormJsx}
      {visibleReservations.map(r => {
        const typeInfo = RES_TYPES.find(t => t.id === r.type) || RES_TYPES[0];
        const isConfirmingDelete = confirmDeleteId === r.id;
        if (editingId === r.id && showForm) return <div key={r.id} style={{ marginBottom: 10 }}>{resFormJsx}</div>;
        return (
          <div key={r.id} style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 14,
            padding: "12px 14px", marginBottom: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            {isConfirmingDelete ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ flex: 1, fontSize: 12, color: "#ef4444" }}>Delete "{r.name}"?</span>
                <button onClick={() => { onUpdate && onUpdate({ ...trip, reservations: reservations.filter(x => x.id !== r.id) }); setConfirmDeleteId(null); }}
                  style={{ background: "#ef4444", border: "none", borderRadius: 7, padding: "5px 12px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Delete</button>
                <button onClick={() => setConfirmDeleteId(null)}
                  style={{ background: "#f5f5f5", border: "none", borderRadius: 7, padding: "5px 10px", color: "#555", fontSize: 11, cursor: "pointer" }}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: "#faf5f0",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                  {typeInfo.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{r.name}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: STATUS_COLORS[r.status] || "#888",
                      background: (STATUS_COLORS[r.status] || "#888") + "18", borderRadius: 20,
                      padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>
                    {typeInfo.label}{r.ref ? ` · Ref: ${r.ref}` : ""}
                  </div>
                  {r.notes && <div style={{ fontSize: 11, color: "#bbb", marginTop: 4, fontStyle: "italic" }}>{r.notes}</div>}
                </div>
                {onUpdate && (
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button onClick={() => openEdit(r)} title="Edit"
                      style={{ background: "#f5f5f5", border: "none", borderRadius: 7, padding: "5px 10px", color: "#555", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Edit</button>
                    <button onClick={() => setConfirmDeleteId(r.id)} title="Delete"
                      style={{ background: "none", border: "none", color: "#ddd", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "2px 4px" }}>×</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {!showForm && (
        <button onClick={openAdd}
          style={{ width: "100%", background: "#f0ede8", border: "1.5px dashed #d4c8bc", borderRadius: 12,
            padding: "11px", color: "#7B2D42", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          + Add Reservation
        </button>
      )}
    </div>
  );
}

// Tries Open-Meteo then Nominatim across progressively shorter query forms.
// Returns { name, latitude, longitude } or null.
async function resolveWeatherLocation(destination, signal) {
  const full = destination.trim();
  const firstSegment = full.split(",")[0].trim();
  const firstWords = firstSegment.split(/\s+/).slice(0, 2).join(" ");
  const candidates = [...new Set([full, firstSegment, firstWords])].filter(Boolean);

  for (const query of candidates) {
    try {
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=1&language=en&format=json`,
        { signal }
      );
      const data = await res.json();
      const loc = data.results?.[0];
      if (loc && loc.latitude != null && loc.longitude != null) {
        return { name: loc.name, latitude: loc.latitude, longitude: loc.longitude };
      }
    } catch (e) { if (e.name === "AbortError") throw e; }
  }

  for (const query of candidates) {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
        { signal, headers: { "Accept-Language": "en", "User-Agent": "TravelSlate/1.0" } }
      );
      const data = await res.json();
      const loc = data?.[0];
      if (loc && loc.lat != null && loc.lon != null) {
        return { name: loc.display_name.split(",")[0].trim(), latitude: parseFloat(loc.lat), longitude: parseFloat(loc.lon) };
      }
    } catch (e) { if (e.name === "AbortError") throw e; }
  }

  return null;
}

// ─── WEATHER TAB ──────────────────────────────────────────────────────────────
function WeatherTab({ trip }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tooFarAhead, setTooFarAhead] = useState(null); // { cityName, locName }
  const [retryKey, setRetryKey] = useState(0);

  const WMO = {
    0: { label: "Clear", icon: "☀️" }, 1: { label: "Mostly Clear", icon: "🌤" },
    2: { label: "Partly Cloudy", icon: "⛅" }, 3: { label: "Overcast", icon: "☁️" },
    45: { label: "Foggy", icon: "🌫" }, 48: { label: "Icy Fog", icon: "🌫" },
    51: { label: "Drizzle", icon: "🌦" }, 53: { label: "Drizzle", icon: "🌦" },
    55: { label: "Heavy Drizzle", icon: "🌧" }, 61: { label: "Light Rain", icon: "🌧" },
    63: { label: "Rain", icon: "🌧" }, 65: { label: "Heavy Rain", icon: "🌧" },
    71: { label: "Light Snow", icon: "🌨" }, 73: { label: "Snow", icon: "❄️" },
    75: { label: "Heavy Snow", icon: "❄️" }, 80: { label: "Showers", icon: "🌦" },
    81: { label: "Showers", icon: "🌦" }, 82: { label: "Heavy Showers", icon: "⛈" },
    95: { label: "Thunderstorm", icon: "⛈" }, 96: { label: "Thunderstorm", icon: "⛈" },
    99: { label: "Thunderstorm", icon: "⛈" },
  };

  useEffect(() => {
    if (!trip.destination || !trip.startDate) return;
    const controller = new AbortController(); // eslint-disable-next-line react-hooks/exhaustive-deps
    (async () => {
      setLoading(true); setError(""); setWeather(null);
      try {
        const loc = await resolveWeatherLocation(trip.destination, controller.signal);
        if (!loc) throw new Error(`Could not find weather coordinates for "${trip.destination}".`);

        const tripStart = new Date(trip.startDate + "T12:00:00");
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const daysUntil = Math.ceil((tripStart - today) / 86400000);
        const isHistorical = daysUntil < -1;
        const isTooFarAhead = daysUntil > 16;

        if (isTooFarAhead) {
          setTooFarAhead({ locName: loc.name });
          setLoading(false);
          return;
        }
        setTooFarAhead(null);

        const end = trip.endDate || trip.startDate;
        // Use archive API for past trips, forecast API for upcoming
        const baseUrl = isHistorical
          ? `https://archive-api.open-meteo.com/v1/archive`
          : `https://api.open-meteo.com/v1/forecast`;
        const wRes = await fetch(
          `${baseUrl}?latitude=${loc.latitude}&longitude=${loc.longitude}&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weathercode&start_date=${trip.startDate}&end_date=${end}&timezone=auto&temperature_unit=fahrenheit`,
          { signal: controller.signal }
        );
        const wData = await wRes.json();
        if (wData.error) throw new Error(wData.reason || "Weather data unavailable for these dates.");
        if (!wData.daily?.time?.length) throw new Error("No weather data returned for these dates.");
        setWeather({ locName: loc.name, daily: wData.daily });
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Could not load weather.");
      }
      setLoading(false);
    })();
    return () => controller.abort();
  }, [trip.destination, trip.startDate, trip.endDate, retryKey]);

  if (!trip.startDate) return (
    <div style={{ padding: "40px 20px", textAlign: "center", color: "#bbb" }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🌤</div>
      <div style={{ fontSize: 13 }}>Add trip dates to see the weather forecast</div>
    </div>
  );

  return (
    <div onClick={e => e.stopPropagation()} style={{ padding: "16px 20px" }}>
      {loading && (
        <div style={{ textAlign: "center", padding: "32px 0", color: "#bbb" }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>🌤</div>
          <div style={{ fontSize: 12 }}>Loading forecast…</div>
        </div>
      )}
      {error && (
        <div style={{ padding: "16px", background: "#fff5f5", border: "1px solid #fecaca",
          borderRadius: 12, color: "#ef4444", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <span>{error}</span>
          <button onClick={() => setRetryKey(k => k + 1)}
            style={{ flexShrink: 0, background: "#ef4444", border: "none", borderRadius: 8,
              padding: "5px 12px", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
            Retry
          </button>
        </div>
      )}
      {tooFarAhead && !loading && (
        <div style={{ textAlign: "center", padding: "32px 20px" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🌤</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#333", marginBottom: 4 }}>Forecast not available yet</div>
          <div style={{ fontSize: 12, color: "#aaa", marginBottom: 20 }}>Check back within 16 days of your trip.</div>
          <a href={`https://www.google.com/search?q=weather+${encodeURIComponent(tooFarAhead.locName)}`}
            target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-block", background: "#000", color: "#fff", borderRadius: 10,
              padding: "10px 20px", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
            View forecast for {tooFarAhead.locName} ↗
          </a>
        </div>
      )}
      {weather && !loading && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", letterSpacing: "0.07em",
            textTransform: "uppercase", marginBottom: 12 }}>
            Forecast · {weather.locName}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(weather.daily.time || []).map((date, i) => {
              const info = WMO[weather.daily.weathercode?.[i]] || { label: "—", icon: "🌡" };
              const hi = Math.round(weather.daily.temperature_2m_max?.[i] ?? 0);
              const lo = Math.round(weather.daily.temperature_2m_min?.[i] ?? 0);
              const rain = weather.daily.precipitation_probability_max?.[i] ?? 0;
              const dayLabel = new Date(date + "T12:00:00").toLocaleDateString("en-US",
                { weekday: "short", month: "short", day: "numeric" });
              return (
                <div key={date} style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 14,
                  padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{info.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{dayLabel}</div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                      {info.label}{rain > 20 ? ` · 💧 ${rain}%` : ""}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>{hi}°</div>
                    <div style={{ fontSize: 11, color: "#bbb" }}>{lo}°</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <div style={{ fontSize: 10, color: "#ccc" }}>Powered by Open-Meteo</div>
            <a href={`https://www.google.com/search?q=weather+${encodeURIComponent(weather.locName)}`}
              target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: "#555", textDecoration: "none", fontWeight: 500 }}>
              Full forecast ↗
            </a>
          </div>
        </>
      )}
    </div>
  );
}

// ─── FRIEND LOGS TAB ──────────────────────────────────────────────────────────
function FriendLogsTab({ trip, entries, allUsers, friendState, onUpdate, onViewProfile }) {
  const [pickingDayFor, setPickingDayFor] = useState(null); // entry id whose day selector is open

  const friendEntries = (entries || []).filter(e => friendState?.[e.userId] === "friend");

  // Match against destination city (part before first comma) and state/region (part after)
  const destParts = (trip.destination || "").split(",").map(s => s.trim().toLowerCase());
  const destCity = destParts[0] || "";
  const destState = destParts[1] || "";
  const nearby = friendEntries.filter(e => {
    const eCity = (e.city || "").toLowerCase().trim();
    const eState = (e.state || "").toLowerCase().trim();
    if (destCity && eCity && (destCity.includes(eCity) || eCity.includes(destCity))) return true;
    if (destState && eState && (destState.includes(eState) || eState.includes(destState))) return true;
    return false;
  });

  const addToItinerary = (entry, dayIdx) => {
    if (!onUpdate || !trip.plan?.days?.length) return;
    const target = dayIdx ?? 0;
    const newEntry = {
      ...entry,
      id: "fl_" + Date.now(),
      visited: false,
      // preserve coords if the source entry has them
      lat: entry.lat ?? null,
      lon: entry.lon ?? null,
    };
    const newDays = trip.plan.days.map((d, i) =>
      i === target ? { ...d, entries: [...(d.entries || []), newEntry] } : d
    );
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
    setPickingDayFor(null);
  };

  const formatWhen = ts => {
    if (!ts) return "";
    const diff = Math.floor((Date.now() - new Date(ts)) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7) return `${diff}d ago`;
    if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
    return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div onClick={e => e.stopPropagation()} style={{ padding: "16px 20px" }}>
      {nearby.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px 0", color: "#bbb" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
          <div style={{ fontSize: 13 }}>No friend logs near {trip.destination}</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>When your friends log places here, they'll appear</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", letterSpacing: "0.07em",
            textTransform: "uppercase", marginBottom: 12 }}>
            {nearby.length} log{nearby.length !== 1 ? "s" : ""} near {trip.destination}
          </div>
          {nearby.map(e => {
            const user = (allUsers || []).find(u => u.id === e.userId);
            return (
              <div key={e.id} style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 14,
                padding: "11px 13px", marginBottom: 8, display: "flex", gap: 10, alignItems: "flex-start",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: "#f0ede8",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                  {{ restaurant: "🍽", hotel: "🏨", brewery: "🍺", cafe: "☕", activity: "🏔", hiking: "🥾", national_park: "🏕" }[e.type] || "📍"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111", lineHeight: 1.3 }}>{e.name}</div>
                  <div style={{ fontSize: 11, color: "#bbb", marginTop: 2 }}>
                    {[e.type, e.city].filter(Boolean).join(" · ")}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: "#888" }}>{user?.avatar || "👤"} {user ? (
                      <span onClick={ev => { ev.stopPropagation(); onViewProfile && onViewProfile(user.id); }}
                        style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "#ccc" }}>
                        @{user.username || "friend"}
                      </span>
                    ) : "@friend"}</span>
                    <span style={{ color: "#ddd" }}>·</span>
                    <span style={{ fontSize: 11, color: "#bbb" }}>{formatWhen(e.created_at || e.createdAt)}</span>
                  </div>
                </div>
                <div style={{ flexShrink: 0, position: "relative" }}>
                  {pickingDayFor === e.id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, background: "#fff",
                      border: "1px solid #e0e0e0", borderRadius: 10, padding: "6px 8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)", position: "absolute", right: 0, top: 0,
                      zIndex: 100, minWidth: 120 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", marginBottom: 2,
                        textTransform: "uppercase", letterSpacing: "0.05em" }}>Add to which day?</div>
                      {(trip.plan?.days || []).map((d, i) => (
                        <button key={i} onClick={() => addToItinerary(e, i)}
                          style={{ border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer",
                            background: "#f9f5f2", color: "#7B2D42", fontSize: 12, fontWeight: 600,
                            textAlign: "left", whiteSpace: "nowrap" }}>
                          Day {i + 1}{d.name ? ` — ${d.name}` : ""}
                        </button>
                      ))}
                      <button onClick={() => setPickingDayFor(null)}
                        style={{ border: "none", background: "none", color: "#bbb", fontSize: 11,
                          cursor: "pointer", padding: "2px 0", marginTop: 1 }}>Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => {
                      if ((trip.plan?.days?.length ?? 0) > 1) {
                        setPickingDayFor(e.id);
                      } else {
                        addToItinerary(e, 0);
                      }
                    }} style={{ border: "none", borderRadius: 8, padding: "5px 12px", cursor: "pointer",
                      background: "#f0ede8", color: "#7B2D42", fontSize: 12, fontWeight: 600,
                      whiteSpace: "nowrap" }}>+ Add</button>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ─── TRIP CARD COMPONENT ─────────────────────────────────────────────────────
function getDestinationFlag(destination) {
  const FLAGS = { france:"🇫🇷", italy:"🇮🇹", spain:"🇪🇸", japan:"🇯🇵", "united kingdom":"🇬🇧", uk:"🇬🇧", england:"🇬🇧", germany:"🇩🇪", canada:"🇨🇦", mexico:"🇲🇽", thailand:"🇹🇭", australia:"🇦🇺", brazil:"🇧🇷", india:"🇮🇳", greece:"🇬🇷", portugal:"🇵🇹", indonesia:"🇮🇩", bali:"🇮🇩", vietnam:"🇻🇳", peru:"🇵🇪", colombia:"🇨🇴", argentina:"🇦🇷", "new zealand":"🇳🇿", ireland:"🇮🇪", netherlands:"🇳🇱", switzerland:"🇨🇭", sweden:"🇸🇪", norway:"🇳🇴", iceland:"🇮🇸", china:"🇨🇳", "south korea":"🇰🇷", singapore:"🇸🇬", malaysia:"🇲🇾", turkey:"🇹🇷", egypt:"🇪🇬", morocco:"🇲🇦", "south africa":"🇿🇦", kenya:"🇰🇪", "costa rica":"🇨🇷", cuba:"🇨🇺", jamaica:"🇯🇲", nepal:"🇳🇵", philippines:"🇵🇭" };
  const parts = (destination || "").split(",").map(s => s.trim().toLowerCase());
  for (let i = parts.length - 1; i >= 0; i--) {
    if (FLAGS[parts[i]]) return FLAGS[parts[i]];
  }
  return null;
}

function getDestEmoji(destination) {
  const d = (destination || "").toLowerCase();
  if (/beach|hawaii|maldives|bali|cancun|caribbean|bahamas|fiji|phuket|barbados|miami|tulum|mykonos|santorini/i.test(d)) return "🏖";
  if (/paris|rome|barcelona|london|berlin|amsterdam|prague|florence|venice|madrid|lisbon|athens|vienna|budapest|edinburgh/i.test(d)) return "🏛";
  if (/amazon|costa rica|jungle|rainforest|borneo|ecuador|madagascar|belize/i.test(d)) return "🌿";
  if (/tokyo|kyoto|seoul|beijing|shanghai|bangkok|singapore|hong kong|taipei|vietnam|india|bali|jakarta|hanoi/i.test(d)) return "🏯";
  if (/mountain|alps|rockies|colorado|denver|aspen|vail|jackson|banff|whistler|patagonia|nepal|tibet|swiss/i.test(d)) return "🏔";
  if (/safari|kenya|tanzania|africa|serengeti|kruger|botswana|namibia|rwanda/i.test(d)) return "🦁";
  if (/desert|sahara|arizona|utah|dubai|abu dhabi|marrakech|jordan|petra/i.test(d)) return "🌵";
  return "✈️";
}

function getIdeaGradient(destination) {
  const d = (destination || "").toLowerCase();
  if (/beach|hawaii|maldives|bali|cancun|caribbean|fiji|phuket|miami|tulum|mykonos/i.test(d)) return "linear-gradient(135deg, #7dd3fc 0%, #0369a1 100%)";
  if (/paris|rome|barcelona|london|berlin|amsterdam|prague|florence|venice|madrid|lisbon|athens|vienna/i.test(d)) return "linear-gradient(135deg, #fcd5b5 0%, #c4724a 100%)";
  if (/amazon|costa rica|jungle|rainforest|borneo|ecuador|madagascar/i.test(d)) return "linear-gradient(135deg, #86efac 0%, #14532d 100%)";
  if (/tokyo|kyoto|seoul|beijing|shanghai|bangkok|singapore|taipei|vietnam|india|hong kong/i.test(d)) return "linear-gradient(135deg, #c4b5fd 0%, #6b21a8 100%)";
  if (/safari|kenya|tanzania|africa|serengeti/i.test(d)) return "linear-gradient(135deg, #fde68a 0%, #92400e 100%)";
  if (/mountain|alps|colorado|aspen|banff|patagonia|nepal|swiss/i.test(d)) return "linear-gradient(135deg, #bfdbfe 0%, #1e3a8a 100%)";
  if (/desert|sahara|utah|dubai|marrakech/i.test(d)) return "linear-gradient(135deg, #fde68a 0%, #b45309 100%)";
  return "linear-gradient(135deg, #f5f5f4 0%, #78716c 100%)";
}

function TripCard({ trip, entries, onDelete, onComplete, onReactivate, past, friendState, allUsers, onInviteFriend, onAddPlaces, onImportToLogs, onImportSingle, onUpdate, onEdit, onShare, onDuplicate, onCopyToCurrentTrip, focusSection, onFocusHandled, onViewProfile, leaveMode }) {
  const cardRef = useRef(null);
  const [savedOffline, setSavedOffline] = useState(() => !!localStorage.getItem("ts_offline_" + trip.id));

  // Keep offline cache in sync whenever trip data changes
  useEffect(() => {
    if (savedOffline) localStorage.setItem("ts_offline_" + trip.id, JSON.stringify(trip));
  }, [trip, savedOffline]); // eslint-disable-line react-hooks/exhaustive-deps
  const regenAbortRef = useRef(null);
  useEffect(() => () => regenAbortRef.current?.abort(), []);
  const [expanded, setExpanded] = useState(!!focusSection);
  // eslint-disable-next-line no-unused-vars
  const [regenNotes, setRegenNotes] = useState(trip.notes || "");
  // eslint-disable-next-line no-unused-vars
  const [regenLoading, setRegenLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [regenError, setRegenError] = useState("");
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [confirmingComplete, setConfirmingComplete] = useState(false);
  const [completePhotoStep, setCompletePhotoStep] = useState(false); // after confirm → photo prompt
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [showInviteDropdown, setShowInviteDropdown] = useState(false);
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteRole, setInviteRole] = useState("collaborator");
  const [shareSearch, setShareSearch] = useState("");
  const [editingUnlocked, setEditingUnlocked] = useState(false);

  const canEdit = !past || editingUnlocked;

  const [newPackItem, setNewPackItem] = useState("");
  const [showPackList, setShowPackList] = useState(focusSection === "packList");
  const [showBookRemind, setShowBookRemind] = useState(focusSection === "bookRemind");
  const [showIdeasNote, setShowIdeasNote] = useState(focusSection === "ideasNote");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [initialMapDay, setInitialMapDay] = useState(null);
  const [collapsedDays, setCollapsedDays] = useState(new Set());
  const toggleDayCollapse = idx => setCollapsedDays(prev => {
    const s = new Set(prev); s.has(idx) ? s.delete(idx) : s.add(idx); return s;
  });
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [overflowMenuPos, setOverflowMenuPos] = useState(null);
  const overflowBtnRef = useRef(null);
  const utilitySectionRef = useRef(null);
  const dragPlaceRef = useRef(null);
  const [localTripNotes, setLocalTripNotes] = useState(trip.notes || "");
  const tripNotesTimer = useRef(null);
  const [localDayNotes, setLocalDayNotes] = useState(() =>
    Object.fromEntries((trip.plan?.days || []).map((d, i) => [i, d.notes || ""]))
  );
  const dayNotesTimers = useRef({});
  const [localDayAccom, setLocalDayAccom] = useState(() =>
    Object.fromEntries((trip.plan?.days || []).map((d, i) => [i, d.accommodation || ""]))
  );
  const accomTimers = useRef({});
  const [editingAccomIdx, setEditingAccomIdx] = useState(null);
  const [plannerTab, setPlannerTab] = useState(
    focusSection === "packList" ? "packList" :
    (focusSection === "bookRemind" || focusSection === "reservations" || focusSection === "reservations_todo" || focusSection === "reservations_done") ? "reservations" :
    focusSection === "ideasNote" ? "notes" : "itinerary"
  );
  const [tabCollapsed, setTabCollapsed] = useState(false);
  const [reservationFilter, setReservationFilter] = useState(
    focusSection === "reservations_todo" ? "todo" :
    focusSection === "reservations_done" ? "done" : "all"
  );
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const [tripSearch, setTripSearch] = useState("");
  const [tripSearchOpen, setTripSearchOpen] = useState(false);
  const placeRefs = useRef({});
  const [editPlanEntry, setEditPlanEntry] = useState(null); // { dayIdx, entry }
  const [expandedNoteIds, setExpandedNoteIds] = useState(new Set());
  const toggleNoteExpand = id => setExpandedNoteIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const [editingDayIdx, setEditingDayIdx] = useState(null);
  const [localDayName, setLocalDayName] = useState(null);
  const dayNameTimer = useRef(null);
  const onFocusHandledRef = useRef(onFocusHandled);
  useEffect(() => { onFocusHandledRef.current = onFocusHandled; });

  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuId]);

  const openKebab = (ev, id) => {
    ev.stopPropagation();
    ev.preventDefault();
    if (openMenuId === id) { setOpenMenuId(null); return; }
    const rect = ev.currentTarget.getBoundingClientRect();
    const maxMenuHeight = Math.min(320, window.innerHeight * 0.8);
    const spaceBelow = window.innerHeight - rect.bottom;
    let top = spaceBelow < maxMenuHeight
      ? Math.max(8, rect.top - maxMenuHeight - 4)
      : rect.bottom + 4;
    // Clamp so menu never extends below viewport
    top = Math.min(top, window.innerHeight - maxMenuHeight - 8);
    setMenuPos({ top: Math.max(8, top), right: window.innerWidth - rect.right });
    setOpenMenuId(id);
  };

  useEffect(() => {
    if (!expanded) setConfirmingComplete(false);
  }, [expanded]);

  useEffect(() => {
    if (!showBookRemind && !showPackList && !showIdeasNote) return;
    const handleClickOutside = e => {
      if (utilitySectionRef.current && !utilitySectionRef.current.contains(e.target)) {
        setShowBookRemind(false);
        setShowPackList(false);
        setShowIdeasNote(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showBookRemind, showPackList, showIdeasNote]);

  // No click-outside-to-collapse: TripCard has too many portaled elements
  // (autocompletes, menus, map modal) that would all falsely trigger a collapse.
  // The card header click is the explicit collapse gesture.

  useEffect(() => {
    if (!focusSection) return;
    const timer = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      onFocusHandledRef.current?.();
    }, 100);
    return () => clearTimeout(timer);
  }, [focusSection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showShareDropdown) return;
    const close = () => { setShowShareDropdown(false); setShareSearch(""); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [showShareDropdown]);

  useEffect(() => {
    if (!showInviteDropdown) return;
    const close = () => { setShowInviteDropdown(false); setInviteSearch(""); setInviteRole("collaborator"); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [showInviteDropdown]);

  useEffect(() => {
    if (!showOverflowMenu) return;
    const close = () => setShowOverflowMenu(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [showOverflowMenu]);

  useEffect(() => {
    if (!showOverflowMenu) {
      setConfirmingDelete(false);
      setConfirmingComplete(false);
    }
  }, [showOverflowMenu]);

  const openOverflowMenu = (e) => {
    e.stopPropagation();
    const rect = overflowBtnRef.current?.getBoundingClientRect();
    if (rect) setOverflowMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    setShowOverflowMenu(v => !v);
  };

  const toggleVisited = (dayIdx, entryId) => {
    if (!onUpdate) return;
    const newDays = trip.plan.days.map((d, i) =>
      i === dayIdx
        ? { ...d, entries: (d.entries || d.items || []).map(e => e.id === entryId ? { ...e, visited: !e.visited } : e) }
        : d
    );
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
  };

  // eslint-disable-next-line no-unused-vars
  const regenerateFromNotes = async () => {
    if (!regenNotes.trim()) return;
    setRegenLoading(true);
    setRegenError("");
    try {
      const n = trip.plan.days.length;
      const dest = trip.destination || "the destination";
      const distUnit = usesImperial(dest) ? "miles" : "km";
      const distLabel = distUnit === "miles" ? "mi" : "km";
      const existingPlaces = trip.plan.days
        .flatMap(d => (d.entries || []).map(e => `${e.name}${e.city ? " (" + e.city + ")" : ""}`))
        .join(", ");
      const regenController = new AbortController();
      regenAbortRef.current = regenController;
      const response = await fetch("/api/generate-itinerary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: regenController.signal,
        body: JSON.stringify({
          model: "llama-3.3-70b-versatile",
          max_tokens: 3500,
          messages: [{
            role: "user",
            content: `You are an expert travel itinerary planner. Rebuild this ${n}-day trip to ${dest} using the updated notes below.

Instructions:
1. Extract every place, restaurant, bar, attraction, or activity mentioned in the notes.
2. Group places by neighborhood or geographic area — places within walking distance or a short drive go on the same day.
3. Name each day after its area: "Day 1 — Downtown & LoDo", "Day 2 — RiNo & Five Points", etc.
4. Order days logically as a loop: start near arrival, explore outward, end near departure.
5. 3–5 places per day, ordered by time of day: breakfast → lunch → afternoon → dinner → bar/evening.
6. For each place infer type (restaurant/cafe/brewery/bar/activity/hiking), cuisine if food, city if mentioned.
7. "Must go" / "highly recommend" → verdict "must_go". Otherwise "want_to_try".
8. If user specifies a day for a place ("Day 2: Red Rocks"), honor it.
9. Default city to ${dest} if not mentioned.
10. For each item in the notes, apply these rules:
    a. If it is a specific named place (e.g. "Napali Coast", "Blue Bottle Coffee") — use it exactly as-is.
    b. If it is a generic activity or category (e.g. "coffee", "beach", "hike", "food", "drive", "lunch") — suggest 1–2 real, well-known places at ${dest} that best match that activity. The place names must actually exist.
    c. If the user says "plan my full itinerary" or "suggest places" — generate a complete, realistic itinerary with real well-known places at ${dest} for each day.
    Never fabricate a place name that does not exist in the real world.${existingPlaces ? `\nCurrently in the trip: ${existingPlaces}` : ""}
11. For every place EXCEPT the first in each day, estimate the travel distance from the previous stop using ${distUnit} (e.g. "0.8 ${distLabel}") and approximate travel mode + time (e.g. "10 min walk" or "5 min drive"). Set distanceFromPrev and travelTimeFromPrev. Leave both null for the first place of each day.

Updated notes:
<user_notes>${regenNotes}</user_notes>

Return ONLY valid JSON, no explanation, no markdown fences:
{"days":[{"id":"s1","name":"Day 1 — Neighborhood","entries":[{"id":"i1_1","name":"Place Name","type":"restaurant","notes":"tip","city":"City","state":"CO","verdict":"must_go","cuisine":"Italian","distanceFromPrev":null,"travelTimeFromPrev":null}]}]}`
          }]
        })
      });
      if (!response.ok && response.status === 429) throw new Error("AI is over capacity right now. Wait a minute and try again.");
      const data = await response.json();
      if (data.error) {
        const t = data.error.type || "";
        if (t === "rate_limit_error" || t === "overloaded_error" || response.status === 429)
          throw new Error("AI is over capacity right now. Wait a minute and try again.");
        throw new Error(data.error.message || "AI service error. Please try again.");
      }
      const text = data.content?.[0]?.text || "";
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("AI response didn't contain valid JSON.");
      const parsed = JSON.parse(jsonMatch[0]);
      if (parsed.days && parsed.days.length > 0) {
        const newDays = parsed.days.map((d, i) => ({
          ...d,
          day: i + 1,
          name: d.name,
          entries: (d.entries || d.items || []).filter(e => e && e.name?.trim()).map((e, j) => ({
            id: e.id || ("r_" + i + "_" + j),
            name: e.name.trim(), type: e.type || "activity",
            city: e.city || dest, state: e.state || "",
            verdict: e.verdict || "must_go",
            cuisine: e.cuisine || "", notes: e.notes || e.note || "",
            distanceFromPrev: e.distanceFromPrev || null,
            travelTimeFromPrev: e.travelTimeFromPrev || null
          }))
        }));
        onUpdate({ ...trip, notes: regenNotes, plan: { ...trip.plan, days: newDays } });
        setRegenError("");
      } else {
        throw new Error("AI returned an empty plan.");
      }
    } catch(err) {
      setRegenError("Could not regenerate: " + (err.message || "Unknown error"));
    }
    setRegenLoading(false);
  };

  const removePlace = (dayIdx, entryId) => {
    if (!onUpdate) return;
    const newDays = trip.plan.days.map((d, i) =>
      i === dayIdx ? { ...d, entries: (d.entries || d.items || []).filter(e => e.id !== entryId) } : d
    );
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
    showToast("Place removed", "success");
  };

  const movePlace = (fromDayIdx, entryId, toDayIdx) => {
    if (!onUpdate) return;
    const fromDay = trip.plan.days[fromDayIdx];
    const entry = (fromDay.entries || fromDay.items || []).find(e => e.id === entryId);
    if (!entry) return;
    const newDays = trip.plan.days.map((d, i) => {
      if (i === fromDayIdx) return { ...d, entries: (d.entries || d.items || []).filter(e => e.id !== entryId) };
      if (i === toDayIdx) return { ...d, entries: [...(d.entries || d.items || []), entry] };
      return d;
    });
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
  };

  const reorderPlace = (dayIdx, fromIdx, toIdx) => {
    if (!onUpdate || fromIdx === toIdx) return;
    const newDays = trip.plan.days.map((d, i) => {
      if (i !== dayIdx) return d;
      const entries = [...(d.entries || d.items || [])];
      const [moved] = entries.splice(fromIdx, 1);
      entries.splice(toIdx, 0, moved);
      return { ...d, entries };
    });
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
  };

  const updateDayName = (dayIdx, name) => {
    if (!onUpdate) return;
    const newDays = trip.plan.days.map((d, i) => i === dayIdx ? { ...d, name } : d);
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
  };

  const updateDayNotes = (dayIdx, notes) => {
    if (!onUpdate) return;
    const newDays = trip.plan.days.map((d, i) => i === dayIdx ? { ...d, notes } : d);
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
  };

  const updateDayAccom = (dayIdx, accommodation) => {
    if (!onUpdate) return;
    const newDays = trip.plan.days.map((d, i) => i === dayIdx ? { ...d, accommodation } : d);
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
  };

  const deleteDay = (dayIdx) => {
    if (!onUpdate) return;
    const newDays = trip.plan.days.filter((_, i) => i !== dayIdx);
    onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
  };




  // Progress counts
  const allPlanEntries = trip.plan ? trip.plan.days.flatMap(d => d.entries || d.items || []) : [];
  const totalPlaces = allPlanEntries.length;
  const visitedCount = allPlanEntries.filter(e => e.visited).length;

  // Date range string
  const dateStr = trip.startDate
    ? new Date(trip.startDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
      (trip.endDate ? " – " + new Date(trip.endDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "")
    : trip.createdAt;

  // Overflow menu item helper
  const OMenuItem = ({ icon, label, onClick, danger }) => (
    <button onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer",
        color: danger ? "#ef4444" : "#222", fontSize: 13, transition: "background 0.1s",
        width: "100%", background: "transparent", border: "none", textAlign: "left", fontFamily: "inherit", outline: "none" }}
      onMouseEnter={e => e.currentTarget.style.background = "#f7f7f7"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      onFocus={e => e.currentTarget.style.background = "#f0f0f0"}
      onBlur={e => e.currentTarget.style.background = "transparent"}>
      <span style={{ fontSize: 15, width: 20, textAlign: "center" }}>{icon}</span>
      {label}
    </button>
  );


  return (
    <div ref={cardRef} style={{
      background: past ? "#fff" : "linear-gradient(160deg, #0f1a2e 0%, #080c14 100%)",
      border: past ? "1px solid #e8e0d4" : "none",
      borderRadius: past ? 12 : 16,
      boxShadow: past ? "0 1px 4px rgba(0,0,0,0.06)" : "0 12px 40px rgba(0,0,0,0.35)",
      transition: "box-shadow 0.2s, transform 0.2s",
      overflow: "hidden" }}
      onMouseEnter={e => {
        if (past) { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)"; }
        else { e.currentTarget.style.boxShadow = "0 16px 48px rgba(0,0,0,0.45)"; e.currentTarget.style.transform = "translateY(-2px)"; }
      }}
      onMouseLeave={e => {
        if (past) { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; }
        else { e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.35)"; e.currentTarget.style.transform = "none"; }
      }}>

      {/* ── ACTIVE TRIP: Immersive hero ── */}
      {!past && (
        <>
          <div style={{ position: "relative", height: 260, overflow: "hidden", cursor: "pointer" }}
            onClick={() => setExpanded(!expanded)}>
            {/* Cover photo — same source as home card */}
            <DestinationPhoto destination={trip.destination} coverPhoto={trip.coverPhoto}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
            {/* Dark gradient overlay */}
            <div style={{ position: "absolute", inset: 0,
              background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.82) 100%)" }} />

            {/* Flex column: live bar pinned top, destination pinned bottom, never overlap */}
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "14px 14px 0" }}>
              {/* Top bar */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                {/* Left: animated Live badge */}
                <div style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)",
                  border: "1px solid rgba(74,222,128,0.45)", borderRadius: 20, padding: "5px 12px",
                  display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="trips-pulse-dot" />
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#4ade80",
                    fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.03em" }}>Live</span>
                </div>
                {/* Right: overflow menu */}
                <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                  <button ref={overflowBtnRef} onClick={openOverflowMenu}
                    style={{ width: 32, height: 32, background: "rgba(255,255,255,0.15)",
                      backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 8, cursor: "pointer", color: "#fff", fontSize: 15,
                      letterSpacing: "2px", lineHeight: 1, fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
                    ···
                  </button>
                </div>
              </div>

            {/* Bottom content stack */}
            <div style={{ padding: "0 4px 16px" }}>
              {/* Flag */}
              {getDestinationFlag(trip.destination) && (
                <div style={{ fontSize: 20, marginBottom: 4, lineHeight: 1 }}>
                  {getDestinationFlag(trip.destination)}
                </div>
              )}
              {/* Destination name */}
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 600,
                color: "#fff", lineHeight: 1.15, marginBottom: 7 }}>
                {trip.destination}
              </div>
              {/* Meta row */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
                  {trip.days} day{trip.days !== 1 ? "s" : ""}
                </span>
                <span style={{ color: "rgba(255,255,255,0.3)" }}>·</span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
                  {trip.kids ? "Family" : "Adults"}
                </span>
                {trip.startDate && <>
                  <span style={{ color: "rgba(255,255,255,0.3)" }}>·</span>
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>{dateStr}</span>
                </>}
              </div>
              {/* Collaborators row — stacked avatar circles */}
              {(trip.invitedFriends || []).length > 0 && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {(trip.invitedFriends || []).slice(0, 4).map((uid, idx) => {
                      const u = allUsers?.find(x => x.id === uid);
                      if (!u) return null;
                      return (
                        <div key={uid} onClick={ev => { ev.stopPropagation(); onViewProfile && onViewProfile(uid); }}
                          title={u.name}
                          style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                            marginLeft: idx > 0 ? -6 : 0, border: "2px solid rgba(255,255,255,0.25)",
                            background: "#c4956a", overflow: "hidden", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 10, fontWeight: 700, color: "#fff", zIndex: 4 - idx }}>
                          {u.avatarImg || u.avatar_url
                            ? <img src={u.avatarImg || u.avatar_url} alt={u.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : (u.name || "?").charAt(0).toUpperCase()}
                        </div>
                      );
                    })}
                  </div>
                  <span style={{ marginLeft: 8, fontSize: 12, color: "rgba(255,255,255,0.6)",
                    fontFamily: "'DM Sans', sans-serif" }}>
                    {(trip.invitedFriends || []).slice(0, 2).map((uid, i, arr) => {
                      const u = allUsers?.find(x => x.id === uid);
                      if (!u) return null;
                      return (
                        <span key={uid}>
                          <span onClick={ev => { ev.stopPropagation(); onViewProfile && onViewProfile(u.id); }}
                            style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.4)" }}>
                            @{u.username || u.name.split(" ")[0].toLowerCase()}
                          </span>
                          {i < arr.filter(id => allUsers?.find(x => x.id === id)).length - 1 ? ", " : ""}
                        </span>
                      );
                    })}
                    {(trip.invitedFriends || []).length > 2 ? ` +${(trip.invitedFriends || []).length - 2}` : ""}
                  </span>
                </div>
              )}
              {/* Progress bar */}
              {totalPlaces > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5,
                    fontFamily: "'DM Sans', sans-serif" }}>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>{totalPlaces} place{totalPlaces !== 1 ? "s" : ""} planned</span>
                    {visitedCount > 0 && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>{visitedCount} visited</span>}
                  </div>
                  <div style={{ height: 3, background: "rgba(255,255,255,0.15)", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", borderRadius: 2, transition: "width 0.4s",
                      width: `${(visitedCount / totalPlaces) * 100}%`,
                      background: visitedCount === totalPlaces ? "#4ade80" : "#c4956a" }} />
                  </div>
                </div>
              )}
              {/* Action button */}
              <div onClick={e => e.stopPropagation()}>
                <button onClick={() => { setExpanded(true); setPlannerTab("itinerary"); }}
                  style={{ width: "100%", background: "#8b2a2a", border: "none", borderRadius: 8,
                    padding: "9px 0", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    boxShadow: "0 4px 14px rgba(139,42,42,0.5)" }}>
                  Continue planning
                </button>
              </div>
            </div>
          </div>
          </div>
          {/* Suggestions from collaborators */}
          {(trip.collaboratorSuggestions || []).length > 0 && (
            <div style={{ padding: "10px 18px", borderBottom: "1px solid #f0f0f0", background: "#fffdf8" }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 8 }}>
                💡 Suggestions from your group
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(trip.collaboratorSuggestions || []).map(s => {
                  const suggester = allUsers?.find(x => x.id === s.userId);
                  return (
                    <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8,
                      background: "#fff", border: "1px solid #efefef", borderRadius: 10,
                      padding: "8px 12px" }}>
                      <span style={{ fontSize: 15 }}>{suggester?.avatar || "👤"}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#000" }}>{s.name}</div>
                        <div style={{ fontSize: 10, color: "#888" }}>{suggester?.name?.split(" ")[0]} · Day {s.dayIdx + 1}</div>
                      </div>
                      <button onClick={() => {
                        const updated = { ...trip };
                        const days = [...(updated.plan?.days || [])];
                        if (days[s.dayIdx]) {
                          days[s.dayIdx] = { ...days[s.dayIdx], entries: [...(days[s.dayIdx].entries || []),
                            { id: "sg_" + Date.now(), name: s.name, type: s.type || "restaurant", city: s.city, state: s.state, verdict: "must_go" }] };
                        }
                        updated.plan = { ...updated.plan, days };
                        updated.collaboratorSuggestions = (trip.collaboratorSuggestions || []).filter(x => x.id !== s.id);
                        onUpdate && onUpdate(updated);
                      }} style={{ background: "#22c55e", border: "none", borderRadius: 8, padding: "4px 10px",
                        fontSize: 11, color: "#fff", cursor: "pointer", fontWeight: 600 }}>
                        Add ✓
                      </button>
                      <button onClick={() => onUpdate && onUpdate({ ...trip,
                        collaboratorSuggestions: (trip.collaboratorSuggestions || []).filter(x => x.id !== s.id) })}
                        style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 16 }}>
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── PAST TRIP: Compact horizontal card ── */}
      {past && (
        <div onClick={() => setExpanded(!expanded)}
          style={{ display: "flex", cursor: "pointer", height: 90 }}>
          {/* Left thumbnail */}
          <div style={{ width: 90, flexShrink: 0, overflow: "hidden", position: "relative", background: getIdeaGradient(trip.destination) }}>
            <DestinationPhoto destination={trip.destination} coverPhoto={trip.coverPhoto}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
          </div>
          {/* Right body */}
          <div style={{ flex: 1, padding: "10px 14px", minWidth: 0, display: "flex", flexDirection: "column",
            justifyContent: "space-between" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 600,
                  color: "#1a0a00", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {trip.destination.split(",")[0]}
                </div>
                <div style={{ fontSize: 10, color: "#a09080", marginTop: 2,
                  fontFamily: "'DM Sans', sans-serif" }}>{dateStr}</div>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
                <button ref={overflowBtnRef} onClick={openOverflowMenu}
                  style={{ width: 28, height: 28, background: "transparent",
                    border: "1px solid #e8e0d4", borderRadius: 7, cursor: "pointer", fontSize: 13,
                    color: "#a09080", display: "flex", alignItems: "center", justifyContent: "center",
                    letterSpacing: "2px", lineHeight: 1, fontWeight: 700 }}>
                  ···
                </button>
                <div style={{ width: 24, height: 24, display: "flex", alignItems: "center",
                  justifyContent: "center", color: "#ccc", fontSize: 11,
                  transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 10, color: "#a09080", fontFamily: "'DM Sans', sans-serif" }}>
                📍 {totalPlaces} place{totalPlaces !== 1 ? "s" : ""} logged
              </span>
              <span style={{ background: "#f4f4f2", color: "#a09080", borderRadius: 20,
                padding: "2px 9px", fontSize: 9, fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Complete
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── INVITE PANEL (inline, triggered from ⋯) ── */}
      {showInviteDropdown && (
        <div onClick={e => e.stopPropagation()}
          style={{ margin: "0 20px 16px", background: "#fafafa", border: "1px solid #efefef",
            borderRadius: 14, padding: "14px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#555", letterSpacing: "0.04em" }}>👥 INVITE FRIENDS</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[["collaborator","Full Access"],["viewer","Read Only"]].map(([r, label]) => (
                <button key={r} onClick={() => setInviteRole(r)}
                  style={{ fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 20, cursor: "pointer",
                    border: `1px solid ${inviteRole === r ? "#111" : "#e0e0e0"}`,
                    background: inviteRole === r ? "#111" : "#f8f8f8",
                    color: inviteRole === r ? "#fff" : "#888" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {(trip.invitedFriends || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
              {(trip.invitedFriends || []).map(uid => {
                const u = (allUsers || []).find(x => x.id === uid);
                return u ? (
                  <span key={uid} onClick={() => onInviteFriend(uid, inviteRole)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#111",
                      color: "#fff", borderRadius: 20, padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>
                    {u.avatar} {u.name.split(" ")[0]} <span style={{ opacity: 0.6, fontSize: 11 }}>✕</span>
                  </span>
                ) : null;
              })}
            </div>
          )}
          <input autoFocus value={inviteSearch} onChange={e => setInviteSearch(e.target.value)}
            placeholder="Search friends…"
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e8e8e8", borderRadius: 8,
              padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff", color: "#222" }} />
          <div style={{ marginTop: 6 }}>
            {(allUsers || []).filter(u => friendState?.[u.id] === "friend" &&
              (!inviteSearch.trim() || u.name.toLowerCase().includes(inviteSearch.toLowerCase())))
              .map(u => (
                <div key={u.id} onClick={() => onInviteFriend(u.id, inviteRole)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px",
                    cursor: "pointer", borderRadius: 8 }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <span style={{ fontSize: 18 }}>{u.avatar}</span>
                  <span style={{ fontSize: 13, color: "#222", flex: 1 }}>{u.name}</span>
                  {(trip.invitedFriends || []).includes(u.id) && <span style={{ color: "#22c55e", fontSize: 13 }}>✓</span>}
                </div>
              ))}
          </div>
          <button onClick={() => { setShowInviteDropdown(false); setInviteSearch(""); setInviteRole("collaborator"); }}
            style={{ marginTop: 10, width: "100%", background: "#111", border: "none", borderRadius: 8,
              padding: "9px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Done
          </button>
        </div>
      )}

      {/* ── SHARE PANEL (inline, triggered from ⋯) ── */}
      {showShareDropdown && (
        <div onClick={e => e.stopPropagation()}
          style={{ margin: "0 20px 16px", background: "#fafafa", border: "1px solid #efefef",
            borderRadius: 14, padding: "14px 16px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 10, letterSpacing: "0.04em" }}>
            🔗 SHARE TRIP
          </div>
          {(trip.sharedWith || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
              {(trip.sharedWith || []).map(uid => {
                const u = (allUsers || []).find(x => x.id === uid);
                return u ? (
                  <span key={uid} onClick={() => onShare(uid)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#111",
                      color: "#fff", borderRadius: 20, padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>
                    {u.avatar} {u.name.split(" ")[0]} <span style={{ opacity: 0.6, fontSize: 11 }}>✕</span>
                  </span>
                ) : null;
              })}
            </div>
          )}
          <input autoFocus value={shareSearch} onChange={e => setShareSearch(e.target.value)}
            placeholder="Search friends…"
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e8e8e8", borderRadius: 8,
              padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff", color: "#222" }} />
          <div style={{ marginTop: 6 }}>
            {(allUsers || []).filter(u => friendState?.[u.id] === "friend" &&
              (!shareSearch.trim() || u.name.toLowerCase().includes(shareSearch.toLowerCase())))
              .map(u => (
                <div key={u.id} onClick={() => onShare(u.id)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px",
                    cursor: "pointer", borderRadius: 8 }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <span style={{ fontSize: 18 }}>{u.avatar}</span>
                  <span style={{ fontSize: 13, color: "#222", flex: 1 }}>{u.name}</span>
                  {(trip.sharedWith || []).includes(u.id) && <span style={{ color: "#22c55e", fontSize: 13 }}>✓</span>}
                </div>
              ))}
          </div>
          <button onClick={() => { setShowShareDropdown(false); setShareSearch(""); }}
            style={{ marginTop: 10, width: "100%", background: "#111", border: "none", borderRadius: 8,
              padding: "9px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Done
          </button>
        </div>
      )}

      {/* ── EXPANDED BODY — Tabbed Planner ── */}
      {expanded && trip.plan && (
        <div style={{ borderTop: "1px solid #f0f0f0", background: "#f8f6f2" }}>

          {/* Progress bar — always visible above tabs */}
          {totalPlaces > 0 && (
            <div style={{ padding: "12px 20px", borderBottom: "1px solid #f5f5f5" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#aaa", letterSpacing: "0.07em", textTransform: "uppercase" }}>Progress</span>
                <span style={{ fontSize: 12, color: "#888" }}>
                  {visitedCount} / {totalPlaces} visited{visitedCount === totalPlaces && totalPlaces > 0 && " 🎉"}
                </span>
              </div>
              <div style={{ height: 5, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.round((visitedCount / totalPlaces) * 100)}%`,
                  background: visitedCount === totalPlaces ? "#22c55e" : "#7B2D42", borderRadius: 3,
                  transition: "width 0.4s ease" }} />
              </div>
            </div>
          )}

          {/* Unlock editing banner — past trips only */}
          {past && (
            trip.isShared ? (
              <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 16px", background: "#fafafa", borderBottom: "1px solid #f0f0f0", gap: 10 }}>
                <span style={{ fontSize: 12, color: "#999" }}>👁 Shared trip — view only</span>
                {onCopyToCurrentTrip && (
                  <button onClick={onCopyToCurrentTrip}
                    style={{ fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 20, cursor: "pointer",
                      border: "1px solid #111", background: "#111", color: "#fff", whiteSpace: "nowrap" }}>
                    Use as my trip
                  </button>
                )}
              </div>
            ) : (
              <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 16px", background: editingUnlocked ? "#f0fdf4" : "#fafafa",
                borderBottom: "1px solid #f0f0f0", gap: 10 }}>
                <span style={{ fontSize: 12, color: editingUnlocked ? "#16a34a" : "#999" }}>
                  {editingUnlocked ? "✏️ Editing unlocked — changes will be saved" : "🔒 This trip is archived"}
                </span>
                <button onClick={() => setEditingUnlocked(v => !v)}
                  style={{ fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 20, cursor: "pointer",
                    border: `1px solid ${editingUnlocked ? "#16a34a" : "#d0d0d0"}`,
                    background: editingUnlocked ? "#16a34a" : "#fff",
                    color: editingUnlocked ? "#fff" : "#666", whiteSpace: "nowrap" }}>
                  {editingUnlocked ? "Lock" : "Unlock editing"}
                </button>
              </div>
            )
          )}

          {/* Tab bar */}
          <div onClick={e => e.stopPropagation()} style={{ display: "flex", overflowX: "auto", borderBottom: "1px solid #f0f0f0",
            background: "#fff", msOverflowStyle: "none", scrollbarWidth: "none" }}>
            {[
              { id: "itinerary", label: "Itinerary", icon: "🗓" },
              { id: "map", label: "Map", icon: "🗺" },
              { id: "notes", label: "Notes", icon: "📝" },
              { id: "packList", label: "Pack List", icon: "🎒" },
              { id: "reservations", label: "Reservations", icon: "📋" },
              { id: "weather", label: "Weather", icon: "🌤" },
              { id: "friendLogs", label: "Friend Logs", icon: "👥" },
            ].map(t => (
              <button key={t.id} onClick={() => {
                  if (plannerTab === t.id) { setTabCollapsed(c => !c); }
                  else { setPlannerTab(t.id); setTabCollapsed(false); }
                }}
                style={{ flexShrink: 0, border: "none", background: "transparent", cursor: "pointer",
                  padding: "14px 22px", fontSize: 15, fontWeight: plannerTab === t.id ? 700 : 500,
                  color: plannerTab === t.id ? "#7B2D42" : "#888",
                  borderBottom: plannerTab === t.id ? "2px solid #7B2D42" : "2px solid transparent",
                  display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                  transition: "color 0.15s" }}>
                <span style={{ fontSize: 17 }}>{t.icon}</span> {t.label}
              </button>
            ))}
          </div>

          {/* ── ITINERARY TAB ── */}
          {plannerTab === "itinerary" && !tabCollapsed && (
            <div style={{ padding: "20px 20px 24px" }}>
              {/* Trip place search */}
              {(() => {
                const tripSearchResults = tripSearch.trim().length > 0
                  ? (trip.plan?.days || []).flatMap((day, dayIdx) =>
                      (day.entries || day.items || [])
                        .filter(e => e.name.toLowerCase().includes(tripSearch.toLowerCase()))
                        .map(e => ({ ...e, dayIdx, dayLabel: day.name || `Day ${dayIdx + 1}` }))
                    )
                  : [];
                return (
                  <div style={{ position: "relative", marginBottom: 16 }}>
                    <input
                      value={tripSearch}
                      onChange={ev => { setTripSearch(ev.target.value); setTripSearchOpen(true); }}
                      onFocus={() => setTripSearchOpen(true)}
                      placeholder="Search places in this trip…"
                      style={{ width: "100%", boxSizing: "border-box", padding: "9px 14px",
                        borderRadius: 10, border: "1px solid #e8e0d4", fontSize: 13,
                        fontFamily: "'DM Sans', sans-serif", outline: "none", background: "#faf7f2" }}
                    />
                    {tripSearchOpen && tripSearchResults.length > 0 && (
                      <>
                        <div onClick={() => setTripSearchOpen(false)}
                          style={{ position: "fixed", inset: 0, zIndex: 100 }} />
                        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                          background: "#fff", borderRadius: 10,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                          border: "1px solid #efefef", zIndex: 101,
                          maxHeight: 220, overflowY: "auto", overscrollBehavior: "contain" }}
                          onWheel={e => e.stopPropagation()}>
                          {tripSearchResults.map(e => (
                            <button key={e.id} onClick={() => {
                              setCollapsedDays(prev => { const next = new Set(prev); next.delete(e.dayIdx); return next; });
                              setTripSearch("");
                              setTripSearchOpen(false);
                              setTimeout(() => {
                                placeRefs.current[e.id]?.scrollIntoView({ behavior: "smooth", block: "center" });
                              }, 80);
                            }}
                              style={{ width: "100%", textAlign: "left", background: "none",
                                border: "none", padding: "10px 14px", cursor: "pointer",
                                display: "flex", flexDirection: "column", gap: 2 }}
                              onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
                              onMouseLeave={ev => ev.currentTarget.style.background = "none"}>
                              <span style={{ fontSize: 13, fontWeight: 500, color: "#111" }}>{e.name}</span>
                              <span style={{ fontSize: 11, color: "#999" }}>{e.dayLabel}{e.city ? ` · ${e.city}` : ""}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              {trip.plan.days.map((day, dayIdx) => {
                const dayColor = DAY_COLORS[dayIdx % DAY_COLORS.length];
                const dayEntries = day.entries || day.items || [];
                const isLast = dayIdx === trip.plan.days.length - 1;
                const isDayCollapsed = collapsedDays.has(dayIdx);
                const dayVisited = dayEntries.filter(e => e.visited).length;
                const dayDate = trip.startDate ? (() => {
                  const d = new Date(trip.startDate + "T12:00:00");
                  d.setDate(d.getDate() + dayIdx);
                  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                })() : null;

                return (
                  <div key={dayIdx} style={{ display: "flex", gap: 0, marginBottom: isLast ? 0 : isDayCollapsed ? 12 : 28 }}>
                    {/* Timeline spine */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
                      marginRight: 16, flexShrink: 0, width: 28 }}>
                      <div onClick={() => toggleDayCollapse(dayIdx)}
                        style={{ width: 28, height: 28, borderRadius: "50%", background: dayColor,
                          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 800, flexShrink: 0, zIndex: 1,
                          boxShadow: `0 0 0 3px ${dayColor}22`, cursor: "pointer",
                          opacity: isDayCollapsed ? 0.65 : 1, transition: "opacity 0.15s" }}>
                        {dayIdx + 1}
                      </div>
                      {!isLast && (
                        <div style={{ width: 2, flex: 1, background: `${dayColor}30`, marginTop: 6,
                          minHeight: isDayCollapsed ? 8 : 20, borderRadius: 1 }} />
                      )}
                    </div>

                    {/* Day content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                        marginBottom: isDayCollapsed ? 0 : 10, gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {(editingDayIdx === dayIdx) ? (
                            <input autoFocus
                              value={localDayName !== null ? localDayName : (day.name || ("Day " + (dayIdx + 1) + (day.city ? " — " + day.city : "")))}
                              onChange={e => {
                                const val = e.target.value;
                                setLocalDayName(val);
                                clearTimeout(dayNameTimer.current);
                                dayNameTimer.current = setTimeout(() => updateDayName(dayIdx, val), 500);
                              }}
                              onClick={e => e.stopPropagation()}
                              onBlur={() => {
                                if (localDayName !== null) updateDayName(dayIdx, localDayName);
                                setEditingDayIdx(null);
                                setLocalDayName(null);
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter" || e.key === "Escape") {
                                  e.stopPropagation();
                                  if (localDayName !== null) updateDayName(dayIdx, localDayName);
                                  setEditingDayIdx(null);
                                  setLocalDayName(null);
                                }
                              }}
                              style={{ fontSize: 14, fontWeight: 700, color: "#111", border: "1.5px solid #7B2D42",
                                borderRadius: 8, padding: "4px 10px", width: "100%", background: "#fafafa", outline: "none" }} />
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <div onClick={e => {
                                  e.stopPropagation();
                                  if (canEdit && onUpdate) {
                                    setLocalDayName(day.name || ("Day " + (dayIdx + 1) + (day.city ? " — " + day.city : "")));
                                    setEditingDayIdx(dayIdx);
                                  }
                                }}
                                style={{ fontSize: 13, fontWeight: 700, color: dayColor, lineHeight: 1.3,
                                  background: `${dayColor}18`, borderRadius: 20, padding: "3px 10px",
                                  border: `1.5px solid ${dayColor}35`, letterSpacing: 0.1,
                                  cursor: (canEdit && onUpdate) ? "text" : "pointer",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {day.name || ("Day " + (dayIdx + 1) + (day.city ? " — " + day.city : ""))}
                              </div>
                            </div>
                          )}
                          {dayDate && <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>{dayDate}</div>}

                          {/* Accommodation row */}
                          {editingAccomIdx === dayIdx ? (
                            <div onClick={e => e.stopPropagation()} style={{ marginTop: 5 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ fontSize: 13 }}>🏨</span>
                                <AccommodationAutocomplete
                                  value={localDayAccom[dayIdx] ?? ""}
                                  onChange={val => {
                                    setLocalDayAccom(prev => ({ ...prev, [dayIdx]: val }));
                                    clearTimeout(accomTimers.current[dayIdx]);
                                    accomTimers.current[dayIdx] = setTimeout(() => updateDayAccom(dayIdx, val), 600);
                                  }}
                                  onSelect={val => {
                                    setLocalDayAccom(prev => ({ ...prev, [dayIdx]: val }));
                                    updateDayAccom(dayIdx, val);
                                    setEditingAccomIdx(null);
                                  }}
                                  onBlur={() => {
                                    updateDayAccom(dayIdx, localDayAccom[dayIdx] ?? "");
                                    setEditingAccomIdx(null);
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === "Escape") {
                                      e.stopPropagation();
                                      updateDayAccom(dayIdx, localDayAccom[dayIdx] ?? "");
                                      setEditingAccomIdx(null);
                                    }
                                  }}
                                  inputStyle={{ width: "100%", fontSize: 14, border: "1.5px solid #7B2D42", borderRadius: 8,
                                    padding: "7px 10px", outline: "none", background: "#fafafa", color: "#333" }}
                                />
                              </div>
                              {dayIdx > 0 && (localDayAccom[dayIdx - 1] || (trip.plan.days[dayIdx - 1]?.accommodation)) && (
                                <button
                                  onMouseDown={e => {
                                    e.preventDefault();
                                    const prev = localDayAccom[dayIdx - 1] || trip.plan.days[dayIdx - 1]?.accommodation || "";
                                    setLocalDayAccom(p => ({ ...p, [dayIdx]: prev }));
                                    updateDayAccom(dayIdx, prev);
                                    setEditingAccomIdx(null);
                                  }}
                                  style={{ marginTop: 4, fontSize: 11, color: "#7B2D42", background: "#fdf4f6",
                                    border: "1px solid #f0d8de", borderRadius: 20, padding: "3px 10px",
                                    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  ↑ Same as Day {dayIdx}: {(localDayAccom[dayIdx - 1] || trip.plan.days[dayIdx - 1]?.accommodation || "").slice(0, 24)}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div onClick={e => { e.stopPropagation(); if (canEdit && onUpdate) setEditingAccomIdx(dayIdx); }}
                              style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4,
                                cursor: canEdit && onUpdate ? "pointer" : "default" }}>
                              {(localDayAccom[dayIdx] || day.accommodation) ? (
                                <span style={{ fontSize: 13, color: "#555", background: "#f7f3ff",
                                  border: "1px solid #ede8f8", borderRadius: 20, padding: "4px 12px",
                                  display: "inline-flex", alignItems: "center", gap: 5, maxWidth: "100%" }}>
                                  🏨 {localDayAccom[dayIdx] || day.accommodation}
                                </span>
                              ) : canEdit && onUpdate ? (
                                <span style={{ fontSize: 13, color: "#aaa", borderRadius: 20,
                                  padding: "2px 0", display: "inline-flex", alignItems: "center", gap: 3 }}>
                                  🏨 <span style={{ textDecoration: "underline dotted" }}>Add accommodation</span>
                                </span>
                              ) : null}
                            </div>
                          )}

                          {isDayCollapsed && dayEntries.length > 0 && (
                            <div onClick={() => toggleDayCollapse(dayIdx)} style={{ fontSize: 11, color: "#aaa", marginTop: 3, cursor: "pointer" }}>
                              {dayEntries.length} place{dayEntries.length !== 1 ? "s" : ""}
                              {dayVisited > 0 ? ` · ${dayVisited} visited` : ""}
                              {" · "}<span style={{ color: "#bbb" }}>
                                {dayEntries.slice(0, 2).map(e => e.name).join(", ")}
                                {dayEntries.length > 2 ? ` +${dayEntries.length - 2}` : ""}
                              </span>
                            </div>
                          )}
                          {isDayCollapsed && dayEntries.length === 0 && (
                            <div onClick={() => toggleDayCollapse(dayIdx)} style={{ fontSize: 11, color: "#ddd", marginTop: 3, cursor: "pointer" }}>No places yet</div>
                          )}
                        </div>
                        {!isDayCollapsed && (
                          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                            <button onClick={e => { e.stopPropagation(); toggleNoteExpand(`day_${dayIdx}`); }}
                              title="Day notes"
                              style={{ background: "#f5f5f5", border: "none", borderRadius: 8, padding: "5px 7px",
                                fontSize: 15, cursor: "pointer", lineHeight: 1,
                                color: (localDayNotes[dayIdx] || day.notes) ? "#f59e0b" : "#aaa" }}>📝</button>
                            <DayMapButton day={day}
                              onOpenMap={trip.plan?.days?.length ? () => { setInitialMapDay(dayIdx); setShowMapModal(true); } : undefined}
                              onDelete={canEdit && onUpdate ? () => deleteDay(dayIdx) : undefined} />
                          </div>
                        )}
                      </div>

                      {!isDayCollapsed && (<>
                        {expandedNoteIds.has(`day_${dayIdx}`) && (
                          canEdit && onUpdate ? (
                            <textarea autoFocus value={localDayNotes[dayIdx] ?? (day.notes || "")}
                              onChange={e => {
                                const val = e.target.value;
                                setLocalDayNotes(prev => ({ ...prev, [dayIdx]: val }));
                                clearTimeout(dayNotesTimers.current[dayIdx]);
                                dayNotesTimers.current[dayIdx] = setTimeout(() => updateDayNotes(dayIdx, val), 600);
                              }}
                              onClick={e => e.stopPropagation()}
                              onWheel={e => {
                                const el = e.currentTarget;
                                const atTop = el.scrollTop === 0 && e.deltaY < 0;
                                const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight && e.deltaY > 0;
                                if (!atTop && !atBottom) e.stopPropagation();
                              }}
                              placeholder="Notes for this day… (tips, reminders, must-dos)" rows={4}
                              style={{ width: "100%", boxSizing: "border-box", fontSize: 13, color: "#555",
                                border: "1.5px solid #e8e8e8", borderRadius: 10, padding: "10px 12px", marginBottom: 8,
                                background: "#fafafa", resize: "vertical", outline: "none", lineHeight: 1.6,
                                overscrollBehavior: "contain" }} />
                          ) : day.notes ? (
                            <div style={{ fontSize: 12, color: "#aaa", marginBottom: 8, fontStyle: "italic", lineHeight: 1.5 }}>{day.notes}</div>
                          ) : null
                        )}

                        <div
                          onDragOver={ev => ev.preventDefault()}
                          onDrop={ev => {
                            ev.preventDefault();
                            const from = dragPlaceRef.current;
                            if (!from || from.dayIdx === dayIdx) return;
                            movePlace(from.dayIdx, from.id, dayIdx);
                            dragPlaceRef.current = null;
                          }}
                          style={{ minHeight: dayEntries.length === 0 && onUpdate ? 40 : 0 }}>
                        {dayEntries.map((e, entIdx) => (
                          <div key={e.id}
                            draggable={!!onUpdate}
                            onDragStart={() => { dragPlaceRef.current = { dayIdx, index: entIdx, id: e.id }; }}
                            onDragOver={ev => ev.preventDefault()}
                            onDrop={ev => {
                              ev.preventDefault();
                              const from = dragPlaceRef.current;
                              if (!from) return;
                              if (from.dayIdx === dayIdx) {
                                reorderPlace(dayIdx, from.index, entIdx);
                              } else {
                                movePlace(from.dayIdx, from.id, dayIdx);
                              }
                              dragPlaceRef.current = null;
                            }}>
                            {entIdx > 0 && (e.distanceFromPrev || e.travelTimeFromPrev) && (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "0 0 6px 18px" }}>
                                <div style={{ width: 1, height: 14, background: "#e0e0e0" }} />
                                <span style={{ fontSize: 10, color: "#bbb", fontWeight: 500 }}>
                                  {[e.distanceFromPrev, e.travelTimeFromPrev].filter(Boolean).join(" · ")}
                                </span>
                              </div>
                            )}
                            <div onClick={ev => ev.stopPropagation()}
                              ref={el => { if (el) placeRefs.current[e.id] = el; else delete placeRefs.current[e.id]; }}
                              style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "11px 13px",
                                marginBottom: 6, background: e.visited ? "#fafafa" : "#fff",
                                border: `1px solid ${e.visited ? "#f0f0f0" : "#efefef"}`,
                                borderRadius: 14, opacity: e.visited ? 0.6 : 1, transition: "all 0.15s",
                                boxShadow: e.visited ? "none" : "0 1px 3px rgba(0,0,0,0.04)" }}>
                              {onUpdate && (
                                <div style={{ display: "flex", alignItems: "center", alignSelf: "center",
                                  color: "#ccc", fontSize: 16, cursor: "grab", flexShrink: 0, userSelect: "none",
                                  marginLeft: -4, marginRight: -4 }}>⠿</div>
                              )}
                              {e.photos && e.photos.length > 0 ? (
                                <div style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0, overflow: "hidden" }}>
                                  <img src={e.photos[0]} alt={e.name}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                </div>
                              ) : (
                                <div style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                                  background: dayColor + "18", display: "flex", alignItems: "center",
                                  justifyContent: "center", fontSize: 20 }}>
                                  {{ restaurant: "🍽", hotel: "🏨", brewery: "🍺", cafe: "☕", activity: "🏔", hiking: "🥾", national_park: "🏕" }[e.type] || "📍"}
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#111",
                                  textDecoration: e.visited ? "line-through" : "none", lineHeight: 1.3 }}>
                                  <a href={e.lat != null && e.lon != null
                                    ? `https://www.google.com/maps/search/?api=1&query=${e.lat},${e.lon}`
                                    : `https://www.google.com/maps/search/${encodeURIComponent([e.name, e.city, e.state].filter(Boolean).join(" "))}`}
                                    target="_blank" rel="noopener noreferrer"
                                    onClick={ev => ev.stopPropagation()}
                                    style={{ color: "inherit", textDecoration: "none" }}>
                                    {e.name} <span style={{ color: "#4285f4", fontSize: 11 }}>↗</span>
                                  </a>
                                </div>
                                <div style={{ fontSize: 11, color: "#bbb", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                                  {[e.cuisine || e.type, e.city].filter(Boolean).join(" · ")}
                                  {e.lat != null && e.lon != null && <span style={{ color: "#22c55e", fontSize: 9, fontWeight: 700 }} title="Exact coordinates stored">📍</span>}
                                </div>
                                {e.notes && <ExpandableNote text={e.notes} limit={80} />}
                              </div>
                              {(onUpdate || onImportSingle) && (
                                <div style={{ flexShrink: 0 }}>
                                  <button onClick={ev => openKebab(ev, e.id)}
                                    style={{ width: 36, height: 36, borderRadius: 9, border: "1.5px solid #e0e0e0",
                                      background: "transparent", cursor: "pointer", display: "flex", alignItems: "center",
                                      justifyContent: "center", fontSize: 20, color: "#aaa", letterSpacing: 1 }}>⋯</button>
                                  {openMenuId === e.id && createPortal(
                                    <div onClick={ev => ev.stopPropagation()} onWheel={ev => ev.stopPropagation()}
                                      style={{ position: "fixed", top: menuPos.top, right: menuPos.right,
                                        background: "#fff", border: "1px solid #efefef", borderRadius: 12,
                                        boxShadow: "0 4px 20px rgba(0,0,0,0.13)", zIndex: 99999, minWidth: 180,
                                        maxHeight: "80vh", overflowY: "auto", overscrollBehavior: "contain" }}>
                                      {onUpdate && (
                                        <div onClick={() => { setEditPlanEntry({ dayIdx, entry: e }); setOpenMenuId(null); }}
                                          style={{ padding: "11px 16px", cursor: "pointer", fontSize: 13, color: "#222",
                                            display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid #f5f5f5" }}
                                          onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
                                          onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
                                          <span style={{ fontSize: 15 }}>✏️</span>
                                          Edit place
                                        </div>
                                      )}
                                      {onUpdate && (
                                        <div onClick={() => { toggleVisited(dayIdx, e.id); setOpenMenuId(null); }}
                                          style={{ padding: "11px 16px", cursor: "pointer", fontSize: 13, color: "#222",
                                            display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid #f5f5f5" }}
                                          onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
                                          onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
                                          <span style={{ fontSize: 15, color: e.visited ? "#22c55e" : "#aaa" }}>✓</span>
                                          {e.visited ? "Unmark visited" : "Mark visited"}
                                        </div>
                                      )}
                                      {onImportSingle && (
                                        <div onClick={() => { onImportSingle(e); setOpenMenuId(null); }}
                                          style={{ padding: "11px 16px", cursor: "pointer", fontSize: 13, color: "#b07040",
                                            display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid #f5f5f5" }}
                                          onMouseEnter={ev => ev.currentTarget.style.background = "#fdf6f0"}
                                          onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
                                          <span style={{ fontSize: 15 }}>📓</span>
                                          Log to diary
                                        </div>
                                      )}
                                      {onUpdate && trip.plan.days.length > 1 && (<>
                                        <div style={{ padding: "8px 16px 4px", fontSize: 10, color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Move to day</div>
                                        {trip.plan.days.map((d, i) => i !== dayIdx && (
                                          <div key={i} onClick={() => { movePlace(dayIdx, e.id, i); setOpenMenuId(null); }}
                                            style={{ padding: "8px 16px 8px 24px", cursor: "pointer", fontSize: 12, color: "#444",
                                              display: "flex", alignItems: "center", gap: 6, borderBottom: "1px solid #f8f8f8" }}
                                            onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
                                            onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
                                            Day {i + 1}{d.name ? `: ${d.name}` : ""}
                                          </div>
                                        ))}
                                      </>)}
                                      {["hiking", "national_park"].includes(e.type) && (
                                        <a href={`https://www.alltrails.com/search#q=${encodeURIComponent(e.name + (e.city ? " " + e.city : ""))}`}
                                          target="_blank" rel="noopener noreferrer"
                                          onClick={() => setOpenMenuId(null)}
                                          style={{ padding: "11px 16px", cursor: "pointer", fontSize: 13, color: "#16a34a",
                                            display: "flex", alignItems: "center", gap: 10, borderTop: "1px solid #f5f5f5",
                                            textDecoration: "none" }}
                                          onMouseEnter={ev => ev.currentTarget.style.background = "#f0fdf4"}
                                          onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
                                          <span style={{ fontSize: 15 }}>🥾</span>
                                          Search AllTrails
                                        </a>
                                      )}
                                      {onUpdate && (
                                        <div onClick={() => { removePlace(dayIdx, e.id); setOpenMenuId(null); }}
                                          style={{ padding: "11px 16px", cursor: "pointer", fontSize: 13, color: "#dc2626",
                                            display: "flex", alignItems: "center", gap: 10 }}
                                          onMouseEnter={ev => ev.currentTarget.style.background = "#fff5f5"}
                                          onMouseLeave={ev => ev.currentTarget.style.background = "#fff"}>
                                          <span style={{ fontSize: 15 }}>✕</span>
                                          Remove
                                        </div>
                                      )}
                                    </div>,
                                    document.body
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        </div>{/* end drop zone wrapper */}

                        {canEdit && onUpdate && (
                          <AddPlaceInput entries={entries} trip={trip}
                            onAdd={entry => {
                              if (!onUpdate) return;
                              const newEntry = entry.id
                                ? { ...entry, id: "q_" + Date.now() }
                                : { id: "q_" + Date.now(), name: entry.name.trim(), type: entry.type || "activity",
                                    city: entry.city || trip.destination, state: entry.state || "",
                                    verdict: entry.verdict || "must_go", cuisine: entry.cuisine || "" };
                              const newDays = trip.plan.days.map((d, i) =>
                                i === dayIdx ? { ...d, entries: [...(d.entries || d.items || []), newEntry] } : d
                              );
                              onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
                              showToast(`${newEntry.name} added to itinerary`, "success");
                            }} />
                        )}
                      </>)}
                    </div>
                  </div>
                );
              })}

              {canEdit && onUpdate && (
                <button onClick={() => {
                  const n = trip.plan.days.length + 1;
                  const newDay = { id: "d_" + Date.now(), name: "Day " + n, entries: [], notes: "" };
                  onUpdate({ ...trip, plan: { ...trip.plan, days: [...trip.plan.days, newDay] } });
                }} style={{ width: "100%", background: "none", border: "1.5px dashed #e0e0e0", borderRadius: 12,
                  padding: "10px", color: "#aaa", fontSize: 13, cursor: "pointer", marginTop: 8,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <span style={{ fontSize: 16 }}>+</span> Add Day
                </button>
              )}
            </div>
          )}

          {/* ── MAP TAB ── */}
          {plannerTab === "map" && !tabCollapsed && (
            <div onClick={e => e.stopPropagation()}>
              {trip.plan.days.some(d => (d.entries || d.items || []).length > 0) ? (
                <MiniTripMap trip={trip} onOpenFull={() => setShowMapModal(true)} onUpdate={onUpdate} />
              ) : (
                <div style={{ padding: "40px 20px", textAlign: "center", color: "#bbb" }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🗺</div>
                  <div style={{ fontSize: 13 }}>Add places to your itinerary to see them on the map</div>
                </div>
              )}
              <div style={{ padding: "12px 16px" }}>
                <button onClick={() => { setShowMapModal(true); }}
                  style={{ width: "100%", background: "linear-gradient(135deg, #7B2D42, #a0394f)",
                    border: "none", borderRadius: 12, padding: "11px", color: "#fff",
                    fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Open Full Map
                </button>
              </div>
            </div>
          )}

          {/* ── NOTES TAB ── */}
          {plannerTab === "notes" && !tabCollapsed && (
            <div onClick={e => e.stopPropagation()} style={{ padding: "16px 20px" }}>
              {/* Notebook-style trip notes */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 15 }}>📓</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#555", letterSpacing: "0.04em" }}>Trip Notes</span>
                  <span style={{ fontSize: 11, color: "#ccc", marginLeft: 4 }}>— auto-saved</span>
                </div>
                <div style={{ position: "relative", background: "#fffdf7",
                  border: "1.5px solid #e8e4d4", borderRadius: 12, overflow: "hidden" }}>
                  {/* Ruled lines background */}
                  <div style={{ position: "absolute", inset: 0, pointerEvents: "none",
                    backgroundImage: "repeating-linear-gradient(transparent, transparent 27px, #f0ece0 27px, #f0ece0 28px)",
                    backgroundPositionY: "12px" }} />
                  <textarea value={localTripNotes}
                    onChange={e => {
                      const val = e.target.value;
                      setLocalTripNotes(val);
                      clearTimeout(tripNotesTimer.current);
                      tripNotesTimer.current = setTimeout(() => {
                        if (onUpdate) onUpdate({ ...trip, notes: val });
                      }, 600);
                    }}
                    onWheel={e => {
                      const el = e.currentTarget;
                      const atTop = el.scrollTop === 0 && e.deltaY < 0;
                      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight && e.deltaY > 0;
                      if (!atTop && !atBottom) e.stopPropagation();
                    }}
                    placeholder={"Do this, do that…\n• Check out the local market\n• Reserve dinner at the rooftop spot\n• Don't forget hiking boots"}
                    rows={8}
                    style={{ position: "relative", width: "100%", boxSizing: "border-box",
                      fontSize: 13, color: "#2a2a2a", border: "none", borderRadius: 12,
                      padding: "12px 14px", background: "transparent",
                      resize: "vertical", outline: "none", lineHeight: "28px",
                      fontFamily: "'Georgia', 'Lora', serif", zIndex: 1,
                      overscrollBehavior: "contain" }} />
                </div>
              </div>

            </div>
          )}

          {/* ── PACK LIST TAB ── */}
          {plannerTab === "packList" && !tabCollapsed && (
            <div onClick={e => e.stopPropagation()} style={{ padding: "16px 20px" }}>
              {(trip.packList || []).length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0 12px", color: "#bbb", fontSize: 13, fontStyle: "italic" }}>
                  Nothing in your pack list yet
                </div>
              ) : (
                Object.entries((trip.packList || []).reduce((acc, item) => {
                  const cat = item.category || "General";
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(item);
                  return acc;
                }, {})).map(([cat, items]) => (
                  <div key={cat} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", letterSpacing: "0.07em",
                      textTransform: "uppercase", marginBottom: 8 }}>{cat}</div>
                    {items.map(item => (
                      <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6,
                        background: "#fff", borderRadius: 10, padding: "9px 12px", border: "1px solid #f0f0f0" }}>
                        <input type="checkbox" checked={item.packed} onChange={() =>
                          onUpdate && onUpdate({ ...trip, packList: (trip.packList || []).map(x => x.id === item.id ? { ...x, packed: !x.packed } : x) })}
                          style={{ cursor: "pointer", accentColor: "#7B2D42", width: 16, height: 16 }} />
                        <span style={{ flex: 1, fontSize: 13, color: item.packed ? "#bbb" : "#222",
                          textDecoration: item.packed ? "line-through" : "none" }}>{item.text}</span>
                        <button onClick={() => onUpdate && onUpdate({ ...trip, packList: (trip.packList || []).filter(x => x.id !== item.id) })}
                          style={{ background: "none", border: "none", color: "#ddd", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                ))
              )}
              <button onClick={() => setShowTemplatePicker(v => !v)}
                style={{ background: showTemplatePicker ? "#7B2D42" : "#f0ede8", border: "none", borderRadius: 8,
                  padding: "6px 12px", fontSize: 11, color: showTemplatePicker ? "#fff" : "#7B2D42",
                  cursor: "pointer", fontWeight: 600, marginBottom: 10, display: "flex", alignItems: "center", gap: 5 }}>
                📋 Use Template {showTemplatePicker ? "▲" : "▼"}
              </button>
              {showTemplatePicker && (
                <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12,
                  padding: "12px", marginBottom: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4 }}>Choose a template — items will be added to your list</div>
                  {Object.entries(PACK_LIST_TEMPLATES).map(([label, items]) => (
                    <button key={label} onClick={() => {
                      const existing = (trip.packList || []).map(x => x.text.toLowerCase());
                      const newItems = items.filter(i => !existing.includes(i.toLowerCase()))
                        .map(text => ({ id: "p_" + Date.now() + "_" + Math.random(), text, packed: false }));
                      if (newItems.length) onUpdate && onUpdate({ ...trip, packList: [...(trip.packList || []), ...newItems] });
                      setShowTemplatePicker(false);
                    }} style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8,
                      padding: "8px 12px", fontSize: 12, color: "#333", cursor: "pointer",
                      textAlign: "left", fontWeight: 500, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>{label}</span>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{items.length} items →</span>
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 6 }}>
                <input value={newPackItem} onChange={e => setNewPackItem(e.target.value)}
                  placeholder="Add an item…"
                  onKeyDown={e => {
                    if (e.key === "Enter" && newPackItem.trim()) {
                      e.preventDefault();
                      e.stopPropagation();
                      const text = newPackItem.trim();
                      setNewPackItem("");
                      onUpdate && onUpdate({ ...trip, packList: [...(trip.packList || []), { id: "p_" + Date.now(), text, packed: false }] });
                    }
                  }}
                  style={{ flex: 1, background: "#fff", border: "1px solid #e8e8e8", borderRadius: 8,
                    padding: "8px 12px", fontSize: 12, color: "#222", outline: "none" }} />
                <button onClick={() => {
                  if (!newPackItem.trim()) return;
                  onUpdate && onUpdate({ ...trip, packList: [...(trip.packList || []), { id: "p_" + Date.now(), text: newPackItem.trim(), packed: false }] });
                  setNewPackItem("");
                }} style={{ background: "#7B2D42", border: "none", borderRadius: 8, padding: "8px 14px",
                  color: "#fff", fontSize: 12, cursor: "pointer", fontWeight: 600, flexShrink: 0 }}>+ Add</button>
              </div>
            </div>
          )}

          {/* ── RESERVATIONS TAB ── */}
          {plannerTab === "reservations" && !tabCollapsed && (
            <ReservationsTab trip={trip} onUpdate={onUpdate} initialFilter={reservationFilter} onFilterChange={setReservationFilter} />
          )}

          {/* ── WEATHER TAB ── */}
          {plannerTab === "weather" && !tabCollapsed && (
            <WeatherTab trip={trip} />
          )}

          {/* ── FRIEND LOGS TAB ── */}
          {plannerTab === "friendLogs" && !tabCollapsed && (
            <FriendLogsTab trip={trip} entries={entries} allUsers={allUsers} friendState={friendState} onUpdate={onUpdate} onViewProfile={onViewProfile} />
          )}

        </div>
      )}

      {/* ── OVERFLOW MENU (portal) ── */}
      {showOverflowMenu && overflowMenuPos && createPortal(
        <div onClick={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}
          style={{ position: "fixed", top: overflowMenuPos.top, right: overflowMenuPos.right,
            background: "#fff", borderRadius: 14, boxShadow: "0 8px 40px rgba(0,0,0,0.16)",
            border: "1px solid #f0f0f0", minWidth: 215, zIndex: 9000, padding: "6px 0",
            overflow: "hidden", overscrollBehavior: "contain" }}>
          {!confirmingComplete && !completePhotoStep && !confirmingDelete && (
            <>
              <OMenuItem icon="🗺" label="Full Map View" onClick={() => { setShowOverflowMenu(false); setShowMapModal(true); setExpanded(true); }} />
              {!past && onEdit && <OMenuItem icon="✏️" label="Edit Trip" onClick={() => { setShowOverflowMenu(false); onEdit(); }} />}
              {!past && onInviteFriend && <OMenuItem icon="👥" label="Invite Friends" onClick={() => { setShowOverflowMenu(false); setShowInviteDropdown(true); setExpanded(true); }} />}
              {past && !trip.isShared && onShare && <OMenuItem icon="🔗" label="Share Trip" onClick={() => { setShowOverflowMenu(false); setShowShareDropdown(true); setExpanded(true); }} />}
              {onImportToLogs && <OMenuItem icon="📍" label="Import All to Logs" onClick={() => { setShowOverflowMenu(false); onImportToLogs(); }} />}
              {past && !trip.isShared && onReactivate && <OMenuItem icon="↩" label="Move to Current" onClick={() => { setShowOverflowMenu(false); onReactivate(); }} />}
              {past && trip.isShared && onCopyToCurrentTrip && <OMenuItem icon="📋" label="Use as My Trip" onClick={() => { setShowOverflowMenu(false); onCopyToCurrentTrip(); }} />}
              {!trip.isShared && onDuplicate && <OMenuItem icon="⧉" label="Duplicate Trip" onClick={() => { setShowOverflowMenu(false); onDuplicate(); }} />}
              <OMenuItem icon={savedOffline ? "✓" : "📥"} label={savedOffline ? "Saved Offline" : "Save for Offline"} onClick={() => {
                if (savedOffline) {
                  localStorage.removeItem("ts_offline_" + trip.id);
                  setSavedOffline(false);
                  showToast("Removed offline save", "success");
                } else {
                  localStorage.setItem("ts_offline_" + trip.id, JSON.stringify(trip));
                  setSavedOffline(true);
                  showToast("Trip saved for offline use", "success");
                }
                setShowOverflowMenu(false);
              }} />
              <div style={{ height: 1, background: "#f5f5f5", margin: "4px 0" }} />
              {!past && onComplete && (
                <OMenuItem icon="✓" label="Mark as Done" onClick={() => setConfirmingComplete(true)} />
              )}
              <OMenuItem icon={leaveMode ? "🚪" : "🗑"} label={leaveMode ? "Leave Trip" : "Delete Trip"} onClick={() => setConfirmingDelete(true)} danger />
            </>
          )}
          {!past && onComplete && confirmingComplete && !completePhotoStep && (
            <div style={{ padding: "8px 16px" }}>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>Move to Past Trips?</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => { setConfirmingComplete(false); setCompletePhotoStep(true); }}
                  style={{ flex: 1, background: "#111", border: "none", borderRadius: 8, padding: "7px",
                    color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Yes</button>
                <button onClick={() => setConfirmingComplete(false)}
                  style={{ flex: 1, background: "#f5f5f5", border: "none", borderRadius: 8, padding: "7px",
                    color: "#555", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}
          {!past && onComplete && completePhotoStep && (
            <div style={{ padding: "10px 16px" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#000", marginBottom: 4 }}>Add a cover photo?</div>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 10, lineHeight: 1.5 }}>
                Pick a photo from this trip — it'll appear on your Home screen and Trips page.
              </div>
              <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                background: "#000", border: "none", borderRadius: 8, padding: "9px",
                color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", marginBottom: 6 }}>
                📷 Choose Photo
                <input type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const img = new Image();
                    const url = URL.createObjectURL(file);
                    img.onload = () => {
                      const MAX = 800;
                      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
                      const canvas = document.createElement("canvas");
                      canvas.width = Math.round(img.width * scale);
                      canvas.height = Math.round(img.height * scale);
                      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                      URL.revokeObjectURL(url);
                      onComplete(canvas.toDataURL("image/jpeg", 0.7));
                      setCompletePhotoStep(false);
                      setShowOverflowMenu(false);
                    };
                    img.src = url;
                  }} />
              </label>
              <button onClick={() => { onComplete(null); setCompletePhotoStep(false); setShowOverflowMenu(false); }}
                style={{ width: "100%", background: "#f5f5f5", border: "none", borderRadius: 8, padding: "7px",
                  color: "#888", fontSize: 12, cursor: "pointer" }}>
                Skip — use destination photo
              </button>
            </div>
          )}
          {confirmingDelete && (
            <div style={{ padding: "8px 16px" }}>
              <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 8 }}>
                {leaveMode ? "Leave this trip? You'll lose access." : "Delete this trip?"}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => { onDelete(); setShowOverflowMenu(false); }}
                  style={{ flex: 1, background: "#ef4444", border: "none", borderRadius: 8, padding: "7px",
                    color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {leaveMode ? "Leave" : "Delete"}
                </button>
                <button onClick={() => setConfirmingDelete(false)}
                  style={{ flex: 1, background: "#f5f5f5", border: "none", borderRadius: 8, padding: "7px",
                    color: "#555", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Map modal */}
      {showMapModal && (
        <TripMapModal trip={trip} onClose={() => { setShowMapModal(false); setInitialMapDay(null); }}
          onUpdate={onUpdate} initialDayFilter={initialMapDay} />
      )}

      {/* Edit plan entry modal */}
      {editPlanEntry && createPortal(
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 18, padding: "24px", width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>Edit Place</span>
              <button onClick={() => setEditPlanEntry(null)} style={{ background: "#f5f5f5", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", fontSize: 16, color: "#888", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>
            {[
              { field: "name", label: "Name", placeholder: "Place name" },
              { field: "city", label: "City", placeholder: "City" },
              { field: "state", label: "State", placeholder: "State" },
              { field: "cuisine", label: "Cuisine / Category", placeholder: "e.g. Italian, Hiking trail" },
            ].map(({ field, label, placeholder }) => (
              <div key={field} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>{label}</label>
                <input value={editPlanEntry.entry[field] || ""}
                  onChange={ev => setEditPlanEntry(prev => ({ ...prev, entry: { ...prev.entry, [field]: ev.target.value } }))}
                  placeholder={placeholder}
                  style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e8e8e8", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", color: "#111", background: "#fafafa" }} />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>Type</label>
              <select value={editPlanEntry.entry.type || "activity"}
                onChange={ev => setEditPlanEntry(prev => ({ ...prev, entry: { ...prev.entry, type: ev.target.value } }))}
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e8e8e8", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", color: "#111", background: "#fafafa" }}>
                <option value="restaurant">Restaurant</option>
                <option value="cafe">Cafe</option>
                <option value="brewery">Brewery / Bar</option>
                <option value="hotel">Hotel</option>
                <option value="activity">Activity</option>
                <option value="hiking">Hiking</option>
                <option value="national_park">National Park</option>
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>Notes</label>
              <textarea value={editPlanEntry.entry.notes || ""}
                onChange={ev => setEditPlanEntry(prev => ({ ...prev, entry: { ...prev.entry, notes: ev.target.value } }))}
                rows={2}
                placeholder="Notes, tips, reminders…"
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e8e8e8", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none", color: "#111", background: "#fafafa", resize: "vertical" }} />
            </div>
            <button onClick={() => {
              if (!onUpdate) return;
              const { dayIdx, entry: updated } = editPlanEntry;
              const newDays = trip.plan.days.map((d, i) =>
                i === dayIdx ? { ...d, entries: (d.entries || d.items || []).map(e => e.id === updated.id ? updated : e) } : d
              );
              onUpdate({ ...trip, plan: { ...trip.plan, days: newDays } });
              setEditPlanEntry(null);
            }} style={{ width: "100%", background: "#7B2D42", border: "none", borderRadius: 8, padding: "11px", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              Save Changes
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}


const TYPE_NORMALIZE = {
  national_park: "national_park", nature_reserve: "national_park",
  natural_feature: "hiking", park: "hiking",
  trail: "hiking", path: "hiking", campground: "hiking",
  lodging: "hotel", establishment: "restaurant",
  food: "restaurant", bar: "brewery", night_club: "brewery",
  point_of_interest: "activity", tourist_attraction: "activity",
  zoo: "activity", aquarium: "activity", amusement_park: "activity",
  museum: "museum", church: "landmark", place_of_worship: "landmark",
};



const EXPLORE_GRADIENT = {
  restaurant: { bg: "linear-gradient(135deg, #fef3c7 0%, #f59e0b 100%)", icon: "🍽" },
  cafe:       { bg: "linear-gradient(135deg, #fde8d0 0%, #92400e 100%)", icon: "☕" },
  brewery:    { bg: "linear-gradient(135deg, #ede9fe 0%, #6d28d9 100%)", icon: "🍺" },
  hotel:      { bg: "linear-gradient(135deg, #dbeafe 0%, #1d4ed8 100%)", icon: "🏨" },
  hiking:     { bg: "linear-gradient(135deg, #d1fae5 0%, #065f46 100%)", icon: "🥾" },
  activity:   { bg: "linear-gradient(135deg, #e0f2fe 0%, #0369a1 100%)", icon: "🏔" },
  museum:     { bg: "linear-gradient(135deg, #fce7f3 0%, #9d174d 100%)", icon: "🏛" },
  landmark:   { bg: "linear-gradient(135deg, #fef9c3 0%, #854d0e 100%)", icon: "🗺" },
};

// ─── EXPLORE TAB COMPONENT ───────────────────────────────────────────────────
function ExploreTab({ entries, friendState, savedTrips, onAddToTrip, onViewProfile, savedBookmarks, onToggleBookmark, currentUserId, friendProfiles, onSaveToIdeas }) {
  const [exploreTab, setExploreTab] = useState("network"); // network | foryou
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const activeTrips = (savedTrips || []).filter(t => !t.completed && !t.draft);
  const draftTrips = (savedTrips || []).filter(t => t.draft);
  // For You filters
  const [forYouCity, setForYouCity] = useState("");
  const [forYouState, setForYouState] = useState("");
  // Network filters
  const [networkCategory, setNetworkCategory] = useState("all");
  const [networkSearch, setNetworkSearch] = useState("");
  const [networkVerdict, setNetworkVerdict] = useState("all");
  const [networkSort, setNetworkSort] = useState("recent"); // recent | rating | bookmarked
  const [networkMinRating, setNetworkMinRating] = useState(0);
  const [showNetworkFilters, setShowNetworkFilters] = useState(false);
  const activeFilterCount = (networkSort !== "recent" ? 1 : 0) + (networkMinRating > 0 ? 1 : 0) + (networkVerdict !== "all" ? 1 : 0);

  const allUsers = friendProfiles || [];
  const networkEntries = entries.filter(e => e.userId !== currentUserId && friendState?.[e.userId] === "friend" && e.verdict && e.rating);

  const filteredNetwork = networkEntries.filter(e => {
    if (networkSearch.trim()) {
      const q = networkSearch.toLowerCase();
      if (!entryMatchesSearch(e, q)) return false;
    }
    if (networkVerdict !== "all" && e.verdict !== networkVerdict) return false;
    if (networkMinRating > 0 && (e.rating || 0) < networkMinRating) return false;
    if (networkSort === "bookmarked" && !(savedBookmarks || new Set()).has(e.id)) return false;
    if (networkCategory === "hotel") return e.type === "hotel";
    if (networkCategory === "food") return ["restaurant","cafe","brewery"].includes(e.type);
    if (networkCategory === "hiking") return e.type === "hiking";
    if (networkCategory === "kids") return e.kidFriendly === true;
    if (networkCategory === "activities") return e.type === "activity";
    return true;
  }).sort((a, b) => {
    if (networkSort === "rating") return (b.rating || 0) - (a.rating || 0);
    // recent: sort by created_at descending
    const tA = new Date(a.created_at || a.createdAt || 0).getTime();
    const tB = new Date(b.created_at || b.createdAt || 0).getTime();
    return tB - tA;
  });

  // "For You" recommendations: must_go entries your friends logged that you haven't logged
  const myLoggedNames = new Set(entries.filter(e => e.userId === currentUserId).map(e => e.name.toLowerCase()));
  // Derive available cities/states from the must_go friend entries
  const forYouPool = networkEntries.filter(e => e.verdict === "must_go" && !myLoggedNames.has(e.name.toLowerCase()));
  const forYouStates = [...new Set(forYouPool.map(e => e.state).filter(Boolean))].sort();
  const forYouCities = [...new Set(
    forYouPool.filter(e => !forYouState || e.state === forYouState).map(e => e.city).filter(Boolean)
  )].sort();
  const forYouEntries = forYouPool
    .filter(e => {
      if (forYouState && e.state !== forYouState) return false;
      if (forYouCity && e.city !== forYouCity) return false;
      return true;
    })
    .reduce((acc, e) => { // deduplicate by name
      if (!acc.find(x => x.name.toLowerCase() === e.name.toLowerCase())) acc.push(e);
      return acc;
    }, [])
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, 12);

  const networkActivities = entries.filter(e => {
    if (e.userId === currentUserId) return false;
    if (friendState?.[e.userId] !== "friend") return false;
    if (networkCategory === "hiking") return e.type === "hiking";
    if (networkCategory === "kids") return e.kidFriendly === true;
    if (networkCategory === "activities") return e.type === "activity";
    return false;
  });

  const catBtn = (val, current, setter, label) => (
    <button key={val} onClick={() => setter(val)}
      style={{ background: current === val ? "#000" : "#f8f8f8",
        border: `1px solid ${current === val ? "#000" : "#efefef"}`,
        color: current === val ? "#fff" : "#555",
        borderRadius: 20, padding: "6px 14px", cursor: "pointer",
        fontSize: 12, whiteSpace: "nowrap", flexShrink: 0 }}>
      {label}
    </button>
  );

  const renderExploreCard = (e, { user, isBookmarked, rankIndex } = {}) => {
    const hasUserPhoto = e.photos && e.photos.length > 0;
    const typeKey = TYPE_NORMALIZE[e.type] || e.type || "restaurant";
    const cfg = EXPLORE_GRADIENT[typeKey] || { bg: "linear-gradient(135deg, #f3f4f6 0%, #6b7280 100%)", icon: "📍" };
    return (
      <div key={e.id} style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 14px rgba(0,0,0,0.08)", marginBottom: 14 }}>
        <div style={{ position: "relative", height: 220, overflow: "hidden", flexShrink: 0 }}>
          {hasUserPhoto ? (
            <img src={e.photos[0]} alt={e.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: `center ${e.coverPosition || 50}%`, userSelect: "none", pointerEvents: "none" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", background: cfg.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56, userSelect: "none" }}>
              {cfg.icon}
            </div>
          )}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.62) 100%)", pointerEvents: "none" }} />
          <button onClick={ev => { ev.stopPropagation(); onToggleBookmark && onToggleBookmark(e.id); }}
            title={isBookmarked ? "Saved" : "Save"}
            style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", padding: "4px 6px", fontSize: 26, cursor: "pointer", lineHeight: 1, zIndex: 2, color: isBookmarked ? "#e8c84a" : "rgba(255,255,255,0.85)", textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}>
            {isBookmarked ? "♥" : "♡"}
          </button>
          {rankIndex !== undefined && rankIndex < 3 && (
            <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: rankIndex === 0 ? "#FFB800" : rankIndex === 1 ? "#9ca3af" : "#b45309", color: "#fff", borderRadius: 20, fontSize: 10, fontWeight: 800, padding: "3px 9px" }}>
              #{rankIndex + 1}
            </div>
          )}
          {user && (
            <div onClick={ev => { ev.stopPropagation(); onViewProfile && onViewProfile(user.id); }}
              style={{ position: "absolute", bottom: 11, left: 11, display: "flex", alignItems: "center", gap: 6, zIndex: 2, cursor: "pointer" }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.8)", overflow: "hidden", flexShrink: 0, background: "#e8e8e8", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {user.avatar_url
                  ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <span style={{ fontSize: 10, fontWeight: 700, color: "#555" }}>{(user.name || user.username || "?")[0].toUpperCase()}</span>
                }
              </div>
              <span style={{ color: "rgba(255,255,255,0.92)", fontSize: 11, fontWeight: 500, textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>@{user.username}</span>
            </div>
          )}
          {(e.rating || 0) > 0 && (
            <div style={{ position: "absolute", bottom: 11, right: 11, zIndex: 2, display: "flex", gap: 1 }}>
              {[1,2,3,4,5].map(i => (
                <span key={i} style={{ color: i <= (e.rating || 0) ? "#e8c84a" : "rgba(255,255,255,0.45)", fontSize: 13, lineHeight: 1 }}>★</span>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: "14px 16px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
            <a href={`https://www.google.com/maps/search/${encodeURIComponent(e.name + " " + (e.city || ""))}`}
              target="_blank" rel="noopener noreferrer"
              onClick={ev => ev.stopPropagation()}
              style={{ fontFamily: "'Cormorant Garamond', 'Lora', serif", fontSize: 22, fontWeight: 600, color: "#1a1a1a", textDecoration: "none", lineHeight: 1.2, flex: 1 }}
              onMouseEnter={ev => ev.currentTarget.style.textDecoration = "underline"}
              onMouseLeave={ev => ev.currentTarget.style.textDecoration = "none"}>
              {e.name}
            </a>
            {e.verdict && <VerdictBadge verdict={e.verdict} />}
          </div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            {[e.city, e.state].filter(Boolean).join(", ")}{(e.cuisine || TYPE_NORMALIZE[e.type] || e.type) ? ` · ${e.cuisine || TYPE_NORMALIZE[e.type] || e.type}` : ""}
          </div>
          {e.difficulty && (
            <span style={{ display: "inline-block", fontSize: 10, fontWeight: 600, color: "#555", background: "#f0f0f0", borderRadius: 4, padding: "2px 6px", marginBottom: 6 }}>{e.difficulty}</span>
          )}
          {e.notes && (
            <div style={{ borderLeft: "3px solid #c8a96e", paddingLeft: 12, margin: "10px 0 8px", fontFamily: "'Cormorant Garamond', 'Lora', serif", fontStyle: "italic", fontSize: 15, color: "#333", lineHeight: 1.6 }}>
              {`"${!expandedNotes.has(e.id) && e.notes.length > 160 ? e.notes.slice(0, 160) + '…' : e.notes}"`}
              {e.notes.length > 160 && (
                <span onClick={ev => { ev.stopPropagation(); setExpandedNotes(prev => { const s = new Set(prev); s.has(e.id) ? s.delete(e.id) : s.add(e.id); return s; }); }}
                  style={{ display: "inline-block", marginLeft: 6, fontSize: 11, color: "#c8a96e", cursor: "pointer", fontStyle: "normal", fontFamily: "'DM Sans', sans-serif" }}>
                  {expandedNotes.has(e.id) ? "less" : "more"}
                </span>
              )}
            </div>
          )}
          <div style={{ height: 1, background: "#f0ece6", margin: "10px 0" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 14, color: "#666", fontWeight: 500 }}>📍 {[e.city, e.state].filter(Boolean).join(", ")}</span>
            <button
              onClick={ev => {
                ev.stopPropagation();
                if (openMenuId === e.id) { setOpenMenuId(null); return; }
                const rect = ev.currentTarget.getBoundingClientRect();
                setMenuPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX - 200 });
                setOpenMenuId(e.id);
              }}
              style={{ background: "#f4f4f4", border: "1px solid #e8e8e8", borderRadius: 8, cursor: "pointer",
                fontSize: 20, color: "#555", padding: "6px 12px", lineHeight: 1, flexShrink: 0 }}>
              ···
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Portal dropdown for explore card actions
  const ExploreCardMenu = openMenuId ? (() => {
    const e = entries.find(x => x.id === openMenuId);
    if (!e) return null;
    const isBookmarked = (savedBookmarks || new Set()).has(e.id);
    return createPortal(
      <>
        <div onClick={() => setOpenMenuId(null)} style={{ position: "fixed", inset: 0, zIndex: 1000 }} />
        <div onWheel={e => e.stopPropagation()}
          style={{ position: "absolute", top: menuPos.top, left: Math.max(8, menuPos.left),
          width: 200, background: "#fff", borderRadius: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
          border: "1px solid #efefef", zIndex: 1001, overflow: "hidden", fontSize: 13, overscrollBehavior: "contain" }}>
          {/* Bookmark */}
          <button onClick={() => { onToggleBookmark && onToggleBookmark(e.id); setOpenMenuId(null); }}
            style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "11px 16px",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: "#111" }}
            onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
            onMouseLeave={ev => ev.currentTarget.style.background = "none"}>
            <span>{isBookmarked ? "♥" : "♡"}</span>
            {isBookmarked ? "Remove bookmark" : "Save to bookmarks"}
          </button>
          {/* Add to Trip */}
          {activeTrips.length > 0 && (
            <>
              <div style={{ height: 1, background: "#f0f0f0", margin: "0 12px" }} />
              {activeTrips.map(t => (
                <button key={t.id} onClick={() => { onAddToTrip && onAddToTrip(t.id, e); setOpenMenuId(null); }}
                  style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 16px",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: "#111" }}
                  onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
                  onMouseLeave={ev => ev.currentTarget.style.background = "none"}>
                  <span>✈️</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Add to {t.destination}
                  </span>
                </button>
              ))}
            </>
          )}
          {/* Add to Ideas */}
          {draftTrips.length > 0 && (
            <>
              <div style={{ height: 1, background: "#f0f0f0", margin: "0 12px" }} />
              {draftTrips.map(t => (
                <button key={"idea_" + t.id} onClick={() => { onSaveToIdeas && onSaveToIdeas(t.id, e.name); setOpenMenuId(null); }}
                  style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 16px",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 10, color: "#111" }}
                  onMouseEnter={ev => ev.currentTarget.style.background = "#f8f8f8"}
                  onMouseLeave={ev => ev.currentTarget.style.background = "none"}>
                  <span>💡</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Ideas for {t.destination}
                  </span>
                </button>
              ))}
            </>
          )}
          {activeTrips.length === 0 && draftTrips.length === 0 && (
            <div style={{ padding: "10px 16px", fontSize: 12, color: "#aaa" }}>No active trips to add to</div>
          )}
        </div>
      </>,
      document.body
    );
  })() : null;

  return (
    <div style={{ background: "#faf7f2" }}>
      {ExploreCardMenu}
      {/* Main tabs: Network | For You */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, background: "#f0f0f0", borderRadius: 10, padding: 3 }}>
        {[["network", "👥 Network"], ["foryou", "💡 For You"]].map(([id, label]) => (
          <button key={id} onClick={() => setExploreTab(id)}
            style={{ flex: 1, background: exploreTab === id ? "#fff" : "transparent",
              border: "none", borderRadius: 8, padding: "10px 8px", cursor: "pointer",
              color: exploreTab === id ? "#000" : "#888", fontWeight: exploreTab === id ? 600 : 400,
              fontSize: 13, boxShadow: exploreTab === id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              transition: "all 0.2s" }}>
            {label}
          </button>
        ))}
      </div>

      {/* NETWORK TAB */}
      {exploreTab === "network" && (
        <div>
          {/* Search + Filter row */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1 }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#aaa", pointerEvents: "none", fontSize: 16 }}>🔍</span>
              <input value={networkSearch} onChange={e => setNetworkSearch(e.target.value)}
                placeholder="Search places, cities, cuisine..."
                style={{ width: "100%", background: "#f5f5f5", border: "1.5px solid #e8e8e8", borderRadius: 12,
                  padding: "13px 36px 13px 40px", color: "#000", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              {networkSearch && <button onClick={() => setNetworkSearch("")}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>}
            </div>
            <button onClick={() => setShowNetworkFilters(v => !v)}
              style={{ flexShrink: 0, position: "relative", background: showNetworkFilters || activeFilterCount > 0 ? "#111" : "#f5f5f5",
                border: "1.5px solid #e8e8e8", borderRadius: 12, padding: "13px 16px",
                color: showNetworkFilters || activeFilterCount > 0 ? "#fff" : "#555",
                fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              ⚙ Filter
              {activeFilterCount > 0 && (
                <span style={{ background: "#fff", color: "#111", borderRadius: "50%", width: 16, height: 16,
                  fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Category pills */}
          <div style={{ display: "flex", gap: 8, marginBottom: 10, overflowX: "auto", paddingBottom: 2 }}>
            {catBtn("all", networkCategory, setNetworkCategory, "All")}
            {catBtn("food", networkCategory, setNetworkCategory, "🍽 Food")}
            {catBtn("hotel", networkCategory, setNetworkCategory, "🏨 Hotel")}
            {catBtn("hiking", networkCategory, setNetworkCategory, "🥾 Hiking")}
            {catBtn("kids", networkCategory, setNetworkCategory, "👶 Kids")}
            {catBtn("activities", networkCategory, setNetworkCategory, "⛺ Activities")}
          </div>

          {/* Collapsible Sort & Filter panel */}
          {showNetworkFilters && (
            <div style={{ background: "#fafafa", border: "1px solid #efefef", borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 10, color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Sort</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[["recent","Recent"],["rating","Top Rated"],["bookmarked","🔖 Saved"]].map(([v,l]) => (
                      <button key={v} onClick={() => setNetworkSort(v)}
                        style={{ background: networkSort === v ? "#111" : "#fff", border: `1px solid ${networkSort === v ? "#111" : "#e0e0e0"}`,
                          color: networkSort === v ? "#fff" : "#555", borderRadius: 20, padding: "6px 13px", cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Min Rating</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[0,3,4,5].map(r => (
                      <button key={r} onClick={() => setNetworkMinRating(r)}
                        style={{ background: networkMinRating === r ? "#111" : "#fff", border: `1px solid ${networkMinRating === r ? "#111" : "#e0e0e0"}`,
                          color: networkMinRating === r ? "#fff" : "#555", borderRadius: 20, padding: "6px 12px", cursor: "pointer", fontSize: 12 }}>
                        {r === 0 ? "Any" : r + "+ ★"}
                      </button>
                    ))}
                  </div>
                </div>
                {networkCategory === "food" && (
                  <div>
                    <div style={{ fontSize: 10, color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Verdict</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[["all","All"],["must_go","✓ Must Go"],["skip","✕ Skip"]].map(([v,l]) => (
                        <button key={v} onClick={() => setNetworkVerdict(v)}
                          style={{ background: networkVerdict === v ? "#111" : "#fff", border: `1px solid ${networkVerdict === v ? "#111" : "#e0e0e0"}`,
                            color: networkVerdict === v ? "#fff" : "#555", borderRadius: 20, padding: "6px 13px", cursor: "pointer", fontSize: 12 }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {activeFilterCount > 0 && (
                <button onClick={() => { setNetworkSort("recent"); setNetworkMinRating(0); setNetworkVerdict("all"); }}
                  style={{ marginTop: 12, background: "none", border: "none", color: "#888", fontSize: 11, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                  Reset filters
                </button>
              )}
            </div>
          )}

          {/* Results count */}
          <div style={{ fontSize: 11, color: "#aaa", marginBottom: 10 }}>
            {networkCategory === "all" || networkCategory === "food"
              ? `${filteredNetwork.length} places from your network`
              : `${networkActivities.length} ${networkCategory} from your network`}
          </div>

          {/* Entries — single-column editorial feed */}
          {(networkCategory === "all" || networkCategory === "food") && (
            <div>
              {filteredNetwork.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#aaa", background: "#fff", borderRadius: 16, border: "1px dashed #e8e8e8" }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>
                    {networkSort === "bookmarked" ? "🔖" : networkEntries.length === 0 ? "👥" : "🔍"}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#666", marginBottom: 4 }}>
                    {networkSort === "bookmarked"
                      ? "No saved places yet"
                      : networkEntries.length === 0
                      ? "Add friends to see their picks"
                      : "No results found"}
                  </div>
                  <div style={{ fontSize: 12, color: "#bbb" }}>
                    {networkSort === "bookmarked"
                      ? "Tap ☆ on any place below to save it"
                      : networkEntries.length === 0
                      ? "Connect with people you trust to see where they eat, hike & stay"
                      : "Try a different search or filter"}
                  </div>
                </div>
              ) : filteredNetwork.map(e => {
                const user = allUsers.find(u => u.id === e.userId);
                const isBookmarked = (savedBookmarks || new Set()).has(e.id);
                return renderExploreCard(e, { user, isBookmarked });
              })}
            </div>
          )}

          {/* Activities — single-column editorial feed */}
          {(networkCategory === "hiking" || networkCategory === "kids" || networkCategory === "activities") && (
            <div>
              {networkActivities.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#aaa", background: "#fff", borderRadius: 16 }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🏔</div>
                  <div style={{ fontSize: 14 }}>No {networkCategory} from your network</div>
                </div>
              ) : networkActivities.map(e => {
                const user = allUsers.find(u => u.id === e.userId);
                const isBookmarked = (savedBookmarks || new Set()).has(e.id);
                return renderExploreCard(e, { user, isBookmarked });
              })}
            </div>
          )}
        </div>
      )}

      {/* FOR YOU TAB */}
      {exploreTab === "foryou" && (
        <div>
          {/* State + City filters */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <select value={forYouState} onChange={e => { setForYouState(e.target.value); setForYouCity(""); }}
              style={{ flex: 1, background: forYouState ? "#000" : "#f8f8f8", color: forYouState ? "#fff" : "#555",
                border: `1px solid ${forYouState ? "#000" : "#efefef"}`, borderRadius: 20,
                padding: "7px 12px", fontSize: 12, cursor: "pointer", outline: "none" }}>
              <option value="">All States</option>
              {forYouStates.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={forYouCity} onChange={e => setForYouCity(e.target.value)}
              style={{ flex: 1, background: forYouCity ? "#000" : "#f8f8f8", color: forYouCity ? "#fff" : "#555",
                border: `1px solid ${forYouCity ? "#000" : "#efefef"}`, borderRadius: 20,
                padding: "7px 12px", fontSize: 12, cursor: "pointer", outline: "none" }}>
              <option value="">All Cities</option>
              {forYouCities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Results count */}
          <div style={{ fontSize: 12, color: "#888", marginBottom: 12, fontWeight: 500 }}>
            {forYouEntries.length} picks from your network{forYouCity ? ` in ${forYouCity}` : forYouState ? ` in ${forYouState}` : ""}
          </div>

          {/* For You — single-column editorial feed */}
          <div>
            {forYouEntries.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#aaa", background: "#fff", borderRadius: 16 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>💡</div>
                <div style={{ fontSize: 14 }}>No new recommendations yet</div>
                <div style={{ fontSize: 12, color: "#bbb", marginTop: 4 }}>Follow more friends to get personalized picks</div>
              </div>
            ) : forYouEntries.map((e, i) => {
              const user = allUsers.find(u => u.id === e.userId);
              const isBookmarked = (savedBookmarks || new Set()).has(e.id);
              return renderExploreCard(e, { user, isBookmarked, rankIndex: i });
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DESTINATION GRADIENT ────────────────────────────────────────────────────
function _destGradient(destination) {
  const d = (destination || "").toLowerCase();
  if (/maldive|bali|hawaii|caribb|phuket|cancun|jamaica|bahama|fiji|tahiti|seychell|barbado|aruba|tulum|koh |boracay|langkawi/.test(d))
    return "linear-gradient(160deg, #0891b2 0%, #06b6d4 45%, #f97316 100%)";
  if (/paris|france|rome|italy|barcelona|madrid|spain|greece|santorini|amalfi|lisbon|portugal|amsterdam|berlin|london|england|\buk\b|vienna|prague|budapest|croatia|dubrovnik|sicily|tuscany|florence|venice|milan|seville|monaco|nice/.test(d))
    return "linear-gradient(160deg, #9a3412 0%, #ea580c 45%, #fbbf24 100%)";
  if (/alps|alpine|switzerland|zurich|geneva|rockies|colorado|aspen|vail|breckenridge|banff|jasper|whistler|chamonix|zermatt|tahoe|patagonia|himalayas|nepal|kathmandu|kilimanjaro|yosemite|yellowstone|grand teton/.test(d))
    return "linear-gradient(160deg, #14532d 0%, #1d4ed8 50%, #1e3a5f 100%)";
  if (/tokyo|japan|osaka|kyoto|seoul|korea|singapore|hong kong|bangkok|thailand|vietnam|hanoi|saigon|shanghai|beijing|china|taipei|taiwan|malaysia|kuala lumpur|cambodia|myanmar|philippines|manila/.test(d))
    return "linear-gradient(160deg, #1e1b4b 0%, #6d28d9 50%, #be185d 100%)";
  if (/india|mumbai|delhi|jaipur|goa|kerala|bangalore|rajasthan|agra|sri lanka|colombo/.test(d))
    return "linear-gradient(160deg, #7c2d12 0%, #b45309 45%, #fbbf24 100%)";
  if (/dubai|uae|emirates|abu dhabi|morocco|marrakech|egypt|cairo|saudi|jordan|petra|oman|qatar|doha|israel|tel aviv|arizona|phoenix|las vegas|nevada/.test(d))
    return "linear-gradient(160deg, #78350f 0%, #b45309 40%, #eab308 100%)";
  if (/iceland|reykjavik|norway|oslo|bergen|tromso|finland|helsinki|sweden|stockholm|greenland|alaska|arctic|lapland|faroe/.test(d))
    return "linear-gradient(160deg, #0f172a 0%, #1d4ed8 45%, #34d399 100%)";
  if (/kenya|nairobi|tanzania|safari|serengeti|zanzibar|south africa|cape town|ethiopia|rwanda|botswana|namibia/.test(d))
    return "linear-gradient(160deg, #431407 0%, #15803d 50%, #b45309 100%)";
  if (/mexico|cancun|oaxaca|colombia|bogota|cartagena|peru|lima|cusco|machu picchu|brazil|rio|argentina|buenos aires|chile|ecuador|cuba|havana|costa rica|panama|belize|guatemala/.test(d))
    return "linear-gradient(160deg, #14532d 0%, #d97706 50%, #7c2d12 100%)";
  if (/amazon|borneo|jungle|rainforest|new zealand|queenstown|auckland|papua|vanuatu/.test(d))
    return "linear-gradient(160deg, #052e16 0%, #15803d 50%, #65a30d 100%)";
  if (/new york|nyc|manhattan|los angeles|\bla\b|chicago|miami|san francisco|seattle|boston|washington|atlanta|dallas|houston|austin|portland|nashville|new orleans|montreal|toronto|vancouver/.test(d))
    return "linear-gradient(160deg, #0f172a 0%, #1e3a5f 45%, #1e40af 100%)";
  if (/australia|sydney|melbourne|brisbane|perth|cairns|gold coast|adelaide/.test(d))
    return "linear-gradient(160deg, #164e63 0%, #0ea5e9 50%, #0891b2 100%)";
  return "linear-gradient(160deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)";
}

// Shows destination gradient by default; fades in user cover photo on top when available
function DestinationPhoto({ destination, coverPhoto, style }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  return (
    <div style={{ ...style, background: _destGradient(destination), position: style?.position || "absolute", overflow: "hidden" }}>
      {coverPhoto && (
        <img src={coverPhoto} alt={destination || ""}
          onLoad={() => setImgLoaded(true)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center",
            opacity: imgLoaded ? 1 : 0, transition: "opacity 0.5s ease" }} />
      )}
    </div>
  );
}

// ─── TRIP WEATHER BADGE ──────────────────────────────────────────────────────
const WMO_ICONS = {
  0:"☀️",1:"🌤",2:"⛅",3:"☁️",45:"🌫",48:"🌫",
  51:"🌦",53:"🌦",55:"🌧",61:"🌧",63:"🌧",65:"🌧",
  71:"🌨",73:"❄️",75:"❄️",80:"🌦",81:"🌦",82:"⛈",
  95:"⛈",96:"⛈",99:"⛈"
};
function TripWeatherBadge({ destination, startDate, onClick }) {
  const [wx, setWx] = useState(null);
  useEffect(() => {
    if (!destination || !startDate) return;
    const controller = new AbortController();
    (async () => {
      try {
        const loc = await resolveWeatherLocation(destination, controller.signal);
        if (!loc) return;
        const tripStart = new Date(startDate + "T12:00:00");
        const today = new Date(); today.setHours(0,0,0,0);
        const daysUntil = Math.ceil((tripStart - today) / 86400000);
        if (daysUntil > 16) { setWx({ tooFar: true }); return; }
        const isHistorical = daysUntil < -1;
        const baseUrl = isHistorical
          ? "https://archive-api.open-meteo.com/v1/archive"
          : "https://api.open-meteo.com/v1/forecast";
        const wRes = await fetch(
          `${baseUrl}?latitude=${loc.latitude}&longitude=${loc.longitude}&daily=temperature_2m_max,temperature_2m_min,weathercode&start_date=${startDate}&end_date=${startDate}&timezone=auto&temperature_unit=fahrenheit`,
          { signal: controller.signal }
        );
        const wData = await wRes.json();
        if (!wData.daily?.time?.length) return;
        const icon = WMO_ICONS[wData.daily.weathercode[0]] || "🌡";
        const hi = Math.round(wData.daily.temperature_2m_max[0]);
        const lo = Math.round(wData.daily.temperature_2m_min[0]);
        setWx({ icon, hi, lo });
      } catch {}
    })();
    return () => controller.abort();
  }, [destination, startDate]);
  if (!wx) return null;
  const SANS = "'DM Sans', sans-serif";
  if (wx.tooFar) return (
    <div style={{ fontFamily: SANS, fontSize: 10, color: "rgba(255,255,255,0.65)", marginTop: 5 }}>
      🌤 Weather available closer to departure
    </div>
  );
  if (onClick) return (
    <button onClick={e => { e.stopPropagation(); onClick(); }}
      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 4, marginTop: 5 }}>
      <span style={{ fontFamily: SANS, fontSize: 11, color: "rgba(255,255,255,0.88)" }}>
        {wx.icon} {wx.hi}°F / {wx.lo}°F on departure day
      </span>
    </button>
  );
  return (
    <div style={{ fontFamily: SANS, fontSize: 11, color: "rgba(255,255,255,0.88)", marginTop: 5, display: "flex", alignItems: "center", gap: 4 }}>
      {wx.icon} {wx.hi}°F / {wx.lo}°F on departure day
    </div>
  );
}

// ─── HOME TAB COMPONENT ──────────────────────────────────────────────────────
function HomeTab({ currentUser, savedTrips, sharedTrips, entries, allUsers, onGoToTrips, onGoToPastTrips, onGoToExplore, onGoToTrip, onUpdateCoverPhoto, onViewProfile, onLogPlace, savedBookmarks, onToggleBookmark }) {
  const SERIF = "'Lora', 'Georgia', serif";
  const SANS = "'DM Sans', sans-serif";
  const [heroIdx, setHeroIdx] = useState(0);
  const heroTouchStartX = useRef(null);

  const ownUpcoming = (savedTrips || []).filter(t => !t.completed && !t.draft);
  // sharedTrips only contains pending invites (accepted ones are already in savedTrips),
  // so exclude them from Upcoming entirely to avoid showing unaccepted trips.
  const upcomingTrips = ownUpcoming.slice(0, 3);
  const friendIds = new Set((allUsers || []).map(u => u.id));
  const friendEntries = entries.filter(e => friendIds.has(e.userId)).slice(-4).reverse();
  const pastTrips = (savedTrips || []).filter(t => t.completed).slice(-4).reverse();
  const userMap = Object.fromEntries((allUsers || []).map(u => [u.id, u]));
  const collaboratorLine = (trip) => {
    if (trip.sharerProfile) {
      const u = trip.sharerProfile;
      const label = u.username ? `@${u.username}` : u.name;
      if (!label) return null;
      return <>with <span onClick={ev => { ev.stopPropagation(); onViewProfile && onViewProfile(u.id); }}
        style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.4)" }}>{label}</span></>;
    }
    const ids = trip.invitedFriends || [];
    if (!ids.length) return null;
    const users = ids.map(id => userMap[id]).filter(u => u?.username);
    if (!users.length) return `with ${ids.length} friend${ids.length > 1 ? "s" : ""}`;
    const shown = users.slice(0, 2);
    return <>with {shown.map((u, i) => (
      <span key={u.id}>
        <span onClick={ev => { ev.stopPropagation(); onViewProfile && onViewProfile(u.id); }}
          style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.4)" }}>
          @{u.username}
        </span>
        {i < shown.length - 1 ? ", " : ""}
      </span>
    ))}{users.length > 2 ? ` +${users.length - 2}` : ""}</>;
  };
  // eslint-disable-next-line no-unused-vars
  const myEntries = entries.filter(e => e.userId === currentUser.id);

  const verdictLabel = { must_go: "Must Go", liked: "Liked", meh: "Meh", avoid: "Avoid" };
  const verdictBg    = { must_go: "#E8873A", liked: "#16a34a", meh: "#d97706", avoid: "#ef4444" };

  const SectionHeader = ({ title, onSeeAll }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <div style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, color: "#9e8878", textTransform: "uppercase", letterSpacing: "0.1em" }}>{title}</div>
      {onSeeAll && <button onClick={onSeeAll} style={{ background: "none", border: "none", fontFamily: SANS, fontSize: 14, color: "#b09e94", cursor: "pointer", padding: 0 }}>See all</button>}
    </div>
  );

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const getRegion = (destination) => {
    const parts = (destination || "").split(",").map(s => s.trim());
    if (parts.length < 2) return null;
    const last = parts[parts.length - 1].toLowerCase();
    const usStates = ["alabama","alaska","arizona","arkansas","california","colorado","connecticut","delaware","florida","georgia","hawaii","idaho","illinois","indiana","iowa","kansas","kentucky","louisiana","maine","maryland","massachusetts","michigan","minnesota","mississippi","missouri","montana","nebraska","nevada","new hampshire","new jersey","new mexico","new york","north carolina","north dakota","ohio","oklahoma","oregon","pennsylvania","rhode island","south carolina","south dakota","tennessee","texas","utah","vermont","virginia","washington","west virginia","wisconsin","wyoming","dc","district of columbia"];
    if (usStates.includes(last) || last === "usa" || last === "united states") return "United States 🇺🇸";
    const FLAGS = { "france":"🇫🇷","italy":"🇮🇹","spain":"🇪🇸","japan":"🇯🇵","united kingdom":"🇬🇧","uk":"🇬🇧","england":"🇬🇧","scotland":"🇬🇧","wales":"🇬🇧","germany":"🇩🇪","canada":"🇨🇦","mexico":"🇲🇽","thailand":"🇹🇭","australia":"🇦🇺","brazil":"🇧🇷","india":"🇮🇳","nepal":"🇳🇵","greece":"🇬🇷","portugal":"🇵🇹","indonesia":"🇮🇩","bali":"🇮🇩","vietnam":"🇻🇳","peru":"🇵🇪","colombia":"🇨🇴","argentina":"🇦🇷","new zealand":"🇳🇿","ireland":"🇮🇪","netherlands":"🇳🇱","switzerland":"🇨🇭","austria":"🇦🇹","sweden":"🇸🇪","norway":"🇳🇴","denmark":"🇩🇰","finland":"🇫🇮","iceland":"🇮🇸","china":"🇨🇳","south korea":"🇰🇷","korea":"🇰🇷","singapore":"🇸🇬","malaysia":"🇲🇾","philippines":"🇵🇭","turkey":"🇹🇷","egypt":"🇪🇬","morocco":"🇲🇦","south africa":"🇿🇦","kenya":"🇰🇪","tanzania":"🇹🇿","costa rica":"🇨🇷","cuba":"🇨🇺","dominican republic":"🇩🇴","jamaica":"🇯🇲" };
    const flag = FLAGS[last];
    return flag ? `${parts[parts.length - 1]} ${flag}` : parts[parts.length - 1];
  };

  const getTripStats = (trip) => {
    const allTripEntries = (trip.plan?.days || []).flatMap(d => d.entries || d.items || []);
    const places = allTripEntries.length;
    const food = allTripEntries.filter(e => ["restaurant","cafe","brewery"].includes(e.type)).length;
    const totalDays = trip.days || 1;
    const daysPlanned = (trip.plan?.days || []).filter(d => (d.entries || d.items || []).length > 0).length;
    const packedCount = (trip.packList||[]).filter(x => x.packed).length;
    const totalPack = (trip.packList||[]).length;
    const pct = Math.round((daysPlanned / totalDays) * 100);
    return { places, food, totalDays, daysPlanned, packedCount, totalPack, pct };
  };

  const friendsWentCount = (placeName) => new Set(
    entries.filter(e => friendIds.has(e.userId) && e.name?.toLowerCase().trim() === (placeName||"").toLowerCase().trim()).map(e => e.userId)
  ).size;

  return (
    <div className="home-desktop-grid">

    {/* ── LEFT COLUMN ── */}
    <div className="home-left-col">

      {/* Greeting */}
      <div style={{ paddingTop: 6 }}>
        <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 600, color: "#1a0a00", lineHeight: 1.2 }}>
          {greeting}, {currentUser.name.split(" ")[0]} 👋
        </div>
        <div style={{ fontFamily: SANS, fontSize: 13, color: "#9e8878", marginTop: 5 }}>
          Where will your next memory take you?
        </div>
      </div>

      {/* Quick action row */}
      <div style={{ display: "flex", gap: 10 }}>
        {[
          { icon: "📍", title: "Log a place", sub: "Food, stay or experience", onClick: onLogPlace },
          { icon: "🧳", title: "Plan a trip", sub: "Organize places & days", onClick: onGoToTrips },
          { icon: "👥", title: "See friend recs", sub: "Trusted picks near you", onClick: onGoToExplore },
        ].map(({ icon, title, sub, onClick }) => (
          <button key={title} onClick={onClick} style={{
            flex: 1, background: "#f9f5f2", border: "1px solid #ede8e2",
            borderRadius: 16, padding: "13px 8px 12px", cursor: "pointer",
            textAlign: "left", display: "flex", flexDirection: "column", gap: 5
          }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
              boxShadow: "0 1px 4px rgba(0,0,0,0.10)" }}>
              {icon}
            </div>
            <div style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, color: "#1a0a00", lineHeight: 1.3 }}>{title}</div>
            <div style={{ fontFamily: SANS, fontSize: 9.5, color: "#9e8878", lineHeight: 1.4 }}>{sub}</div>
          </button>
        ))}
      </div>

      {/* ── Upcoming Trips ── */}
      <div>
        <SectionHeader title="Upcoming Trips" onSeeAll={onGoToTrips} />
        {upcomingTrips.length === 0 ? (
          <div style={{ background: "rgba(61,31,20,0.04)", border: "1px dashed #d8cfc7", borderRadius: 18, padding: "28px 20px", textAlign: "center" }}>
            <div style={{ fontFamily: SERIF, fontSize: 15, color: "#9e8878", marginBottom: 12 }}>No trips planned yet</div>
            <button onClick={onGoToTrips} style={{ background: "#3d1f14", border: "none", borderRadius: 10, padding: "9px 20px", color: "#fff", fontFamily: SANS, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              + Plan a Trip
            </button>
          </div>
        ) : (
          <div>
            {/* Hero trip card */}
            {(() => {
              const clampedIdx = Math.min(heroIdx, upcomingTrips.length - 1);
              const trip = upcomingTrips[clampedIdx];
              const { places, packedCount, totalPack } = getTripStats(trip);
              let daysAway = null;
              if (trip.startDate) {
                daysAway = Math.ceil((new Date(trip.startDate + "T12:00:00") - new Date().setHours(0,0,0,0)) / (1000*60*60*24));
              }
              const region = getRegion(trip.destination);
              const cityName = trip.destination.split(",")[0].trim();
              const startFmt = trip.startDate ? new Date(trip.startDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
              const endFmt = trip.endDate ? new Date(trip.endDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
              const dateLine = startFmt
                ? (endFmt ? `${startFmt} – ${endFmt}` : startFmt) + ` · ${trip.days} day${trip.days !== 1 ? "s" : ""}`
                : `${trip.days} day${trip.days !== 1 ? "s" : ""}`;
              const toBookCount = (trip.reservations || []).filter(r => r.status === "need_to_reserve" || r.status === "pending").length;
              const bookedCount = (trip.reservations || []).filter(r => r.status === "reserved" || r.status === "confirmed").length;
              const remindersCount = (trip.bookRemind || []).length;
              return (
                <div style={{ borderRadius: 22, overflow: "hidden", position: "relative", boxShadow: "0 8px 32px rgba(0,0,0,0.24)" }}
                  onTouchStart={e => { heroTouchStartX.current = e.touches[0].clientX; }}
                  onTouchEnd={e => {
                    if (heroTouchStartX.current === null) return;
                    const dx = e.changedTouches[0].clientX - heroTouchStartX.current;
                    heroTouchStartX.current = null;
                    if (dx < -50 && clampedIdx < upcomingTrips.length - 1) setHeroIdx(clampedIdx + 1);
                    else if (dx > 50 && clampedIdx > 0) setHeroIdx(clampedIdx - 1);
                  }}>
                  <DestinationPhoto destination={trip.destination} coverPhoto={trip.coverPhoto}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.76) 100%)" }} />
                  <div style={{ position: "relative", padding: "18px 18px 0" }}>
                    {/* Top row: days badge */}
                    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start" }}>
                      {daysAway !== null ? (
                        <div style={{ background: "rgba(255,255,255,0.20)", backdropFilter: "blur(6px)", borderRadius: 20, padding: "5px 14px" }}>
                          <span style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            {daysAway <= 0 ? "🛫 Today!" : `In ${daysAway} day${daysAway !== 1 ? "s" : ""}`}
                          </span>
                        </div>
                      ) : <div />}
                    </div>
                    {/* Destination info */}
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontFamily: SERIF, fontSize: 25, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
                        {cityName}
                      </div>
                      {region && (
                        <div style={{ fontFamily: SANS, fontSize: 13, color: "rgba(255,255,255,0.76)", marginTop: 3 }}>
                          {region}
                        </div>
                      )}
                      <div style={{ fontFamily: SANS, fontSize: 12, color: "rgba(255,255,255,0.65)", marginTop: 3 }}>
                        {dateLine}{collaboratorLine(trip) ? <> · {collaboratorLine(trip)}</> : ""}
                      </div>
                      <TripWeatherBadge destination={trip.destination} startDate={trip.startDate} onClick={() => onGoToTrip && onGoToTrip(trip.id, "weather")} />
                    </div>
                    {/* Stats grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 6, marginTop: 16, background: "rgba(255,255,255,0.13)", backdropFilter: "blur(8px)", borderRadius: 14, padding: "12px 6px" }}>
                      {[
                        { val: places ?? "—", label: "Places", section: "itinerary" },
                        { val: toBookCount ?? "—", label: "To book", section: "reservations_todo" },
                        { val: bookedCount ?? "—", label: "Booked", section: "reservations_done" },
                        { val: remindersCount ?? "—", label: "Reminders", section: "bookRemind" },
                        { val: totalPack > 0 ? `${packedCount}/${totalPack}` : "—", label: "Packed", section: "packList" },
                      ].map(({ val, label, section }) => (
                        <button key={label} onClick={e => { e.stopPropagation(); onGoToTrip && onGoToTrip(trip.id, section); }}
                          style={{ textAlign: "center", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                          <div style={{ fontFamily: SANS, fontSize: 15, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{val}</div>
                          <div style={{ fontFamily: SANS, fontSize: 8, color: "rgba(255,255,255,0.68)", marginTop: 3, lineHeight: 1.3 }}>{label}</div>
                        </button>
                      ))}
                    </div>
                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: 10, marginTop: 14, paddingBottom: 18 }}>
                      <button onClick={() => onGoToTrip && onGoToTrip(trip.id)} style={{
                        flex: 1, background: "#7c2d3e", border: "none",
                        borderRadius: 12, padding: "11px 0", fontFamily: SANS, fontSize: 13, fontWeight: 600,
                        color: "#fff", cursor: "pointer"
                      }}>View trip</button>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Dot indicators */}
            {upcomingTrips.length > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12 }}>
                {upcomingTrips.map((_, i) => (
                  <button key={i} onClick={() => setHeroIdx(i)} style={{
                    width: heroIdx === i ? 20 : 7, height: 7, borderRadius: 4,
                    border: "none", cursor: "pointer", padding: 0,
                    background: heroIdx === i ? "#7c2d3e" : "#d8cfc7",
                    transition: "all 0.2s"
                  }} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

    </div>{/* end home-left-col */}

    {/* ── RIGHT COLUMN ── */}
    <div className="home-right-col">

      {/* ── Friends Activity ── */}
      <div>
        <SectionHeader title="Friends Activity" onSeeAll={onGoToExplore} />
        {friendEntries.length === 0 ? (
          <div style={{ background: "rgba(61,31,20,0.04)", border: "1px dashed #d8cfc7", borderRadius: 16, padding: "24px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>👥</div>
            <div style={{ fontFamily: SANS, fontSize: 13, color: "#9e8878" }}>No recent activity from friends</div>
          </div>
        ) : (
          <div className="home-friends-scroll">
            {friendEntries.map((e, idx) => {
              const user = allUsers.find(u => u.id === e.userId);
              const userAvatar = user?.avatarImg || user?.avatar_url;
              const hasPhoto = e.photos && e.photos.length > 0;
              const typeKey = TYPE_NORMALIZE[e.type] || e.type || "restaurant";
              const cfg = EXPLORE_GRADIENT[typeKey] || { bg: "linear-gradient(135deg, #f3f4f6 0%, #6b7280 100%)", icon: "📍" };
              const bg = verdictBg[e.verdict] || "#E8873A";
              const vLabel = verdictLabel[e.verdict] || "";
              const wentCount = friendsWentCount(e.name);
              const isBookmarked = (savedBookmarks || new Set()).has(e.id);
              return (
                <div key={e.id} onClick={() => onViewProfile && user && onViewProfile(user.id)}
                  className="home-friend-card"
                  style={{
                    borderRadius: 16, overflow: "hidden",
                    boxShadow: "0 3px 14px rgba(0,0,0,0.16)",
                    cursor: "pointer", display: "flex", flexDirection: "column",
                    background: "#fff"
                  }}>
                  {/* Photo / Gradient */}
                  <div style={{ position: "relative", height: 130, flexShrink: 0 }}>
                    {hasPhoto ? (
                      <img src={e.photos[0]} alt={e.name}
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ position: "absolute", inset: 0, background: cfg.bg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 44, userSelect: "none" }}>
                        {cfg.icon}
                      </div>
                    )}
                    {/* Gradient overlay */}
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 80,
                      background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)" }} />
                    {/* Friends went badge — top left */}
                    {wentCount > 0 && (
                      <div style={{ position: "absolute", top: 8, left: 8,
                        background: "rgba(0,0,0,0.52)", backdropFilter: "blur(4px)",
                        borderRadius: 8, padding: "3px 7px",
                        fontFamily: SANS, fontSize: 8, fontWeight: 700,
                        color: "#fff", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {wentCount} {wentCount === 1 ? "friend" : "friends"} went
                      </div>
                    )}
                    {/* Place name — opens this specific entry */}
                    <div style={{ position: "absolute", bottom: 28, left: 8, right: 8,
                        fontFamily: SERIF, fontSize: 12, fontWeight: 700, color: "#fff",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.name}
                    </div>
                    {/* Verdict badge bottom-left, bookmark bottom-right */}
                    {e.verdict && (
                      <div style={{ position: "absolute", bottom: 8, left: 8,
                        background: bg, color: "#fff",
                        fontFamily: SANS, fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                        letterSpacing: "0.06em", borderRadius: 8, padding: "3px 8px" }}>
                        {vLabel}
                      </div>
                    )}
                    <button onClick={ev => { ev.stopPropagation(); onToggleBookmark && onToggleBookmark(e.id); }}
                      title={isBookmarked ? "Saved" : "Save"}
                      style={{ position: "absolute", top: 10, right: 10,
                        background: "none", border: "none", padding: "4px 6px",
                        fontSize: 26, cursor: "pointer", lineHeight: 1, zIndex: 2,
                        color: isBookmarked ? "#e8c84a" : "rgba(255,255,255,0.85)",
                        textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}>
                      {isBookmarked ? "♥" : "♡"}
                    </button>
                  </div>
                  {/* Info strip */}
                  <div style={{ padding: "7px 10px", borderTop: "1px solid #f5f0ec" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                      {user && (
                        <div style={{ width: 22, height: 22, background: "#f0ebe6", borderRadius: "50%",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, flexShrink: 0, overflow: "hidden" }}>
                          {userAvatar
                            ? <img src={userAvatar} alt={user.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : (user.avatar || "✈️")}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: SANS, fontSize: 10, fontWeight: 700, color: "#3d1f14",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          @{user?.username || ""}
                        </div>
                        {(e.city || e.state) && (
                          <div style={{ fontFamily: SANS, fontSize: 9, color: "#9e8878", marginTop: 1,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {[e.city, e.state].filter(Boolean).join(", ")}{e.type ? ` · ${e.type}` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                    {e.rating > 0 && (
                      <div style={{ display: "flex", gap: 1, marginBottom: 4 }}>
                        {[1,2,3,4,5].map(s => (
                          <span key={s} style={{ fontSize: 9, color: s <= e.rating ? "#E8873A" : "#ddd" }}>★</span>
                        ))}
                      </div>
                    )}
                    {e.notes && (
                      <div style={{ fontFamily: SANS, fontSize: 12, color: "#555", fontStyle: "italic",
                        overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", lineHeight: 1.4 }}>
                        "{e.notes}"
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Past Trips ── */}
      {pastTrips.length > 0 && (
        <div>
          <SectionHeader title="Past Trips" onSeeAll={onGoToPastTrips || onGoToTrips} />
          <div style={{ display: "flex", gap: 10 }}>
            {pastTrips.slice(0, 2).map(trip => {
              const dateLabel = trip.startDate
                ? (() => {
                    const s = new Date(trip.startDate + "T12:00:00");
                    const endDate = trip.endDate ? new Date(trip.endDate + "T12:00:00") : null;
                    if (endDate) {
                      return `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
                    }
                    return s.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                  })()
                : null;
              // Count logged activities: plan entries visited + entries by user within trip dates
              const planEntries = (trip.plan?.days || []).flatMap(d => d.entries || d.items || []);
              const visitedCount = planEntries.filter(e => e.visited).length;
              const totalPlanCount = planEntries.length;
              const activityBadge = visitedCount > 0
                ? `${visitedCount}/${totalPlanCount} visited`
                : totalPlanCount > 0 ? `${totalPlanCount} places planned` : null;
              return (
                <div key={trip.id} style={{
                  flex: 1, position: "relative", borderRadius: 16, overflow: "hidden",
                  cursor: "pointer", boxShadow: "0 3px 14px rgba(0,0,0,0.18)"
                }}>
                  <div style={{ height: 120, position: "relative" }}>
                    <DestinationPhoto destination={trip.destination} coverPhoto={trip.coverPhoto}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                    <div onClick={onGoToPastTrips || onGoToTrips} style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.04) 60%)" }} />
                    {/* 📷 Change photo button */}
                    <label title="Change cover photo" style={{
                      position: "absolute", top: 8, right: 8,
                      background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)",
                      border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8,
                      padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                      color: "#fff", fontSize: 11, fontFamily: SANS, fontWeight: 600, zIndex: 2
                    }}>
                      📷
                      <input type="file" accept="image/*" style={{ display: "none" }}
                        onChange={e => {
                          const file = e.target.files[0];
                          if (!file || !onUpdateCoverPhoto) return;
                          const reader = new FileReader();
                          reader.onload = ev => onUpdateCoverPhoto(trip.id, ev.target.result);
                          reader.readAsDataURL(file);
                        }} />
                    </label>
                    <div onClick={onGoToPastTrips || onGoToTrips} style={{ position: "absolute", bottom: 8, left: 10, right: 8 }}>
                      <div style={{ fontFamily: SERIF, fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>{trip.destination}</div>
                      {dateLabel && <div style={{ fontFamily: SANS, fontSize: 10, color: "rgba(255,255,255,0.72)", marginTop: 1 }}>{dateLabel}</div>}
                    </div>
                  </div>
                  {activityBadge && (
                    <div onClick={onGoToPastTrips || onGoToTrips} style={{ background: "#fff", padding: "7px 12px",
                      display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12 }}>📍</span>
                      <span style={{ fontFamily: SANS, fontSize: 11, fontWeight: 600, color: "#444" }}>{activityBadge}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>{/* end home-right-col */}

    </div>
  );
}

// ─── MY LOGS TAB COMPONENT ───────────────────────────────────────────────────
const LOG_MENU_ITEM_STYLE = { display: "flex", alignItems: "center", gap: 9, width: "100%", background: "none", border: "none", borderBottom: "1px solid #f5f5f5", padding: "11px 15px", fontSize: 13, color: "#1a1a1a", cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans', sans-serif" };

function MyLogsTab({ entries, currentUser, savedTrips, setEntries, setEditingEntry, showImport, setShowImport, onEditProfile, onGoToTrips, savedBookmarks, onToggleBookmark, hasMoreEntries, loadMoreEntries, loadingMore, friendProfiles }) {
  const [myLogsView, setMyLogsView] = useState("logs"); // "logs" | "saved"
  const [logsCategory, setLogsCategory] = useState("all");
  const [logsSearch, setLogsSearch] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [logsVerdictFilter, setLogsVerdictFilter] = useState(null);
  const [logsCityFilter, setLogsCityFilter] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedLogIds, setSelectedLogIds] = useState(new Set());
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [logsCountryFilter, setLogsCountryFilter] = useState(null);
  const [showStatePicker, setShowStatePicker] = useState(false);
  const [logsStateFilter, setLogsStateFilter] = useState(null);
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());
  const [openMenuId, setOpenMenuId] = useState(null);
  const [repositioningId, setRepositioningId] = useState(null);
  const toggleMonth = key => setCollapsedMonths(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  const myEntries = entries.filter(e => e.userId === currentUser.id);
  const savedEntries = savedBookmarks && savedBookmarks.size > 0
    ? entries.filter(e => savedBookmarks.has(e.id))
    : [];
  const normalizeCountry = c => (c === "US" || c === "United States" || c === "United States of America") ? "USA" : c;
  const normalizeCity = c => c ? c.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ") : c;
  const normalizeState = s => {
    if (!s) return s;
    const trimmed = s.trim();
    const upper = trimmed.toUpperCase();
    const lower = trimmed.toLowerCase();
    if (Object.values(STATE_NAMES).includes(upper)) return upper;
    if (STATE_NAMES[lower]) return STATE_NAMES[lower].toUpperCase();
    return trimmed;
  };
  const myCities = [...new Set(myEntries.map(e => normalizeCity(e.city)).filter(Boolean))].sort();
  const myCountries = [...new Set(myEntries.map(e => e.country).filter(Boolean).map(normalizeCountry))].sort();
  const isUSEntry = e => !e.country || e.country === "USA" || e.country === "US" || e.country === "United States" || e.country === "United States of America";
  const myStates = [...new Set(myEntries.filter(isUSEntry).map(e => normalizeState(e.state)).filter(Boolean))].sort();


  const filteredLogs = myEntries.filter(e => {
    if (logsSearch.trim()) {
      const q = logsSearch.toLowerCase();
      if (!entryMatchesSearch(e, q)) return false;
    }
    if (logsVerdictFilter && e.verdict !== logsVerdictFilter) return false;
    if (logsCityFilter && normalizeCity(e.city) !== logsCityFilter) return false;
    if (logsCountryFilter && normalizeCountry(e.country) !== logsCountryFilter) return false;
    if (logsStateFilter && normalizeState(e.state) !== logsStateFilter) return false;
    if (logsCategory === "food") return ["restaurant","cafe","brewery"].includes(e.type);
    if (logsCategory === "hotel") return e.type === "hotel";
    if (logsCategory === "hiking") return e.type === "hiking";
    if (logsCategory === "kids") return e.kidFriendly === true;
    if (logsCategory === "activities") return e.type === "activity";
    return true;
  });

  const catBtn = (val, label) => (
    <button key={val} onClick={() => setLogsCategory(val)}
      style={{ background: logsCategory === val ? "#000" : "#f8f8f8",
        border: `1px solid ${logsCategory === val ? "#000" : "#efefef"}`,
        color: logsCategory === val ? "#fff" : "#555",
        borderRadius: 20, padding: "6px 14px", cursor: "pointer",
        fontSize: 12, whiteSpace: "nowrap", flexShrink: 0 }}>
      {label}
    </button>
  );

  const logsMonthGroups = (() => {
    if (filteredLogs.length === 0) return [];
    const groups = {};
    filteredLogs.forEach(e => {
      const d = new Date(e.created_at || e.createdAt || Date.now());
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = { label, entries: [] };
      groups[key].entries.push(e);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, val]) => ({ key, ...val }));
  })();

  return (
    <div style={{ background: "#fffef9", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g'%3E%3CfeTurbulence baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23g)' opacity='0.035'/%3E%3C/svg%3E\")", backgroundRepeat: "repeat", backgroundSize: "200px 200px" }}>
      {/* Profile card */}
      <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 12, padding: "16px 18px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 52, height: 52, background: "#f0f0f0", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, overflow: "hidden", flexShrink: 0 }}>
            {currentUser.avatarImg
              ? <img src={currentUser.avatarImg} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : currentUser.avatar}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#000" }}>{currentUser.name}</div>
            <div style={{ fontSize: 12, color: "#888" }}>@{currentUser.username}</div>
            {currentUser.bio && <div style={{ fontSize: 12, color: "#555", marginTop: 3 }}>{currentUser.bio}</div>}
          </div>
          <button onClick={onEditProfile}
            style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8, padding: "6px 12px",
              fontSize: 11, color: "#555", cursor: "pointer", flexShrink: 0 }}>
            ✏️ Edit Profile
          </button>
        </div>
        <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
          {[
            { label: "Logs", value: myEntries.length, action: () => { setLogsVerdictFilter(null); setLogsCityFilter(null); setLogsCountryFilter(null); setLogsStateFilter(null); setShowCityPicker(false); setShowCountryPicker(false); setShowStatePicker(false); }, active: !logsVerdictFilter && !logsCityFilter && !logsCountryFilter && !logsStateFilter },
            { label: "Must Go", value: myEntries.filter(e=>e.verdict==="must_go").length, action: () => { setLogsVerdictFilter(logsVerdictFilter === "must_go" ? null : "must_go"); setShowCityPicker(false); setShowCountryPicker(false); setShowStatePicker(false); }, active: logsVerdictFilter === "must_go" },
            { label: "Cities", value: myCities.length, action: () => { setShowCityPicker(s => !s); setShowCountryPicker(false); setShowStatePicker(false); }, active: showCityPicker || !!logsCityFilter },
            { label: "States", value: myStates.length, action: () => { setShowStatePicker(s => !s); setShowCityPicker(false); setShowCountryPicker(false); }, active: showStatePicker || !!logsStateFilter },
            { label: "Countries", value: myCountries.length, action: () => { setShowCountryPicker(s => !s); setShowCityPicker(false); setShowStatePicker(false); }, active: showCountryPicker || !!logsCountryFilter },
            { label: "Past Trips", value: savedTrips.filter(t => t.completed).length, action: onGoToTrips, active: false },
          ].map(({ label, value, action, active }) => (
            <div key={label} onClick={action || undefined}
              style={{ cursor: action ? "pointer" : "default" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: active ? "#000" : "#000" }}>{value}</div>
              <div style={{ fontSize: 11, color: active ? "#000" : "#888", fontWeight: active ? 700 : 400, borderBottom: active ? "2px solid #000" : "2px solid transparent", paddingBottom: 1, transition: "all 0.15s" }}>{label}</div>
            </div>
          ))}
        </div>

        {/* City picker — shown when Cities stat is tapped */}
        {showCityPicker && (
          <div style={{ marginTop: 14, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Filter by City</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {logsCityFilter && (
                <button onClick={() => setLogsCityFilter(null)}
                  style={{ background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#555", cursor: "pointer" }}>
                  ✕ Clear
                </button>
              )}
              {myCities.map(c => {
                const count = myEntries.filter(e => normalizeCity(e.city) === c).length;
                return (
                  <button key={c} onClick={() => setLogsCityFilter(logsCityFilter === c ? null : c)}
                    style={{ background: logsCityFilter === c ? "#000" : "#f8f8f8",
                      border: `1px solid ${logsCityFilter === c ? "#000" : "#e8e8e8"}`,
                      color: logsCityFilter === c ? "#fff" : "#444",
                      borderRadius: 20, padding: "4px 12px", fontSize: 11, cursor: "pointer" }}>
                    📍 {c} <span style={{ opacity: 0.6 }}>({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* State picker — shown when States stat is tapped */}
        {showStatePicker && (
          <div style={{ marginTop: 14, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>US States Visited ({myStates.length})</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {logsStateFilter && (
                <button onClick={() => setLogsStateFilter(null)}
                  style={{ background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#555", cursor: "pointer" }}>
                  ✕ Clear
                </button>
              )}
              {myStates.map(s => {
                const count = myEntries.filter(e => normalizeState(e.state) === s).length;
                return (
                  <button key={s} onClick={() => setLogsStateFilter(logsStateFilter === s ? null : s)}
                    style={{ background: logsStateFilter === s ? "#000" : "#f8f8f8",
                      border: `1px solid ${logsStateFilter === s ? "#000" : "#e8e8e8"}`,
                      color: logsStateFilter === s ? "#fff" : "#444",
                      borderRadius: 20, padding: "4px 12px", fontSize: 11, cursor: "pointer" }}>
                    🇺🇸 {s} <span style={{ opacity: 0.6 }}>({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Country picker — shown when Countries stat is tapped */}
        {showCountryPicker && (
          <div style={{ marginTop: 14, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#bbb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Countries Visited ({myCountries.length})</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {logsCountryFilter && (
                <button onClick={() => setLogsCountryFilter(null)}
                  style={{ background: "#f8f8f8", border: "1px solid #ddd", borderRadius: 20, padding: "4px 12px", fontSize: 11, color: "#555", cursor: "pointer" }}>
                  ✕ Clear
                </button>
              )}
              {myCountries.map(c => {
                const count = myEntries.filter(e => e.country === c).length;
                return (
                  <button key={c} onClick={() => setLogsCountryFilter(logsCountryFilter === c ? null : c)}
                    style={{ background: logsCountryFilter === c ? "#000" : "#f8f8f8",
                      border: `1px solid ${logsCountryFilter === c ? "#000" : "#e8e8e8"}`,
                      color: logsCountryFilter === c ? "#fff" : "#444",
                      borderRadius: 20, padding: "4px 12px", fontSize: 11, cursor: "pointer" }}>
                    🌍 {c} <span style={{ opacity: 0.6 }}>({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* My Logs / Saved tab switcher */}
      <div style={{ display: "flex", gap: 0, marginBottom: 18, background: "#f0f0f0", borderRadius: 10, padding: 3 }}>
        {[["logs", "My Logs"], ["saved", `Saved (${savedEntries.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => { setMyLogsView(id); setShowCityPicker(false); setShowStatePicker(false); setShowCountryPicker(false); }}
            style={{ flex: 1, background: myLogsView === id ? "#fff" : "transparent",
              border: "none", borderRadius: 8, padding: "10px 8px", cursor: "pointer",
              color: myLogsView === id ? "#000" : "#888", fontWeight: myLogsView === id ? 600 : 400,
              fontSize: 13, boxShadow: myLogsView === id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              transition: "all 0.2s" }}>
            {label}
          </button>
        ))}
      </div>

      {/* SAVED VIEW */}
      {myLogsView === "saved" && (
        <div>
          {savedEntries.length === 0 ? (
            <div style={{ textAlign: "center", padding: "52px 20px", color: "#aaa", background: "#fafafa", borderRadius: 16, border: "1px dashed #e8e8e8" }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>🔖</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#555", marginBottom: 4 }}>No saved places yet</div>
              <div style={{ fontSize: 13, color: "#aaa" }}>Tap 🔖 on any place in the Network feed to save it here</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {savedEntries.map(e => {
                const user = (friendProfiles || []).find(u => u.id === e.userId);
                const hasUserPhoto = e.photos && e.photos.length > 0;
                const typeKey = TYPE_NORMALIZE[e.type] || e.type || "restaurant";
                const cfg = EXPLORE_GRADIENT[typeKey] || { bg: "linear-gradient(135deg, #f3f4f6 0%, #6b7280 100%)", icon: "📍" };
                return (
                  <div key={e.id} style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 14, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
                    <div style={{ position: "relative", height: 150, overflow: "hidden", flexShrink: 0 }}>
                      {hasUserPhoto ? (
                        <img src={e.photos[0]} alt={e.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: `center ${e.coverPosition || 50}%`, userSelect: "none", pointerEvents: "none" }} />
                      ) : (
                        <div style={{ width: "100%", height: "100%", background: cfg.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 44, userSelect: "none" }}>
                          {cfg.icon}
                        </div>
                      )}
                      <button onClick={() => onToggleBookmark && onToggleBookmark(e.id)}
                        title="Remove from saved"
                        style={{ position: "absolute", top: 6, right: 6, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "3px 7px", fontSize: 12, cursor: "pointer", color: "#d97706", lineHeight: 1 }}>
                        ★
                      </button>
                      {e.verdict && (
                        <span style={{ position: "absolute", bottom: 8, left: 8,
                          background: e.verdict === "must_go" ? "rgba(21,128,61,0.88)" : "rgba(220,38,38,0.82)",
                          color: "#fff", borderRadius: 20, padding: "3px 9px", fontSize: 10,
                          fontFamily: "'DM Sans', sans-serif", fontWeight: 700, letterSpacing: "0.06em",
                          backdropFilter: "blur(4px)" }}>
                          {e.verdict === "must_go" ? "MUST GO" : "SKIP IT"}
                        </span>
                      )}
                    </div>
                    <div style={{ padding: "14px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 4 }}>
                        <a href={`https://www.google.com/maps/search/${encodeURIComponent(e.name + ' ' + (e.city || ''))}`}
                          target="_blank" rel="noopener noreferrer"
                          onClick={ev => ev.stopPropagation()}
                          style={{ color: "#000", fontSize: 14, fontWeight: 700, textDecoration: "none", lineHeight: 1.3 }}
                          onMouseEnter={ev => ev.currentTarget.style.textDecoration = "underline"}
                          onMouseLeave={ev => ev.currentTarget.style.textDecoration = "none"}>
                          {e.name} <span style={{ color: "#4285f4", fontSize: 11, fontWeight: 400 }}>↗</span>
                        </a>
                        <StarRating value={e.rating} size={11} />
                      </div>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        {e.city}{e.state ? `, ${e.state}` : ""} · {e.cuisine || (TYPE_NORMALIZE[e.type] || e.type)}
                      </div>
                      {e.difficulty && (
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#555", background: "#f0f0f0", borderRadius: 4, padding: "2px 6px", alignSelf: "flex-start" }}>{e.difficulty}</span>
                      )}
                      {e.notes && (
                        <ExpandableNote text={e.notes} />
                      )}
                      {user && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, borderTop: "1px solid #f0f0f0", paddingTop: 6 }}>
                          <span style={{ fontSize: 12 }}>{user.avatar}</span>
                          <span style={{ color: "#555", fontSize: 10, fontFamily: "'DM Sans', sans-serif" }}>
                            @{user.username}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* MY LOGS VIEW */}
      {myLogsView === "logs" && (
      <div>

      {/* Search + Import */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#aaa", pointerEvents: "none" }}>🔍</span>
          <input value={logsSearch} onChange={e => setLogsSearch(e.target.value)}
            placeholder="Search by name, city, state, cuisine..."
            style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
              padding: "11px 12px 11px 34px", color: "#000", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          {logsSearch && <button onClick={() => setLogsSearch("")}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 16 }}>×</button>}
        </div>
        <button onClick={() => { if (showImport) setImportResult(null); setShowImport(!showImport); }}
          style={{ background: showImport ? "#000" : "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
            padding: "11px 14px", fontSize: 12, color: showImport ? "#fff" : "#555", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
          🥾 Import
        </button>
      </div>

      {/* AllTrails Import Panel */}
      {showImport && (
        <div style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 12, padding: "16px", marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#000", marginBottom: 6 }}>Import from AllTrails</div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 12, lineHeight: 1.6 }}>
            AllTrails → Profile → Settings → Export Data → download CSV, then select below.
          </div>
          {!importResult ? (
            <div style={{ background: "#fff0e0", border: "1px solid #fdd", borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#888", lineHeight: 1.6 }}>
              📲 File upload coming soon — will work once the app is deployed. Stay tuned!
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: "#000", marginBottom: 10 }}>
                {importResult.length === 0 ? "No hikes found — check the file format." : <><strong>{importResult.length}</strong> hike{importResult.length !== 1 ? "s" : ""} found</>}
              </div>
              {importResult.slice(0, 3).map((h) => (
                <div key={h.name} style={{ fontSize: 12, color: "#555", padding: "4px 0", borderBottom: "1px solid #efefef" }}>
                  🥾 {h.name} {h.distance ? `· ${h.distance}` : ""}
                </div>
              ))}
              {importResult.length > 3 && <div style={{ fontSize: 12, color: "#aaa", padding: "4px 0" }}>+{importResult.length - 3} more</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={() => { setEntries(prev => [...importResult, ...prev]); setImportResult(null); setShowImport(false); setLogsCategory("hiking"); }}
                  style={{ flex: 1, background: "#000", border: "none", borderRadius: 8, padding: "10px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Import All
                </button>
                <button onClick={() => { setImportResult(null); setShowImport(false); }}
                  style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 8, padding: "10px 14px", color: "#555", fontSize: 13, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", paddingBottom: 2 }}>
        {catBtn("all", "All")}
        {catBtn("food", "🍽 Food")}
        {catBtn("hotel", "🏨 Hotel")}
        {catBtn("hiking", "🥾 Hiking")}
        {catBtn("kids", "👶 Kids")}
        {catBtn("activities", "⛺ Activities")}
      </div>

      {/* Count + select toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#888", fontWeight: 500 }}>
          {filteredLogs.length} {logsCategory === "all" ? "logs" : logsCategory}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {selectMode && selectedLogIds.size > 0 && (
            <button onClick={async () => {
              const ids = [...selectedLogIds];
              const categoryLabel = { all: "log", food: "food log", hotel: "hotel log", hiking: "hiking log", kids: "kids log", activities: "activity log" }[logsCategory] || "log";
              const plural = ids.length === 1 ? categoryLabel : categoryLabel + "s";
              if (!window.confirm(`Delete ${ids.length} ${plural}?`)) return;
              const { error } = await supabase.from("entries").delete().in("id", ids).eq("user_id", currentUser.id);
              if (error) { showToast("Failed to delete. Please try again.", "error"); return; }
              setEntries(prev => prev.filter(e => !selectedLogIds.has(e.id)));
              setSelectedLogIds(new Set());
              setSelectMode(false);
              showToast(`${ids.length} log${ids.length > 1 ? "s" : ""} deleted`, "success");
            }} style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 8,
              padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              Delete {selectedLogIds.size}
            </button>
          )}
          <button onClick={() => { setSelectMode(v => !v); setSelectedLogIds(new Set()); }}
            style={{ background: selectMode ? "#000" : "#f5f5f5", color: selectMode ? "#fff" : "#555",
              border: "none", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            {selectMode ? "Cancel" : "Select"}
          </button>
        </div>
      </div>

      {/* Entries — journal timeline */}
      {filteredLogs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "52px 20px", color: "#aaa", background: "#fff", borderRadius: 16, border: "1px dashed #e8e8e8" }}>
          <div style={{ fontSize: 44, marginBottom: 12, filter: "grayscale(0.2)" }}>📍</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#555", marginBottom: 4 }}>{logsSearch || logsCategory !== "all" ? "No results for this filter" : "Nothing logged yet"}</div>
          {!logsSearch && logsCategory === "all" && <div style={{ fontSize: 13, color: "#aaa" }}>Tap <strong>+ Log</strong> to start building your food diary</div>}
        </div>
      ) : (
        <div style={{ position: "relative", paddingLeft: 28 }}>
          <div style={{ position: "absolute", left: 7, top: 0, bottom: 0, width: 2, background: "#ede8df" }} />
          {logsMonthGroups.map(({ key, label, entries: groupEntries }) => {
            const isCollapsed = collapsedMonths.has(key);
            return (
            <div key={key}>
              <div onClick={() => toggleMonth(key)} style={{ display: "flex", alignItems: "center", gap: 10, margin: "24px 0 14px", position: "relative", cursor: "pointer" }}>
                <div style={{ position: "absolute", left: -24, width: 16, height: 16, borderRadius: "50%", background: "#c8a96e", border: "3px solid #fffef9" }} />
                <span style={{ fontFamily: "'Cormorant Garamond', 'Lora', serif", fontSize: 12, fontWeight: 600, color: "#a0977f", letterSpacing: "0.12em", textTransform: "uppercase" }}>{label}</span>
                <span style={{ fontSize: 10, color: "#c8a96e", fontWeight: 600 }}>({groupEntries.length})</span>
                <div style={{ flex: 1, height: 1, background: "#ede8df" }} />
                <span style={{ fontSize: 11, color: "#ccc", transform: isCollapsed ? "rotate(-90deg)" : "none", transition: "transform 0.2s" }}>▾</span>
              </div>
              {!isCollapsed && groupEntries.map(e => {
                const isSelected = selectedLogIds.has(e.id);
                const dateStr = new Date(e.created_at || e.createdAt || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const hasUserPhoto = e.photos && e.photos.length > 0;
                const logTypeKey = TYPE_NORMALIZE[e.type] || e.type || "restaurant";
                const logCfg = EXPLORE_GRADIENT[logTypeKey] || { bg: "linear-gradient(135deg, #f3f4f6 0%, #6b7280 100%)", icon: "📍" };
                return (
                  <div key={e.id} style={{ position: "relative", marginBottom: 16 }}
                    onClick={selectMode ? () => setSelectedLogIds(prev => { const s = new Set(prev); s.has(e.id) ? s.delete(e.id) : s.add(e.id); return s; }) : undefined}>
                    <div style={{ position: "absolute", left: -21, top: 22, width: 10, height: 10, borderRadius: "50%", background: isSelected ? "#7B2D42" : "#fff", border: `2px solid ${isSelected ? "#7B2D42" : "#c8a96e"}`, zIndex: 1 }} />
                    <div style={{ background: "#fff", borderRadius: 14, overflow: "hidden",
                      border: `1.5px solid ${isSelected ? "#7B2D42" : "#ede8df"}`,
                      boxShadow: isSelected ? "0 0 0 3px rgba(123,45,66,0.12)" : "0 2px 10px rgba(0,0,0,0.06)",
                      cursor: selectMode ? "pointer" : "default", position: "relative" }}>
                      {selectMode && (
                        <div style={{ position: "absolute", top: 8, left: 8, zIndex: 10,
                          width: 22, height: 22, borderRadius: "50%", border: `2px solid ${isSelected ? "#7B2D42" : "#fff"}`,
                          background: isSelected ? "#7B2D42" : "rgba(0,0,0,0.35)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, color: "#fff", fontWeight: 700 }}>
                          {isSelected ? "✓" : ""}
                        </div>
                      )}
                      <div style={{ position: "relative" }}>
                        {hasUserPhoto ? (
                          <CoverPhoto entry={e} height={160}
                            repositioning={repositioningId === e.id}
                            onPositionChange={async pos => {
                              setEntries(prev => prev.map(en => en.id === e.id ? { ...en, coverPosition: pos } : en));
                              await supabase.from("entries").update({ cover_position: pos }).eq("id", e.id);
                            }}
                            onDoneReposition={() => setRepositioningId(null)}
                            onCancelReposition={() => setRepositioningId(null)} />
                        ) : (
                          <div style={{ height: 160, background: logCfg.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56, userSelect: "none" }}>
                            {logCfg.icon}
                          </div>
                        )}
                        {!selectMode && (
                          <button
                            onClick={ev => { ev.stopPropagation(); setOpenMenuId(openMenuId === e.id ? null : e.id); }}
                            style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.48)", backdropFilter: "blur(4px)", border: "none", borderRadius: 20, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: 18, fontWeight: 700, zIndex: 10, lineHeight: 1 }}>
                            ⋯
                          </button>
                        )}
                        {openMenuId === e.id && (
                          <>
                            <div style={{ position: "fixed", inset: 0, zIndex: 19 }} onClick={() => setOpenMenuId(null)} />
                            <div style={{ position: "absolute", top: 42, right: 8, background: "#fff", border: "1px solid #efefef", borderRadius: 12, boxShadow: "0 8px 28px rgba(0,0,0,0.16)", zIndex: 20, minWidth: 168, overflow: "hidden" }}
                              onClick={ev => ev.stopPropagation()}>
                              <button style={LOG_MENU_ITEM_STYLE} onClick={() => { setOpenMenuId(null); setEditingEntry(e); }}>✏️ Edit</button>
                              {hasUserPhoto && (
                                <button style={LOG_MENU_ITEM_STYLE} onClick={() => { setOpenMenuId(null); setRepositioningId(e.id); }}>↕ Reposition photo</button>
                              )}
                              <button style={LOG_MENU_ITEM_STYLE} onClick={() => { setOpenMenuId(null); if (selectMode && isSelected) { setSelectedLogIds(prev => { const s = new Set(prev); s.delete(e.id); return s; }); } else { setSelectMode(true); setSelectedLogIds(new Set([e.id])); } }}>
                                {selectMode && isSelected ? "✓ Unselect" : "☑ Select"}
                              </button>
                              <label style={{ ...LOG_MENU_ITEM_STYLE, cursor: "pointer" }}>
                                📷 Add photo
                                <input type="file" accept="image/*" style={{ display: "none" }}
                                  onChange={async ev => {
                                    const file = ev.target.files[0];
                                    if (!file) return;
                                    ev.target.value = "";
                                    setOpenMenuId(null);
                                    const url = await uploadPhoto(file, currentUser?.id);
                                    if (!url) { showToast("Couldn't upload photo. Try again."); return; }
                                    const newPhotos = [...(e.photos || []), url];
                                    setEntries(prev => prev.map(en => en.id === e.id ? { ...en, photos: newPhotos } : en));
                                    await supabase.from("entries").update({ photos: newPhotos }).eq("id", e.id);
                                  }} />
                              </label>
                              <button style={LOG_MENU_ITEM_STYLE} onClick={async () => { setOpenMenuId(null); const np = !e.isPrivate; setEntries(prev => prev.map(en => en.id === e.id ? { ...en, isPrivate: np } : en)); await supabase.from("entries").update({ is_private: np }).eq("id", e.id); }}>
                                {e.isPrivate ? "🌐 Make public" : "🔒 Make private"}
                              </button>
                              <button style={{ ...LOG_MENU_ITEM_STYLE, color: "#dc2626", borderBottom: "none" }} onClick={async () => { setOpenMenuId(null); if (!window.confirm(`Delete "${e.name}"?`)) return; const { error } = await supabase.from("entries").delete().eq("id", e.id).eq("user_id", currentUser.id); if (error) { showToast("Couldn't delete. Try again."); return; } setEntries(prev => prev.filter(en => en.id !== e.id)); showToast("Log deleted", "success"); }}>
                                🗑 Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                      <div style={{ padding: "14px 16px" }}>
                        {(() => {
                          const missing = [];
                          if (!e.rating) missing.push("stars");
                          if (!e.verdict) missing.push("verdict");
                          if (!missing.length) return null;
                          return (
                            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 10,
                              background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6,
                              padding: "5px 9px", fontSize: 11, color: "#92400e" }}>
                              <span>⚠</span>
                              <span>Missing: <strong>{missing.join(" & ")}</strong></span>
                            </div>
                          );
                        })()}
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                          <a href={`https://www.google.com/maps/search/${encodeURIComponent(e.name + ' ' + (e.city || ''))}`}
                            target="_blank" rel="noopener noreferrer"
                            onClick={ev => ev.stopPropagation()}
                            style={{ fontFamily: "'Cormorant Garamond', 'Lora', serif", fontSize: 21, fontWeight: 600, color: "#1a1a1a", textDecoration: "none", lineHeight: 1.2, flex: 1 }}
                            onMouseEnter={ev => ev.currentTarget.style.textDecoration = "underline"}
                            onMouseLeave={ev => ev.currentTarget.style.textDecoration = "none"}>
                            {e.name}
                          </a>
                          <StarRating value={e.rating} size={12} />
                        </div>
                        <div style={{ fontSize: 12, color: "#a0977f", marginBottom: 6, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                          <span>{[e.city, e.state].filter(Boolean).join(", ")}</span>
                          {(e.cuisine || TYPE_NORMALIZE[e.type] || e.type) && <><span style={{ opacity: 0.4 }}>·</span><span>{e.cuisine || TYPE_NORMALIZE[e.type] || e.type}</span></>}
                          <span style={{ opacity: 0.4 }}>·</span><span>{dateStr}</span>
                          <span style={{ marginLeft: "auto", fontSize: 10, color: "#ccc" }}>{e.isPrivate ? "🔒" : "🌐"}</span>
                        </div>
                        {e.difficulty && (
                          <span style={{ display: "inline-block", fontSize: 9, fontWeight: 600, color: "#888", background: "#f5f5f5", borderRadius: 4, padding: "2px 6px", marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>{e.difficulty}</span>
                        )}
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center", marginBottom: 4 }}>
                          {e.verdict && (
                            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                              color: e.verdict === "must_go" ? "#4d7a3a" : "#888",
                              background: e.verdict === "must_go" ? "#eff6ea" : "#f5f5f5",
                              borderRadius: 4, padding: "2px 7px" }}>
                              {e.verdict === "must_go" ? "Must Go" : "Skip It"}
                            </span>
                          )}
                          {e.type && (
                            <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
                              color: "#aaa", background: "#f8f6f2", borderRadius: 4, padding: "2px 7px" }}>
                              {{ restaurant:"🍽", hotel:"🏨", brewery:"🍺", cafe:"☕", activity:"🏔", hiking:"🥾" }[e.type] || "📍"} {TYPE_NORMALIZE[e.type] || e.type}
                            </span>
                          )}
                          {e.kidFriendly && (
                            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                              color: "#9a7a2a", background: "#fef9ee", borderRadius: 4, padding: "2px 7px" }}>
                              👶 Kids
                            </span>
                          )}
                        </div>
                        {e.notes && (
                          <div style={{ borderLeft: "3px solid #c8a96e", paddingLeft: 12, margin: "8px 0 10px",
                            fontFamily: "'Cormorant Garamond', 'Lora', serif", fontStyle: "italic",
                            fontSize: 15, color: "#333", lineHeight: 1.6 }}>
                            {`"${!expandedNotes.has(e.id) && e.notes.length > 160 ? e.notes.slice(0, 160) + '…' : e.notes}"`}
                            {e.notes.length > 160 && (
                              <span onClick={ev => { ev.stopPropagation(); setExpandedNotes(prev => { const s = new Set(prev); s.has(e.id) ? s.delete(e.id) : s.add(e.id); return s; }); }}
                                style={{ display: "inline-block", marginLeft: 6, fontSize: 11, color: "#c8a96e", cursor: "pointer", fontStyle: "normal", fontFamily: "'DM Sans', sans-serif" }}>
                                {expandedNotes.has(e.id) ? "less" : "more"}
                              </span>
                            )}
                          </div>
                        )}
                        {(e.photos || []).length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <PhotoStrip photos={e.photos} inline />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })}
        </div>
      )}
      {hasMoreEntries && entries.length < 500 && !logsSearch.trim() && logsCategory === "all" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button onClick={loadMoreEntries} disabled={loadingMore}
            style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 20, padding: "10px 28px", fontSize: 14, color: "#555", cursor: loadingMore ? "default" : "pointer", fontFamily: "'DM Sans', sans-serif" }}>
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
      </div>
      )}
    </div>
  );
}


// ─── EDIT PROFILE MODAL ──────────────────────────────────────────────────────
const MAX_USERNAME_LEN = 30;
const MAX_NAME_LEN = 50;
const MAX_BIO_LEN = 150;

function EditProfileModal({ user, onClose, onSave }) {
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  const [name, setName] = useState(user.name);
  const [username, setUsername] = useState(user.username);
  const [bio, setBio] = useState(user.bio || "");
  const [avatar, setAvatar] = useState(user.avatar);
  const [avatarImg, setAvatarImg] = useState(user.avatarImg || null);
  const [usernameError, setUsernameError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const handlePhoto = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type) || file.size > MAX_PHOTO_SIZE) return;
    const reader = new FileReader();
    reader.onload = ev => { setAvatarImg(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleUsernameChange = e => {
    const val = e.target.value.replace(/\s/g, "").toLowerCase().slice(0, MAX_USERNAME_LEN);
    setUsername(val);
    if (val && !/^[a-z0-9_]+$/.test(val)) {
      setUsernameError("Only letters, numbers, and underscores");
    } else {
      setUsernameError("");
    }
  };

  const canSave = name.trim() && username.trim() && !usernameError;

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setSaveError("");

    // Upload new photo if the user picked one (base64 data URL → storage URL)
    let resolvedAvatarUrl = avatarImg && !avatarImg.startsWith("data:") ? avatarImg : null;
    if (avatarImg && avatarImg.startsWith("data:")) {
      const blob = await fetch(avatarImg).then(r => r.blob());
      const file = new File([blob], "avatar.jpg", { type: blob.type });
      resolvedAvatarUrl = await uploadPhoto(file, user.id);
      if (!resolvedAvatarUrl) {
        setSaveError("Couldn't upload photo. Try again.");
        setSaving(false);
        return;
      }
    }

    const profileUpdates = {
      name: name.trim(),
      username: username.trim(),
      bio: bio || null,
      avatar: avatarImg ? null : avatar,
      avatar_url: resolvedAvatarUrl,
    };

    const { error: profileErr } = await supabase.from("profiles").update(profileUpdates).eq("id", user.id);
    if (profileErr) {
      setSaveError("Couldn't save profile. Try again.");
      setSaving(false);
      return;
    }

    // Keep auth metadata in sync so currentUser is correct after reload/login
    await supabase.auth.updateUser({ data: {
      name: profileUpdates.name,
      username: profileUpdates.username,
      bio: profileUpdates.bio,
      avatar: profileUpdates.avatar,
      avatar_url: resolvedAvatarUrl,
    }});

    onSave({
      ...user,
      name: profileUpdates.name,
      username: profileUpdates.username,
      bio: profileUpdates.bio || "",
      avatar: profileUpdates.avatar || user.avatar,
      avatarImg: resolvedAvatarUrl,
      avatarUrl: resolvedAvatarUrl,
    });
  };

  const inputStyle = { width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
    padding: "11px 12px", fontSize: 14, color: "#000", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "#888", letterSpacing: "0.06em",
    textTransform: "uppercase", display: "block", marginBottom: 6 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
      zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 20, padding: "28px 28px 24px",
        width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto",
        animation: "slideUp 0.22s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', 'Lora', serif", margin: 0, fontSize: 22, fontWeight: 600 }}>Edit Profile</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: "#aaa", cursor: "pointer" }}>×</button>
        </div>

        <div style={{ height: 1, background: "#f0f0f0", marginBottom: 20 }} />

        {/* Avatar */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f0f0f0", display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: 30, overflow: "hidden", flexShrink: 0,
            border: "2px solid #efefef" }}>
            {avatarImg
              ? <img src={avatarImg} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : avatar}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f8f8f8",
              border: "1px dashed #ddd", borderRadius: 8, padding: "7px 14px", fontSize: 12, color: "#555", cursor: "pointer" }}>
              📷 Upload Photo
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} />
            </label>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input value={avatar} onChange={e => { setAvatar(e.target.value); setAvatarImg(null); }}
                placeholder="or type emoji"
                style={{ ...inputStyle, padding: "6px 10px", fontSize: 20, width: 60, textAlign: "center" }} />
              <span style={{ fontSize: 11, color: "#aaa" }}>or emoji</span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Name</label>
              <span style={{ fontSize: 10, color: name.length >= MAX_NAME_LEN ? "#ef4444" : "#bbb" }}>{name.length}/{MAX_NAME_LEN}</span>
            </div>
            <input value={name} onChange={e => setName(e.target.value.slice(0, MAX_NAME_LEN))} style={inputStyle} />
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Username</label>
              <span style={{ fontSize: 10, color: username.length >= MAX_USERNAME_LEN ? "#ef4444" : "#bbb" }}>{username.length}/{MAX_USERNAME_LEN}</span>
            </div>
            <input value={username} onChange={handleUsernameChange}
              style={{ ...inputStyle, borderColor: usernameError ? "#ef4444" : undefined }} />
            {usernameError && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{usernameError}</div>}
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Bio</label>
              <span style={{ fontSize: 10, color: bio.length >= MAX_BIO_LEN ? "#ef4444" : "#bbb" }}>{bio.length}/{MAX_BIO_LEN}</span>
            </div>
            <textarea value={bio} onChange={e => setBio(e.target.value.slice(0, MAX_BIO_LEN))} rows={3}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} />
          </div>
        </div>

        {saveError && (
          <div style={{ marginTop: 16, fontSize: 12, color: "#ef4444", textAlign: "center" }}>{saveError}</div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
          <button onClick={handleSave} disabled={!canSave || saving}
            style={{ flex: 1, background: canSave && !saving ? "#000" : "#ccc", border: "none", borderRadius: 10, padding: "12px",
              color: "#fff", fontSize: 14, fontWeight: 600, cursor: canSave && !saving ? "pointer" : "not-allowed" }}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
          <button onClick={onClose}
            style={{ background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10, padding: "12px 20px",
              fontSize: 14, color: "#555", cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── NOTIFICATION PANEL ──────────────────────────────────────────────────────
function NotificationPanel({ notifications, onClose, onMarkAllRead, onMarkRead, onGoToTrip, onGoToTrips, onOpenNetwork, onAddSharedTrip, addingTripId, onDeclineSharedTrip }) {
  const unreadCount = notifications.filter(n => !n.read).length;
  const handleClick = (n) => {
    if (!n.read && onMarkRead) onMarkRead(n.id);
    if (n.tripId) { onGoToTrip(n.tripId, n.section); onClose(); }
    else if (n.navType === "trips") { onGoToTrips && onGoToTrips(); onClose(); }
    else if (n.navType === "network") { onOpenNetwork && onOpenNetwork(); onClose(); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", flexDirection: "column" }}
      onClick={onClose}>
      <div style={{ position: "absolute", top: 57, right: 0, left: 0, maxWidth: 420, margin: "0 auto",
        background: "#fff", border: "1px solid #efefef", borderRadius: "0 0 16px 16px",
        boxShadow: "0 8px 30px rgba(0,0,0,0.12)", overflow: "hidden", animation: "slideUp 0.18s ease" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: "14px 18px 10px", borderBottom: "1px solid #f0f0f0",
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#000" }}>
            Notifications {unreadCount > 0 && <span style={{ background: "#000", color: "#fff", borderRadius: 20,
              fontSize: 10, padding: "1px 7px", marginLeft: 6, fontWeight: 700 }}>{unreadCount}</span>}
          </div>
          {unreadCount > 0 && (
            <button onClick={onMarkAllRead} style={{ background: "none", border: "none", fontSize: 11,
              color: "#888", cursor: "pointer", padding: 0 }}>Mark all read</button>
          )}
        </div>
        <div onWheel={e => e.stopPropagation()} style={{ maxHeight: 360, overflowY: "auto", overscrollBehavior: "contain" }}>
          {notifications.length === 0 ? (
            <div style={{ padding: "32px 20px", textAlign: "center", color: "#aaa" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
              <div style={{ fontSize: 13 }}>No notifications yet</div>
            </div>
          ) : notifications.map(n => {
            const isSharedTrip = !!n.sharedTripId;
            const isAdding = addingTripId === n.sharedTripId;
            return (
              <div key={n.id}
                style={{ padding: "12px 18px", borderBottom: "1px solid #f8f8f8",
                  background: n.read ? "#fff" : "#fafafa",
                  display: "flex", gap: 12, alignItems: "flex-start",
                  transition: "background 0.15s" }}>
                <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{n.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: "#000", fontWeight: n.read ? 400 : 600, lineHeight: 1.4 }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>{n.body}</div>
                  {isSharedTrip ? (
                    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                      <button
                        onClick={e => { e.stopPropagation(); onAddSharedTrip && onAddSharedTrip(n.sharedTripId); }}
                        disabled={isAdding}
                        style={{ background: "#000", color: "#fff", border: "none",
                          borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600,
                          cursor: isAdding ? "default" : "pointer", opacity: isAdding ? 0.5 : 1 }}>
                        {isAdding ? "Adding…" : "＋ Join Trip"}
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); onDeclineSharedTrip && onDeclineSharedTrip(n.sharedTripId); onMarkRead && onMarkRead(n.id); }}
                        style={{ background: "#f5f5f5", color: "#555", border: "none",
                          borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        Decline
                      </button>
                    </div>
                  ) : (n.tripId || n.navType) ? (
                    <div onClick={() => handleClick(n)} style={{ fontSize: 10, color: "#bbb", marginTop: 4, cursor: "pointer" }}>Tap to view →</div>
                  ) : null}
                </div>
                {!n.read && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#000",
                  flexShrink: 0, marginTop: 6 }} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function buildNotifications(savedTrips, sharedTrips, pendingIncoming, acceptedByThem, invitesSent) {
  const notes = [];
  const today = new Date();
  (savedTrips || []).filter(t => !t.completed && !t.draft).forEach(t => {
    if (t.startDate) {
      const start = new Date(t.startDate + "T12:00:00");
      const days = Math.round((start - today) / 86400000);
      if (days >= 0 && days <= 7) {
        notes.push({ id: "trip_" + t.id, icon: "✈️", title: `${t.destination} in ${days === 0 ? "today!" : days + (days === 1 ? " day" : " days")}`,
          body: "Your trip is coming up. Make sure you're packed!", tripId: t.id, section: "packList", read: false });
      }
    }
    if ((t.bookRemind || []).length > 0) {
      const unbooked = (t.bookRemind || []).filter(r => !r.done);
      if (unbooked.length > 0) {
        notes.push({ id: "book_" + t.id, icon: "📋", title: `${unbooked.length} booking${unbooked.length > 1 ? "s" : ""} to do for ${t.destination}`,
          body: unbooked.slice(0, 2).map(r => r.text).join(" · "), tripId: t.id, section: "bookRemind", read: false });
      }
    }
    const packTotal = (t.packList || []).length;
    const packDone = (t.packList || []).filter(x => x.packed).length;
    if (packTotal > 0 && packDone === packTotal) {
      notes.push({ id: "packed_" + t.id, icon: "🎒", title: `All packed for ${t.destination}!`,
        body: "You're ready. Bon voyage!", tripId: t.id, section: "packList", read: true });
    }
  });
  (sharedTrips || []).forEach(t => {
    const sharerName = t.sharerProfile?.name || "A friend";
    const isCollaborator = t.memberRole === "collaborator";
    notes.push({ id: "shared_" + t.id, icon: "✈️",
      title: `${sharerName} added you to the ${t.destination} trip`,
      body: isCollaborator ? `${t.days} day${t.days !== 1 ? "s" : ""} · You can edit this trip` : `${t.days} day${t.days !== 1 ? "s" : ""} · View-only access`,
      navType: "trips", sharedTripId: t.id, read: false });
  });
  // Friend request notifications
  (pendingIncoming || []).forEach(req => {
    notes.push({ id: "freq_" + req.from, icon: "👤",
      title: `${req.profile?.name || "Someone"} sent you a friend request`,
      body: `@${req.profile?.username || req.from} wants to connect`, navType: "network", read: false });
  });
  // Accepted request notifications
  (acceptedByThem || []).forEach(f => {
    notes.push({ id: "facc_" + f.id, icon: "🤝",
      title: `${f.profile?.name || "A traveler"} accepted your friend request`,
      body: `You and @${f.profile?.username || f.id} are now friends`, navType: "network", read: false });
  });
  // Trip invite tracking — show accepted/pending status to the inviter
  (invitesSent || []).forEach(invite => {
    const trip = (savedTrips || []).find(t => t.id === invite.tripId);
    if (!trip) return;
    const accepted = (trip.invitedFriends || []).includes(invite.userId);
    notes.push({
      id: `tipinv_${invite.tripId}_${invite.userId}`,
      icon: accepted ? "✅" : "⏳",
      title: accepted
        ? `${invite.userName} joined your ${invite.tripDest} trip!`
        : `Invite pending: ${invite.userName} for ${invite.tripDest}`,
      body: accepted ? "They're now a collaborator" : "Waiting for them to join",
      tripId: trip.id, read: accepted,
    });
  });
  return notes;
}

// ─── SUPABASE HELPERS ────────────────────────────────────────────────────────
function dbEntryToLocal(row) {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    city: row.city,
    state: row.state,
    country: row.country,
    type: row.type,
    cuisine: row.cuisine,
    dish: row.dish,
    rating: row.rating,
    verdict: row.verdict,
    notes: row.notes,
    tags: row.tags || [],
    photos: (row.photos || []).filter(p => p && !p.includes("unsplash.com") && !p.includes("randomuser.me")),
    coverPosition: row.cover_position ?? 50,
    kidFriendly: row.kid_friendly || false,
    isPrivate: row.is_private || false,
    privateNote: row.private_note || "",
    visitDate: row.visit_date || "",
    createdAt: row.created_at,
    lat: row.lat ?? null,
    lon: row.lon ?? null,
  };
}

function localEntryToDb(entry, userId) {
  return {
    user_id: userId,
    name: entry.name,
    city: entry.city || null,
    state: entry.state || null,
    country: entry.country || "USA",
    type: entry.type || null,
    cuisine: entry.cuisine || null,
    dish: entry.dish || null,
    rating: entry.rating || null,
    verdict: entry.verdict || null,
    notes: entry.notes || null,
    tags: entry.tags || [],
    photos: entry.photos || [],
    cover_position: entry.coverPosition ?? 50,
    kid_friendly: entry.kidFriendly || false,
    is_private: entry.isPrivate || false,
    private_note: entry.privateNote || null,
    visit_date: entry.visitDate || null,
    lat: entry.lat ?? null,
    lon: entry.lon ?? null,
  };
}

function dbTripToLocal(row) {
  const days = (row.trip_days || [])
    .sort((a, b) => a.day_number - b.day_number)
    .map(d => ({
      id: d.id,
      day: d.day_number,
      name: d.name || `Day ${d.day_number}`,
      city: d.city,
      notes: d.notes || "",
      accommodation: d.accommodation || "",
      accomLat: d.accom_lat ?? null,
      accomLon: d.accom_lon ?? null,
      entries: (d.trip_places || [])
        .sort((a, b) => a.order_idx - b.order_idx)
        .map(p => ({
          id: p.id,
          name: p.name,
          type: p.type,
          city: p.city,
          state: p.state,
          verdict: p.verdict,
          cuisine: p.cuisine,
          notes: p.notes || "",
          distanceFromPrev: p.distance_from_prev,
          travelTimeFromPrev: p.travel_time_from_prev,
          visited: p.visited || false,
          entry_id: p.entry_id,
          lat: p.lat ?? null,
          lon: p.lon ?? null,
        })),
    }));

  return {
    id: row.id,
    destination: row.destination,
    days: days.length,
    startDate: row.start_date || "",
    endDate: row.end_date || "",
    createdAt: new Date(row.created_at).toLocaleDateString(),
    completed: row.completed || false,
    notes: row.notes || "",
    coverPhoto: row.cover_photo || null,
    invitedFriends: (row.trip_members || []).filter(m => m.role === "collaborator").map(m => m.user_id),
    sharedWith: (row.trip_members || []).filter(m => m.role === "viewer").map(m => m.user_id),
    packList: (row.pack_list_items || []).sort((a, b) => a.order_idx - b.order_idx).map(i => ({ id: i.id, text: i.text, packed: i.packed })),
    bookRemind: (row.book_remind_items || []).sort((a, b) => a.order_idx - b.order_idx).map(i => ({ id: i.id, text: i.text })),
    reservations: row.reservations || [],
    collaboratorSuggestions: [],
    ideasNotes: [],
    draft: row.draft || false,
    ideas: (row.trip_ideas || []).sort((a, b) => a.order_idx - b.order_idx).map(i => ({ id: i.id, name: i.name, type: i.type || "", notes: i.notes || "" })),
    plan: { days },
  };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUUID = id => typeof id === "string" && UUID_RE.test(id);

// Saves a trip to the DB. For new trips (no prevTrip) does a full batch insert.
// For existing trips, diffs against prevTrip and only writes what changed —
// days/places are never deleted wholesale, only the specific rows that were removed.
// If new rows were inserted (temp IDs replaced by real UUIDs), calls onNeedReload(tripId)
// so the caller can refresh local state with the real IDs.
async function saveTripToDb(trip, userId, prevTrip, onNeedReload) {
  const isNew = !isUUID(trip.id);
  const tripFields = {
    destination: trip.destination, start_date: trip.startDate || null, end_date: trip.endDate || null,
    completed: trip.completed || false, notes: trip.notes || null, cover_photo: trip.coverPhoto || null,
    reservations: trip.reservations?.length ? trip.reservations : null, draft: trip.draft || false,
  };
  const tripOp = isNew
    ? supabase.from("trips").insert({ user_id: userId, ...tripFields })
    : supabase.from("trips").update(tripFields).eq("id", trip.id);
  const { data: tripRow, error: tripErr } = await tripOp.select().single();
  if (tripErr) { console.error("saveTripToDb:", tripErr); return null; }
  const tripId = tripRow.id;

  // ── NEW TRIP: full batch insert ──────────────────────────────────────────
  if (!prevTrip || isNew) {
    const { error: delDaysErr } = await supabase.from("trip_days").delete().eq("trip_id", tripId);
    if (delDaysErr) { console.error("saveTripToDb: delete days:", delDaysErr); return null; }
    if (trip.plan?.days?.length) {
      const { data: insertedDays, error: daysErr } = await supabase.from("trip_days").insert(
        trip.plan.days.map((d, i) => ({ trip_id: tripId, day_number: i + 1, name: d.name || null, city: d.city || null, notes: d.notes || null, accommodation: d.accommodation || null, accom_lat: d.accomLat ?? null, accom_lon: d.accomLon ?? null }))
      ).select();
      if (daysErr) { console.error("saveTripToDb: insert days:", daysErr); return null; }
      const allPlaces = insertedDays.flatMap((dayRow, i) =>
        (trip.plan.days[i].entries || trip.plan.days[i].items || []).map((e, j) => ({
          day_id: dayRow.id, entry_id: e.entry_id || null,
          name: e.name, type: e.type || null, city: e.city || null, state: e.state || null,
          verdict: e.verdict || null, cuisine: e.cuisine || null, notes: e.notes || null,
          distance_from_prev: e.distanceFromPrev || null, travel_time_from_prev: e.travelTimeFromPrev || null,
          visited: e.visited || false, order_idx: j,
          lat: e.lat ?? null, lon: e.lon ?? null,
        }))
      );
      if (allPlaces.length) {
        const { error: placesErr } = await supabase.from("trip_places").insert(allPlaces);
        if (placesErr) { console.error("saveTripToDb: insert places:", placesErr); return null; }
      }
    }
    if (trip.packList?.length) {
      const { error } = await supabase.from("pack_list_items").insert(
        trip.packList.map((item, i) => ({ trip_id: tripId, text: item.text, packed: item.packed || false, order_idx: i }))
      );
      if (error) { console.error("saveTripToDb: insert pack list:", error); return null; }
    }
    if (trip.bookRemind?.length) {
      const { error } = await supabase.from("book_remind_items").insert(
        trip.bookRemind.map((item, i) => ({ trip_id: tripId, text: item.text, order_idx: i }))
      );
      if (error) { console.error("saveTripToDb: insert book reminders:", error); return null; }
    }
    if (trip.ideas?.length) {
      const { error } = await supabase.from("trip_ideas").insert(
        trip.ideas.map((idea, i) => ({ trip_id: tripId, name: idea.name, type: idea.type || null, notes: idea.notes || null, order_idx: i }))
      );
      if (error) { console.error("saveTripToDb: insert ideas:", error); return null; }
    }
    return tripId;
  }

  // ── EXISTING TRIP: targeted diff updates ────────────────────────────────
  let hadNewItems = false;
  const prevDays = prevTrip.plan?.days || [];
  const newDays = trip.plan?.days || [];

  // Delete days that were removed (cascades to their trip_places via FK)
  const newDayIdSet = new Set(newDays.filter(d => isUUID(d.id)).map(d => d.id));
  const deletedDayIds = prevDays.filter(d => isUUID(d.id) && !newDayIdSet.has(d.id)).map(d => d.id);
  if (deletedDayIds.length) {
    const { error } = await supabase.from("trip_days").delete().in("id", deletedDayIds);
    if (error) { console.error("saveTripToDb: delete days:", error); return null; }
  }

  // Insert newly added days (temp IDs like "d_<timestamp>")
  const daysToInsert = newDays.filter(d => !isUUID(d.id));
  const insertedDayIdMap = new Map();
  if (daysToInsert.length) {
    const { data: inserted, error } = await supabase.from("trip_days").insert(
      daysToInsert.map(d => ({ trip_id: tripId, day_number: newDays.indexOf(d) + 1, name: d.name || null, city: d.city || null, notes: d.notes || null, accommodation: d.accommodation || null, accom_lat: d.accomLat ?? null, accom_lon: d.accomLon ?? null }))
    ).select();
    if (error) { console.error("saveTripToDb: insert days:", error); return null; }
    daysToInsert.forEach((d, i) => insertedDayIdMap.set(d.id, inserted[i].id));
    hadNewItems = true;
  }

  // Update existing days where name, city, notes, or position changed
  const prevDayMap = new Map(prevDays.map(d => [d.id, d]));
  for (const newDay of newDays.filter(d => isUUID(d.id))) {
    const prev = prevDayMap.get(newDay.id);
    const newNum = newDays.indexOf(newDay) + 1;
    const prevNum = prev ? prevDays.indexOf(prev) + 1 : -1;
    if (!prev || newDay.name !== prev.name || newDay.city !== prev.city || newDay.notes !== prev.notes || newNum !== prevNum ||
        (newDay.accommodation || null) !== (prev.accommodation || null) ||
        (newDay.accomLat ?? null) !== (prev.accomLat ?? null) || (newDay.accomLon ?? null) !== (prev.accomLon ?? null)) {
      const { error } = await supabase.from("trip_days")
        .update({ name: newDay.name || null, city: newDay.city || null, notes: newDay.notes || null, day_number: newNum, accommodation: newDay.accommodation || null, accom_lat: newDay.accomLat ?? null, accom_lon: newDay.accomLon ?? null })
        .eq("id", newDay.id);
      if (error) { console.error("saveTripToDb: update day:", error); return null; }
    }
  }

  // Delete places that were removed (check globally so movePlace doesn't delete the moved place)
  const allNewPlaceIds = new Set(newDays.flatMap(d => (d.entries || d.items || []).filter(p => isUUID(p.id)).map(p => p.id)));
  const deletedPlaceIds = prevDays.flatMap(d => (d.entries || d.items || []).filter(p => isUUID(p.id) && !allNewPlaceIds.has(p.id)).map(p => p.id));
  if (deletedPlaceIds.length) {
    const { error } = await supabase.from("trip_places").delete().in("id", deletedPlaceIds);
    if (error) { console.error("saveTripToDb: delete places:", error); return null; }
  }

  // Build prev place map with day + order context for change detection
  const prevPlaceMap = new Map();
  prevDays.forEach(d => (d.entries || d.items || []).forEach((p, idx) => {
    prevPlaceMap.set(p.id, { ...p, dayId: isUUID(d.id) ? d.id : null, orderIdx: idx });
  }));

  const placesToInsert = [];
  const placesToUpdate = [];
  for (const newDay of newDays) {
    const dbDayId = isUUID(newDay.id) ? newDay.id : insertedDayIdMap.get(newDay.id);
    if (!dbDayId) continue;
    (newDay.entries || newDay.items || []).forEach((p, orderIdx) => {
      const row = {
        day_id: dbDayId, entry_id: p.entry_id || null,
        name: p.name, type: p.type || null, city: p.city || null, state: p.state || null,
        verdict: p.verdict || null, cuisine: p.cuisine || null, notes: p.notes || null,
        distance_from_prev: p.distanceFromPrev || null, travel_time_from_prev: p.travelTimeFromPrev || null,
        visited: p.visited || false, order_idx: orderIdx,
        lat: p.lat ?? null, lon: p.lon ?? null,
      };
      if (!isUUID(p.id)) {
        placesToInsert.push(row);
      } else {
        const prev = prevPlaceMap.get(p.id);
        if (!prev || p.name !== prev.name || p.visited !== prev.visited || (p.notes || "") !== (prev.notes || "") ||
            p.verdict !== prev.verdict || p.cuisine !== prev.cuisine || p.type !== prev.type ||
            p.city !== prev.city || p.state !== prev.state ||
            (p.distanceFromPrev || null) !== (prev.distanceFromPrev || null) ||
            (p.travelTimeFromPrev || null) !== (prev.travelTimeFromPrev || null) ||
            orderIdx !== prev.orderIdx || dbDayId !== prev.dayId ||
            (p.lat ?? null) !== (prev.lat ?? null) || (p.lon ?? null) !== (prev.lon ?? null)) {
          placesToUpdate.push({ id: p.id, ...row });
        }
      }
    });
  }
  if (placesToInsert.length) {
    const { error } = await supabase.from("trip_places").insert(placesToInsert);
    if (error) { console.error("saveTripToDb: insert places:", error); return null; }
    hadNewItems = true;
  }
  if (placesToUpdate.length) {
    const { error } = await supabase.from("trip_places").upsert(placesToUpdate, { onConflict: "id" });
    if (error) { console.error("saveTripToDb: update places:", error); return null; }
  }

  // Pack list, book/remind, ideas — diff by item ID
  const packResult = await diffSaveList("pack_list_items", prevTrip.packList || [], trip.packList || [],
    (item, i) => ({ trip_id: tripId, text: item.text, packed: item.packed || false, order_idx: i }),
    (a, b) => a.text !== b.text || a.packed !== b.packed);
  if (!packResult) return null;

  const bookResult = await diffSaveList("book_remind_items", prevTrip.bookRemind || [], trip.bookRemind || [],
    (item, i) => ({ trip_id: tripId, text: item.text, order_idx: i }),
    (a, b) => a.text !== b.text);
  if (!bookResult) return null;

  const ideasResult = await diffSaveList("trip_ideas", prevTrip.ideas || [], trip.ideas || [],
    (item, i) => ({ trip_id: tripId, name: item.name, type: item.type || null, notes: item.notes || null, order_idx: i }),
    (a, b) => a.name !== b.name || a.type !== b.type || a.notes !== b.notes);
  if (!ideasResult) return null;

  hadNewItems = hadNewItems || packResult.hadNew || bookResult.hadNew || ideasResult.hadNew;
  if (hadNewItems && onNeedReload) onNeedReload(tripId);
  return tripId;
}

// Diffs a list of items by id, inserting new ones, updating changed ones, deleting removed ones.
async function diffSaveList(table, prevItems, newItems, toRow, hasChanged) {
  const newIdSet = new Set(newItems.filter(i => isUUID(i.id)).map(i => i.id));
  const deletedIds = prevItems.filter(i => isUUID(i.id) && !newIdSet.has(i.id)).map(i => i.id);
  if (deletedIds.length) {
    const { error } = await supabase.from(table).delete().in("id", deletedIds);
    if (error) { console.error(`saveTripToDb: delete ${table}:`, error); return null; }
  }
  const prevMap = new Map(prevItems.map((i, idx) => [i.id, { ...i, orderIdx: idx }]));
  const toInsert = [];
  const toUpdate = [];
  newItems.forEach((item, i) => {
    const row = toRow(item, i);
    if (!isUUID(item.id)) {
      toInsert.push(row);
    } else {
      const prev = prevMap.get(item.id);
      if (!prev || hasChanged(prev, item) || i !== prev.orderIdx) toUpdate.push({ id: item.id, ...row });
    }
  });
  if (toInsert.length) {
    const { error } = await supabase.from(table).insert(toInsert);
    if (error) { console.error(`saveTripToDb: insert ${table}:`, error); return null; }
  }
  if (toUpdate.length) {
    const { error } = await supabase.from(table).upsert(toUpdate, { onConflict: "id" });
    if (error) { console.error(`saveTripToDb: update ${table}:`, error); return null; }
  }
  return { hadNew: toInsert.length > 0 };
}

// ─── DRAFT TRIP CARD ─────────────────────────────────────────────────────────
function DraftTripCard({ trip, onUpdate, onDelete, onBuildItinerary, inline = false }) {
  const [expanded, setExpanded] = useState(inline);
  const [newIdea, setNewIdea] = useState("");
  const [editingIdeaId, setEditingIdeaId] = useState(null);
  const [editingIdeaText, setEditingIdeaText] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const ideas = trip.ideas || [];

  const addIdea = (value) => {
    const name = (value ?? newIdea).trim();
    if (!name) return;
    onUpdate({ ...trip, ideas: [...ideas, { id: "idea_" + Date.now(), name, type: "", notes: "" }] });
    setNewIdea("");
  };

  const startEditIdea = (e, idea) => {
    e.stopPropagation();
    setEditingIdeaId(idea.id);
    setEditingIdeaText(idea.name);
  };

  const saveEditIdea = (ideaId) => {
    const name = editingIdeaText.trim();
    if (!name) return;
    onUpdate({ ...trip, ideas: ideas.map(i => i.id === ideaId ? { ...i, name } : i) });
    setEditingIdeaId(null);
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #efefef", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
      {!inline && (
        <div onClick={() => setExpanded(v => !v)} style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>{trip.destination}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#7B2D42", background: "#f5eaed", borderRadius: 20, padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Idea</span>
            </div>
            <div style={{ fontSize: 12, color: "#aaa", marginTop: 3 }}>{ideas.length} place{ideas.length !== 1 ? "s" : ""} saved</div>
          </div>
          <span style={{ color: "#ccc", fontSize: 12, flexShrink: 0 }}>{expanded ? "▲" : "▼"}</span>
        </div>
      )}

      {expanded && (
        <div style={{ borderTop: "1px solid #f5f5f5", padding: "16px 20px 20px" }}>
          <div style={{ marginBottom: 14 }} onClick={e => e.stopPropagation()}>
            <PlaceNameAutocomplete
              value={newIdea}
              onChange={setNewIdea}
              onSelect={label => { addIdea(label); }}
              placeholder="Add a restaurant, hike, hotel, or place…"
              style={{ width: "100%", border: "1.5px solid #e8e0d8", borderRadius: 10, padding: "9px 13px", fontSize: 14, fontFamily: "inherit", outline: "none", background: "#fafaf8", boxSizing: "border-box" }}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addIdea(); } }}
            />
          </div>

          {ideas.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#ccc", fontSize: 13 }}>
              No places saved yet — add a restaurant, activity, hike, hotel, or place above
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ideas.map(idea => (
                <div key={idea.id} onClick={e => e.stopPropagation()}
                  style={{ background: "#fdfcfb", border: "1.5px solid #ede8e2", borderRadius: 10, overflow: "hidden" }}>
                  {editingIdeaId === idea.id ? (
                    <div style={{ display: "flex", gap: 6, padding: "8px 10px" }}>
                      <input
                        autoFocus
                        value={editingIdeaText}
                        onChange={e => setEditingIdeaText(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveEditIdea(idea.id); if (e.key === "Escape") setEditingIdeaId(null); }}
                        style={{ flex: 1, border: "1.5px solid #e8e0d8", borderRadius: 8, padding: "6px 10px", fontSize: 14, fontFamily: "inherit", outline: "none", background: "#fff" }}
                      />
                      <button onClick={() => saveEditIdea(idea.id)}
                        style={{ background: "#7B2D42", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Save</button>
                      <button onClick={() => setEditingIdeaId(null)}
                        style={{ background: "#f0f0f0", color: "#555", border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px" }}>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: "#1a1a1a" }}>{idea.name}</span>
                      <button onClick={e => startEditIdea(e, idea)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 13, padding: "2px 6px", borderRadius: 6 }}
                        title="Edit">✏️</button>
                      <button onClick={e => { e.stopPropagation(); onUpdate({ ...trip, ideas: ideas.filter(i => i.id !== idea.id) }); }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#bbb", fontSize: 16, padding: "2px 6px", borderRadius: 6, lineHeight: 1 }}
                        title="Delete">🗑</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {!confirmingDelete ? (
              <button onClick={e => { e.stopPropagation(); setConfirmingDelete(true); }}
                style={{ background: "none", border: "1px solid #f5c0c0", borderRadius: 8, cursor: "pointer", color: "#e87070", fontSize: 12, padding: "5px 12px" }}>
                Delete Idea
              </button>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#888" }}>Delete this idea?</span>
                <button onClick={e => { e.stopPropagation(); onDelete(trip.id); }}
                  style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "5px 12px" }}>Delete</button>
                <button onClick={e => { e.stopPropagation(); setConfirmingDelete(false); }}
                  style={{ background: "#f0f0f0", color: "#555", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, padding: "5px 12px" }}>Cancel</button>
              </div>
            )}
            <button
              onClick={e => { e.stopPropagation(); onBuildItinerary(trip); }}
              disabled={ideas.length === 0}
              style={{ background: ideas.length > 0 ? "linear-gradient(135deg, #f0a882, #e87a4a)" : "#e8e0d8", border: "none", borderRadius: 20, padding: "7px 18px", color: ideas.length > 0 ? "#fff" : "#b0a090", fontSize: 12, fontWeight: 600, cursor: ideas.length > 0 ? "pointer" : "default", whiteSpace: "nowrap", transition: "all 0.2s" }}>
              {ideas.length > 0 ? "Build Itinerary →" : "Build Itinerary →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────
function MainApp({ user, onLogout }) {
  const [tab, setTab] = useState("home");
  const [showNetwork, setShowNetwork] = useState(false);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [entries, setEntries] = useState([]);
  const [hasMoreEntries, setHasMoreEntries] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE_SIZE = 50;

  // Ensure profile row exists once on login (not on every trip save)
  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").upsert({
      id: user.id,
      username: user.user_metadata?.username || user.email?.split("@")[0] || user.id,
      name: user.user_metadata?.name || user.email?.split("@")[0] || "User",
      avatar: user.user_metadata?.avatar || "✈️",
    }, { onConflict: "id", ignoreDuplicates: true }).then(({ error }) => {
      if (error) console.error("Profile upsert failed:", error);
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load entries from Supabase on mount — own entries only (friends loaded separately)
  useEffect(() => {
    if (!user) return;
    supabase
      .from("entries")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .range(0, PAGE_SIZE - 1)
      .then(({ data, error }) => {
        if (!error && data) {
          setEntries(data.map(dbEntryToLocal));
          setHasMoreEntries(data.length === PAGE_SIZE);
        }
        setEntriesLoaded(true);
      });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreEntries = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const { data, error } = await supabase
      .from("entries")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .range(entries.length, entries.length + PAGE_SIZE - 1);
    if (!error && data) {
      setEntries(prev => [...prev, ...data.map(dbEntryToLocal)]);
      setHasMoreEntries(data.length === PAGE_SIZE);
    }
    setLoadingMore(false);
  };

  const [friendEntries, setFriendEntries] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [prefillEntry, setPrefillEntry] = useState(null);
  const [showTripModal, setShowTripModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [tripsView, setTripsView] = useState("active");
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);
  const [newDraftDest, setNewDraftDest] = useState("");
  const [viewProfile, setViewProfile] = useState(null);
  const [friendState, setFriendState] = useState({});
  const [pendingIncoming, setPendingIncoming] = useState([]);
  const [friendProfiles, setFriendProfiles] = useState([]);
  const [acceptedByThem, setAcceptedByThem] = useState([]); // my sent requests that were accepted
  const [invitesSent, setInvitesSent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ts_invites_sent") || "[]"); } catch { return []; }
  });

  // Load real friendships from Supabase
  useEffect(() => {
    if (!user) return;
    const loadFriendships = () => {
      supabase.from("friendships").select("*, profiles!friendships_friend_id_fkey(*), requester:profiles!friendships_user_id_fkey(*)")
        .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`)
        .then(({ data }) => {
          if (!data) return;
          const state = {};
          const incoming = [];
          const profiles = [];
          const accepted = [];
          data.forEach(f => {
            const isSender = f.user_id === user.id;
            const otherId = isSender ? f.friend_id : f.user_id;
            const otherProfile = isSender ? f.profiles : f.requester;
            if (f.status === "accepted") {
              state[otherId] = "friend";
              if (otherProfile) profiles.push(otherProfile);
              if (isSender) accepted.push({ id: otherId, profile: otherProfile });
            } else if (f.status === "pending") {
              if (isSender) state[otherId] = "pending";
              else incoming.push({ from: otherId, profile: otherProfile });
            }
          });
          setFriendState(state);
          setPendingIncoming(incoming);
          setFriendProfiles(profiles);
          setAcceptedByThem(accepted);
        });
    };
    loadFriendships();
    const channel = supabase.channel(`friendships_${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "friendships", filter: `user_id=eq.${user.id}` }, loadFriendships)
      .on("postgres_changes", { event: "*", schema: "public", table: "friendships", filter: `friend_id=eq.${user.id}` }, loadFriendships)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || !friendProfiles.length) { setFriendEntries([]); return; }
    const friendIds = friendProfiles.map(f => f.id);
    supabase
      .from("entries")
      .select("*")
      .in("user_id", friendIds)
      .eq("is_private", false)
      .order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setFriendEntries(data.map(dbEntryToLocal)); });
  }, [friendProfiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const [editingEntry, setEditingEntry] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importToLogsMsg, setImportToLogsMsg] = useState("");
  const [sharedTrips, setSharedTrips] = useState([]); // trips shared by friends
  const sharedTripsInitialized = useRef(false);
  const knownSharedTripIds = useRef(new Set());
  const readNotifIdsRef = useRef(new Set()); // mirrors readNotifIds state for use in callbacks
  const [savedTrips, setSavedTrips] = useState([]);
  const [tripsLoaded, setTripsLoaded] = useState(false);
  const [entriesLoaded, setEntriesLoaded] = useState(false);
  const sharedTripChannels = useRef({});
  const savingTripIds = useRef(new Set()); // tracks trips currently being saved to suppress realtime reloads
  const reloadTimers = useRef({}); // debounce timers for realtime reloads

  // Fetches the latest data for a single trip from the DB and patches savedTrips in place.
  // Preserves isShared / memberRole flags so the trip stays in the correct category.
  const reloadTripData = useCallback(async (tripId) => {
    const [{ data: tripData }, { data: days }, { data: pack }, { data: book }, { data: ideas }] = await Promise.all([
      supabase.from("trips").select("*").eq("id", tripId).single(),
      supabase.from("trip_days").select("*, trip_places(*)").eq("trip_id", tripId),
      supabase.from("pack_list_items").select("*").eq("trip_id", tripId),
      supabase.from("book_remind_items").select("*").eq("trip_id", tripId),
      supabase.from("trip_ideas").select("*").eq("trip_id", tripId),
    ]);
    if (!tripData) return;
    const updated = dbTripToLocal({
      ...tripData,
      trip_days: days || [],
      trip_members: [],
      pack_list_items: pack || [],
      book_remind_items: book || [],
      trip_ideas: ideas || [],
    });
    setSavedTrips(prev => prev.map(t =>
      t.id === tripId ? { ...updated, isShared: t.isShared, memberRole: t.memberRole } : t
    ));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sets up Supabase realtime channels for a shared trip so both parties
  // see each other's edits immediately (trips row + day-level changes).
  const subscribeToSharedTrip = useCallback((tripId) => {
    if (sharedTripChannels.current[tripId]) return;
    const debouncedReload = () => {
      // Skip realtime reloads triggered by our own save to avoid overwriting optimistic state.
      if (savingTripIds.current.has(tripId)) return;
      clearTimeout(reloadTimers.current[tripId]);
      reloadTimers.current[tripId] = setTimeout(() => reloadTripData(tripId), 800);
    };
    const channel = supabase.channel(`shared_data_${tripId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "trips", filter: `id=eq.${tripId}` },
        debouncedReload)
      .on("postgres_changes", { event: "*", schema: "public", table: "trip_days", filter: `trip_id=eq.${tripId}` },
        debouncedReload)
      .on("postgres_changes", { event: "*", schema: "public", table: "pack_list_items", filter: `trip_id=eq.${tripId}` },
        debouncedReload)
      .on("postgres_changes", { event: "*", schema: "public", table: "book_remind_items", filter: `trip_id=eq.${tripId}` },
        debouncedReload)
      .on("postgres_changes", { event: "*", schema: "public", table: "trip_ideas", filter: `trip_id=eq.${tripId}` },
        debouncedReload)
      .subscribe();
    sharedTripChannels.current[tripId] = channel;
  }, [reloadTripData]);

  // Loads all accepted shared trips into savedTrips and wires up realtime for each.
  const loadAcceptedSharedTrips = useCallback(async () => {
    if (!user?.id) return;
    const { data: memberRows } = await supabase
      .from("trip_members").select("trip_id, role").eq("user_id", user.id).eq("status", "accepted");
    if (!memberRows?.length) return;
    const tripIds = memberRows.map(r => r.trip_id);
    const roleMap = {};
    memberRows.forEach(r => { roleMap[r.trip_id] = r.role; });
    const { data: trips } = await supabase.from("trips").select("*").in("id", tripIds);
    if (!trips?.length) return;
    const [{ data: allDays }, { data: allPack }, { data: allBook }, { data: allIdeas }] = await Promise.all([
      supabase.from("trip_days").select("*, trip_places(*)").in("trip_id", tripIds),
      supabase.from("pack_list_items").select("*").in("trip_id", tripIds),
      supabase.from("book_remind_items").select("*").in("trip_id", tripIds),
      supabase.from("trip_ideas").select("*").in("trip_id", tripIds),
    ]);
    const daysByTrip = {}, packByTrip = {}, bookByTrip = {}, ideasByTrip = {};
    (allDays || []).forEach(d => { if (!daysByTrip[d.trip_id]) daysByTrip[d.trip_id] = []; daysByTrip[d.trip_id].push(d); });
    (allPack || []).forEach(i => { if (!packByTrip[i.trip_id]) packByTrip[i.trip_id] = []; packByTrip[i.trip_id].push(i); });
    (allBook || []).forEach(i => { if (!bookByTrip[i.trip_id]) bookByTrip[i.trip_id] = []; bookByTrip[i.trip_id].push(i); });
    (allIdeas || []).forEach(i => { if (!ideasByTrip[i.trip_id]) ideasByTrip[i.trip_id] = []; ideasByTrip[i.trip_id].push(i); });
    // Only treat trips the current user does NOT own as shared — never mark your own trip isShared.
    const acceptedTrips = trips.filter(t => t.user_id !== user.id).map(t => ({
      ...dbTripToLocal({
        ...t,
        trip_days: daysByTrip[t.id] || [],
        trip_members: [],
        pack_list_items: packByTrip[t.id] || [],
        book_remind_items: bookByTrip[t.id] || [],
        trip_ideas: ideasByTrip[t.id] || [],
      }),
      isShared: true,
      memberRole: roleMap[t.id] || "viewer",
    }));
    // Keep all owned trips (isShared falsy) plus the accepted shared-by-others trips.
    setSavedTrips(prev => [...prev.filter(t => !t.isShared), ...acceptedTrips]);
    tripIds.forEach(subscribeToSharedTrip);
  }, [user?.id, subscribeToSharedTrip]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    supabase
      .from("trips")
      .select(`*, trip_days(*, trip_places(*)), pack_list_items(*), book_remind_items(*), trip_ideas(*)`)
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(async ({ data, error }) => {
        if (error) {
          console.error("loadTrips error:", error);
          const offline = Object.keys(localStorage)
            .filter(k => k.startsWith("ts_offline_"))
            .map(k => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } })
            .filter(Boolean);
          if (offline.length) { setSavedTrips(offline); showToast("You're offline — showing saved trips", "info"); }
          setTripsLoaded(true);
          return;
        }
        if (data) {
          const tripIds = data.map(t => t.id);
          const { data: members } = tripIds.length
            ? await supabase.from("trip_members").select("trip_id, user_id, role").in("trip_id", tripIds)
            : { data: [] };
          const membersByTrip = {};
          (members || []).forEach(m => {
            if (!membersByTrip[m.trip_id]) membersByTrip[m.trip_id] = [];
            membersByTrip[m.trip_id].push(m);
          });
          setSavedTrips(data.map(t => dbTripToLocal({ ...t, trip_members: membersByTrip[t.id] || [] })));
          // Subscribe to realtime for own trips that have any members (collaborators or viewers)
          const sharedTripIds = data
            .filter(t => (membersByTrip[t.id] || []).length > 0)
            .map(t => t.id);
          sharedTripIds.forEach(subscribeToSharedTrip);
        }
        setTripsLoaded(true);
      });
  }, [user?.id, subscribeToSharedTrip]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load accepted shared trips after owned trips are ready to avoid race conditions
  useEffect(() => {
    if (tripsLoaded) loadAcceptedSharedTrips();
  }, [tripsLoaded, loadAcceptedSharedTrips]);

  const loadSharedTrips = useCallback(async () => {
    if (!user) return;
    // Try RPC first, fall back to direct query if it fails
    let memberRows;
    const { data: rpcData, error: rpcError } = await supabase.rpc("get_my_trip_memberships");
    if (rpcError || !rpcData) {
      const { data: directData, error: directError } = await supabase
        .from("trip_members").select("trip_id, role, status").eq("user_id", user.id);
      if (directError) { console.error("loadSharedTrips error:", directError); setSharedTrips([]); return; }
      memberRows = directData;
    } else {
      memberRows = rpcData;
    }
    // Only show pending invitations in notifications; accepted trips live in savedTrips
    memberRows = memberRows.filter(r => r.status !== "accepted");
    if (!memberRows?.length) { sharedTripsInitialized.current = true; setSharedTrips([]); return; }
    const tripIds = memberRows.map(r => r.trip_id);
    const roleMap = {};
    memberRows.forEach(r => { roleMap[r.trip_id] = r.role; });
    const { data: trips, error: tripsError } = await supabase
      .from("trips").select("*").in("id", tripIds);
    if (tripsError) { console.error("loadSharedTrips trips error:", tripsError); setSharedTrips([]); return; }
    if (!trips?.length) { sharedTripsInitialized.current = true; setSharedTrips([]); return; }
    const [{ data: allDays }, { data: allPack }, { data: allBook }] = await Promise.all([
      supabase.from("trip_days").select("*, trip_places(*)").in("trip_id", tripIds),
      supabase.from("pack_list_items").select("*").in("trip_id", tripIds),
      supabase.from("book_remind_items").select("*").in("trip_id", tripIds),
    ]);
    const daysByTrip = {}, packByTrip = {}, bookByTrip = {};
    (allDays || []).forEach(d => { if (!daysByTrip[d.trip_id]) daysByTrip[d.trip_id] = []; daysByTrip[d.trip_id].push(d); });
    (allPack || []).forEach(i => { if (!packByTrip[i.trip_id]) packByTrip[i.trip_id] = []; packByTrip[i.trip_id].push(i); });
    (allBook || []).forEach(i => { if (!bookByTrip[i.trip_id]) bookByTrip[i.trip_id] = []; bookByTrip[i.trip_id].push(i); });
    const ownerIds = [...new Set(trips.map(t => t.user_id))];
    const { data: profiles } = await supabase
      .from("profiles").select("id, username, name, avatar_url").in("id", ownerIds);
    const profileMap = {};
    (profiles || []).forEach(p => { profileMap[p.id] = p; });
    const mapped = trips.map(t => ({
      ...dbTripToLocal({ ...t, trip_days: daysByTrip[t.id] || [], trip_members: [], pack_list_items: packByTrip[t.id] || [], book_remind_items: bookByTrip[t.id] || [] }),
      sharedBy: t.user_id,
      sharerProfile: profileMap[t.user_id] || null,
      sharedAt: new Date(t.created_at).toLocaleDateString(),
      memberRole: roleMap[t.id] || "viewer",
    }));
    if (sharedTripsInitialized.current) {
      // Mid-session: toast any brand-new invites that just arrived
      const newInvites = mapped.filter(t => !knownSharedTripIds.current.has(t.id));
      if (newInvites.length > 0) {
        newInvites.forEach(t => {
          const name = t.sharerProfile?.name || "Someone";
          showToast(`✈️ ${name} added you to the ${t.destination} trip!`, "success", 8000);
        });
        setShowNotifications(true);
      }
    } else {
      // First load after login: toast any unread invites (covers "was logged out when invited")
      const unreadInvites = mapped.filter(t => !readNotifIdsRef.current.has("shared_" + t.id));
      if (unreadInvites.length > 0) {
        unreadInvites.forEach(t => {
          const name = t.sharerProfile?.name || "Someone";
          showToast(`✈️ ${name} added you to the ${t.destination} trip!`, "success", 8000);
        });
        setShowNotifications(true);
      }
    }
    mapped.forEach(t => knownSharedTripIds.current.add(t.id));
    sharedTripsInitialized.current = true;
    setSharedTrips(mapped);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user?.id) return;
    loadSharedTrips();
    const channel = supabase.channel(`trip_invites_${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "trip_members", filter: `user_id=eq.${user.id}` },
        () => { loadSharedTrips(); })
      .subscribe();
    const poll = setInterval(loadSharedTrips, 30000);
    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
      // Clean up per-trip realtime channels on logout
      Object.values(sharedTripChannels.current).forEach(ch => supabase.removeChannel(ch));
      sharedTripChannels.current = {};
    };
  }, [user?.id, loadSharedTrips]); // eslint-disable-line react-hooks/exhaustive-deps

  const [currentUser, setCurrentUser] = useState(() => {
    // Use the real auth user if available, fall back to mock for dev without credentials
    if (user) {
      return {
        id: user.id,
        username: user.user_metadata?.username || user.email?.split("@")[0] || "me",
        name: user.user_metadata?.name || user.email?.split("@")[0] || "You",
        avatar: user.user_metadata?.avatar || "✈️",
        avatarImg: user.user_metadata?.avatar_url || null,
        bio: user.user_metadata?.bio || "",
        tags: [],
      };
    }
    return { id: "", username: "me", name: "You", avatar: "✈️", avatarImg: null, bio: "", tags: [] };
  });
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [focusTripSection, setFocusTripSection] = useState(null); // { tripId, section }
  const [addingTripId, setAddingTripId] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [readNotifIds, setReadNotifIds] = useState(new Set());
  // Keep ref in sync so loadSharedTrips (useCallback) can read current value without stale closure
  useEffect(() => { readNotifIdsRef.current = readNotifIds; }, [readNotifIds]);
  useEffect(() => {
    if (!user?.id) { setReadNotifIds(new Set()); readNotifIdsRef.current = new Set(); return; }
    supabase.from("profiles").select("read_notif_ids").eq("id", user.id).single()
      .then(({ data }) => {
        if (data?.read_notif_ids?.length) {
          setReadNotifIds(new Set(data.read_notif_ids));
        } else {
          try {
            const stored = localStorage.getItem(`notif_read_${user.id}`);
            setReadNotifIds(stored ? new Set(JSON.parse(stored)) : new Set());
          } catch { setReadNotifIds(new Set()); }
        }
      });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const markNotifsRead = (ids) => {
    setReadNotifIds(ids);
    const arr = [...ids];
    try { localStorage.setItem(`notif_read_${user.id}`, JSON.stringify(arr)); } catch {}
    supabase.from("profiles").update({ read_notif_ids: arr }).eq("id", user.id).then(({ error }) => { if (error) console.error("Mark notifications read failed:", error); });
  };
  const [savedBookmarks, setSavedBookmarks] = useState(new Set());
  const bookmarkPending = useRef(new Set());

  const handleToggleBookmark = async id => {
    if (bookmarkPending.current.has(id)) return;
    bookmarkPending.current.add(id);
    const wasBookmarked = savedBookmarks.has(id);
    setSavedBookmarks(prev => { const s = new Set(prev); wasBookmarked ? s.delete(id) : s.add(id); return s; });
    const { error } = wasBookmarked
      ? await supabase.from("bookmarks").delete().eq("user_id", user?.id).eq("entry_id", id)
      : await supabase.from("bookmarks").insert({ user_id: user?.id, entry_id: id });
    bookmarkPending.current.delete(id);
    if (error) { setSavedBookmarks(prev => { const s = new Set(prev); wasBookmarked ? s.add(id) : s.delete(id); return s; }); showToast("Couldn't save bookmark. Try again."); }
    else if (!wasBookmarked) showToast("Bookmarked!", "success");
  };

  useEffect(() => {
    if (!user) return;
    supabase.from("bookmarks").select("entry_id").eq("user_id", user.id)
      .then(({ data }) => { if (data) setSavedBookmarks(new Set(data.map(b => b.entry_id))); });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-move trips to past when their end date has passed
  useEffect(() => {
    if (!tripsLoaded) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const toAutoComplete = savedTrips.filter(t => {
      if (t.completed) return false;
      let endStr = t.endDate;
      if (!endStr && t.startDate && t.days) {
        const s = new Date(t.startDate + "T12:00:00");
        s.setDate(s.getDate() + (t.days - 1));
        endStr = s.toISOString().slice(0, 10);
      }
      if (!endStr) return false;
      const end = new Date(endStr + "T12:00:00");
      end.setHours(0, 0, 0, 0);
      return end < today;
    });
    if (toAutoComplete.length === 0) return;
    setSavedTrips(prev => prev.map(t => toAutoComplete.some(x => x.id === t.id) ? { ...t, completed: true } : t));
    toAutoComplete.forEach(t => {
      supabase.from("trips").update({ completed: true }).eq("id", t.id)
        .then(({ error }) => { if (error) console.error("Auto-complete trip error:", t.id, error); });
    });
  }, [tripsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Geolocation-based "Log this place?" reminder
  useEffect(() => {
    if (!navigator.geolocation || !user) return;
    const notifiedIds = new Set();
    const haversineKm = (lat1, lon1, lat2, lon2) => {
      const R = 6371, dLat = (lat2 - lat1) * Math.PI / 180, dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };
    const watchId = navigator.geolocation.watchPosition(pos => {
      const { latitude: lat, longitude: lon } = pos.coords;
      const activeTrip = savedTrips.find(t => !t.completed && !t.draft);
      if (!activeTrip?.plan?.days) return;
      const allPlanEntries = activeTrip.plan.days.flatMap(d => d.entries || d.items || []);
      for (const place of allPlanEntries) {
        if (place.lat == null || place.lon == null || place.visited || notifiedIds.has(place.id)) continue;
        const dist = haversineKm(lat, lon, place.lat, place.lon);
        if (dist < 0.5) { // within 500m
          notifiedIds.add(place.id);
          showToast(`📍 You're near ${place.name} — want to log it?`, "success");
          break;
        }
      }
    }, err => {
      if (err.code === 1) showToast("Location access denied — nearby reminders won't work.", "info");
    }, { enableHighAccuracy: false, maximumAge: 60000, timeout: 30000 });
    return () => navigator.geolocation.clearWatch(watchId);
  }, [user, savedTrips]); // eslint-disable-line react-hooks/exhaustive-deps

  const allEntries = [...entries, ...friendEntries];

  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); showToast("Back online.", "success"); };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => { window.removeEventListener("online", handleOnline); window.removeEventListener("offline", handleOffline); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const notifications = buildNotifications(savedTrips, sharedTrips, pendingIncoming, acceptedByThem, invitesSent).map(n => ({
    ...n, read: readNotifIds.has(n.id) || n.read
  }));
  const unreadNotifCount = notifications.filter(n => !n.read).length;

  if (!entriesLoaded || !tripsLoaded) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 32 }}>✈️</div>
        <div style={{ fontSize: 13, color: "#aaa", fontFamily: "'DM Sans', sans-serif" }}>Loading your travels…</div>
      </div>
    );
  }

  if (viewProfile) {
    const user = friendProfiles.find(u => u.id === viewProfile);
    if (!user) return null;
    return (
      <div style={{ minHeight: "100vh", background: "#fafafa", padding: "20px 16px", maxWidth: 600, margin: "0 auto" }}>
        <style>{``}</style>
        <ProfileView user={user} entries={allEntries} onBack={() => setViewProfile(null)}
          savedTrips={savedTrips}
          onAddToTrip={async (tripId, entry) => {
            let updatedTrip;
            setSavedTrips(prev => prev.map(t => {
              if (t.id !== tripId) return t;
              const updatedDays = t.plan?.days ? [...t.plan.days] : [];
              if (updatedDays.length === 0) updatedDays.push({ day: 1, city: t.destination, entries: [], activity: "" });
              updatedDays[0] = { ...updatedDays[0], entries: [...updatedDays[0].entries, entry] };
              updatedTrip = { ...t, plan: { ...t.plan, days: updatedDays } };
              return updatedTrip;
            }));
            if (updatedTrip) {
              const prev = savedTrips.find(t => t.id === tripId);
              await saveTripToDb(updatedTrip, user?.id, prev);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#FCFAF8", fontFamily: "'DM Sans', sans-serif", color: "#000" }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: #ede8e2; } ::-webkit-scrollbar-thumb { background: #c5b9af; border-radius: 4px; }
        input:focus, textarea:focus { border-color: #3d1f14 !important; outline: none; }
        button { font-family: 'DM Sans', sans-serif; }
        button:active { opacity: 0.75; transform: scale(0.97); }
        button:focus-visible, a:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible { outline: 2px solid #7B2D42; outline-offset: 2px; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        .home-photo-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Offline banner */}
      {!isOnline && (
        <div style={{ background: "#1a1a1a", color: "#fff", textAlign: "center", padding: "8px 16px", fontSize: 13, fontWeight: 500, position: "sticky", top: 0, zIndex: 60, letterSpacing: "0.01em" }}>
          No internet connection — changes won't save until you're back online.
        </div>
      )}

      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #ede8e2", padding: "12px 20px", position: "sticky", top: 0, zIndex: 50, boxShadow: "0 1px 10px rgba(61,31,20,0.06)" }}>
        <div className="ts-inner" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="15" fill="#7B2D42" fillOpacity="0.1" stroke="#7B2D42" strokeWidth="1.5"/>
              <path d="M16 6C12.13 6 9 9.13 9 13c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#7B2D42"/>
              <circle cx="16" cy="13" r="2.5" fill="#fff"/>
              <path d="M8 24l4-2 4 2 4-2 4 2" stroke="#7B2D42" strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.5"/>
            </svg>
            <div>
              <div className="ts-brand-name" style={{ fontFamily: "'Lora', 'Georgia', serif", color: "#1a0a00", fontSize: 20, fontWeight: 700, letterSpacing: "0.01em", lineHeight: 1 }}>Travel Slate</div>
              <div className="ts-brand-sub" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 9, color: "#b09e94", letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 3 }}>Your Travel Diary</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {(tab === "mine" || tab === "home") && (
              <button className="ts-log-header" onClick={() => setShowAddModal(true)}
                style={{ background: "#7B2D42", border: "none", borderRadius: 10, padding: "9px 18px",
                  color: "#fff", fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13, cursor: "pointer", fontWeight: 600, letterSpacing: "0.01em" }}>
                + Log
              </button>
            )}
            {/* Avatar menu */}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowAvatarMenu(v => !v)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center" }}
                title={currentUser.name}
              >
                <div style={{ width: 36, height: 36, borderRadius: "50%", overflow: "hidden", background: "#ede8e2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, border: "2px solid #d8cfc7", position: "relative" }}>
                  {currentUser.avatarImg
                    ? <img src={currentUser.avatarImg} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : currentUser.avatar}
                  {unreadNotifCount > 0 && (
                    <span style={{ position: "absolute", top: 1, right: 1, width: 8, height: 8, background: "#e53935", borderRadius: "50%", border: "1.5px solid #fff" }} />
                  )}
                </div>
              </button>
              {showAvatarMenu && (
                <>
                  <div onClick={() => setShowAvatarMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", background: "#fff", border: "1px solid #ede8e2", borderRadius: 12, boxShadow: "0 4px 20px rgba(61,31,20,0.12)", minWidth: 180, zIndex: 100, overflow: "hidden" }}>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #f5f0eb" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1a0a00" }}>{currentUser.name}</div>
                      <div style={{ fontSize: 11, color: "#b09e94" }}>@{currentUser.username}</div>
                    </div>
                    <button onClick={() => { setShowAvatarMenu(false); setShowNotifications(v => { if (!v) { markNotifsRead(new Set(notifications.filter(n => !n.sharedTripId).map(n => n.id))); loadSharedTrips(); } return !v; }); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: "none", padding: "11px 14px", cursor: "pointer", fontSize: 13, color: "#3d1f14", fontFamily: "inherit", textAlign: "left" }}>
                      <span style={{ position: "relative", fontSize: 16 }}>
                        🔔
                        {unreadNotifCount > 0 && <span style={{ position: "absolute", top: -2, right: -3, width: 7, height: 7, background: "#e53935", borderRadius: "50%", border: "1px solid #fff" }} />}
                      </span>
                      Notifications
                      {unreadNotifCount > 0 && <span style={{ marginLeft: "auto", fontSize: 11, background: "#e53935", color: "#fff", borderRadius: 10, padding: "1px 6px" }}>{unreadNotifCount}</span>}
                    </button>
                    <button onClick={() => { setShowAvatarMenu(false); setShowNetwork(true); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: "none", padding: "11px 14px", cursor: "pointer", fontSize: 13, color: "#3d1f14", fontFamily: "inherit", textAlign: "left" }}>
                      <span style={{ fontSize: 16 }}>👥</span>
                      Friends & Network
                    </button>
                    <div style={{ borderTop: "1px solid #f5f0eb" }}>
                      <button onClick={onLogout}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: "none", padding: "11px 14px", cursor: "pointer", fontSize: 13, color: "#9e3030", fontFamily: "inherit", textAlign: "left" }}>
                        <span style={{ fontSize: 16 }}>🚪</span>
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notification Panel */}
      {showNotifications && (
        <NotificationPanel
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
          onMarkAllRead={() => markNotifsRead(new Set(notifications.map(n => n.id)))}
          onMarkRead={id => markNotifsRead(new Set([...readNotifIds, id]))}
          onGoToTrip={(tripId, section) => { setFocusTripSection({ tripId, section }); setTab("trips"); setShowNotifications(false); }}
          onGoToTrips={() => { setTab("trips"); setShowNotifications(false); }}
          onOpenNetwork={() => { setShowNetwork(true); setShowNotifications(false); }}
          addingTripId={addingTripId}
          onAddSharedTrip={async sharedTripId => {
            const trip = sharedTrips.find(t => t.id === sharedTripId);
            if (!trip) return;
            if (savedTrips.some(t => t.isShared && t.id === sharedTripId)) {
              showToast(`You already added the ${trip.destination} trip.`, "info");
              setSharedTrips(prev => prev.filter(t => t.id !== sharedTripId));
              markNotifsRead(new Set([...readNotifIds, "shared_" + sharedTripId]));
              return;
            }
            setAddingTripId(sharedTripId);
            const { error } = await supabase.from("trip_members")
              .update({ status: "accepted" })
              .eq("trip_id", sharedTripId)
              .eq("user_id", user.id);
            setAddingTripId(null);
            if (!error) {
              // Add the live shared trip into savedTrips (no copy — same record as owner's)
              const liveTrip = { ...trip, isShared: true, memberRole: trip.memberRole || "viewer" };
              setSavedTrips(prev => [...prev, liveTrip]);
              subscribeToSharedTrip(sharedTripId);
              setSharedTrips(prev => prev.filter(t => t.id !== sharedTripId));
              knownSharedTripIds.current.delete(sharedTripId);
              markNotifsRead(new Set([...readNotifIds, "shared_" + sharedTripId]));
              showToast(`${trip.destination} trip added to your planner!`, "success");
              setShowNotifications(false);
              setTab("trips");
            } else {
              showToast("Failed to add trip. Please try again.", "error");
            }
          }}
          onDeclineSharedTrip={async sharedTripId => {
            await supabase.from("trip_members").delete().eq("trip_id", sharedTripId).eq("user_id", user.id);
            setSharedTrips(prev => prev.filter(t => t.id !== sharedTripId));
            knownSharedTripIds.current.delete(sharedTripId);
            markNotifsRead(new Set([...readNotifIds, "shared_" + sharedTripId]));
            setShowNotifications(false);
          }}
        />
      )}

      {/* Nav */}
      <div style={{ borderBottom: "1px solid #ede8e2", background: "#faf7f5", position: "sticky", top: 57, zIndex: 40 }}>
        <div style={{ display: "flex", width: "100%" }}>
          {[["home","Home"],["explore","Explore"],["trips","Trips"],["mine","My Logs"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className="ts-tab"
              style={{ flex: 1, border: "none",
                borderBottom: tab === id
                  ? `3px solid ${id === "trips" ? "#8b2a2a" : "#3d1f14"}`
                  : "3px solid transparent",
                background: tab === id ? "rgba(123,45,66,0.07)" : "transparent",
                color: tab === id ? "#3d1f14" : "#555", cursor: "pointer",
                fontWeight: tab === id ? 700 : 500,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14, padding: "13px 4px 11px",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.18s", letterSpacing: "0.01em" }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="ts-content">

        {/* HOME TAB */}
        {tab === "home" && (
          <HomeTab
            currentUser={currentUser}
            savedTrips={savedTrips}
            sharedTrips={sharedTrips}
            entries={allEntries}
            allUsers={friendProfiles}
            onGoToTrips={() => setTab("trips")}
            onGoToPastTrips={() => { setTripsView("past"); setTab("trips"); }}
            onGoToExplore={() => setTab("explore")}
            onGoToTrip={(tripId, section) => { setFocusTripSection({ tripId, section }); setTab("trips"); }}
            onUpdateCoverPhoto={async (tripId, photo) => {
              setSavedTrips(prev => prev.map(t => t.id === tripId ? { ...t, coverPhoto: photo } : t));
              await supabase.from("trips").update({ cover_photo: photo || null }).eq("id", tripId);
            }}
            onViewProfile={setViewProfile}
            onLogPlace={() => setShowAddModal(true)}
            savedBookmarks={savedBookmarks}
            onToggleBookmark={handleToggleBookmark}
          />
        )}

        {/* EXPLORE TAB */}
        {tab === "explore" && (
          <ExploreTab
            entries={allEntries}
            friendState={friendState}
            savedTrips={savedTrips}
            savedBookmarks={savedBookmarks}
            onToggleBookmark={handleToggleBookmark}
            onAddToTrip={async (tripId, entry) => {
              const prevTrip = savedTrips.find(t => t.id === tripId);
              if (!prevTrip) return;
              const updatedDays = prevTrip.plan?.days ? [...prevTrip.plan.days] : [];
              if (updatedDays.length === 0) updatedDays.push({ day: 1, city: prevTrip.destination, entries: [], activity: "" });
              updatedDays[0] = { ...updatedDays[0], entries: [...updatedDays[0].entries, entry] };
              const updatedTrip = { ...prevTrip, plan: { ...prevTrip.plan, days: updatedDays } };
              const ok = await saveTripToDb(updatedTrip, user?.id, prevTrip, reloadTripData);
              if (ok) setSavedTrips(prev => prev.map(t => t.id === tripId ? updatedTrip : t));
            }}
            onViewProfile={setViewProfile}
            currentUserId={currentUser.id}
            friendProfiles={friendProfiles}
            onSaveToIdeas={async (tripId, placeName) => {
              const prevTrip = savedTrips.find(t => t.id === tripId);
              if (!prevTrip) return;
              const updatedTrip = { ...prevTrip, ideas: [...(prevTrip.ideas || []), { id: "idea_" + Date.now(), name: placeName, type: "", notes: "" }] };
              const ok = await saveTripToDb(updatedTrip, user?.id, prevTrip, reloadTripData);
              if (ok) {
                setSavedTrips(prev => prev.map(t => t.id === tripId ? updatedTrip : t));
                showToast(`💡 Added "${placeName}" to ideas`, "success");
              }
            }}
          />
        )}

        {/* MY LOGS TAB */}
        {tab === "mine" && (
          <MyLogsTab
            entries={allEntries}
            currentUser={currentUser}
            savedTrips={savedTrips}
            setEntries={setEntries}
            setEditingEntry={setEditingEntry}
            showImport={showImport}
            setShowImport={setShowImport}
            onEditProfile={() => setShowEditProfile(true)}
            onGoToTrips={() => setTab("trips")}
            savedBookmarks={savedBookmarks}
            onToggleBookmark={handleToggleBookmark}
            hasMoreEntries={hasMoreEntries}
            loadMoreEntries={loadMoreEntries}
            loadingMore={loadingMore}
            friendProfiles={friendProfiles}
          />
        )}

        {/* MY TRIPS TAB */}
        {tab === "trips" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 24,
            background: "#faf7f2", margin: "-20px -16px", padding: "20px 16px 80px", minHeight: "100vh" }}>
            {importToLogsMsg && (
              <div style={{ background: "#000", color: "#fff", borderRadius: 8, padding: "12px 16px",
                fontSize: 13, textAlign: "center", fontFamily: "'DM Sans', sans-serif" }}>
                {importToLogsMsg}
              </div>
            )}

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ paddingTop: 4 }}>
                <div style={{ lineHeight: 1.1 }}>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400,
                    fontSize: 30, color: "#1a0a00" }}>My </span>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic",
                    fontWeight: 600, fontSize: 30, color: "#8b2a2a" }}>Trips</span>
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#a09080",
                  marginTop: 4, fontStyle: "italic" }}>Plan, track and share your travels</div>
              </div>
              <button onClick={() => setShowTripModal(true)}
                style={{ background: "#8b2a2a", border: "none", borderRadius: 8,
                  padding: "10px 16px", color: "#fff", fontSize: 12, fontWeight: 700,
                  cursor: "pointer", flexShrink: 0, marginTop: 6,
                  fontFamily: "'DM Sans', sans-serif",
                  boxShadow: "0 4px 14px rgba(139,42,42,0.4)" }}>
                ✦ Plan Trip
              </button>
            </div>

            {/* Stat counter tiles */}
            {(() => {
              const activeCount = savedTrips.filter(t => !t.completed && !t.draft).length;
              const ideasCount = savedTrips.filter(t => t.draft).length;
              const pastCount = savedTrips.filter(t => t.completed).length;
              return (
                <div style={{ display: "flex", gap: 10, padding: "0 0" }}>
                  {[
                    { label: "Active", value: activeCount, view: "active" },
                    { label: "Ideas", value: ideasCount, view: "ideas" },
                    { label: "Past", value: pastCount, view: "past" },
                  ].map(s => (
                    <div key={s.view} onClick={() => setTripsView(s.view)}
                      style={{ flex: 1, background: tripsView === s.view ? "#8b2a2a" : "#fff",
                        border: tripsView === s.view ? "1px solid #8b2a2a" : "1px solid #e8e0d4",
                        borderRadius: 10, padding: "16px 12px", textAlign: "center",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)", cursor: "pointer",
                        transition: "all 0.15s" }}>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 700,
                        color: tripsView === s.view ? "#fff" : "#1a0a00" }}>{s.value}</div>
                      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 9,
                        color: tripsView === s.view ? "rgba(255,255,255,0.8)" : "#a09080",
                        marginTop: 4, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* ── ACTIVE VIEW ── */}
            {tripsView === "active" && (
              <>
                <div>
                  {savedTrips.filter(t => !t.completed && !t.draft).length === 0 ? (
                    <div style={{ background: "#fafafa", border: "1px dashed #e8e8e8", borderRadius: 16, padding: "48px 20px", textAlign: "center" }}>
                      <div style={{ fontSize: 44, marginBottom: 12 }}>✈️</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#555", marginBottom: 4 }}>No trips planned yet</div>
                      <div style={{ fontSize: 13, color: "#aaa" }}>Tap <strong>+ Plan Trip</strong> to start your next adventure</div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {savedTrips.filter(t => !t.completed && !t.draft).map(trip => (
                        <TripCard key={trip.id} trip={trip} entries={allEntries}
                          leaveMode={!!trip.isShared}
                          onDelete={async () => {
                            if (trip.isShared) {
                              // Collaborator/viewer: leave the trip (remove membership), don't delete
                              const { error } = await supabase.from("trip_members").delete().eq("trip_id", trip.id).eq("user_id", user.id);
                              if (error) { showToast("Couldn't leave trip. Try again."); return; }
                              if (sharedTripChannels.current[trip.id]) {
                                supabase.removeChannel(sharedTripChannels.current[trip.id]);
                                delete sharedTripChannels.current[trip.id];
                              }
                              setSavedTrips(prev => prev.filter(t => t.id !== trip.id));
                            } else {
                              const { error } = await supabase.from("trips").delete().eq("id", trip.id);
                              if (error) { showToast("Couldn't delete trip. Try again."); return; }
                              setSavedTrips(prev => prev.filter(t => t.id !== trip.id));
                            }
                          }}
                          onComplete={async coverPhoto => { const { error } = await supabase.from("trips").update({ completed: true, cover_photo: coverPhoto || null }).eq("id", trip.id); if (error) { showToast("Couldn't mark trip complete. Try again."); return; } setSavedTrips(prev => prev.map(t => t.id === trip.id ? { ...t, completed: true, ...(coverPhoto ? { coverPhoto } : {}) } : t)); showToast("Trip moved to Past Trips!"); }}
                          onInviteFriend={async (uid, role = "collaborator") => {
                            const already = (trip.invitedFriends || []).includes(uid) || (trip.sharedWith || []).includes(uid);
                            if (already) {
                              const { error } = await supabase.from("trip_members").delete().eq("trip_id", trip.id).eq("user_id", uid);
                              if (error) { console.error("Remove member error:", error); return; }
                              setInvitesSent(prev => { const next = prev.filter(x => !(x.tripId === trip.id && x.userId === uid)); try { localStorage.setItem("ts_invites_sent", JSON.stringify(next)); } catch {} return next; });
                            } else {
                              const { error } = await supabase.from("trip_members").upsert({ trip_id: trip.id, user_id: uid, role }, { onConflict: "trip_id,user_id" });
                              if (error) { console.error("Add member error:", error); return; }
                              const friend = friendProfiles.find(u => u.id === uid);
                              showToast(`✓ ${friend?.name || "Friend"} invited with ${role === "collaborator" ? "full access" : "read-only"} access`, "success");
                              setInvitesSent(prev => {
                                if (prev.some(x => x.tripId === trip.id && x.userId === uid)) return prev;
                                const next = [...prev, { tripId: trip.id, userId: uid, userName: friend?.name || "Friend", tripDest: trip.destination }];
                                try { localStorage.setItem("ts_invites_sent", JSON.stringify(next)); } catch {} return next;
                              });
                            }
                            const { data: members } = await supabase.from("trip_members").select("user_id, role").eq("trip_id", trip.id);
                            if (members) {
                              setSavedTrips(prev => prev.map(t => t.id === trip.id ? { ...t,
                                invitedFriends: members.filter(m => m.role === "collaborator").map(m => m.user_id),
                                sharedWith: members.filter(m => m.role === "viewer").map(m => m.user_id),
                              } : t));
                            }
                          }}
                          onAddPlaces={() => setShowTripModal(true)}
                          onUpdate={async updated => { const prevTrip = savedTrips.find(t => t.id === updated.id); savingTripIds.current.add(updated.id); setSavedTrips(ts => ts.map(t => t.id === updated.id ? { ...updated, isShared: t.isShared, memberRole: t.memberRole } : t)); const ok = await saveTripToDb(updated, user?.id, prevTrip, reloadTripData); savingTripIds.current.delete(updated.id); if (!ok) alert("Couldn't save changes. Check your connection and try again."); }}
                          onEdit={() => setEditingTrip(trip)}
                          focusSection={focusTripSection?.tripId === trip.id ? focusTripSection.section : null}
                          onFocusHandled={() => setFocusTripSection(null)}
                          onShare={async userId => {
                            const friend = friendProfiles.find(u => u.id === userId);
                            const wasShared = (trip.sharedWith || []).includes(userId);
                            if (wasShared) {
                              const { error } = await supabase.from("trip_members").delete().eq("trip_id", trip.id).eq("user_id", userId).eq("role", "viewer");
                              if (error) { console.error("Unshare trip error:", error); return; }
                            } else {
                              const { error } = await supabase.from("trip_members").upsert({ trip_id: trip.id, user_id: userId, role: "viewer" }, { onConflict: "trip_id,user_id" });
                              if (error) { console.error("Share trip error:", error); return; }
                            }
                            const { data: members } = await supabase.from("trip_members").select("user_id, role").eq("trip_id", trip.id);
                            if (members) {
                              setSavedTrips(prev => prev.map(t => t.id !== trip.id ? t : { ...t,
                                invitedFriends: members.filter(m => m.role === "collaborator").map(m => m.user_id),
                                sharedWith: members.filter(m => m.role === "viewer").map(m => m.user_id),
                              }));
                            }
                            if (!wasShared) {
                              setImportToLogsMsg(`✓ Trip shared with ${friend?.name || "friend"}!`);
                              setTimeout(() => setImportToLogsMsg(""), 3000);
                            }
                          }}
                          onImportToLogs={() => importTripToLogs(trip, setEntries, setImportToLogsMsg, user?.id)}
                          onImportSingle={entry => setPrefillEntry(entry)}
                          onDuplicate={async () => {
                            const copy = { ...trip, id: "t" + Date.now(), destination: trip.destination + " (Copy)",
                              completed: false, createdAt: new Date().toLocaleDateString(),
                              invitedFriends: [], sharedWith: [] };
                            const dbId = await saveTripToDb(copy, user?.id);
                            if (dbId) { setSavedTrips(prev => [{ ...copy, id: dbId }, ...prev]); showToast("Trip duplicated!", "success"); }
                            else showToast("Couldn't duplicate trip. Try again.");
                          }}
                          friendState={friendState}
                          allUsers={friendProfiles}
                          onViewProfile={setViewProfile}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {sharedTrips.length > 0 && (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <div style={{ width: 28, height: 1, background: "#c4956a", flexShrink: 0 }} />
                      <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 12, fontWeight: 600,
                        color: "#a09080", textTransform: "uppercase", letterSpacing: "0.14em" }}>
                        Shared With You
                      </span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {sharedTrips.map(trip => {
                        const isCollaborator = trip.memberRole === "collaborator";
                        return (
                          <TripCard key={trip.id} trip={trip} entries={allEntries}
                            allUsers={friendProfiles}
                            friendState={friendState}
                            onViewProfile={setViewProfile}
                            onImportToLogs={entry => setPrefillEntry(entry)}
                            onImportSingle={entry => setPrefillEntry(entry)}
                            leaveMode
                            onDelete={async () => {
                              const { error } = await supabase.from("trip_members").delete()
                                .eq("trip_id", trip.id).eq("user_id", user.id);
                              if (error) { showToast("Couldn't leave trip. Try again."); return; }
                              setSharedTrips(prev => prev.filter(t => t.id !== trip.id));
                              knownSharedTripIds.current.delete(trip.id);
                              markNotifsRead(new Set([...readNotifIds, "shared_" + trip.id]));
                              showToast("You've left the trip.", "success");
                            }}
                            onUpdate={isCollaborator ? async updated => {
                              const prevTrip = sharedTrips.find(t => t.id === updated.id);
                              savingTripIds.current.add(updated.id);
                              setSharedTrips(ts => ts.map(t => t.id === updated.id ? updated : t));
                              const ok = await saveTripToDb(updated, trip.sharedBy, prevTrip);
                              savingTripIds.current.delete(updated.id);
                              if (!ok) showToast("Couldn't save changes. Check your connection and try again.");
                            } : undefined}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── IDEAS VIEW ── */}
            {tripsView === "ideas" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Create new draft */}
                {(() => {
                  const createDraft = async () => {
                    const dest = newDraftDest.trim();
                    if (!dest) return;
                    const draft = { id: "t" + Date.now(), destination: dest, draft: true, ideas: [], completed: false,
                      plan: { days: [] }, packList: [], bookRemind: [], reservations: [], notes: "",
                      invitedFriends: [], sharedWith: [], createdAt: new Date().toLocaleDateString() };
                    const dbId = await saveTripToDb(draft, user?.id);
                    if (dbId) { setSavedTrips(prev => [{ ...draft, id: dbId }, ...prev]); setNewDraftDest(""); }
                    else showToast("Couldn't create draft. Try again.");
                  };
                  return (
                    <div>
                      <DestinationAutocomplete
                        value={newDraftDest}
                        onChange={setNewDraftDest}
                        onSelect={async label => {
                          const draft = { id: "t" + Date.now(), destination: label, draft: true, ideas: [], completed: false,
                            plan: { days: [] }, packList: [], bookRemind: [], reservations: [], notes: "",
                            invitedFriends: [], sharedWith: [], createdAt: new Date().toLocaleDateString() };
                          const dbId = await saveTripToDb(draft, user?.id);
                          if (dbId) { setSavedTrips(prev => [{ ...draft, id: dbId }, ...prev]); setNewDraftDest(""); }
                          else showToast("Couldn't create draft. Try again.");
                        }}
                        placeholder="Where are you thinking of going? (press Enter to add)"
                        style={{ width: "100%", border: "1.5px solid #e8e0d8", borderRadius: 12, padding: "11px 14px", fontSize: 14, fontFamily: "inherit", outline: "none", background: "#fafaf8", boxSizing: "border-box" }}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); createDraft(); } }}
                      />
                    </div>
                  );
                })()}

                {savedTrips.filter(t => t.draft).length === 0 ? (
                  <div style={{ background: "#fafafa", border: "1px dashed #e8e8e8", borderRadius: 16, padding: "48px 20px", textAlign: "center" }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>💡</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#555", marginBottom: 4 }}>No ideas yet</div>
                    <div style={{ fontSize: 13, color: "#aaa" }}>Enter a destination above to start collecting ideas before you plan</div>
                  </div>
                ) : (() => {
                  const drafts = savedTrips.filter(t => t.draft);
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {drafts.map(idea => {
                        const isSelected = idea.id === selectedIdeaId;
                        const placesCount = idea.ideas?.length || 0;
                        return (
                          <div key={idea.id}>
                            {/* Collapsed row */}
                            <div onClick={() => setSelectedIdeaId(isSelected ? null : idea.id)}
                              style={{ background: "#fff", border: isSelected ? "2px solid #7B2D42" : "1px solid #e8e0d4",
                                borderRadius: isSelected ? "14px 14px 0 0" : 14, overflow: "hidden", cursor: "pointer",
                                boxShadow: isSelected ? "0 2px 12px rgba(123,45,66,0.12)" : "0 1px 4px rgba(0,0,0,0.06)",
                                transition: "all 0.15s", display: "flex", height: 76 }}>
                              <div style={{ width: 76, flexShrink: 0, position: "relative", overflow: "hidden",
                                background: getIdeaGradient(idea.destination),
                                display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {idea.coverPhoto
                                  ? <img src={idea.coverPhoto} alt={idea.destination}
                                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                                  : <div style={{ fontSize: 30, opacity: 0.9 }}>{getDestEmoji(idea.destination)}</div>
                                }
                              </div>
                              <div style={{ flex: 1, padding: "10px 14px", display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16,
                                  fontWeight: 600, color: "#1a0a00", lineHeight: 1.2, marginBottom: 3,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {idea.destination.split(",")[0]}
                                </div>
                                <div style={{ fontSize: 11, color: "#a09080", fontFamily: "'DM Sans', sans-serif" }}>
                                  📍 {placesCount} place{placesCount !== 1 ? "s" : ""} saved
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", paddingRight: 14, color: "#ccc", fontSize: 13,
                                transform: isSelected ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</div>
                            </div>
                            {/* Inline expanded detail */}
                            {isSelected && (
                              <div style={{ border: "2px solid #7B2D42", borderTop: "none", borderRadius: "0 0 14px 14px",
                                overflow: "hidden", background: "#fff" }}>
                                <DraftTripCard key={idea.id} trip={idea} inline
                                  onUpdate={async updated => { const prevTrip = savedTrips.find(t => t.id === updated.id); const ok = await saveTripToDb(updated, user?.id, prevTrip, reloadTripData); if (ok) setSavedTrips(ts => ts.map(t => t.id === updated.id ? updated : t)); }}
                                  onDelete={async id => { const { error } = await supabase.from("trips").delete().eq("id", id); if (error) { showToast("Couldn't delete draft. Try again."); return; } setSavedTrips(prev => prev.filter(t => t.id !== id)); setSelectedIdeaId(null); }}
                                  onBuildItinerary={draft => setEditingTrip(draft)}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* New idea row */}
                      <div onClick={() => document.querySelector('[placeholder*="Where are you thinking"]')?.focus()}
                        style={{ border: "2px dashed #e8e0d4", borderRadius: 14, display: "flex",
                          alignItems: "center", justifyContent: "center", cursor: "pointer",
                          padding: "16px", gap: 8, transition: "border-color 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "#c4956a"}
                        onMouseLeave={e => e.currentTarget.style.borderColor = "#e8e0d4"}>
                        <div style={{ fontSize: 20, color: "#c4956a" }}>＋</div>
                        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#a09080" }}>Add a new destination idea</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── PAST VIEW ── */}
            {tripsView === "past" && (
              <div>
                {savedTrips.filter(t => t.completed).length === 0 ? (
                  <div style={{ background: "#fafafa", border: "1px dashed #e8e8e8", borderRadius: 16, padding: "48px 20px", textAlign: "center" }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>🗺</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#555", marginBottom: 4 }}>No past trips yet</div>
                    <div style={{ fontSize: 13, color: "#aaa" }}>Completed trips will appear here</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {savedTrips.filter(t => t.completed).map(trip => (
                      <TripCard key={trip.id} trip={trip} entries={allEntries}
                        leaveMode={!!trip.isShared}
                        onDelete={async () => {
                          if (trip.isShared) {
                            const { error } = await supabase.from("trip_members").delete().eq("trip_id", trip.id).eq("user_id", user.id);
                            if (error) { showToast("Couldn't leave trip. Try again."); return; }
                            setSavedTrips(prev => prev.filter(t => t.id !== trip.id));
                          } else {
                            const { error } = await supabase.from("trips").delete().eq("id", trip.id);
                            if (error) { showToast("Couldn't delete trip. Try again."); return; }
                            setSavedTrips(prev => prev.filter(t => t.id !== trip.id));
                          }
                        }}
                        past
                        onUpdate={trip.isShared ? undefined : async updated => { const prevTrip = savedTrips.find(t => t.id === updated.id); savingTripIds.current.add(updated.id); setSavedTrips(ts => ts.map(t => t.id === updated.id ? updated : t)); const ok = await saveTripToDb(updated, user?.id, prevTrip, reloadTripData); savingTripIds.current.delete(updated.id); if (!ok) showToast("Couldn't save changes. Check your connection and try again."); }}
                        onReactivate={trip.isShared ? undefined : async () => { const { error } = await supabase.from("trips").update({ completed: false }).eq("id", trip.id); if (error) { showToast("Couldn't reactivate trip. Try again."); return; } setSavedTrips(prev => prev.map(t => t.id === trip.id ? {...t, completed: false} : t)); }}
                        onImportToLogs={() => importTripToLogs(trip, setEntries, setImportToLogsMsg, user?.id)}
                        onImportSingle={entry => setPrefillEntry(entry)}
                        onShare={trip.isShared ? undefined : async userId => {
                          const friend = friendProfiles.find(u => u.id === userId);
                          const wasShared = (trip.sharedWith || []).includes(userId);
                          if (wasShared) {
                            const { error } = await supabase.from("trip_members").delete().eq("trip_id", trip.id).eq("user_id", userId).eq("role", "viewer");
                            if (error) { console.error("Unshare trip error:", error); return; }
                          } else {
                            const { error } = await supabase.from("trip_members").upsert({ trip_id: trip.id, user_id: userId, role: "viewer" }, { onConflict: "trip_id,user_id" });
                            if (error) { console.error("Share trip error:", error); return; }
                          }
                          setSavedTrips(prev => prev.map(t => t.id !== trip.id ? t : {
                            ...t, sharedWith: wasShared ? (t.sharedWith || []).filter(x => x !== userId) : [...new Set([...(t.sharedWith || []), userId])]
                          }));
                          if (!wasShared) {
                            setImportToLogsMsg(`✓ Trip shared with ${friend?.name || "friend"}!`);
                            setTimeout(() => setImportToLogsMsg(""), 3000);
                          }
                        }}
                        onDuplicate={trip.isShared ? undefined : async () => {
                          const { isShared: _s, memberRole: _r, sharedBy: _b, ...rest } = trip;
                          const copy = { ...rest, id: "t" + Date.now(), destination: trip.destination + " (Copy)",
                            completed: false, createdAt: new Date().toLocaleDateString(),
                            invitedFriends: [], sharedWith: [] };
                          const dbId = await saveTripToDb(copy, user?.id);
                          if (dbId) { setSavedTrips(prev => [{ ...copy, id: dbId }, ...prev]); showToast("Trip duplicated!", "success"); }
                          else showToast("Couldn't duplicate trip. Try again.");
                        }}
                        onCopyToCurrentTrip={trip.isShared ? async () => {
                          const { isShared: _s, memberRole: _r, sharedBy: _b, ...rest } = trip;
                          const copy = { ...rest, id: "t" + Date.now(), destination: trip.destination,
                            completed: false, createdAt: new Date().toLocaleDateString(),
                            invitedFriends: [], sharedWith: [] };
                          const dbId = await saveTripToDb(copy, user?.id);
                          if (dbId) { setSavedTrips(prev => [{ ...copy, id: dbId }, ...prev]); showToast("Trip copied to your current trips!", "success"); }
                          else showToast("Couldn't copy trip. Try again.");
                        } : undefined}
                        friendState={friendState}
                        allUsers={friendProfiles}
                        onViewProfile={setViewProfile}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showEditProfile && <EditProfileModal user={currentUser} onClose={() => setShowEditProfile(false)} onSave={updated => { setCurrentUser(updated); setShowEditProfile(false); }} />}
      {showAddModal && <AddEntryModal key="add-entry-modal" userId={user?.id} onClose={() => setShowAddModal(false)} onSave={async e => {
        const { data, error } = await supabase.from("entries").insert(localEntryToDb(e, user?.id)).select().single();
        if (!error && data) { setEntries(prev => [dbEntryToLocal(data), ...prev]); return true; }
        return false;
      }} entries={entries} />}
      {prefillEntry && <AddEntryModal key={prefillEntry.id || "prefill"} userId={user?.id} prefill={prefillEntry} onClose={() => setPrefillEntry(null)} onSave={async e => {
        const { data, error } = await supabase.from("entries").insert(localEntryToDb(e, user?.id)).select().single();
        if (!error && data) { setEntries(prev => [dbEntryToLocal(data), ...prev]); showToast("Saved to My Logs ✓", "success"); return true; }
        return false;
      }} entries={entries} />}
      {editingEntry && <EditEntryModal entry={editingEntry} userId={user?.id} onClose={() => setEditingEntry(null)} onSave={async updated => {
        const { data, error } = await supabase.from("entries").update(localEntryToDb(updated, user?.id)).eq("id", updated.id).select().single();
        if (!error && data) { setEntries(prev => prev.map(e => e.id === updated.id ? dbEntryToLocal(data) : e)); return true; }
        return false;
      }} onDelete={async () => {
        const { error } = await supabase.from("entries").delete().eq("id", editingEntry.id);
        if (error) { showToast("Couldn't delete entry. Try again."); return; }
        setEntries(prev => prev.filter(e => e.id !== editingEntry.id));
        setEditingEntry(null);
      }} />}
      {showNetwork && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200 }}>
          <div onClick={() => setShowNetwork(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "min(380px, 95vw)", background: "#fff", overflowY: "auto", overscrollBehavior: "contain", boxShadow: "-4px 0 20px rgba(0,0,0,0.15)" }}>
            <div style={{ padding: "20px 16px", borderBottom: "1px solid #efefef", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#000" }}>Network</div>
              <button onClick={() => setShowNetwork(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#aaa" }}>×</button>
            </div>
            <div style={{ padding: "16px" }}>
              <NetworkTab
                entries={allEntries}
                onViewProfile={id => { setViewProfile(id); setShowNetwork(false); }}
                friendState={friendState}
                setFriendState={setFriendState}
                pendingIncoming={pendingIncoming}
                setPendingIncoming={setPendingIncoming}
                currentUser={currentUser}
                friendProfiles={friendProfiles}
                setFriendProfiles={setFriendProfiles}
              />
            </div>
          </div>
        </div>
      )}
      {(showTripModal || editingTrip) && <TripPlannerModal entries={allEntries}
        onClose={() => { setShowTripModal(false); setEditingTrip(null); }}
        savedTrips={savedTrips}
        editTrip={editingTrip || null}
        onSaveTrip={async trip => {
          const dbId = await saveTripToDb(trip, user?.id);
          if (!dbId) { alert("Failed to save trip. Please check your connection and try again."); return; }
          setSavedTrips(prev => [{ ...trip, id: dbId }, ...prev]);
        }}
        onUpdateTrip={async updated => {
          const wasDraft = editingTrip?.draft;
          const final = wasDraft ? { ...updated, draft: false } : updated;
          const ok = await saveTripToDb(final, user?.id);
          if (!ok) { alert("Failed to save trip changes. Please check your connection and try again."); return; }
          setSavedTrips(prev => prev.map(t => t.id === final.id ? final : t));
          if (wasDraft) showToast("Itinerary built! Find it in Active Trips.");
          setEditingTrip(null);
        }} />}

      {/* Floating Log button — mobile only, only on Home and My Logs tabs */}
      {(tab === "home" || tab === "mine") && (
        <button className="ts-fab" onClick={() => setShowAddModal(true)}>
          ＋ Log
        </button>
      )}

    </div>
  );
}

// ─── TOAST ───────────────────────────────────────────────────────────────────
let _toastFn = null;
export function showToast(msg, type = "error", duration = 4000) { _toastFn?.(msg, type, duration); }

function ToastProvider() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    _toastFn = (msg, type, duration = 4000) => {
      const id = Date.now() + Math.random();
      setToasts(t => [...t, { id, msg, type }]);
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), duration);
    };
    return () => { _toastFn = null; };
  }, []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 9999, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", pointerEvents: "none" }}>
      {toasts.map(t => (
        <div key={t.id} style={{ background: t.type === "success" ? "#16a34a" : t.type === "info" ? "#0f172a" : "#cc0000", color: "#fff", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, boxShadow: "0 4px 16px rgba(0,0,0,0.18)", maxWidth: 320, textAlign: "center" }}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ─── LANDING PAGE ───────────────────────────────────────────────────────────
function LandingPage({ onGetStarted, onLogin, onGoogleLogin, googleLoading, authError }) {
  return (
    <div style={{ minHeight: "100vh", background: "#ffffff", fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
      <style>{`
        * { box-sizing: border-box; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .auth-btn:hover:not(:disabled) { opacity: 0.85; }
      `}</style>

      <div style={{ width: "100%", maxWidth: 380, animation: "fadeUp 0.5s ease-out", textAlign: "center" }}>
        {/* Logo */}
        <div style={{ marginBottom: 8, fontSize: 36 }}>🗺</div>
        <div style={{ fontSize: 32, fontWeight: 700, color: "#000", letterSpacing: "-0.03em", marginBottom: 6 }}>Travel Slate</div>
        <div style={{ fontSize: 15, color: "#888", marginBottom: 40, lineHeight: 1.5 }}>
          Travel recs from people you actually trust
        </div>

        {/* Auth buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Google */}
          <button className="auth-btn" onClick={onGoogleLogin} disabled={googleLoading}
            style={{ width: "100%", background: "#fff", border: "1px solid #dbdbdb", borderRadius: 8,
              padding: "13px 16px", fontSize: 15, fontWeight: 500, cursor: googleLoading ? "wait" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              color: "#000", transition: "opacity 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              opacity: googleLoading ? 0.6 : 1 }}>
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.9 33.4 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.9 18.9 13 24 13c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.3 0-9.8-3.6-11.3-8.5l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.7-.4-3.9z"/>
            </svg>
            {googleLoading ? "Redirecting…" : "Continue with Google"}
          </button>

          {authError && (
            <div style={{ background: "#fff0f0", border: "1px solid #ffd0d0", borderRadius: 8, padding: "10px 14px", color: "#cc0000", fontSize: 13 }}>
              {authError}
            </div>
          )}

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
            <div style={{ flex: 1, height: 1, background: "#dbdbdb" }} />
            <span style={{ color: "#aaa", fontSize: 13, fontWeight: 500 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "#dbdbdb" }} />
          </div>

          {/* Sign Up */}
          <button className="auth-btn" onClick={onGetStarted}
            style={{ width: "100%", background: "#000", border: "none", borderRadius: 8,
              padding: "13px 16px", fontSize: 15, fontWeight: 600, cursor: "pointer",
              color: "#fff", transition: "opacity 0.2s" }}>
            Sign up with email
          </button>

          {/* Sign In */}
          <button className="auth-btn" onClick={onLogin}
            style={{ width: "100%", background: "transparent", border: "1px solid #dbdbdb", borderRadius: 8,
              padding: "13px 16px", fontSize: 15, fontWeight: 500, cursor: "pointer",
              color: "#000", transition: "opacity 0.2s" }}>
            Log in
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── AUTH SCREEN ─────────────────────────────────────────────────────────────
function AuthScreen({ mode: initialMode, onAuth, onBack }) {
  const [mode, setMode] = useState(initialMode || "signup");
  const [form, setForm] = useState({ email: "", password: "", name: "", inviteCode: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleForgotPassword = async () => {
    if (!form.email) { setError("Enter your email above first."); return; }
    setLoading(true); setError("");
    const { error } = await supabase.auth.resetPasswordForEmail(form.email, { redirectTo: window.location.origin + "?reset=1" });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setResetSent(true);
  };

  const handleSubmit = async () => {
    setError("");
    if (mode === "signup") {
      if (!form.email || !form.password || !form.name) { setError("Please fill in all fields."); return; }
      if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    } else {
      if (!form.email || !form.password) { setError("Please enter your email and password."); return; }
    }
    setLoading(true);
    if (mode === "signup") {
      const username = form.email.split("@")[0].toLowerCase().replace(/[^a-z0-9_]/g, "_");
      const { data, error } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: { data: { name: form.name.trim(), username } }
      });
      if (error) { setError(error.message); setLoading(false); return; }
      if (data.session) {
        onAuth(data.session.user);
      } else {
        setError("Check your email to confirm your account, then sign in.");
      }
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
      if (error) { setError(error.message); setLoading(false); return; }
      onAuth(data.session.user);
    }
    setLoading(false);
  };

  const inp = (field, label, placeholder, type = "text") => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</label>
      <input type={type} value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        placeholder={placeholder}
        style={{ width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
          padding: "11px 12px", color: "#000", fontSize: 14,
          marginTop: 5, boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" }}
        onFocus={e => e.target.style.borderColor = "#000"}
        onBlur={e => e.target.style.borderColor = "#efefef"}
      />
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#fff", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <style>{`
        
        * { box-sizing: border-box; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div style={{ width: "100%", maxWidth: 420, animation: "fadeUp 0.5s ease-out" }}>
        <button onClick={onBack}
          style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 19, fontWeight: 600, padding: "4px 0", marginBottom: 24 }}>
          Back
        </button>

        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#000", letterSpacing: "-0.03em" }}>Travel Slate</div>
          <div style={{ color: "#444", fontSize: 10, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.12em", marginTop: 4 }}>
            {mode === "signup" ? "JOIN THE BETA" : "WELCOME BACK"}
          </div>
        </div>

        {/* Mode toggle */}
        <div style={{ display: "flex", gap: 0, marginBottom: 24, background: "#f0f0f0", borderRadius: 8, padding: 3 }}>
          {[["signup", "Sign Up"], ["login", "Sign In"]].map(([id, label]) => (
            <button key={id} onClick={() => { setMode(id); setError(""); setResetSent(false); }}
              style={{ flex: 1, background: mode === id ? "#000" : "transparent",
                border: "none", borderRadius: 6, padding: "10px", cursor: "pointer",
                color: mode === id ? "#fff" : "#555",
                fontSize: 13, fontFamily: "'DM Sans', sans-serif", fontWeight: mode === id ? 700 : 400,
                transition: "all 0.2s" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Google Sign-in */}
        <button disabled={loading}
          onClick={async () => { setLoading(true); const { error } = await supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: window.location.origin } }); if (error) { setError(error.message); setLoading(false); } }}
          style={{ width: "100%", background: "#fff", border: "1px solid #dbdbdb", borderRadius: 10, padding: "11px 16px",
            color: "#000", fontSize: 14, cursor: loading ? "wait" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)", opacity: loading ? 0.6 : 1 }}>
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.9 33.4 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.9 18.9 13 24 13c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.3 0-9.8-3.6-11.3-8.5l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.7-.4-3.9z"/></svg>
          {loading ? "Redirecting…" : "Continue with Google"}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: "#efefef" }} />
          <span style={{ color: "#aaa", fontSize: 11 }}>OR</span>
          <div style={{ flex: 1, height: 1, background: "#efefef" }} />
        </div>

        {mode === "signup" && inp("name", "Full Name", "Sandy Sapkota")}
        {inp("email", "Email", "you@email.com", "email")}
        {inp("password", "Password", mode === "signup" ? "At least 6 characters" : "Your password", "password")}

        {mode === "login" && !resetSent && (
          <div style={{ textAlign: "right", marginTop: -8, marginBottom: 12 }}>
            <span onClick={handleForgotPassword} style={{ color: "#555", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>
              Forgot password?
            </span>
          </div>
        )}

        {resetSent && (
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 14px", color: "#16a34a", fontSize: 13, marginBottom: 16 }}>
            Check your email — we sent a password reset link.
          </div>
        )}

        {error && (
          <div style={{ background: "#fff0f0", border: "1px solid #ffd0d0", borderRadius: 10, padding: "10px 14px",
            color: "#cc0000", fontSize: 13, fontFamily: "'DM Sans', sans-serif", marginBottom: 16 }}>
            {error}
          </div>
        )}

        {!resetSent && (
          <button onClick={handleSubmit} disabled={loading}
            style={{ width: "100%", background: loading ? "#555" : "#000",
              border: "none", borderRadius: 10, padding: "14px", color: "#fff",
              fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif",
              opacity: loading ? 0.7 : 1 }}>
            {loading ? "Authenticating…" : mode === "signup" ? "Create Account →" : "Sign In →"}
          </button>
        )}

      </div>
    </div>
  );
}

// ─── RESET PASSWORD SCREEN ──────────────────────────────────────────────────
function ResetPasswordScreen({ onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async () => {
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true); setError("");
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) { setError(error.message); return; }
    setDone(true);
    setTimeout(onDone, 1500);
  };

  const inputStyle = { width: "100%", background: "#f8f8f8", border: "1px solid #efefef", borderRadius: 10,
    padding: "11px 12px", color: "#000", fontSize: 14, marginTop: 5, boxSizing: "border-box", outline: "none" };
  const labelStyle = { color: "#666", fontSize: 11, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" };

  return (
    <div style={{ minHeight: "100vh", background: "#fff", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#000", letterSpacing: "-0.03em" }}>Travel Slate</div>
          <div style={{ color: "#444", fontSize: 10, fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.12em", marginTop: 4 }}>SET NEW PASSWORD</div>
        </div>
        {done ? (
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "14px", color: "#16a34a", fontSize: 14, textAlign: "center" }}>
            Password updated! Signing you in…
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>New Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 6 characters" style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#000"} onBlur={e => e.target.style.borderColor = "#efefef"} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Confirm Password</label>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Same password again" style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#000"} onBlur={e => e.target.style.borderColor = "#efefef"} />
            </div>
            {error && (
              <div style={{ background: "#fff0f0", border: "1px solid #ffd0d0", borderRadius: 10, padding: "10px 14px",
                color: "#cc0000", fontSize: 13, fontFamily: "'DM Sans', sans-serif", marginBottom: 16 }}>
                {error}
              </div>
            )}
            <button onClick={handleReset} disabled={loading}
              style={{ width: "100%", background: loading ? "#555" : "#000", border: "none", borderRadius: 10,
                padding: "14px", color: "#fff", fontSize: 15, fontWeight: 700,
                cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif", opacity: loading ? 0.7 : 1 }}>
              {loading ? "Saving…" : "Set New Password →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ROOT APP (AUTH GATE) ───────────────────────────────────────────────────
export default function App() {
  const [authUser, setAuthUser] = useState(null);
  const [screen, setScreen] = useState("landing");
  const [sessionLoading, setSessionLoading] = useState(true);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) { setAuthUser(session.user); setScreen("app"); }
      setSessionLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setScreen("reset-password");
      } else if (session?.user) {
        setAuthUser(session.user); setScreen("app");
      } else {
        setAuthUser(null); setScreen("landing");
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true); setAuthError("");
    const { error } = await supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: window.location.origin } });
    if (error) { setAuthError(error.message); setGoogleLoading(false); }
    // on success the browser redirects; googleLoading stays true until navigation
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAuthUser(null);
    setScreen("landing");
  };

  if (sessionLoading) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa", fontSize: 28 }}>✈️</div>;
  }

  if (authUser && screen === "app") {
    return <><MainApp user={authUser} onLogout={handleLogout} /><ToastProvider /></>;
  }

  if (screen === "reset-password") {
    return <><ResetPasswordScreen onDone={() => { supabase.auth.getSession().then(({ data: { session } }) => { if (session?.user) { setAuthUser(session.user); setScreen("app"); } else setScreen("landing"); }); }} /><ToastProvider /></>;
  }

  if (screen === "auth-signup" || screen === "auth-login") {
    return (
      <><AuthScreen
        mode={screen === "auth-signup" ? "signup" : "login"}
        onAuth={(user) => { setAuthUser(user); setScreen("app"); }}
        onBack={() => setScreen("landing")}
      /><ToastProvider /></>
    );
  }

  return (
    <><LandingPage
      onGetStarted={() => setScreen("auth-signup")}
      onLogin={() => setScreen("auth-login")}
      onGoogleLogin={handleGoogleLogin}
      googleLoading={googleLoading}
      authError={authError}
    /><ToastProvider /></>
  );
}
